import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import "./App.css"; // Import the CSS file
import "./Fire.css"; // Import the CSS file
import "./Modal.css"; // Import the CSS file
import { AuthProvider, useAuth } from "./AuthContext";
import AuthModal from "./components/AuthModal";
import { useTranslation } from "react-i18next";
import { apiFetch, getLang } from "./api";
import { fireCarry } from "./guestCarry";
import { readCached, remember } from "./friendCode";
import { applyOverview } from "./groupOverview";
import { notifTime } from "./notifTime";
import { applyProgress } from "./archiveProgress";
import { hydrationFromState, adoptSolvedWord, clearGameCache, prenesenePokusy, percentFromSimilarity } from "./gameStorage";
import { medalFor, winsSummary, todayIso, groupByMonth, winTimeline, trophyStyle } from "./wins";
import OutageNotice from "./OutageNotice";
import { lastPlayedInfo } from "./activity";
import { pluralize, pluralCategory } from "./plurals";
import { compareStandings } from "./standings";
import Msg, { ok as okMsg } from "./Msg";
import { watchKeyboard } from "./keyboard";
import { PlayIcon, TrophyIcon, TrophyFilled, LockIcon, CrownIcon, ChevronRight, FacebookIcon, DiscordIcon } from "./icons";
import { readConsent, writeConsent } from "./consent";
import ArchiveCalendar from "./ArchiveCalendar";
import GuessBoard from "./GuessBoard";
import { premiumBenefitRows } from "./premiumBenefits";
import { fetchScoring } from "./scoringRetry";
import TemaReminder from "./TemaReminder";
import MegaOsobnostBox from "./MegaOsobnostBox";

// Líně: kód pokladny (a Stripe) potřebuje jen de při nákupu.
const OwnCheckout = lazy(() => import("./OwnCheckout"));

// Do kdy se správcům ukazuje box „Novinka" o světě skupiny. Potom blok smazat.
const TEMA_NOVINKA_DO = new Date("2026-11-16T00:00:00");


// Footer language switcher: each enabled language's production domain (from the
// backend registry) with its flag + localized brand name.
const LANGUAGE_LINKS = [
  { code: "cs", flag: "🇨🇿", domain: "prihorivahori.cz", name: "Přihořívá" },
  { code: "sk", flag: "🇸🇰", domain: "prihorieva.sk", name: "Prihorieva" },
  { code: "pl", flag: "🇵🇱", domain: "cieplogoraco.pl", name: "Ciepło Gorąco" },
  { code: "hr", flag: "🇭🇷", domain: "toplije.app", name: "Toplije" },
  { code: "de", flag: "🇩🇪", domain: "wirdwaermer.de", name: "Wird wärmer" },
];

// FAQ entries (each has faq.<key>.title / .content in the locales), rendered on
// the FAQ page in this order.
// The FAQ, in sections. Each entry needs faq.<key>.title / .content in the
// locale; an item whose title is missing is skipped, and a section left with no
// items disappears with it. That is what lets the Czech FAQ carry the whole
// social half — groups, friends, the limits per tier, the second game — while
// the other four markets keep the shorter one they already have. They do not
// sell Premium and their groups are not gated, so most of it would be untrue
// there; translate the strings and the sections appear on their own.
// Otázky o placení a limitech Premium. de je má přeložené (prodej se tam zapíná
// přepínačem), ale ukazují se jen, když se Premium na trhu opravdu prodává.
export const PAID_FAQ_ITEMS = new Set([
  "whatIsPremium", "premiumRenewal", "premiumEnds", "limitsTable", "friendsLimit", "megaLimit",
]);

const FAQ_SECTIONS = [
  {
    key: "play",
    // The first two keep their original keys: the same text is the game's own
    // "how to play" block elsewhere, and copying it here would give us two
    // versions of the rules to keep in step.
    items: [
      { title: "game.description.rulesTitle",
        content: ["game.description.howToPlayLine1", "game.description.howToPlayLine2"] },
      // Pořadí je pořadí zdrojového textu FAQ (24. 9. 2026), ne historie klíčů:
      // teplota → proč se chová divně → kolik mám pokusů → kdy se mění → jak se
      // dozvím slovo → překlepy. „Tipy pro úspěch" jsou schválně až na konci,
      // dávají smysl teprve po zbytku.
      "colors", "aiRating", "attemptsLimit", "attemptsCount", "frequency",
      "revealWord", "typos", "wordForms", "sameWordDiffers",
      { title: "game.description.tipsTitle", content: ["game.description.tipsContent"] },
    ],
  },
  { key: "account", items: ["whySignIn", "whatIsPremium", "premiumRenewal", "premiumEnds", "limitsTable"] },
  { key: "friends", items: ["friendsHow", "friendsLimit"] },
  { key: "groups", items: ["groupsWhat", "groupsCreate", "groupsJoin", "groupsSize", "groupsActivity", "groupsLeave"] },
  { key: "groupWords", items: ["groupWordWhat", "groupWordWho", "groupWordHowMany", "groupWordSleep", "groupWordStuck"] },
  { key: "mega", items: ["megaWhat", "megaLimit"] },
  { key: "archive", items: ["archiveWhat", "resumeWhat"] },
  { key: "anocine", items: ["anocineWhat", "anocineHow"] },
  { key: "other", items: ["history", "community"] },
];

// „Hauz a Hauz 2", „A, B a C" — spojka podle jazyka rozhraní. Intl.ListFormat
// to umí pro všech pět jazyků; kdyby chyběl, poslouží čárka.
export function seznamJmen(jmena, jazyk = getLang()) {
  const cista = (jmena || []).filter(Boolean);
  try {
    return new Intl.ListFormat(jazyk, { style: "long", type: "conjunction" }).format(cista);
  } catch (e) {
    return cista.join(", ");
  }
}

// Odstavce a tučné pasáže v odpovědi FAQ, bez HTML v překladech.
//
// Text odpovědi je JEDEN locale klíč. Dokud se sázel jako `<p>{t(key)}</p>`,
// zlomy `\n\n` se slily do mezery a z odpovědi o pěti odstavcích byla cihla —
// doplněno 24. 9. 2026 spolu s novým FAQ, které dlouhé odpovědi teprve přineslo.
//
// Tučné se značí `**takhle**`. Schválně ne `dangerouslySetInnerHTML`: překlady
// jsou text, ne šablona, a jediné nedopatření v nich by jinak vpustilo cizí
// značky na stránku. Tady se řetězec jen rozřeže a slepí z prvků.
const FAQ_BOLD = /\*\*(.+?)\*\*/g;
export function faqOdstavce(text) {
  return String(text || "")
    .split(/\n{2,}/)
    .map((o) => o.trim())
    .filter(Boolean)
    .map((odstavec) =>
      odstavec
        .split(FAQ_BOLD)
        // split s jednou skupinou vrací [text, uvnitř, text, uvnitř, …] —
        // liché indexy jsou to, co bylo mezi hvězdičkami.
        .map((kus, i) => (i % 2 ? { tucne: true, text: kus } : { tucne: false, text: kus }))
        .filter((k) => k.text !== ""),
    );
}

// Only used until /health answers — the real number comes from the backend,
// which reads it from the per-language settings.
const DEFAULT_MEGA_FREE_ATTEMPTS = 50;

// Same deal for how many groups one account may found: /health carries the real
// number, this is only the value used before it answers.
const DEFAULT_OWNED_GROUP_LIMIT = 3;

// The Denní/Megaslovo switch is persisted under `activeTab`, and THREE lists read
// it: the game board, the friends standings and a group's standings. Only the
// game board restored itself on a reload — the other two started at 'daily'
// whatever the switch said, because they were only ever synced by a click on the
// switch or by navigating to their page. Refreshing on the friends page with
// Megaslovo picked therefore showed the switch on Megaslovo and yesterday's DAILY
// results underneath it. Reported 2026-08-24 from a player's video.
//
// The friends page is the one that actually broke. A group's standings recover by
// accident — the only way onto that page is openGroup(), which re-derives the mode
// from activeTab — but they are seeded here too, because "recovers by accident" is
// one refactor away from the same bug, and this is the key the game board already
// reads.
const storedWordMode = (withGroup) => {
  const at = localStorage.getItem("activeTab") || "daily";
  if (withGroup && at === "group") return "group";
  return at === "weekly" ? "mega" : "daily";
};

// Did /user/claim_guest_win give an answer worth remembering? A win taken, or
// a definite no (nothing to claim, not in the log, too old). A dropped request,
// a non-200 or the server's own "error" leave the marker unset, so the next load
// asks again — until 15. 10. 2026 the marker was written BEFORE the request and
// one failed call lost the claim for good.
export const claimSettled = (reply) => !!reply && (reply.claimed || reply.reason !== "error");

// Stupně velikosti hádaných slov (Nastavení → Předvolby, menu účtu).
export const WORD_SCALES = [1, 1.3, 1.6];

// Generate or retrieve user ID
const getUserId = () => {
  let userId = localStorage.getItem('userId');
  if (!userId) {
    // Generate UUID v4
    userId = 'user_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now().toString(36);
    localStorage.setItem('userId', userId);
  }
  return userId;
};

// Get ISO week string e.g. "2026-W13"
export const getISOWeek = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
  const week1 = new Date(d.getFullYear(), 0, 4);
  return d.getFullYear() + '-W' +
    String(1 + Math.round(((d - week1) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7)).padStart(2, '0');
};

// The word of the day rotates at 03:00 Europe/Prague (cron.yaml). The local
// "game day"/"game week" must roll at the SAME instant — otherwise the board
// resets at the browser's midnight, up to three hours BEFORE the word actually
// changes, and wipes a still-valid solved game (and the mega-word link) while the
// same word is live. gameEpoch() returns Prague wall-clock shifted back by the
// rotation hour, so 00:00–03:00 Prague still maps to the previous day/week. Built
// via toLocaleString so it is correct for players in any timezone and follows
// Prague DST automatically.
export const ROTATION_HOUR = 3;
// "5 h 20 min" / "40 min" — how much of the game day is left. Deliberately
// free of i18n: the number is the message, and h/min read the same in every
// market we serve. The sentence around it comes from the locale.
// What THIS person can do about the group's word right now — one answer, derived
// once.
//
// It used to be a button per state, each discovered from a separate report and
// bolted on where that report happened to land: one in the between-rounds panel,
// one added beside the running round, each with its own label ternary. The third
// one was invisible to the person it was for — "vidím, že prý můžu připravit
// slovo, ale kde?" (2026-09-06) — because the sentence and the button had
// drifted into different words for the same act.
//
// Pure, so the whole matrix is asserted in one test file instead of being
// discovered on screen one state at a time.
//
//   day      "choose"  both days are open, the form asks which
//            "tomorrow" only one day is left to aim at
//   waiting  the word already written down, when it is mine
//   lead     what the line above the button says
//   cta      what the button says
export function wordMove({ round, turn, queued, enabled = true, rotates = false }) {
  if (!enabled || !turn?.isMine || turn?.canSet === false) return null;
  const waiting = queued?.is_mine ? queued : null;
  // A round still playing out, or today's word already spent: either way the
  // next thing this person can give the group is tomorrow's.
  const day = round || turn?.todayUsed ? "tomorrow" : "choose";
  // Whose turn it is, or what the schedule is missing — and which of those two
  // the reader needs depends on the group.
  //
  // In a rotating group a turn genuinely arrives and moves on, so "zítra
  // zadáváš slovo ty" is news. In the default group the founder sets EVERY
  // word, and telling them their turn has come, every day, for ever, states a
  // permanent condition as if something had changed. Nearly half of all groups
  // are in each mode (379 owner / 315 rotation on 2026-09-06), so neither
  // wording can serve both.
  // Ownership is worth saying only where it can be otherwise. In the default
  // group the founder sets EVERY word: "je tvoje" in both halves of the panel,
  // every day, for ever, is one permanent condition stated twice — "není
  // zbytečné, aby tam měl zopakováno to samé 2x, i když chce dál zadávat sám?"
  // (2026-09-06). Where the turn rotates it is the whole news.
  const lead = waiting
    // Not here: tomorrow's band carries a note that already opens with "vybíráš
    // ho ty". A sentence above the word saying the same thing is the repetition
    // that got cut — the DNES band, which has no such note, keeps its own.
    ? null
    : day === "tomorrow"
      ? rotates
        ? "groupWord.yoursTomorrow"
        : "groupWord.tomorrowUnset"
      : rotates
        ? "groupWord.yourTurn"
        : "groupWord.todayUnset";
  return {
    day,
    waiting,
    lead,
    // What follows from having chosen it. Said once, under the word — not a
    // second announcement of whose it is.
    note: waiting ? "groupWord.tomorrowMineNote" : null,
    cta: waiting
      ? "groupWord.queuedChange"
      : day === "tomorrow"
        ? "groupWord.prepareCta"
        : "groupWord.setCta",
  };
}

export const formatLeft = (ms) => {
  const total = Math.max(0, Math.round(ms / 60000));
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h ? `${h} h ${m} min` : `${m} min`;
};

export const gameEpoch = () => {
  const prague = new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/Prague" }));
  prague.setHours(prague.getHours() - ROTATION_HOUR);
  return prague;
};

// Ke kterému hernímu dni patří deska, kterou má hráč PRÁVĚ TEĎ na obrazovce.
//
// Schválně se nepočítá z hodin: `gameEpoch()` řekne, jaký den je, ne jaký den
// ukazuje deska. U aplikace, která běžela přes rotaci ve 3:00, se ta dvě čísla
// rozejdou — a právě to potřebuje server vědět, než od ní převezme počet tipů.
//
// Značky v localStorage píše jedině úklid dne (`checkNewDay`), takže věrně říkají,
// pro který den byla deska naposledy postavena. Chybí-li, vrací se null a server
// si poradí po starém — u čerstvé instalace je značka prostě ještě nenapsaná.
//
// Od 25. 9. 2026 se čte RAZÍTKO DESKY (`attemptsDay`), které se zapisuje týmž
// efektem jako deska sama. `gameDate` zůstal jen jako záloha pro zařízení, která
// razítko ještě nezapsala: nese den POSLEDNÍHO ÚKLIDU, a úklid mohla udělat jiná
// instance hry nad tímtéž úložištěm — takže o téhle desce umí lhát. Přesně tím
// prošla zamrzlá deska přes serverovou kontrolu z e572127.
export const boardDay = (type) => {
  try {
    if (type === "mega") {
      const tyden = localStorage.getItem("weeklyAttemptsWeek");
      if (tyden && JE_HERNI_TYDEN.test(tyden)) return tyden;
      return localStorage.getItem("weeklyISOWeek") || null;
    }
    const razitko = localStorage.getItem("attemptsDay");
    if (razitko && JE_HERNI_DEN.test(razitko)) return razitko;
    const d = localStorage.getItem("gameDate");
    if (!d) return null;
    const parsed = new Date(d);
    return isNaN(parsed) ? null : todayIso(parsed);
  } catch (e) {
    return null; // soukromý režim, zakázané úložiště — nejsme o nic hůř než dřív
  }
};

// Jediná odpověď na „jaký je teď herní den" — pro VŠECHNY spouštěče úklidu.
//
// Větší ze dvou: podle hodin v zařízení (funguje i bez signálu, hned po
// probuzení telefonu) a podle posledního slova serveru (hodiny se můžou mýlit).
// Herní den se jen posouvá dopředu, takže větší je vždy ten pravdivější — a
// hodiny s dotazováním se už nemůžou pohádat o to, kterou rotaci uklízejí: oba
// spouštěče se ptají téže funkce. Do 25. 9. 2026 si každý vedl vlastní značku a
// o shodu se staral jen `lastBoardResetDay`.
//
// Serverová hodnota se bere JEN ve tvaru data. Protože se bere větší z obou,
// jediná nesmyslná hodnota by vyhrála navždy — „x" i „undefined" jsou jako
// řetězce větší než „2026-…" — a den by od té chvíle stál, a to pro oba
// spouštěče najednou. Stará logika měla hodiny a server oddělené, takže by
// přežila aspoň cesta přes hodiny; tahle by nepřežila žádná.
export const JE_HERNI_DEN = /^\d{4}-\d{2}-\d{2}$/;
export const JE_HERNI_TYDEN = /^\d{4}-W\d{2}$/;

export const dnesniHerniDen = () => {
  const podleHodin = todayIso(gameEpoch());
  let podleServeru = null;
  try {
    podleServeru = localStorage.getItem("serverGameDay");
  } catch (e) {
    /* bez úložiště rozhodují jen hodiny */
  }
  if (!podleServeru || !JE_HERNI_DEN.test(podleServeru)) return podleHodin;
  return podleServeru > podleHodin ? podleServeru : podleHodin;
};

// ISO týden herního dne "YYYY-MM-DD" → "2026-W40". Datum se skládá místně, ne
// přes new Date("YYYY-MM-DD"): to by ho četlo jako UTC půlnoc.
export const tydenZDne = (iso) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  return getISOWeek(new Date(y, m - 1, d));
};

// Týden megaslova. Megaslovo se otáčí v pondělí ve 3:00, tedy se stejným
// herním dnem, takže je to prostě týden `dnesniHerniDen()` — a tím i týden,
// o kterém rozhodují hodiny A server společně. Do 25. 9. 2026 ho znaly jen
// hodiny v telefonu; dotazování týden nehlídalo vůbec.
export const dnesniHerniTyden = () => tydenZDne(dnesniHerniDen());

// Ke kterému týdnu patří megaslovní deska, kterou si instance načetla. Razítko
// (`weeklyAttemptsWeek`) chybí všem z doby před 25. 9. 2026; odvodí se
// z `weeklyISOWeek`, jediné značky, kterou starý kód při týdenním úklidu psal —
// a psal ji jen z hodin, takže tu (na rozdíl od denní desky) není co dohledávat
// jinde.
export const pocatecniRazitkoTydne = () => {
  try {
    // Jen ve tvaru týdne — nesmyslné razítko by se nikdy nezdálo starší a
    // deska by se neuklidila (viz JE_HERNI_DEN).
    const razitko = localStorage.getItem("weeklyAttemptsWeek");
    if (razitko && JE_HERNI_TYDEN.test(razitko)) return razitko;
    const tyden = localStorage.getItem("weeklyISOWeek");
    if (tyden && JE_HERNI_TYDEN.test(tyden)) return tyden;
  } catch (e) {
    /* soukromý režim — dál jako čerstvá instalace */
  }
  return dnesniHerniTyden();
};

// Ke kterému dni patří deska, kterou si instance právě načetla z úložiště.
//
// Razítko (`attemptsDay`) chybí u všech zařízení z doby před 25. 9. 2026. Kdyby
// chybění znamenalo „stará deska", první start po nasazení by smazal rozehranou
// hru úplně všem. Odvodí se proto z `gameDate` — přesně z toho, čemu věřil
// dosavadní kód, takže se pro ně nic nemění. Čerstvá instalace nemá ani to, a
// její prázdná deska patří dnešku.
//
// Z OBOU značek, které starý kód při úklidu psal, a vezme se ta pozdější. Úklid
// vyvolaný serverem totiž zapsal `gameDate` podle HODIN, ne podle serveru —
// takže u telefonu, jehož hodiny zaostávají, leží `gameDate` = 13. vedle desky,
// která už patří 14. Razítko jen z `gameDate` by takovému hráči po nasazení
// smazalo rozehranou hru. Pozdější z obou je nejpozdější den, o kterém starý
// kód věděl, že pro něj uklidil.
export const pocatecniRazitkoDesky = () => {
  try {
    const razitko = localStorage.getItem("attemptsDay");
    if (razitko && JE_HERNI_DEN.test(razitko)) return razitko;
    let podleHodin = null;
    const d = localStorage.getItem("gameDate");
    if (d) {
      const parsed = new Date(d);
      if (!isNaN(parsed)) podleHodin = todayIso(parsed);
    }
    let podleServeru = localStorage.getItem("serverGameDay");
    if (podleServeru && !JE_HERNI_DEN.test(podleServeru)) podleServeru = null;
    if (podleHodin || podleServeru) {
      return !podleHodin || (podleServeru && podleServeru > podleHodin) ? podleServeru : podleHodin;
    }
  } catch (e) {
    /* soukromý režim — dál jako čerstvá instalace */
  }
  return dnesniHerniDen();
};

// German capitalises every noun. The game's words ARE nouns, but they are stored
// lowercased for case-insensitive matching, so for the German build we re-capitalise
// the first letter for DISPLAY only (matching is unchanged). Other languages keep
// their words lowercase. Hooked into maskWord() so every word display inherits it.
const CAPITALIZE_WORDS = getLang() === "de";
export const displayWord = (w) => (CAPITALIZE_WORDS && w ? w.charAt(0).toUpperCase() + w.slice(1) : w);
// Switched off in July because "winners today" and "average guesses" were
// counted per server instance and showed a fraction of the real thing. Back on
// (2026-08-12) from a different source: the backend now reads both out of the
// players' own result documents, where one game is one row. See solved_stats.py.
//
// The winner COUNT did not come back. It can only ever count signed-in players,
// and several thousand people play without an account — a number that quietly
// omits most of the room is worse than no number. The average does not have that
// problem: signed-in players are not slower or faster than anyone else, so their
// average describes everybody's.
const StatsRows = ({ stats, t }) => {
  if (!stats) return null;
  const avg = typeof stats.avg_guesses_to_win === "number" ? stats.avg_guesses_to_win : null;
  const faster = typeof stats.faster_than === "number" ? stats.faster_than : null;
  // Nobody has finished yet: no row at all. A dash reads as "broken", and a 0
  // would read as "everybody got it first try".
  if (avg === null && faster === null) return null;
  return (
    <>
      {avg !== null && (
        <tr style={{ borderBottom: '1px solid #eee' }}>
          <td style={{ padding: '0.6rem 0', color: 'var(--text-dim)' }}>{t("stats.avgWinners")}</td>
          <td style={{ padding: '0.6rem 0', textAlign: 'right', fontWeight: 'bold' }}>{avg.toFixed(1)}</td>
        </tr>
      )}
      {faster !== null && (
        <tr>
          <td style={{ padding: '0.6rem 0', color: 'var(--text-dim)' }}>{t("stats.fasterThan")}</td>
          <td style={{ padding: '0.6rem 0', textAlign: 'right', fontWeight: 'bold' }}>
            {t("stats.fasterThanValue", { percent: Math.round(faster * 100) })}
          </td>
        </tr>
      )}
    </>
  );
};

// Terms + privacy are static pages served at real URLs, one set per market, in
// that market's language and adapted to its law. Each service has handlers only
// for its own paths (see app.<lang>.yaml and scripts/seo.js), so a build may
// only ever link to its own row. Germany has a third document: an Impressum is
// required of anyone offering a digital service there, separately from the
// terms. (No support address constant here: the footer's Kontakt is the form,
// and every document carries the address itself.)
const LEGAL_LINKS = {
  cs: { terms: "/podminky", privacy: "/soukromi" },
  sk: { terms: "/podmienky", privacy: "/sukromie" },
  pl: { terms: "/regulamin", privacy: "/prywatnosc" },
  hr: { terms: "/uvjeti", privacy: "/privatnost" },
  de: {
    terms: "/agb", privacy: "/datenschutz", imprint: "/impressum",
    // Německé povinnosti u placených smluv: poučení o odstoupení a dvě tlačítka
    // dostupná bez přihlášení — výpověď (§ 312k BGB) a odstoupení (§ 356a BGB).
    withdrawalInfo: "/widerruf", cancel: "/kuendigen", withdraw: "/widerrufen",
  },
}[getLang()] || { terms: "/podminky", privacy: "/soukromi" };
// The consent banner belongs to the build whose index.html sets the Consent Mode
// defaults — showing it anywhere else would ask a question nothing acts on.
// Every market is now converted: all five are EU and all five report to the same
// GA property, so analytics had to stop running unasked on four of them.
const CONSENT_BANNER = true;
// The Facebook group and the Discord server are Czech-speaking, so they are
// offered to the Czech build only — pointing a Croatian player at a Czech group
// is worse than showing them nothing.
// Kde se Premium prodává napevno. pl a hr jsou samostatné trhy bez plateb — žádné
// „Darovat Premium", nákup dárku ani dárek za výpadek (15. 10. 2026). Backend
// totéž vynutí v pricing.SELLING_LANGS; tohle jen neukazuje tlačítka, která by
// skončila chybou. de se zapíná přepínačem na serveru (features.premium), viz
// `sellsPremium` v App — tahle konstanta pro de zůstává false.
export const SELLS_PREMIUM = ["cs", "sk"].includes(getLang());
// Trh, kde server smí prodej zapnout přepínačem (pricing.SWITCHABLE_LANGS).
export const PREMIUM_SWITCHABLE = getLang() === "de";
// Trh, kde se platí v našem okně s naším tlačítkem „zahlungspflichtig…" a
// zaškrtnutím o okamžitém zahájení (pricing.OWN_BUTTON_LANGS, § 312j a § 357a BGB).
export const OWN_CHECKOUT = getLang() === "de";

const COMMUNITY = getLang() === "cs" ? {
  facebook: "https://www.facebook.com/groups/1750621372583453",
  discord: "https://discord.gg/6AcYzrjYBr",
} : null;
// Anočine — the second game — comes with Premium. Czech only, for the same reason
// as the community links above: the game is Czech and will be for a while, so
// promising it to a Polish or German buyer would be promising them nothing.
//
// Off until anocine.cz is live AND the backend answers /access?product=anocine.
// A benefit printed on the payment screen has to work on the day it is paid for,
// and the one-off plan makes that promise permanent — it cannot be walked back
// from someone who has already bought.
// Set per build target, not edited in this file. Editing a constant to demo
// something on staging means remembering to edit it back, and the one time
// nobody remembers is the time it ships: this promise cannot be withdrawn from
// anyone who has already bought. `build:cs-test` sets it; `build:cs` does not,
// and a test asserts that it never does.

// "O autorech" — a standalone page at /o-autorech, like the contracts. Czech
// only for now, because the text about the family exists only in Czech and a
// footer link that opens a page nobody in that market can read is worse than no
// link. Nothing about it is Czech in principle: translate the page and this
// becomes a list of languages.
const AUTHORS_PAGE = getLang() === "cs" ? "/o-autorech" : null;

// How often the not-page-bound refresh may run. A bell and a turn indicator do
// not need a round trip every time a window is clicked into; see the effect that
// uses it for what that cost.
const BACKGROUND_REFRESH_MS = 60_000;
// Jak často se s tím vezou i skupinová kola.
//
// Zvonek musí být čerstvý po minutě. Kolo skupiny se ale mění nejvýš jednou za
// den, a spočítat ho stojí ~5 čtení Firestoru NA SKUPINU — při ~21 000 voláních
// za hodinu ve špičce je to ta nejtlustší cesta, kterou máme.
//
// Pět minut proto, že to není strop: každá změna kola dnes posílá i zvonek
// (notify_round_opened, notify_turn, notify_round_done), a jakmile v seznamu
// notifikací přibude skupinová, kola se natáhnou při nejbližší obnově. Tohle je
// záloha pro případ, že by ten signál někdo minul.
const ROUNDS_REFRESH_MS = 5 * 60_000;
// Notifikace, po kterých má smysl znovu sáhnout na kola: jsou to přesně ty,
// které vznikají tam, kde se kolo změnilo.
const ROUND_NEWS = new Set(["group_word", "group_turn", "group_round_done"]);

// Rotační okno: minutu, jinak půlhodinu. Rozhoduje o tom, jak rychle se
// k otevřené záložce dostane nové slovo — a mimo rotaci se nemění nic, takže
// přes den je to hlídání něčeho, co se neděje.
const NEAR_ROTATION_MS = 60_000;
const REST_OF_DAY_MS = 30 * 60_000;
// Kolik před rotací a po ní se pollování zrychlí. Před ní krátce, protože přesný
// okamžik určuje cron; po ní o něco dýl, protože rotace pěti jazyků trvá vteřiny
// až minuty a klient nemá jak poznat, že už doběhla.
const NEAR_BEFORE_S = 20 * 60;
const NEAR_AFTER_S = 15 * 60;
const DAY_S = 24 * 60 * 60;
// `focus` se spustí při každém návratu do okna. Bez škrcení by z úspory nezbylo
// nic — a je to totéž číslo, kterým je škrcený /me/refresh.
const WAKE_THROTTLE_MS = 60_000;

/**
 * Za jak dlouho se zeptat znovu. Čistá funkce, aby se dala otestovat bez
 * prohlížeče i bez hodin.
 *
 * `secondsToRotation` hlásí SERVER, ne zařízení: telefon s posunutým časem by si
 * rychlé okno naplánoval vedle, a to je zrovna ta hodina, kdy na něm záleží.
 * `null` znamená „ještě nevíme" a chová se jako přes den — bezpečná strana,
 * protože pomalejší tik nikoho nerozbije, kdežto zbytečně rychlý stojí peníze.
 */
export function pollDelayMs({ hidden, secondsToRotation }) {
  if (hidden) return REST_OF_DAY_MS;
  if (typeof secondsToRotation !== "number" || !Number.isFinite(secondsToRotation)) {
    return REST_OF_DAY_MS;
  }
  const s = ((secondsToRotation % DAY_S) + DAY_S) % DAY_S;
  const blizko = s <= NEAR_BEFORE_S || s >= DAY_S - NEAR_AFTER_S;
  return blizko ? NEAR_ROTATION_MS : REST_OF_DAY_MS;
}

const ANOCINE_LIVE = process.env.REACT_APP_ANOCINE === "1";

// We answer on two hostnames and Firebase keeps the session in IndexedDB, which
// is bound to ONE origin. Someone signed in on www.prihorivahori.cz therefore has
// no session at all on prihorivahori.cz. Anočine used to begin the handover on a
// fixed host, so for those players it always saw onAuthStateChanged(null), sent
// back `#error=not_signed_in`, and dropped them on the sign-in form — every time,
// forever. One of them loaded /sso eight times in eighty seconds and then wrote
// in to ask what he was doing wrong.
//
// Measured 2026-09-09: 153 of 1500 requests to the site arrive on www (10.2 %),
// and Anočine's side saw 136 /sso loads yield only 82 tokens.
//
// So we say which host the click came from and they start there. Allow-listed
// rather than passed through: a staging build or localhost sends no host, and
// Anočine keeps doing what it does today.
const SSO_HOSTS = ["prihorivahori.cz", "www.prihorivahori.cz"];

/** The link to Anočine, naming the host this session actually lives on. Pure. */
export function anocineUrl(hostname) {
  const base = "https://www.anocine.cz/?sso=prihoriva";
  return SSO_HOSTS.includes(hostname) ? `${base}&host=${encodeURIComponent(hostname)}` : base;
}

// One address for the second game, used by the account link and by the benefit
// row that names it. `?sso=prihoriva` hands the session over, so a subscriber
// arrives signed in. Nobody else is shown the link at all — there is no free
// puzzle to arrive at any more.
const ANOCINE_URL = anocineUrl(
  typeof window !== "undefined" && window.location ? window.location.hostname : ""
);
// Where a buyer who arrived FROM Anočine is sent back to. `?premium=hotovo` is
// their signal to re-check the entitlement and bypass their cache — without it
// the player stares at a locked game for another five minutes after paying.
const ANOCINE_RETURN_URL = "https://www.anocine.cz/?premium=hotovo";
// Survives the round-trip to Stripe, which is a full page load on another
// origin: the parameter is gone by the time they come back to ?checkout=success.
const ANOCINE_RETURN_KEY = "anocineReturn";
// Everything that CLAIMS something about Anočine — what it costs, how much of
// it is free, that you only sign in once. Those claims depend on switches in
// the other game, so they wait for the build flag.
const ANOCINE_BENEFIT = ANOCINE_LIVE && getLang() === "cs";
// The way there, and from 2026-08-16 only for someone who pays: Anočine is
// closing its free puzzle, so a link offered to a free account would lead to a
// wall. Combined with `isPaid` at the call site — this half is the market, that
// half is the entitlement, and only the market is known at module load.
const ANOCINE_LINK = getLang() === "cs";
// Udrž oheň, the minigame from the 9.–12. 10. 2026 outage, lives only at
// prihorivahori.cz/ohen.html — the other builds drop it (scripts/seo.js).
const OHEN_LINK = getLang() === "cs";

/** Anočine's own mark: the question it asks, in its own colours.
 *
 * Taken from anocine.cz/favicon.svg — same path, same gradient stops — minus the
 * dark rounded tile, which would read as an app icon dropped into a list of line
 * icons and would sit heavily in the light theme. A clapperboard was there
 * before and was simply wrong: the game asks yes-or-no questions about anything,
 * and nothing about it is film.
 *
 * The gradient needs an id, and an id in a document is global — hence one fixed,
 * specific name rather than something a second copy of this icon could collide
 * with.
 */
const AnocineIcon = ({ size = 17 }) => (
  <svg viewBox="0 0 100 100" width={size} height={size} aria-hidden="true"
       style={{ flexShrink: 0, display: "block" }}>
    <defs>
      <linearGradient id="anocineMark" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#38bdf8" />
        <stop offset="0.5" stopColor="#22d3ee" />
        <stop offset="1" stopColor="#34d399" />
      </linearGradient>
    </defs>
    <g fill="none" stroke="url(#anocineMark)" strokeWidth="12" strokeLinecap="round">
      <path d="M33 40 A 17 17 0 1 1 50 57 L 50 63" />
    </g>
    <circle cx="50" cy="78" r="6.5" fill="url(#anocineMark)" />
  </svg>
);

// Kolik čekat na hodnocení jednoho denního tipu, než se odesílání uvolní.
export const SIMILARITY_TIMEOUT_MS = 45000;

function App() {
  const { t, i18n } = useTranslation();
  const { currentUser, logout, getIdToken, getClaims, loadingAuth, changeEmail, resetPassword } = useAuth();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  // Na kterou záložku okno otevřít. Vždycky se nastavuje spolu s otevřením
  // (viz otevriPrihlaseni), aby po jednom otevření na registraci nezůstala
  // zapamatovaná i pro všechna ostatní místa.
  const [authModalView, setAuthModalView] = useState("login");

  /** Otevři přihlašovací okno na dané záložce. Bez argumentu na přihlášení.
   *
   * Hra měla do 14. 9. 2026 jedinou bránu k účtu a ta uměla jen registraci.
   * To bylo v pořádku, dokud odhlášený člověk nebyl vracející se hráč — a ten
   * se objevil hromadně, když jsme 13. 9. sjednotili adresu z www na apex.
   * Firebase drží relaci na origin, takže se tím tisícům lidí přihlášení
   * odřízlo, a jediné, co pak četli, bylo „Zaregistruj se zdarma". Kdo tomu
   * uvěřil a založil si účet pod jinou adresou, přišel o svoje série.
   */
  const otevriPrihlaseni = useCallback((zalozka = "login") => {
    setAuthModalView(zalozka);
    setIsAuthModalOpen(true);
  }, []);
  const [isTestAccount, setIsTestAccount] = useState(false);
  // Restore the page across a refresh (F5) instead of always dropping to 'game'.
  // Self-sufficient pages restore as-is; deep pages that need a loaded entity
  // (a selected group / archived day) fall back to their parent list, since that
  // entity is gone after a reload. sessionStorage = per-tab, cleared on close.
  const [currentPage, setCurrentPage] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      // A gift link (?gift=CODE) opens the focused activation page; a returning
      // gift purchase lands on the gift page (Moje dárky).
      // The standalone pages carry no bundle, so every footer entry they share
      // with the app has to be reachable by URL. ?kontakt=1 was the first; these
      // are the rest of that same footer.
      if (params.get('faq') === '1') return 'faq';
      // Anočine links straight at the plans (?page=premium&from=anocine).
      // Landing in the game instead and making them find Premium in the
      // menu loses the one visitor who arrived already wanting to pay.
      if (params.get('page') === 'premium') return 'premium';
      if (params.get('gift')) return 'redeem';
      // Dárek za výpadek (odkaz z e-mailu předplatitelům, říjen 2026).
      if (params.has('darek')) return 'gift';
      if (params.get('invite')) return 'invite'; // ?invite=TOKEN → focused accept page
      if (params.get('checkout') === 'success' && localStorage.getItem('pendingGift')) return 'gift';
      // A returning subscription purchase (not a gift) lands on a success screen
      // instead of the buy page. Not in RESTORABLE, so a later refresh goes to game.
      if (params.get('checkout') === 'success') return 'premiumSuccess';
      const saved = sessionStorage.getItem('currentPage');
      if (!saved) return 'game';
      const RESTORABLE = ['game', 'friends', 'friendsManage', 'groups', 'wins', 'inProgress', 'archive', 'settings', 'premium', 'gift', 'faq'];
      // The contract lives at a real URL in every market now; the in-app screens
      // are gone. A session saved before the switch must not reopen a page that
      // no longer renders.
      if (saved === 'terms' || saved === 'privacy') return 'game';
      if (RESTORABLE.includes(saved)) return saved;
      // The win/archive detail keeps its day in sessionStorage, so a reload can
      // reopen exactly that day (re-fetched on mount) instead of dropping to the calendar.
      if (saved === 'archivePlay' && sessionStorage.getItem('archiveDate')) return 'archivePlay';
      return { group: 'groups', groupAdmin: 'groups', archivePlay: 'archive' }[saved] || 'game';
    } catch (e) { return 'game'; }
  });
  const [leaderboard, setLeaderboard] = useState(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [leaderboardError, setLeaderboardError] = useState(null);
  const [weeklyLeaderboard, setWeeklyLeaderboard] = useState(null);
  const [weeklyLeaderboardLoading, setWeeklyLeaderboardLoading] = useState(false);
  const [isTop10ModalOpen, setIsTop10ModalOpen] = useState(false);
  // Read-only: seeded from localStorage, and only the restart flow ever reset it —
  // that flow is gone with the restart modal.
  const [dailyRank] = useState(() => {
    const saved = localStorage.getItem("dailyRank");
    return saved ? JSON.parse(saved) : null; // {rank, total_players}
  });
  const [weeklyRank, setWeeklyRank] = useState(() => {
    const saved = localStorage.getItem("weeklyRank");
    return saved ? JSON.parse(saved) : null; // {leaderboard: [...]}
  });
  const [userHistory, setUserHistory] = useState(null);
  const [streaks, setStreaks] = useState(null); // {current, longest} daily win streak
  const [historyToday, setHistoryToday] = useState(null); // server's current daily period (Prague)
  const streakRailRef = useRef(null); // the scrollable streak timeline
  const winGraphRef = useRef(null); // approach-graph container, measured to fill the viewport
  const [winGraphSize, setWinGraphSize] = useState({ w: 320, h: 260 });
  // Archive (replay past daily words): calendar days, the open day, and its board.
  const [archiveDays, setArchiveDays] = useState(null);
  // Which month the archive calendar is showing. Held HERE, not in the calendar,
  // because opening a day unmounts it — so a player paging back to January and
  // playing a word landed back on the current month, every single word
  // (reported 2026-08-29). Deliberately NOT persisted: within a visit it is what
  // the player is working through, but restored months later it would be a
  // calendar mysteriously open on January.
  const [archiveMonth, setArchiveMonth] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveDate, setArchiveDate] = useState(null);
  // 'daily' = replayable past day; 'mega' = read-only review of a past week's win.
  const [archiveType, setArchiveType] = useState('daily');
  const [archiveAttempts, setArchiveAttempts] = useState([]);
  // Default to chronological order: the review exists to share "how I got to the
  // word step by step", so it should read first-guess-first from the top. Teplota
  // (warmest-first) stays a tab.
  const [winSort, setWinSort] = useState(() => { // win review: 'temp' | 'order' | 'graph'
    try {
      const s = sessionStorage.getItem('winSort');
      return ['temp', 'order', 'graph'].includes(s) ? s : 'order';
    } catch (e) { return 'order'; }
  });
  const [archiveGuess, setArchiveGuess] = useState("");
  const [archiveError, setArchiveError] = useState("");
  const [archiveSolvedWord, setArchiveSolvedWord] = useState(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [archiveLastSubmit, setArchiveLastSubmit] = useState(0);
  const [archiveCooldown, setArchiveCooldown] = useState(2000);
  const [archiveTimeoutProgress, setArchiveTimeoutProgress] = useState(0);
  const [archiveLastAttempt, setArchiveLastAttempt] = useState(null);
  // Stejná animace jako na denní hře: slovo sjede z inputu dolů a vytlačí
  // předchozí kartu. Sdílená komponenta ji umí, plochy ji jen musí řídit.
  const [archiveFlyingWord, setArchiveFlyingWord] = useState(null);
  const [archiveLeavingWord, setArchiveLeavingWord] = useState(null);
  const [roundFlyingWord, setRoundFlyingWord] = useState(null);
  const [roundLeavingWord, setRoundLeavingWord] = useState(null);
  // Bez odkazu na input si letící karta nemá co změřit a spadla by o pevných
  // 150 px — na denní hře se ta vzdálenost počítá z živého inputu.
  const archiveInputRef = useRef(null);
  const roundInputRef = useRef(null);
  // Friends (balík 3): my share code, add-by-code, incoming/outgoing requests,
  // accepted friends' "today". All via backend-mediated /friends endpoints.
  const [friendCode, setFriendCode] = useState(null);
  const [friendCodeCopied, setFriendCodeCopied] = useState(false);
  const [addCodeInput, setAddCodeInput] = useState('');
  const [addFriendMsg, setAddFriendMsg] = useState('');
  const [friends, setFriends] = useState(null); // [{uid, status, direction, display_name}]
  // Kolik mám přátel a kolik smím. Přichází ze stejné odpovědi jako seznam, takže
  // to nestojí žádný dotaz navíc — a bez toho by se „máš plno" dalo zjistit jedině
  // kliknutím na Přijmout, po kterém vyskočí nabídka Premium bez vysvětlení.
  const [friendsLimit, setFriendsLimit] = useState(null); // {accepted, limit, reached}
  const [friendsToday, setFriendsToday] = useState(null); // [{uid, display_name, daily, mega}]
  const [friendsTab, setFriendsTab] = useState(() => storedWordMode(false)); // which word the "today" list shows
  // Groups (balík 4): my groups, create/join, incoming invites; and the open
  // group's detail + standings (daily/mega) + owner's pending invites.
  const [groups, setGroups] = useState(null);
  const [groupRequests, setGroupRequests] = useState([]); // groups I asked to join
  // Skupiny, ve kterých jsem, ale patří jiné jazykové verzi. Nepatří mezi
  // `groups` — otevřením bych hádal cizí slovo v tomhle rozhraní — ale ani se
  // nesmějí ztratit: zvonek o nich mluví dál. Viz list_my_groups_split.
  const [otherMarketGroups, setOtherMarketGroups] = useState([]);
  const [createGroupName, setCreateGroupName] = useState('');
  const [createGroupMsg, setCreateGroupMsg] = useState('');
  const [renameGroupName, setRenameGroupName] = useState('');
  const [renameGroupMsg, setRenameGroupMsg] = useState('');
  const [renameGroupBusy, setRenameGroupBusy] = useState(false);
  const [joinGroupCode, setJoinGroupCode] = useState('');
  const [joinGroupMsg, setJoinGroupMsg] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null); // {gid, name, owner_uid, members, ...}
  const [groupStandings, setGroupStandings] = useState(null);
  const [groupStandingsTab, setGroupStandingsTab] = useState(() => storedWordMode(true));
  const [groupInvites, setGroupInvites] = useState(null); // owner's pending join requests
  const [groupApproveMsg, setGroupApproveMsg] = useState(null); // why an approval was refused
  // The group's own word round: who set it, my board, everyone's progress.
  const [groupRound, setGroupRound] = useState(null);      // {round, setter_name, is_setter}
  const [roundState, setRoundState] = useState(null);      // my guesses on it
  const [roundStandings, setRoundStandings] = useState([]);
  const [roundGuess, setRoundGuess] = useState('');
  const [roundMsg, setRoundMsg] = useState('');
  const [roundBusy, setRoundBusy] = useState(false);
  // The group board is the SAME board as the daily one (GuessBoard), so it needs
  // the same three things the daily board has: a cooldown to animate, the card
  // for the word just entered, and the count on the separator.
  const [roundLastSubmit, setRoundLastSubmit] = useState(0);
  const [roundCooldown, setRoundCooldown] = useState(2000);
  const [roundTimeoutProgress, setRoundTimeoutProgress] = useState(0);
  const [roundLastAttempt, setRoundLastAttempt] = useState(null);
  const [setWordInput, setSetWordInput] = useState('');
  const [setWordMsg, setSetWordMsg] = useState('');
  const [roundTurn, setRoundTurn] = useState(null);        // whose turn it is to set
  const [queuedWord, setQueuedWord] = useState(null);      // a word kept for tomorrow (never the word itself)
  const [lastRound, setLastRound] = useState(null);        // finished round + its word
  const [lastStandings, setLastStandings] = useState([]); // and how everyone did in it
  const [lastOpen, setLastOpen] = useState(false);        // the result is folded away by default
  // Today's round, already over. Not `groupRound`: that one means "running"
  // everywhere it is read. This is the same screen you won on, kept until the
  // day turns — the win, my own tips, and how everyone else did.
  const [finishedRound, setFinishedRound] = useState(null);
  const [finishedState, setFinishedState] = useState(null);
  const [finishedStandings, setFinishedStandings] = useState([]);
  const [myOwnedGroupLimit, setMyOwnedGroupLimit] = useState(null);  // per-account, from /auth/profile
  const [myGroupRounds, setMyGroupRounds] = useState([]);  // groups with a word waiting
  const [groupRoundsLoaded, setGroupRoundsLoaded] = useState(false); // has the list answered once?
  const [roundFailedForGid, setRoundFailedForGid] = useState(null);  // its round would not load
  // Zakladateli propadlo předplatné, takže skupina nic nehlásí. Skupinová
  // stránka to říká nahlas, deska to do 24. 9. 2026 mlčky vykreslila jako
  // „Zatím tu žádné slovo není" — což je věta o slovu, ne o skupině.
  const [roundInactive, setRoundInactive] = useState(false);
  const [groupWordsBusy, setGroupWordsBusy] = useState(false);       // the group's own switch
  const [verifyMsg, setVerifyMsg] = useState(null);   // resend-the-confirmation result
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [openRoundGid, setOpenRoundGid] = useState(null);  // which one the group board shows
  const openRoundGidRef = useRef(null);
  // Which group the round state above actually belongs to. Without it, opening a
  // second group showed the FIRST one's word until the new fetch landed — and a
  // slow reply could overwrite a newer one. Rendering is gated on this matching.
  const [roundForGid, setRoundForGid] = useState(null);
  const [groupCodeCopied, setGroupCodeCopied] = useState(false); // group invite-code copy feedback
  // Which social modal is open: null | 'addFriend' | 'myCode' | 'createGroup' | 'renameGroup' | 'joinGroup' | 'inviteGroup'
  const [socialModal, setSocialModal] = useState(null);
  // Which feature the player just bumped into, so the upgrade modal can name it.
  // Only the setter is used (openPremium stamps the context); the value is unread.
  const [, setPremiumPrompt] = useState(null);
  // The signed-in user's own premium standing, from /auth/profile:
  //   { premium: bool (effective — respects enforcement), enforcing: bool,
  //     entitlement: { status, valid_until, active } }
  // null until loaded / signed out. `premium` drives UI gating so it matches the
  // server gate; `entitlement` is the real paid state shown on the account screen.
  const [myPremium, setMyPremium] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [checkoutBusy, setCheckoutBusy] = useState(null);
  const [checkoutError, setCheckoutError] = useState(null);
  // de: placení v našem okně (OwnCheckout) — {plan, kind, body, giftCode}.
  const [ownCheckout, setOwnCheckout] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false); // opening the Stripe billing portal
  // Gift codes (§6.2): my bought codes, the redeem input, and its result.
  const [giftCodes, setGiftCodes] = useState(null); // [{code,status,days,recipient_email,created_at}]
  // Dárek za výpadek: {eligible, code, deadline} z /kompenzace/darek, nebo null.
  const [darek, setDarek] = useState(null);
  const [darekBusy, setDarekBusy] = useState(false);
  const [giftCodeInput, setGiftCodeInput] = useState(() => {
    try { return (new URLSearchParams(window.location.search).get('gift') || '').toUpperCase(); }
    catch (e) { return ''; }
  });
  const [giftRedeemBusy, setGiftRedeemBusy] = useState(false);
  const [giftRedeemMsg, setGiftRedeemMsg] = useState(null); // {ok} | {ok:false,error}
  const [giftCopied, setGiftCopied] = useState(null); // which code was just copied
  const [giftPreview, setGiftPreview] = useState(null); // activation page: {status,days,from_name} | {error}
  const [giftSendFor, setGiftSendFor] = useState(null); // code whose "email it" form is open
  const [giftSendEmail, setGiftSendEmail] = useState('');
  const [giftSendBusy, setGiftSendBusy] = useState(false);
  const [giftSendMsg, setGiftSendMsg] = useState(null); // {ok} | {ok:false,error} keyed by giftSendFor
  const [justBoughtGift] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return p.get('checkout') === 'success' && !!localStorage.getItem('pendingGift');
    } catch (e) { return false; }
  });
  const [confirmAction, setConfirmAction] = useState(null); // { message, confirmLabel, onConfirm }
  // Téma skupiny (6. 10. 2026): { gid, mode: 'offer'|'edit', text, navrh, busy, msg }.
  // 'offer' = hra sama nabídla po založení/přejmenování; 'edit' = správce zadává.
  const [temaState, setTemaState] = useState(null);
  // Pojistky proti souběhu (kontrola 6. 10. 2026): odpověď modelu nesmí dorazit
  // do okna jiné skupiny ani vyskočit nad obrazovkou, kam správce mezitím odešel;
  // dvojklik nesmí poslat dvě uložení.
  const temaKontextRef = useRef({ gid: null, page: null, modal: null });
  const temaUkladaRef = useRef(false);
  temaKontextRef.current = { gid: selectedGroup?.gid ?? null, page: currentPage, modal: socialModal };
  const [keyboardOpen, setKeyboardOpen] = useState(false); // hide bottom nav while the on-screen keyboard is up
  // When the not-page-bound half of the come-back-to-the-window refresh last ran.
  // A ref, not state: it must not cause a render, and the handler reads it from
  // inside a listener that outlives any one render.
  const lastBackgroundRefresh = useRef(0);
  const lastRoundsRefresh = useRef(0);
  const lastRoundsTry = useRef(0);  // the visible-page timer's last attempt, successful or not
  const roundsDirty = useRef(false);
  const seenNotifIds = useRef(null);
  const [notifOpen, setNotifOpen] = useState(false); // notifications dropdown
  const [notifications, setNotifications] = useState([]); // persisted event notifications
  const [ownerPending, setOwnerPending] = useState([]); // join requests/proposals I must action
  const notifMenuRef = useRef(null);
  // Email invites (friends + groups): the ?invite=TOKEN landing page, plus the
  // "invite by email" inputs added to the add-friend / group-invite modals.
  const [inviteToken] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('invite') || ''; }
    catch (e) { return ''; }
  });
  const [invitePreview, setInvitePreview] = useState(null); // {kind,group_name,inviter_name,status} | {error}
  const [inviteAcceptMsg, setInviteAcceptMsg] = useState(null); // {ok,kind,group_name} | {ok:false,error}
  const [inviteAcceptBusy, setInviteAcceptBusy] = useState(false);
  const [addEmailInput, setAddEmailInput] = useState('');
  const [addEmailMsg, setAddEmailMsg] = useState(null); // {status} — friends email invite result
  const [addEmailBusy, setAddEmailBusy] = useState(false);
  const [inviteEmailInput, setInviteEmailInput] = useState('');
  const [inviteEmailMsg, setInviteEmailMsg] = useState(null); // {status} — group email invite result
  const [inviteEmailBusy, setInviteEmailBusy] = useState(false);
  // Contact form (footer): opened as a modal, posts to /contact. Also reachable
  // at /?kontakt=1 — the legal pages are plain HTML with no bundle, so that link
  // is the only way they can hand someone the form instead of a mailto.
  const [contactOpen, setContactOpen] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('kontakt') === '1'; }
    catch (e) { return false; }
  });
  // Set once, on arrival, and read after the return from Stripe. Stored rather
  // than kept in state because the payment leaves the origin entirely.
  const [cameFromAnocine, setCameFromAnocine] = useState(() => {
    try {
      if (new URLSearchParams(window.location.search).get('from') === 'anocine') {
        localStorage.setItem(ANOCINE_RETURN_KEY, '1');
        return true;
      }
      return localStorage.getItem(ANOCINE_RETURN_KEY) === '1';
    } catch (e) { return false; }
  });
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '', website: '', origin: '' });
  const [contactBusy, setContactBusy] = useState(false);
  const [contactMsg, setContactMsg] = useState(null); // {ok} | {ok:false,error}
  // "Nahlásit chybu" — the contact form's sibling, posting to /bug_report. Kept
  // separate rather than a mode of the one above: it asks different questions,
  // it carries a screenshot, and the two land in the inbox under different
  // subjects so a broken game does not queue behind a question about billing.
  const [bugOpen, setBugOpen] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('chyba') === '1'; }
    catch (e) { return false; }
  });
  const [bugForm, setBugForm] = useState({ description: '', email: '', website: '' });
  const [bugImage, setBugImage] = useState(null); // {dataUrl, type, bytes}
  const [bugBusy, setBugBusy] = useState(false);
  const [bugMsg, setBugMsg] = useState(null); // {ok} | {ok:false,error}
  // Cookie banner: shown until the visitor has chosen, and again whenever they
  // ask for it from the footer. Read once at mount — index.html has already acted
  // on the same stored value, so this only decides whether to ask.
  const [consentOpen, setConsentOpen] = useState(() => {
    if (!CONSENT_BANNER) return false;
    // ?cookies=1 — the footer link on a static page. Withdrawing consent has to
    // be as easy as giving it, and on those pages there is no button to press.
    try {
      if (new URLSearchParams(window.location.search).get('cookies') === '1') return true;
    } catch (e) {}
    return readConsent() === null;
  });
  // Arriving on the link, the account is not restored yet — the footer button
  // prefills by hand, this does the same once the session shows up.
  useEffect(() => {
    if (!contactOpen || !currentUser) return;
    setContactForm(f => ({
      ...f,
      name: f.name || currentUser.displayName || '',
      email: f.email || currentUser.email || '',
    }));
  }, [contactOpen, currentUser]);

  // Feature flags from server
  const [features, setFeatures] = useState({
    registration: false,  // default false until loaded from server
    leaderboard: false
  });
  // Prodává se tu Premium? cs/sk napevno; de jen když to server zapnul.
  const sellsPremium = SELLS_PREMIUM || (PREMIUM_SWITCHABLE && features.premium === true);

  const [nicknameValue, setNicknameValue] = useState('');
  const [profileDisplayName, setProfileDisplayName] = useState('');
  const [nicknameSaving, setNicknameSaving] = useState(false);
  const [nicknameError, setNicknameError] = useState('');
  const [nicknameSaved, setNicknameSaved] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailMsg, setEmailMsg] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  // Theme: dark is the default; the choice is remembered and applied to <html>,
  // which is what the [data-theme="light"] token overrides hang off.
  // Firebase takes ~1s to restore the session, during which currentUser is null —
  // long enough to paint the signed-out header and then visibly swap it. Remember
  // the last known state so the very first paint already matches.
  const [wasSignedIn, setWasSignedIn] = useState(() => localStorage.getItem("wasSignedIn") === "1");
  useEffect(() => {
    if (loadingAuth) return; // don't record anything until auth has actually resolved
    const signedIn = !!currentUser;
    setWasSignedIn(signedIn);
    localStorage.setItem("wasSignedIn", signedIn ? "1" : "0");
  }, [currentUser, loadingAuth]);
  const looksSignedIn = currentUser || (loadingAuth && wasSignedIn);

  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "dark");
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);
  const [isGoogleNicknameModalOpen, setIsGoogleNicknameModalOpen] = useState(false);
  const [googleNicknameValue, setGoogleNicknameValue] = useState('');
  const [googleNicknameError, setGoogleNicknameError] = useState('');
  const [googleNicknameSaving, setGoogleNicknameSaving] = useState(false);
  const accountMenuRef = useRef(null);

  const shortenDisplayName = (name) => {
    const clean = (name || '').trim();
    if (!clean) return '';
    const parts = clean.split(/\s+/);
    if (parts.length >= 2) return `${parts[0]} ${parts[parts.length - 1][0]}.`;
    return clean;
  };

  const getGooglePromptSeenKey = (uid) => `googleNicknamePromptSeen:${uid}`;

  const handleGoogleLoginSuccess = async (googleUser) => {
    const uid = googleUser?.uid;
    if (!uid) return;
    
    // Check if user has already customized their nickname (server-side check)
    try {
      const token = await googleUser.getIdToken();
      const response = await apiFetch(`/user/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const profile = await response.json();
        // First-login-only: skip once the user has set a nickname OR already
        // seen+dismissed the prompt (persisted server-side, so it never
        // reappears on another device).
        if (profile.nickname_customized || profile.nickname_prompt_seen) {
          return;
        }
      }
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      // On error, check localStorage as fallback
      if (localStorage.getItem(getGooglePromptSeenKey(uid)) === '1') return;
    }
    
    const suggested = shortenDisplayName(googleUser?.displayName || googleUser?.email?.split('@')[0] || '');
    setGoogleNicknameValue(suggested);
    setGoogleNicknameError('');
    setIsGoogleNicknameModalOpen(true);
  };

  const closeGoogleNicknameModal = () => {
    if (currentUser?.uid) {
      localStorage.setItem(getGooglePromptSeenKey(currentUser.uid), '1');
      // Persist the dismissal server-side so the one-time prompt never reappears
      // on another device (best-effort; localStorage is only a same-device cache).
      getIdToken().then((token) => {
        if (!token) return;
        apiFetch(`/user/nickname/dismiss`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        }).catch(() => {});
      }).catch(() => {});
    }
    setIsGoogleNicknameModalOpen(false);
    setGoogleNicknameError('');
  };

  const saveGoogleNickname = async () => {
    const trimmed = googleNicknameValue.trim();
    if (!trimmed) { setGoogleNicknameError(t("google.nicknameEmpty")); return; }
    if (trimmed.length > 30) { setGoogleNicknameError(t("google.nicknameTooLong")); return; }
    setGoogleNicknameSaving(true);
    setGoogleNicknameError('');
    try {
      let token = await getIdToken(true);
      if (!token) {
        setGoogleNicknameError(t("google.sessionExpired"));
        return;
      }

      let resp = await apiFetch(`/user/nickname`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ nickname: trimmed }),
      });

      // Token can expire between popup auth and first profile write; retry once with refreshed token.
      if (resp.status === 401) {
        token = await getIdToken(true);
        if (!token) {
          setGoogleNicknameError(t("google.sessionExpired"));
          return;
        }
        resp = await apiFetch(`/user/nickname`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ nickname: trimmed }),
        });
      }

      const data = await resp.json();
      if (!resp.ok) {
        setGoogleNicknameError(data.error || 'Chyba');
        return;
      }
      setProfileDisplayName(trimmed);
      setNicknameValue(trimmed);
      setNicknameSaved(true);
      closeGoogleNicknameModal();
    } catch (e) {
      setGoogleNicknameError(t("google.networkError"));
    } finally {
      setGoogleNicknameSaving(false);
    }
  };

  // Načíst custom claims po přihlášení (test_account flag)
  useEffect(() => {
    if (!currentUser) { setIsTestAccount(false); return; }
    getClaims().then(claims => setIsTestAccount(!!claims.test_account)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);
  
  // Load feature flags from server
  useEffect(() => {
    const loadFeatures = async () => {
      try {
        const response = await apiFetch(`/health`);
        const data = await response.json();
        if (data.features) {
          setFeatures(data.features);
        }
      } catch (error) {
        console.error('Failed to load features:', error);
        // Keep defaults on error
      }
    };
    loadFeatures();
  }, []);

  // Po odhlášení přesměrovat na hlavní stránku — ALE jen z auth-only stránek.
  // Public stránky (invite/redeem/faq/game) musí zůstat: jinak nepřihlášený, co
  // otevře e-mailovou pozvánku (?invite=TOKEN), spadne rovnou do hry a pozvánku
  // vůbec neuvidí. (Podmínky a soukromí jsou statické stránky mimo aplikaci.)
  // 'premium' included: someone arriving from the other game to buy may not
  // be signed in here yet, and bouncing them to the game board answers a
  // question they did not ask. The plan buttons open the sign-in modal.
  const PUBLIC_PAGES = ['game', 'invite', 'redeem', 'faq', 'premium'];
  useEffect(() => {
    if (!currentUser && !loadingAuth) {
      setProfileDisplayName('');
      setCurrentPage((p) => (PUBLIC_PAGES.includes(p) ? p : 'game'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, loadingAuth]);

  // Načíst profilové jméno + premium stav z backendu (Firestore).
  const refreshPremium = useCallback(async () => {
    if (!currentUser) { setMyPremium(null); setMyOwnedGroupLimit(null); return; }
    try {
      const token = await getIdTokenRef.current();
      const resp = await apiFetch(`/auth/profile`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) return;
      applyProfile(await resp.json());
    } catch (e) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, loadingAuth]);

  // What a /auth/profile payload changes on screen. One body, two callers — this
  // one and refreshAll below — so the merged request cannot drift from the
  // separate one it replaces.
  function applyProfile(data) {
    if (data?.display_name) setProfileDisplayName(data.display_name);
    // Only accounts we gave their own number carry it; everyone else uses /health.
    // Every caller passes a whole profile, so a missing number means "none" and
    // clears one we took away since.
    setMyOwnedGroupLimit(typeof data?.owned_group_limit === 'number' ? data.owned_group_limit : null);
    if (typeof data?.premium === 'boolean') {
      setMyPremium({
        premium: data.premium,
        enforcing: !!data.enforcing,
        entitlement: data.entitlement || null,
        has_subscription: !!data.has_subscription,
        subscription: data.subscription || null,
      });
    }
  }

  // The background refresh, in ONE request instead of three.
  //
  // /notifications, /user/group_rounds and /auth/profile fired together from the
  // handler below every time a window came back to the front. Measured at peak
  // on 2026-08-30: 4 778 requests in five minutes, 21% of all backend traffic,
  // for what is logically a single refresh. Same data, same server-side
  // functions — only the count changed.
  //
  // Each part is applied on its own. The three calls this replaces were
  // independently fault-tolerant (whichever answered was used), and the server
  // returns null for a part that failed, so one outage must not blank the other
  // two. `null` means "no answer", which is never a reason to clear what is on
  // screen; an empty list is an answer and does clear it.
  const refreshAll = async (wantProfile) => {
    const now = Date.now();
    // Kola jen když se mohla změnit: uplynulo pět minut, nebo minule přišla
    // skupinová notifikace, což je přesně ten okamžik, kdy se změnila.
    const wantRounds = roundsDirty.current || now - lastRoundsRefresh.current >= ROUNDS_REFRESH_MS;
    const q = [wantProfile ? 'profile=1' : '', wantRounds ? '' : 'rounds=0'].filter(Boolean).join('&');
    try {
      const resp = await friendsApi(`/me/refresh${q ? `?${q}` : ''}`);
      if (!resp.ok) return;
      const d = await resp.json();
      if (Array.isArray(d.notifications)) {
        // Přibyla skupinová novinka? Pak si o kola řekni při nejbližší obnově —
        // tedy do minuty. Poprvé se jen zapamatuje, co už tam bylo, jinak by
        // první běh vypadal jako samá novinka.
        const ids = new Set(d.notifications.map(n => n.id));
        if (seenNotifIds.current) {
          const novinka = d.notifications.some(
            n => ROUND_NEWS.has(n.kind) && !seenNotifIds.current.has(n.id),
          );
          if (novinka) roundsDirty.current = true;
        }
        seenNotifIds.current = ids;
        setNotifications(d.notifications);
      }
      // `null` znamená „bez odpovědi" a nikdy nemaže, co je na obrazovce — ať
      // už proto, že jsme o kola nežádali, nebo že ta část na serveru spadla.
      if (Array.isArray(d.rounds)) {
        setMyGroupRounds(d.rounds);
        lastRoundsRefresh.current = now;
        roundsDirty.current = false;
      }
      if (d.profile) applyProfile(d.profile);
    } catch (e) {}
  };

  useEffect(() => {
    if (!currentUser || loadingAuth) { if (!currentUser) { setMyPremium(null); setMyOwnedGroupLimit(null); } return; }
    refreshPremium();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, loadingAuth]);

  // Load the data a page reads when it's shown but hasn't been fetched yet —
  // covers a refresh that restored straight onto that page (its usual open()
  // handler never ran). Guards on null so normal navigation doesn't double-fetch.
  useEffect(() => {
    if (!currentUser) return;
    // Moje výhry always refetches on open so a just-made win shows without a reload;
    // settings only needs it once (its summary tolerates a slightly stale value).
    if (currentPage === 'wins') fetchUserHistory();
    else if (currentPage === 'settings' && userHistory === null) fetchUserHistory();
    if ((currentPage === 'archive' || currentPage === 'inProgress') && archiveDays === null) fetchArchive();
    if (currentPage === 'friends' && friendsToday === null) { fetchFriendsToday(); fetchFriendCode(); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, currentUser]);

  // Refetch what the current page shows when the window comes back to the front.
  // Reported from testing as "the database seems delayed": we fetched a page's data
  // once and then kept it, so a board left open (or a phone put down and picked up)
  // showed a snapshot. No interval — polling is what blew the logging budget before;
  // the moment the player is actually looking is when it has to be current.
  useEffect(() => {
    if (!currentUser) return;
    const refresh = () => {
      if (document.visibilityState === 'hidden') return;
      if (currentPage === 'wins' || currentPage === 'settings') fetchUserHistory();
      if (currentPage === 'archive' || currentPage === 'inProgress') fetchArchive();
      if (currentPage === 'friends') fetchFriendsToday();
      if (currentPage === 'groups') fetchMyGroups();
      if (currentPage === 'group' && selectedGroup) { fetchStandings(selectedGroup.gid, groupStandingsTab); fetchGroupRound(selectedGroup.gid); }
      // The three below are NOT page-bound, so they fire on every return from
      // every screen — and this handler listens to `focus` AND
      // `visibilitychange`, which both arrive when a window is clicked back
      // into. Measured 2026-08-17: /notifications, /user/group_rounds and
      // /auth/profile came to 241 each in a sample of 3 000, so 24% of ALL
      // backend traffic — about 1.17 million calls a day out of one listener.
      //
      // Once a minute is enough for a bell and a turn indicator. The
      // page-specific fetches above stay immediate: those are what "the database
      // seems delayed" was about, and a minute of staleness there is exactly the
      // complaint this handler exists to answer.
      const now = Date.now();
      if (now - lastBackgroundRefresh.current < BACKGROUND_REFRESH_MS) return;
      lastBackgroundRefresh.current = now;

      // One request for all three. They fetched the same data as separate calls
      // until 2026-08-30, when they were measured at 4 778 requests in five peak
      // minutes — 21% of ALL backend traffic — for what is logically one refresh.
      // Same payloads from the same server-side functions; only the count changed.
      //
      // The entitlement is asked for only while there is something to find. It
      // was read ONLY by the ~30s poll on the premium-success page, so a webhook
      // slower than that left the app believing the player was still free until
      // a full reload — reported 2026-08-16 by somebody who bought Premium and
      // went on being asked to buy it. A subscriber's entitlement does not change
      // from one minute to the next, so theirs is not re-read: the person who
      // needs this is the one who has just paid and is not marked yet.
      refreshAll(!isPaid);
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, currentUser, selectedGroup, groupStandingsTab]);

  // The handler above only runs on coming BACK to the window. A window that stays
  // in front never comes back, so a word set meanwhile never reached it: reported
  // 2026-09-25 from desktop Chrome, group word set at 11:03, still no
  // "Skupinové" tab at 11:16. While the page is visible, ask again every five
  // minutes — the same one request, and only for someone in a group at all
  // (the list holds every group of mine, with or without a word).
  const inAnyGroup = myGroupRounds.length > 0;
  useEffect(() => {
    if (!currentUser || !inAnyGroup) return;
    const id = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      const now = Date.now();
      // Measured from the last TRY as well as the last success: a failing server
      // must not turn five minutes into one for everybody in a group.
      if (now - Math.max(lastRoundsRefresh.current, lastRoundsTry.current) < ROUNDS_REFRESH_MS) return;
      lastRoundsTry.current = now;
      lastBackgroundRefresh.current = now;  // so a click back into the window right after is not a second request
      refreshAll(false);
    }, BACKGROUND_REFRESH_MS);  // a tick costs nothing; the request goes at most every five minutes
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, inAnyGroup]);

  // A school group's page on the classroom projector (30. 9. 2026): "kdyby se
  // posun v teplotě ukazoval live". The projector never leaves the window, so
  // the come-back refresh above never fires. Every 15 s while the page of a
  // school group is visible — standings are written on every guess, so each
  // refresh shows something new. Ordinary groups are untouched.
  // The teacher's screen only — the projector. Pupils play on the board.
  const schoolPageGid = currentPage === 'group' && selectedGroup?.school
    && selectedGroup.owner_uid === currentUser?.uid ? selectedGroup.gid : null;
  useEffect(() => {
    if (!schoolPageGid) return;
    const id = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      fetchGroupRound(schoolPageGid);
      if (groupStandingsTab !== 'group') fetchStandings(schoolPageGid, groupStandingsTab);
    }, 15_000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolPageGid, groupStandingsTab]);

  // The streak timeline opens scrolled to the right (today), so history is to the left.
  useEffect(() => {
    if (currentPage === 'wins' && streakRailRef.current) {
      streakRailRef.current.scrollLeft = streakRailRef.current.scrollWidth;
    }
  }, [currentPage, userHistory]);

  // Measure the approach-graph container so the SVG can be drawn in real pixels
  // (crisp, no aspect-ratio distortion) and fill the space the flex layout gives it.
  useEffect(() => {
    const el = winGraphRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => setWinGraphSize({
      w: Math.max(120, Math.round(el.clientWidth)),
      h: Math.max(160, Math.round(el.clientHeight)),
    });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [currentPage, winSort, archiveAttempts.length]);

  // After a reload landing on the win/archive detail, re-open the day that was
  // showing (its id is kept in sessionStorage) so the review/graph returns instead
  // of dropping to the calendar. Waits for auth to settle so the fetch is authorised.
  useEffect(() => {
    if (loadingAuth) return;
    if (currentPage === 'archivePlay' && !archiveDate) {
      const d = sessionStorage.getItem('archiveDate');
      if (d && sessionStorage.getItem('archiveType') === 'mega') openMegaReview(d);
      else if (d) openArchiveDay(d);
      else goPage('archive');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingAuth]);

  // Remember the win-detail tab (temperature / order / graph) across reloads.
  useEffect(() => {
    try { sessionStorage.setItem('winSort', winSort); } catch (e) {}
  }, [winSort]);

  // Stavy aplikace
  const [guess, setGuess] = useState(""); // Aktuální pokus uživatele
  const [attempts, setAttempts] = useState(() => {
    return JSON.parse(localStorage.getItem("attempts")) || [];
  }); // Historie pokusů
  // Pokusy z hraní bez přihlášení, které server zná jen jako číslo (viz
  // gameStorage.prenesenePokusy). Počet na desce = tipy + tyhle.
  const [attemptsCarried, setAttemptsCarried] = useState(0);
  const dailyCount = attempts.length + attemptsCarried;
  const [guessOrder, setGuessOrder] = useState(() => {
    return JSON.parse(localStorage.getItem("guessOrder")) || [];
  }); // Pořadí hadů (pro uložení detailu hry)
  const [lastAttempt, setLastAttempt] = useState(() => {
    return JSON.parse(localStorage.getItem("lastAttempt")) || null;
  }); // Poslední pokus
  const [isGuessed, setIsGuessed] = useState(() => {
    return JSON.parse(localStorage.getItem("isGuessed")) || false;
  }); // Stav vítězství
  // Ke kterému hernímu dni patří deska V TÉHLE INSTANCI.
  //
  // V paměti, ne jen v úložišti, a to je celá oprava z 25. 9. 2026. Úložiště
  // sdílejí všechny instance hry — nainstalovaná PWA i záložka v prohlížeči. Ráno
  // jedna uklidila a zapsala „dnes uklizeno", druhá, otevřená od včerejška, to
  // pak četla jako hotovou věc a svou včerejší desku si nechala. Zrcadlicí efekt
  // ji zapsal zpátky, a od té chvíle tvrdily všechny značky dnešek, zatímco na
  // desce svítilo včerejší vítězné slovo a vstup byl zamčený. Přežilo to i
  // restart telefonu; nahlásila to eliska.lisowa@gmail.com, v logu téhož dne
  // stejný otisk u dalších nejméně dvanácti hráčů.
  //
  // Každá instance teď zná den SVÉ desky a zapisuje ho spolu s ní. Stará instance
  // zapíše starou desku se starým razítkem — vydávat se za dnešní nemůže.
  const boardDayRef = useRef(null);
  if (boardDayRef.current === null) boardDayRef.current = pocatecniRazitkoDesky();
  // Lifts the centered pre-game input to the top the instant a guess is submitted,
  // so it starts gliding up on Enter instead of waiting for the scored attempt to
  // arrive. Reset whenever the board resets (new day / word change / logout).
  const [heroLifted, setHeroLifted] = useState(false);
  const [megaCtaReady, setMegaCtaReady] = useState(() => {
    const solved = JSON.parse(localStorage.getItem("isGuessed")) || false;
    return solved && localStorage.getItem("megaCtaRevealed") === "true";
  });
  const [lbRevealReady, setLbRevealReady] = useState(false);
  const [weeklyLbRevealReady, setWeeklyLbRevealReady] = useState(false);
  const [errorMessage, setErrorMessage] = useState(""); // Chybová zpráva
  const [highlightedIndex, setHighlightedIndex] = useState(null); // Index zvýrazněného slova
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false); // Statistiky po uhádnutí
  const [isWeeklyStatsModalOpen, setIsWeeklyStatsModalOpen] = useState(false); // Statistiky megaslova

  useEffect(() => {
    if (isStatsModalOpen || isWeeklyStatsModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isStatsModalOpen, isWeeklyStatsModalOpen]);

  // Close account dropdown on outside click
  useEffect(() => {
    if (!isAccountMenuOpen) return;
    const handleClick = (e) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(e.target)) {
        setIsAccountMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isAccountMenuOpen]);

  // ===== WEEKLY / MEGA WORD STATE =====
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem("activeTab") || "daily";
  }); // "daily" | "weekly"
  const [weeklyGuess, setWeeklyGuess] = useState("");
  const [weeklyAttempts, setWeeklyAttempts] = useState(() => {
    return JSON.parse(localStorage.getItem("weeklyAttempts")) || [];
  });
  // Totéž co attemptsCarried, pro megaslovo (gameStorage.prenesenePokusy).
  const [weeklyCarried, setWeeklyCarried] = useState(0);
  const weeklyCount = weeklyAttempts.length + weeklyCarried;
  const [weeklyLastAttempt, setWeeklyLastAttempt] = useState(() => {
    return JSON.parse(localStorage.getItem("weeklyLastAttempt")) || null;
  });
  const [weeklyIsGuessed, setWeeklyIsGuessed] = useState(() => {
    return JSON.parse(localStorage.getItem("weeklyIsGuessed")) || false;
  });
  // Ke kterému týdnu patří megaslovní deska V TÉHLE INSTANCI. Stejný důvod jako
  // boardDayRef u denní desky — sdílená `weeklyISOWeek` o ní umí lhát, když ji
  // zapíše jiná instance hry — jen tady by zaseklá deska vydržela až do pondělí.
  const weeklyBoardWeekRef = useRef(null);
  if (weeklyBoardWeekRef.current === null) weeklyBoardWeekRef.current = pocatecniRazitkoTydne();
  const [weeklyErrorMessage, setWeeklyErrorMessage] = useState("");
  // Mega is an allowance for signed-in free players: how many tips are left today
  // and what the ceiling is. null = unlimited (premium, or the gate is off).
  const [megaLeft, setMegaLeft] = useState(null);
  const [megaLimit, setMegaLimit] = useState(null);
  // `resetDailyBoard` je useCallback s prázdnými závislostmi (viz jeho komentář),
  // takže limit si musí přečíst z refu, ne ze zavřené proměnné.
  const megaLimitRef = useRef(null);
  const [weeklyHighlightedIndex, setWeeklyHighlightedIndex] = useState(null);
  const [weeklyIsSubmitting, setWeeklyIsSubmitting] = useState(false);
  const [weeklyLastSubmitTime, setWeeklyLastSubmitTime] = useState(0);
  const [weeklyTimeoutProgress, setWeeklyTimeoutProgress] = useState(0);
  const [weeklyCooldown, setWeeklyCooldown] = useState(2000);
  const [weeklyFlyingWord, setWeeklyFlyingWord] = useState(null);
  const [weeklyLeavingWord, setWeeklyLeavingWord] = useState(null);
  const weeklyInputRef = useRef(null);
  const lbFetchTimerRef = useRef(null); // debounce ref pro leaderboard fetch

  // Při otevření stats/leaderboard modalu nebo po výhře načti žebříček + obnovuj každých 60s
  // currentUser je v deps, aby se žebříček znovu načetl po přihlášení (s auth tokenem)
  // loadingAuth zajišťuje že nevoláme fetch dřív než Firebase vyřeší přihlášení
  useEffect(() => {
    if (!features.leaderboard) return;
    if (!isStatsModalOpen && !isTop10ModalOpen && !isGuessed && !weeklyIsGuessed) return;
    if (loadingAuth) return;

    // Debounce: zrušit předchozí naplánovaný fetch (zabrání duplicitám při rychlých změnách stavu)
    if (lbFetchTimerRef.current) clearTimeout(lbFetchTimerRef.current);
    lbFetchTimerRef.current = setTimeout(() => {
      lbFetchTimerRef.current = null;

      fetchLeaderboard();

      if (weeklyIsGuessed || isWeeklyStatsModalOpen || activeTab === "weekly") {
        fetchWeeklyLeaderboard();
      }
    }, 300);

    // Interval spustit jen když je modal otevřený
    if (isStatsModalOpen || isTop10ModalOpen || isWeeklyStatsModalOpen) {
      const interval = setInterval(() => {
        fetchLeaderboard();
        if (weeklyIsGuessed || isWeeklyStatsModalOpen || activeTab === "weekly") {
          fetchWeeklyLeaderboard();
        }
      }, 60000);
      return () => {
        clearInterval(interval);
        if (lbFetchTimerRef.current) { clearTimeout(lbFetchTimerRef.current); lbFetchTimerRef.current = null; }
      };
    }
    return () => {
      if (lbFetchTimerRef.current) { clearTimeout(lbFetchTimerRef.current); lbFetchTimerRef.current = null; }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStatsModalOpen, isTop10ModalOpen, isGuessed, weeklyIsGuessed, isWeeklyStatsModalOpen, activeTab, currentUser, loadingAuth, features.leaderboard]);
  // Yesterday's word / last week's mega word, revealed only on a deliberate tap.
  const [yesterdaysWord, setYesterdaysWord] = useState(null);
  // Včerejší slovo skupiny. Drží se i s `gid`, protože hráč mezi skupinami
  // přepíná: bez toho by po přepnutí chvíli svítilo slovo cizí skupiny.
  const [groupYesterday, setGroupYesterday] = useState(null); // {gid, word} | null
  const [showGroupYesterdaysWord, setShowGroupYesterdaysWord] = useState(false);
  const [showYesterdaysWord, setShowYesterdaysWord] = useState(false);
  const [lastWeeksMegaWord, setLastWeeksMegaWord] = useState(null);
  // Kdo vybral megaslovo tohoto týdne (jen česky, přichází s /weekly/last_word).
  const [megaOsobnost, setMegaOsobnost] = useState(null);
  const [showLastWeeksMegaWord, setShowLastWeeksMegaWord] = useState(false);
  // Per-language input validation rules loaded from the backend
  // (single source of truth = languages/<lang>.json). null until loaded → the
  // charset check is skipped and the backend validates authoritatively, so a
  // valid letter of any alphabet is never wrongly rejected on the client.
  const [wordRules, setWordRules] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false); // Prevent rapid submissions
  // Pojistka „Počítám…": hodnocení nestíhá a tip se zkouší znovu (scoringRetry.js).
  // Počítadlo, ne ano/ne: čekat může denní tip i megaslovo naráz a první hotový
  // nesmí zhasnout hlášku tomu druhému.
  const [scoringPendingCount, setScoringPendingCount] = useState(0);
  const scoringPending = scoringPendingCount > 0;
  const withScoringRetry = (doFetch) => {
    let counted = false;
    return fetchScoring(doFetch, {
      onPending: () => {
        if (!counted) {
          counted = true;
          setScoringPendingCount((c) => c + 1);
        }
      },
    }).finally(() => {
      if (counted) setScoringPendingCount((c) => Math.max(0, c - 1));
    });
  };
  const [lastSubmitTime, setLastSubmitTime] = useState(0); // Track last submission time
  const [timeoutProgress, setTimeoutProgress] = useState(0); // Progress bar percentage (100 = just submitted, 0 = ready)
  const [currentCooldown, setCurrentCooldown] = useState(2000); // Dynamic cooldown from backend
  const [flyingWord, setFlyingWord] = useState(null); // Word being animated from input to list
  const [leavingWord, setLeavingWord] = useState(null); // Last attempt word being animated away
  const [dailyStats, setDailyStats] = useState(null); // Daily statistics
  const [weeklyStats, setWeeklyStats] = useState(null); // Weekly statistics
  // One board-wide spoiler switch, driven from the header. It masks EVERY word on
  // screen — the solved word and every guess in the list — so a player can share a
  // screenshot of how they're doing without handing over any answers. Temperatures
  // stay visible; that's the part worth showing off.
  //
  // Remembered on this device (29. 9. 2026): a teacher shows the group on the
  // classroom projector with their own word on screen, and a reload used to put
  // it back in plain sight.
  const [wordsHidden, setWordsHidden] = useState(() => {
    try { return localStorage.getItem("wordsHidden") === "1"; } catch (e) { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("wordsHidden", wordsHidden ? "1" : "0"); } catch (e) { /* private mode */ }
  }, [wordsHidden]);

  // Velikost hádaných slov — pro starší hráče (15. 10. 2026). Zvětšují se jen
  // slova (řádky tipů a políčko pro zadání), ne celá stránka: ta by se na úzkém
  // telefonu začala lámat. Jedna CSS proměnná `--word-scale`, kterou čtou
  // `.attempt_label` a `.input-container input`; volba patří zařízení.
  const [wordScale, setWordScale] = useState(() => {
    try {
      const v = parseFloat(localStorage.getItem("wordScale"));
      return WORD_SCALES.includes(v) ? v : 1;
    } catch (e) { return 1; }
  });
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--word-scale", String(wordScale));
    if (wordScale > 1) root.setAttribute("data-word-scale", String(wordScale));
    else root.removeAttribute("data-word-scale");
    try { localStorage.setItem("wordScale", String(wordScale)); } catch (e) { /* private mode */ }
  }, [wordScale]);
  const wordSizePicker = (
    <div className="word-size" role="radiogroup" aria-label={t("settings.wordSize")}>
      {WORD_SCALES.map((v, i) => (
        <button
          key={v}
          type="button"
          role="radio"
          aria-checked={wordScale === v}
          aria-label={t(`settings.wordSize${["Normal", "Larger", "Largest"][i]}`)}
          className={`word-size__opt${wordScale === v ? " is-on" : ""}`}
          style={{ fontSize: `${0.85 + i * 0.25}rem` }}
          onClick={(e) => { e.stopPropagation(); setWordScale(v); }}
        >
          A
        </button>
      ))}
    </div>
  );
  const [inviteCode, setInviteCode] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [myInvitations, setMyInvitations] = useState([]);
  // The solved words live in state, not in a render-time localStorage read: the
  // cross-device resume fills them in AFTER the first paint, and a bare
  // localStorage.getItem() would never re-render to show it.
  const [guessedWord, setGuessedWord] = useState(() => localStorage.getItem("guessedWord") || "");
  const [weeklyGuessedWord, setWeeklyGuessedWord] = useState(() => localStorage.getItem("weeklyGuessedWord") || "");
  // There used to be a second, per-word eye here (isDailyWordHidden /
  // isWeeklyWordHidden) that covered the solved word and nothing else. It was
  // reported as a bug twice in two days, in the same words both times: "I hid it
  // and it is still there." The intent — cover one word from somebody standing
  // next to you — was real, but nobody reaches for it for that; they reach for
  // it to share a screenshot, and covering one word out of sixty-nine is the
  // wrong half of that job. Two identical eyes a thumb's width apart doing
  // differently-sized things is what produced the reports.
  //
  // Every eye now drives `wordsHidden`. One switch, one meaning.
  const inputRef = useRef(null); // Reference to input element for animation positioning

  // Ukládání stavu do localStorage při změně pokusů, posledního pokusu nebo stavu vítězství
  useEffect(() => {
    localStorage.setItem("attempts", JSON.stringify(attempts));
    localStorage.setItem("lastAttempt", JSON.stringify(lastAttempt));
    localStorage.setItem("isGuessed", JSON.stringify(isGuessed));
    // Razítko jde VŽDY s deskou, ve stejném zápisu. Kdo zapisuje desku, ví, ke
    // kterému dni patří — a nikdo jiný to spolehlivě neví (viz boardDayRef).
    localStorage.setItem("attemptsDay", boardDayRef.current);
  }, [attempts, lastAttempt, isGuessed]);

  // Persist weekly game state
  useEffect(() => {
    localStorage.setItem("weeklyAttempts", JSON.stringify(weeklyAttempts));
    localStorage.setItem("weeklyLastAttempt", JSON.stringify(weeklyLastAttempt));
    localStorage.setItem("weeklyIsGuessed", JSON.stringify(weeklyIsGuessed));
    // Razítko s deskou, ve stejném zápisu — viz weeklyBoardWeekRef.
    localStorage.setItem("weeklyAttemptsWeek", weeklyBoardWeekRef.current);
  }, [weeklyAttempts, weeklyLastAttempt, weeklyIsGuessed]);

  useEffect(() => {
    localStorage.setItem("activeTab", activeTab);
  }, [activeTab]);

  // Cross-device resume (R8): on login pull the server's in-progress state for the
  // daily + mega words and rehydrate the board when the server is ahead of this
  // device. Every guess by a logged-in user is saved server-side, so on a fresh
  // device (empty local board) the server state hydrates straight in — that's what
  // makes a word started elsewhere appear right after login. hydrationFromState
  // still lets a locally-fresher board win (a guess this device made that the
  // server read hasn't caught up to), so resume never clobbers a game in progress
  // here. getIdToken(true) forces a fresh token: right after an interactive login
  // the cached token can be stale/absent, which is why it used to need a refresh.
  // Převzetí výhry hosta: nejvýš jeden pokus o každé slovo za načtení stránky.
  // Značka v localStorage drží jen definitivní odpověď (claimSettled), takže po
  // selhání se to zkusí znovu — ale až při dalším načtení, ne při každém
  // překreslení, kterým tenhle efekt proběhne.
  const claimTriedRef = useRef(new Set());

  useEffect(() => {
    if (!currentUser || loadingAuth) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getIdToken(true);
        const headers = { Authorization: `Bearer ${token}` };
        const fetchState = (type) =>
          apiFetch(`/user/game_state?type=${type}`, { headers })
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null);
        const [daily, mega] = await Promise.all([fetchState("daily"), fetchState("mega")]);
        if (cancelled) return;

        // The solved word is adopted INDEPENDENTLY of board hydration. hydrationFromState
        // bails out when this device is already level with the server (same attempt
        // count), which is exactly the state of a device that pulled the guesses
        // earlier but never had the word — it was won elsewhere, so it only ever
        // lived in the winning device's localStorage. Without this the win box stays
        // blank forever on every device but the one that made the final guess.
        adoptSolvedWord(daily && daily.state, setGuessedWord, "guessedWord");
        adoptSolvedWord(mega && mega.state, setWeeklyGuessedWord, "weeklyGuessedWord");

        const localDaily = JSON.parse(localStorage.getItem("attempts") || "[]");

        // A win made as a guest exists only in this browser — a player without an
        // account has no result document to write it into. The win screen is also
        // exactly where we ask them to register, so "won, then signed up" is the
        // normal path, and without this the new account opens on "Zatím žádná
        // výhra" while the board behind it still celebrates. The server verifies
        // the claim against its own win log; this only points at it. Marked by the
        // solved word so a claim the log cannot back is not retried every load.
        // Today's board if it holds a win; otherwise yesterday's, from the
        // breadcrumb the daily reset leaves behind (checkNewDay) — signing up
        // the evening you won and coming back the next morning is the common
        // shape, and by then the board is gone.
        const serverSolved = !!(daily && daily.state && daily.state.status === "guessed");
        const solvedWord = localStorage.getItem("guessedWord");
        let claiming = null;
        if (solvedWord && !serverSolved) {
          claiming = { key: solvedWord, day: liveDayIso(), attempts: localDaily.length };
        } else if (!solvedWord) {
          try {
            const last = JSON.parse(localStorage.getItem("lastWin") || "null");
            if (last && last.word && last.day) {
              claiming = { key: `${last.day}:${last.word}`, day: last.day, attempts: last.attempts };
            }
          } catch (e) { /* an unreadable breadcrumb is simply no breadcrumb */ }
        }
        // The mega word is the same story over a longer week: it stays live from
        // Monday to Monday, so a win made as a guest is still claimable days
        // later — and a player reported exactly that ("uhodla jsem megaslovo,
        // pak se registrovala, historie nikde").
        const localWeeklyBoard = JSON.parse(localStorage.getItem("weeklyAttempts") || "[]");
        const megaSolvedWord = localStorage.getItem("weeklyGuessedWord");
        const megaServerSolved = !!(mega && mega.state && mega.state.status === "guessed");
        if (megaSolvedWord && !megaServerSolved) {
          claiming = claiming || null; // daily first; mega is claimed alongside
          const megaKey = `mega:${megaSolvedWord}`;
          if (localStorage.getItem("guestMegaWinClaimed") !== megaKey && !claimTriedRef.current.has(megaKey)) {
            claimTriedRef.current.add(megaKey);
            try {
              const c = await apiFetch(`/user/claim_guest_win`, {
                method: "POST",
                headers: { ...headers, "Content-Type": "application/json", "X-User-ID": getUserId() },
                body: JSON.stringify({ attempts: localWeeklyBoard.length, type: "mega" }),
              });
              const r = c.ok ? await c.json() : null;
              if (claimSettled(r)) localStorage.setItem("guestMegaWinClaimed", megaKey);
              if (r && r.claimed && !cancelled) fetchUserHistory();
            } catch (e) { /* the board on screen is unaffected either way */ }
          }
        }

        if (claiming && localStorage.getItem("guestWinClaimed") !== claiming.key && !claimTriedRef.current.has(claiming.key)) {
          claimTriedRef.current.add(claiming.key);
          try {
            const claim = await apiFetch(`/user/claim_guest_win`, {
              method: "POST",
              headers: { ...headers, "Content-Type": "application/json", "X-User-ID": getUserId() },
              body: JSON.stringify({ attempts: claiming.attempts, day: claiming.day }),
            });
            const r = claim.ok ? await claim.json() : null;
            if (claimSettled(r)) localStorage.setItem("guestWinClaimed", claiming.key);
            if (r && r.claimed) {
              localStorage.removeItem("lastWin");
              if (!cancelled) fetchUserHistory();
            }
          } catch (e) { /* the board on screen is unaffected either way */ }
        }
        // The other order: registered BEFORE finishing. Nothing above covers it
        // — there is no win to claim yet — and a guest's tips are never persisted,
        // so the account starts counting from whichever guess they signed up on.
        // A player finishing on 25 appeared in the friends list as having done it
        // in 5 (reported 2026-08-28).
        //
        // Only sent when it would actually raise the server's count, so a player
        // who was never a guest, or who has already been carried over, makes no
        // request at all. The server refuses to lower it, so a stale board here
        // cannot take anything away.
        const carryOver = async (localBoard, state, type) => {
          if (!fireCarry(localBoard, state)) return;
          try {
            await apiFetch(`/user/carry_guest_progress`, {
              method: "POST",
              headers: { ...headers, "Content-Type": "application/json", "X-User-ID": getUserId() },
              // `game_day` je den, ke kterému byla TAHLE DESKA postavena — ne den
              // z hodin. Ten rozdíl je celý smysl: aplikace, která proběhla přes
              // rotaci ve 3:00, si drží včerejší desku, ale `gameEpoch()` by jí
              // spočítalo dnešek a server by neměl podle čeho poznat, že mu
              // někdo nese včerejšek. Značka v localStorage se naproti tomu píše
              // jen při úklidu dne, takže zamrzlou aplikaci prozradí.
              //
              // Nahlášeno 22. 9. 2026: 46 tipů z „velryby" se uložilo pod „duši"
              // jako 46 pokusů, které hráč nikdy neudělal.
              body: JSON.stringify({
                attempts: localBoard.length,
                type,
                game_day: boardDay(type),
              }),
            });
          } catch (e) { /* the board on screen is unaffected either way */ }
        };
        if (!solvedWord) await carryOver(localDaily, daily && daily.state, "daily");
        if (!megaSolvedWord) await carryOver(localWeeklyBoard, mega && mega.state, "mega");

        const dh = hydrationFromState(daily && daily.state, localDaily);
        setAttemptsCarried(prenesenePokusy(daily && daily.state, dh ? dh.attempts : localDaily));
        if (dh) {
          // Server vrací stav AKTUÁLNÍHO dne, takže deska z něj postavená patří
          // dnešku. Nastavit před setAttempts: razítko zapíše až zrcadlicí efekt,
          // a ten si ho přečte z refu.
          boardDayRef.current = dnesniHerniDen();
          setAttempts(dh.attempts);
          setLastAttempt(dh.lastAttempt);
          setIsGuessed(dh.isGuessed);
          if (dh.isGuessed) {
            setMegaCtaReady(true);
            localStorage.setItem("megaCtaRevealed", "true");
          }
        }

        const localWeekly = JSON.parse(localStorage.getItem("weeklyAttempts") || "[]");
        const wh = hydrationFromState(mega && mega.state, localWeekly);
        setWeeklyCarried(prenesenePokusy(mega && mega.state, wh ? wh.attempts : localWeekly));
        if (wh) {
          // Stav AKTUÁLNÍHO týdne ze serveru — viz boardDayRef u denní desky.
          weeklyBoardWeekRef.current = dnesniHerniTyden();
          setWeeklyAttempts(wh.attempts);
          setWeeklyLastAttempt(wh.lastAttempt);
          setWeeklyIsGuessed(wh.isGuessed);
        }
        // Today's mega allowance, so the counter (and the locked input) are right
        // before the player spends a tip. Absent for premium = unlimited.
        if (mega && mega.mega_attempts_left !== undefined) {
          setMegaLeft(mega.mega_attempts_left);
          setMegaLimit(mega.mega_daily_limit ?? null);
          megaLimitRef.current = mega.mega_daily_limit ?? null;
        } else {
          setMegaLeft(null);
        }
      } catch (e) {
        /* resume is best-effort — never block the game on it */
      }
    })();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, loadingAuth]);

  // On logout, clear the cached board so a shared device never bleeds one
  // account's game into the next player's (the next login rehydrates from server).
  // Guards on a real set->null transition so an anonymous first load is untouched.
  const prevUidRef = useRef(null);
  useEffect(() => {
    const uid = currentUser ? currentUser.uid : null;
    if (prevUidRef.current && !uid) {
      clearGameCache();
      setAttempts([]);
      setAttemptsCarried(0);
      setHeroLifted(false);
      setLastAttempt(null);
      setIsGuessed(false);
      setGuessOrder([]);
      setWeeklyAttempts([]);
      setWeeklyCarried(0);
      setWeeklyLastAttempt(null);
      setWeeklyIsGuessed(false);
    }
    prevUidRef.current = uid;
  }, [currentUser]);

  // Update timeout progress bar
  useEffect(() => {
    const updateProgress = () => {
      const now = Date.now();
      const timeSinceLastSubmit = now - lastSubmitTime;
      const progress = Math.max(100 - (timeSinceLastSubmit / currentCooldown) * 100, 0);
      setTimeoutProgress(progress);
      
      if (progress > 0) {
        requestAnimationFrame(updateProgress);
      }
    };
    
    if (lastSubmitTime > 0) {
      updateProgress();
    }
    // currentCooldown is read inside the frame loop on purpose: the bar must animate
    // against the cooldown in force when the guess was submitted, not restart if it
    // changes mid-animation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastSubmitTime]);

  // Archive cooldown bar — same animation as the daily board, so the archive
  // input shows a running timeout instead of greying out while it waits.
  useEffect(() => {
    const update = () => {
      const progress = Math.max(100 - ((Date.now() - archiveLastSubmit) / archiveCooldown) * 100, 0);
      setArchiveTimeoutProgress(progress);
      if (progress > 0) requestAnimationFrame(update);
    };
    if (archiveLastSubmit > 0) update();
  }, [archiveLastSubmit]); // eslint-disable-line react-hooks/exhaustive-deps

  // Group-round cooldown bar — same animation as every other board, so the input
  // shows a running timeout instead of silently ignoring the next Enter.
  useEffect(() => {
    const update = () => {
      const progress = Math.max(100 - ((Date.now() - roundLastSubmit) / roundCooldown) * 100, 0);
      setRoundTimeoutProgress(progress);
      if (progress > 0) requestAnimationFrame(update);
    };
    if (roundLastSubmit > 0) update();
  }, [roundLastSubmit]); // eslint-disable-line react-hooks/exhaustive-deps

  // Weekly timeout progress bar
  useEffect(() => {
    const updateWeeklyProgress = () => {
      const now = Date.now();
      const progress = Math.max(100 - ((now - weeklyLastSubmitTime) / weeklyCooldown) * 100, 0);
      setWeeklyTimeoutProgress(progress);
      if (progress > 0) requestAnimationFrame(updateWeeklyProgress);
    };
    if (weeklyLastSubmitTime > 0) updateWeeklyProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeklyLastSubmitTime]);

  // Fetch daily statistics
  const fetchStats = useCallback(async () => {
    try {
      // The attempt count goes up only once the game is won — it is what the
      // backend compares against to say where this player landed, and asking
      // "how did 3 compare" while still on guess 3 would be a rank in a game
      // that is not over. Winning changes the dependency, so the answer arrives
      // with the win.
      const q = isGuessed && dailyCount ? `?attempts=${dailyCount}` : '';
      const response = await apiFetch(`/stats${q}`);
      if (response.ok) {
        const stats = await response.json();
        setDailyStats(stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [isGuessed, dailyCount]);

  // The counters no longer have a poller of their own — they ride along on the
  // /poll tick below. What is left here is the one moment the answer genuinely
  // changes: the win, when the request first carries `?attempts=` and comes back
  // with the player's rank.
  useEffect(() => {
    if (isGuessed) fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuessed]);

  // Fetch weekly statistics
  const fetchWeeklyStats = useCallback(async () => {
    try {
      const q = weeklyIsGuessed && weeklyCount ? `?attempts=${weeklyCount}` : '';
      const response = await apiFetch(`/weekly/stats${q}`);
      if (response.ok) {
        const stats = await response.json();
        setWeeklyStats(stats);
      }
    } catch (error) {
      console.error('Failed to fetch weekly stats:', error);
    }
  }, [weeklyIsGuessed, weeklyCount]);

  // Same as the daily one: the mega win is when the rank becomes a real question.
  useEffect(() => {
    if (weeklyIsGuessed) fetchWeeklyStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeklyIsGuessed]);

  // What the /poll tick appends so a winner keeps their rank. Kept in a ref
  // rather than a dependency: the tick must be armed once per mount, and making
  // it depend on the score would re-arm it on every guess — the exact mistake
  // useStablePoll exists to prevent.
  const pollQueryRef = useRef("");
  // Vteřiny do nejbližší rotace, jak je hlásí server. `null` = zatím nevíme,
  // což znamená „chovej se jako přes den" — bezpečná strana.
  const rotationInRef = useRef(null);
  useEffect(() => {
    const parts = [];
    if (isGuessed && dailyCount) parts.push(`attempts=${dailyCount}`);
    if (weeklyIsGuessed && weeklyCount) {
      parts.push(`weekly_attempts=${weeklyCount}`);
    }
    pollQueryRef.current = parts.length ? `?${parts.join("&")}` : "";
  }, [isGuessed, dailyCount, weeklyIsGuessed, weeklyCount]);

  // Have we crossed into a new game day (03:00 Prague) since we last looked?
  //
  // Úklid denní desky. Volají ho DVĚ cesty: kontrola podle hodin v zařízení
  // (checkNewDay níž) a kontrola podle herního dne, který posílá server. Proto
  // je vytažený sem a ne zkopírovaný — dvě kopie by se rozešly a jedna z nich
  // by přestala mazat něco, co druhá maže. Přesně tak se rozešly čtyři
  // pollovací cesty, než se sloučily do /poll.
  //
  // `predchoziDen` je ISO datum dne, který právě skončil, nebo null. Slouží jen
  // k drobečku o hostově výhře.
  //
  // `novyDen` je ISO datum dne, do kterého se vstupuje, a je to POJISTKA PROTI
  // DRUHÉMU ÚKLIDU TÉŽE ROTACE. Obě cesty si totiž vedly vlastní značku —
  // `checkNewDay` psal jen `gameDate`, dotazování `gameDate` i `serverGameDay` —
  // takže když rotaci odbavily hodiny, dotazování o tom nevědělo a uklidilo
  // podruhé. Dokud úklid mazal jen desku, nebylo to vidět: po rotaci je stejně
  // prázdná. Od 15. 9. 2026 ráno sahá i na denní příděl tipů na megaslovo, a tím
  // se z toho stala nahlášená vada — brettova.n@gmail.com téhož dne odpoledne:
  // deset tipů na desce a počítadlo na nule, protože druhý úklid jí příděl
  // vrátil na padesát uprostřed hry.
  //
  // Proč se porovnává DEN a ne jen „už jsem běžel": hodiny v zařízení se můžou
  // mýlit. Když se spletou, oba spouštěče pojmenují jiný den, značka se
  // neshodne a server si úklid PROSADÍ — což je přesně to, co má. Shoda nastane
  // jen tehdy, když se oba shodnou, že jde o tutéž rotaci.
  //
  // OD 25. 9. 2026 SE TOU ZNAČKOU UŽ NEBLOKUJE ÚKLID DESKY — jen příděl
  // megaslova. Blokovat celý úklid byla ta díra, kterou prošla zamrzlá deska:
  // `lastBoardResetDay` leží ve sdíleném úložišti, a když ho ráno zapsala JINÁ
  // instance hry, tahle podle něj svou včerejší desku nechala být. O tom, jestli
  // se deska uklízí, teď rozhoduje její vlastní razítko (boardDayRef) proti
  // `dnesniHerniDen()` — a volající to kontrolují dřív, než sem vůbec dojdou.
  // Dnešní rozehranou desku tím nic nesmaže: nese dnešní razítko.
  //
  // A ANI PŘÍDĚL MEGASLOVA UŽ NEBLOKUJE (oprava téhož dne odpoledne). Sdílená
  // značka vadila i tady: když ráno uklidila jiná instance, tahle po vlastním
  // úklidu příděl nevrátila a celý den ukazovala včerejší nulu — přesně vada
  // surovikova@email.cz z 15. 9., kvůli které příděl do úklidu patří. Chránit
  // se přitom už není před čím: tatáž instance tentýž den dvakrát neuklízí,
  // protože po prvním úklidu nese deska dnešní razítko a volající ji nepustí.
  // Značka se dál píše jen kvůli návratu ke starší verzi aplikace.
  const resetDailyBoard = useCallback((predchoziDen, novyDen) => {
    // Než deska zmizí: byla-li to VÝHRA bez účtu, nech drobeček. Hostova výhra
    // neexistuje nikde jinde — server pro člověka bez účtu žádný dokument nemá —
    // takže smazat ji ve 3:00 je okamžik, kdy se stává nenávratnou.
    try {
      const wonWord = localStorage.getItem("guessedWord");
      if (wonWord && JSON.parse(localStorage.getItem("isGuessed") || "false")) {
        localStorage.setItem("lastWin", JSON.stringify({
          word: wonWord,
          attempts: JSON.parse(localStorage.getItem("attempts") || "[]").length,
          day: predchoziDen || null,
        }));
      }
    } catch (e) { /* a breadcrumb is never worth breaking the reset over */ }

    localStorage.removeItem("attempts");
    localStorage.removeItem("lastAttempt");
    localStorage.removeItem("isGuessed");
    localStorage.removeItem("guessedWord");
    setGuessedWord("");
    localStorage.removeItem("wordHash");
    localStorage.removeItem("wordHashDay");
    localStorage.removeItem("megaCtaRevealed");
    // Pořadí tipů patří desce. Jediné místo, které ho mazalo, zmizelo 19. 7. 2026
    // s restart modalem (b13f545), a od té doby se jen připisovalo: u každého
    // hráče všechny tipy od července, celé znovu serializované při každém tipu.
    localStorage.removeItem("guessOrder");
    setGuessOrder([]);
    localStorage.setItem("activeTab", "daily");

    setAttempts([]);
    setAttemptsCarried(0);
    setHeroLifted(false);
    setLastAttempt(null);
    setIsGuessed(false);
    setMegaCtaReady(false);
    setLbRevealReady(false);
    setActiveTab("daily");

    // Denní příděl tipů na megaslovo. Načítá se JEN při startu appky nebo při
    // přihlášení (efekt se závislostmi [currentUser, loadingAuth]), takže komu
    // appka zůstane otevřená přes rotaci, tomu tu zůstane viset včerejší nula a
    // megaslovo má zamčené celý další den, i když má padesát volných tipů.
    //
    // Nahlásila to 15. 9. 2026 surovikova@email.cz: poslední tip v 01:48, tedy
    // ještě herní den 14. 9., a v 11:34 ji to nepustilo. V databázi měla nula
    // využitých na 15. 9. Neplatila za to, přišla o den hraní.
    //
    // Nastavuje se na plný příděl, ne na null: hráč tím rovnou vidí, kolik má,
    // a kdyby mezitím část vyčerpal na jiném zařízení, server ho stejně zastaví
    // a odpověď 429 tuhle hodnotu srovná (viz setMegaLeft(0) u chyby limitu).
    // Autoritou zůstává server, tohle je jen to, co vidí obrazovka.
    setMegaLeft(megaLimitRef.current);

    // Prázdná deska patří dni, do kterého se vstupuje. Zapsat hned, ne až
    // efektem: kdyby se stav nezměnil (deska už prázdná byla), efekt by neproběhl
    // a v úložišti by zůstalo staré razítko.
    boardDayRef.current = novyDen || dnesniHerniDen();
    localStorage.setItem("attemptsDay", boardDayRef.current);

    // Až na konci, ne hned u vstupní podmínky: kdyby se cestou něco pokazilo,
    // ať se úklid raději zopakuje, než aby se značka tvářila, že proběhl.
    if (novyDen) localStorage.setItem("lastBoardResetDay", novyDen);
  }, []);

  // Úklid megaslovní desky do nového týdne. Jedna funkce pro oba spouštěče
  // (hodiny v checkNewDay, server v dotazování) — dvě kopie by se časem rozešly,
  // jako se kdysi rozešly čtyři pollovací cesty. Mazání je beze změny proti
  // tomu, co dřív stálo přímo v checkNewDay; nové je jen razítko.
  const resetWeeklyBoard = useCallback((novyTyden) => {
    localStorage.removeItem("weeklyAttempts");
    localStorage.removeItem("weeklyLastAttempt");
    localStorage.removeItem("weeklyIsGuessed");
    localStorage.removeItem("weeklyGuessedWord");
    setWeeklyGuessedWord("");
    localStorage.removeItem("weeklyWordHash");
    localStorage.removeItem("weeklyWordHashWeek");
    localStorage.removeItem("megaCtaRevealed");
    setWeeklyAttempts([]);
    setWeeklyCarried(0);
    setWeeklyLastAttempt(null);
    setWeeklyIsGuessed(false);
    setMegaCtaReady(false);
    setLbRevealReady(false);
    // Hned, ne až efektem: kdyby deska už prázdná byla, efekt by neproběhl
    // a v úložišti by zůstalo staré razítko.
    weeklyBoardWeekRef.current = novyTyden;
    localStorage.setItem("weeklyAttemptsWeek", novyTyden);
    // Dál kvůli zařízením, která by se vrátila ke starší verzi aplikace.
    localStorage.setItem("weeklyISOWeek", novyTyden);
  }, []);

  // Checked on mount AND every time the app comes back to the foreground. Mount
  // alone is wrong for the shape most people play in: an installed PWA is never
  // closed, so it never mounts again, and one left open across 03:00 kept
  // yesterday's date for as long as the phone lived.
  //
  // The only thing that then noticed was the word-hash poll — and it announces
  // itself as "the administrator changed the word", which for the ordinary
  // nightly rotation is simply untrue. Worse, it needs the network: the first
  // request after a phone wakes usually fails, so the stale hash survived, and
  // the mismatch surfaced ten minutes later, in the middle of a game that was
  // fine. Reported 2026-09-05: it cost a player the first 38 tips of a game the
  // word had never rotated out of, and filed her win as 43 attempts while the
  // screen congratulating her said 5.
  //
  // This check reads the clock and localStorage and nothing else, so it works on
  // a phone that has just woken with no signal — which is exactly when it needs
  // to. Ordering against the poll is safe in either direction: the poll reads
  // savedHash AFTER its await, so this synchronous body has always finished by
  // then and the poll simply finds no hash and stores the current one.
  useEffect(() => {
    const checkNewDay = () => {
      // Rozhoduje razítko TÉHLE desky, ne sdílená `gameDate`. Tu mohla ráno
      // zapsat jiná instance hry a tahle by pak svou včerejší desku považovala
      // za uklizenou (25. 9. 2026, viz boardDayRef).
      const dnes = dnesniHerniDen();
      if (boardDayRef.current < dnes) {
        resetDailyBoard(boardDayRef.current, dnes);
      } else if (boardDayRef.current > dnes) {
        // Razítko z budoucnosti: telefon měl hodiny napřed a pak se opravily.
        // Deska se NEuklízí — hrálo se na ní slovo, které server hodnotil jako
        // dnešní. Jen se srovná razítko, jinak by se zítra nezdálo „starší"
        // a deska by přečkala zítřejší rotaci. Stará logika porovnávala `!==`
        // a tohle zvládala; `<` samo ne.
        boardDayRef.current = dnes;
        localStorage.setItem("attemptsDay", dnes);
      }
      // Dál se píše kvůli zařízením, která by se vrátila ke starší verzi
      // aplikace, a jako záloha pro `boardDay`. O úklidu už nerozhoduje.
      localStorage.setItem("gameDate", gameEpoch().toDateString());

      // Totéž pro megaslovo: razítko TÉHLE týdenní desky proti týdnu, na kterém
      // se shodnou hodiny se serverem — ne sdílená `weeklyISOWeek`.
      const tyden = dnesniHerniTyden();
      if (weeklyBoardWeekRef.current < tyden) {
        resetWeeklyBoard(tyden);
      } else if (weeklyBoardWeekRef.current > tyden) {
        // Totéž co u denní desky — jen by tu zaseknutí trvalo celý týden.
        weeklyBoardWeekRef.current = tyden;
        localStorage.setItem("weeklyAttemptsWeek", tyden);
      }
    };

    checkNewDay();
    // Waking the app is the moment a missed rotation becomes visible, and the
    // moment before the player types their first tip of the day.
    //
    // Tři události, ne jedna. `visibilitychange` mine okno schované za jiným
    // oknem (to se nikdy neskrylo) a `pageshow` je jediné, co přijde, když iOS
    // vrátí zmrazenou stránku z back/forward cache — právě ten případ, kdy
    // hráči ráno svítí včerejší slovo.
    const onVisible = () => { if (document.visibilityState === 'visible') checkNewDay(); };
    const onRestored = (e) => { if (e.persisted) onVisible(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    window.addEventListener('pageshow', onRestored);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      window.removeEventListener('pageshow', onRestored);
    };
    // mount-only: checkNewDay touches localStorage and setters, never a state
    // value, so it needs no dependencies and must not be re-armed on re-render.
    // `resetDailyBoard` i `resetWeeklyBoard` jsou useCallback s prázdnými
    // závislostmi, takže jsou stálé.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Yesterday's word and last week's mega word.
  //
  // Both fail silently. The old version of this put an error banner across the
  // game when the fetch failed — restored verbatim on 2026-08-15 and seen within
  // the hour ("Chyba při načítání včerejšího slova: Failed to fetch"). A
  // decorative extra that nobody asked for yet must never announce itself as a
  // game error; without the word the link simply does not appear.
  //
  // Declared out here rather than inside the mount effect because the rotation
  // watcher has to call them too: they used to be fetched ONCE per app start, so
  // an app left open across 03:00 kept yesterday's answer from the day before.
  // A player on 2026-08-30 was shown "hodina" (the 28th) five hours after the
  // word had rotated, because their PWA had been open since the 29th and a PWA
  // has no refresh to fall back on.
  const fetchYesterdaysWord = useCallback(async () => {
    try {
      const response = await apiFetch(`/yesterdays_word`);
      if (!response.ok) return;
      const data = await response.json();
      setYesterdaysWord(data.yesterdays_word);
    } catch (error) {
      // Silently fail
    }
  }, []);

  // Včerejší slovo JEDNÉ skupiny. Tahá se až když se otevře její deska, ne s
  // každým tikem seznamu skupin: Firestore se platí po přečtených dokumentech a
  // tohle je údaj, který se za celý den změní jednou.
  //
  // `null` v odpovědi není chyba. Skupina nemusí hrát každý den — nikdo nemusel
  // slovo zadat, nebo zadavatel tah přeskočil. Pak se na obrazovce neukáže nic;
  // nabídnout „ukaž včerejší slovo" a pak přiznat, že žádné není, je horší než
  // mlčet.
  // `getIdToken` se z AuthContextu vrací při každém renderu jako nová funkce.
  // V poli závislostí by tedy měnila identitu i tahle funkce, efekt níž by se
  // pouštěl dokola — a odkryté slovo by se hned schovávalo zpátky, protože ten
  // efekt odkrytí ruší. Přes ref je závislost prázdná a chování stabilní.
  const getIdTokenRef = useRef(getIdToken);
  getIdTokenRef.current = getIdToken;

  const fetchGroupYesterdaysWord = useCallback(async (gid) => {
    if (!gid) {
      setGroupYesterday(null);
      return;
    }
    try {
      // S tokenem. Endpoint je jen pro členy skupiny, takže holý `apiFetch`
      // vrátí 401 — a protože se chyba schválně polyká, projeví se to tím, že
      // se nabídka prostě neukáže. Přesně tak to 19. 9. 2026 vypadalo na
      // stagingu: funkce hotová, testy zelené, na obrazovce nic.
      const token = await getIdTokenRef.current();
      const response = await apiFetch(`/groups/${gid}/yesterdays_word`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return;
      const data = await response.json();
      // Mezitím se mohlo přepnout na jinou skupinu. Pozdní odpověď té opuštěné
      // nesmí přepsat tu, která je na obrazovce — jinak si hráč otevře druhou
      // skupinu a její slovo mu zmizí, protože dorazila odpověď té první.
      if (openRoundGidRef.current !== gid) return;
      setGroupYesterday({ gid, word: data.word || null });
    } catch (error) {
      // Silently fail — je to doplněk, ne hra.
    }
  }, []);

  const fetchLastWeeksMegaWord = useCallback(async () => {
    try {
      const response = await apiFetch(`/weekly/last_word`);
      if (response.ok) {
        const data = await response.json();
        setLastWeeksMegaWord(data.last_word || null);
        setMegaOsobnost(data.osobnost || null);
      }
    } catch (error) {
      // Silently fail
    }
  }, []);

  // Check periodically whether the word has rotated under us.
  //
  // This poll is the single biggest source of load on the backend: two requests
  // per tick from every open tab, forever, whether or not anyone is looking. At
  // 30s that was ~5,760 requests a day per tab and the bulk of 3.85M/day — which
  // is instance hours, log ingestion and egress, the three largest lines on the
  // bill. 60s halves it and a mid-day rotation still reaches players inside a
  // minute. A hidden tab stops asking entirely and checks once on the way back,
  // which is when it matters anyway.
  useEffect(() => {
    // Jiný otisk téhož dne maže desku, tak ho nejdřív ověř druhým dotazem.
    // Jediná odpověď z instance, která zrovna nepřečetla slovo, smazala
    // 2. 11. 2026 hráčce rozehranou hru s hláškou o zásahu administrátora.
    // Když se ověření nepovede, nemaže se nic — další poll to zkusí znovu.
    const potvrditNovyOtisk = async (otisk, cesta = `/word_hash`) => {
      try {
        const r = await apiFetch(cesta);
        if (!r || !r.ok) return false;
        const b = await r.json();
        return typeof b.word_hash === "string" && b.word_hash === otisk;
      } catch (e) {
        return false;
      }
    };
    const checkWordChanged = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        // One request for all four things this tick needs — the two hashes and
        // the two counters. See /poll on the backend for the measurement that
        // made it worth doing. `?attempts=` rides along so a winner's rank
        // survives the next tick instead of vanishing from the box.
        const res = await apiFetch(`/poll${pollQueryRef.current}`).catch(() => null);
        if (!res || !res.ok) return;
        const body = await res.json();

        // Kdy se příště rotuje, říká server — ne hodiny zařízení. Telefon
        // s posunutým časem by si rychlé okno naplánoval vedle, a to je zrovna
        // ta hodina, kdy na něm záleží.
        if (typeof body.next_rotation_in === "number") {
          rotationInRef.current = body.next_rotation_in;
        }

        // The counters used to be two more polls on their own timer.
        if (body.stats) setDailyStats(body.stats);
        if (body.weekly_stats) setWeeklyStats(body.weekly_stats);

        // Herní den PODLE SERVERU. Klient si ho dosud počítal z hodin v zařízení
        // a když se telefon splete, hra se splete s ním: 13. 9. 2026 se hráčce
        // držela včerejší deska a její počítadlo 47 tipů odešlo na server jako
        // „přenesené pokusy" nového dne. Kontrola podle hodin zůstává, protože
        // funguje i bez signálu, ale jakmile server promluví, rozhoduje on.
        //
        // A jako u otisku: **chybějící hodnota není změna.** Když ještě žádný
        // den uložený nemáme, jen si ho zapíšeme. Číst prázdno jako nový den by
        // při prvním dotazování po nasazení smazalo hru všem otevřeným
        // záložkám naráz.
        // Porovnává se „je NOVĚJŠÍ", ne „je jiný". Dotazování nemá žádnou
        // pojistku proti překrytí — pomalý požadavek se může vrátit až po
        // rychlejším, který vyjel po něm — a „jiný" by takovou opožděnou
        // odpověď přečetl jako druhou změnu dne a uklidil desku podruhé, tentokrát
        // zbytečně. Herní den se dopředu jen posouvá, takže menší hodnota je vždy
        // stará odpověď: nechá se ležet a ani se nezapíše. Formát "YYYY-MM-DD"
        // se dá porovnat jako řetězec.
        // Jen ve tvaru data: nesmyslná hodnota by se uložila, v dnesniHerniDen()
        // by jako řetězec vyhrála nad hodinami a den by stál navždy.
        if (typeof body.game_day === "string" && JE_HERNI_DEN.test(body.game_day)) {
          // Uložená nesmyslná hodnota se bere jako chybějící. Jinak by ji platná
          // nikdy nepřepsala („je novější" s nesmyslem neprojde) a serverová
          // cesta by na tom zařízení zůstala mrtvá.
          const ulozeny = localStorage.getItem("serverGameDay");
          const savedDay = ulozeny && JE_HERNI_DEN.test(ulozeny) ? ulozeny : null;
          const serverPosunulDen = !!savedDay && body.game_day > savedDay;
          if (!savedDay || serverPosunulDen) {
            localStorage.setItem("serverGameDay", body.game_day);
          }
          // O desce rozhoduje její razítko proti dnešku — tomu samému, na který
          // se ptá kontrola při probuzení, takže se o rotaci nepohádají. Prázdný
          // `savedDay` tu desku nesmaže: razítko stávajících hráčů je odvozené
          // z jejich `gameDate` a na dnešní desce ukáže dnešek.
          const dnes = dnesniHerniDen();
          if (boardDayRef.current < dnes) {
            resetDailyBoard(boardDayRef.current, dnes);
            localStorage.setItem("gameDate", gameEpoch().toDateString());
          } else if (boardDayRef.current > dnes) {
            // Razítko z budoucnosti — viz checkNewDay.
            boardDayRef.current = dnes;
            localStorage.setItem("attemptsDay", dnes);
          }
          // Megaslovo týmž pravidlem. Dotazování týden do 25. 9. 2026 nehlídalo
          // vůbec, takže telefonu s hodinami pozadu zůstala minulotýdenní deska
          // i po pondělní rotaci — až do dalšího pondělí.
          const tyden = dnesniHerniTyden();
          if (weeklyBoardWeekRef.current < tyden) {
            resetWeeklyBoard(tyden);
          } else if (weeklyBoardWeekRef.current > tyden) {
            weeklyBoardWeekRef.current = tyden;
            localStorage.setItem("weeklyAttemptsWeek", tyden);
          }
          if (serverPosunulDen && tydenZDne(body.game_day) !== tydenZDne(savedDay)) {
            // Minulý týden se posunul. Řádek pod deskou se jinak stahoval jen
            // při startu a při změně otisku — a otisk úklid maže, takže po
            // pondělní rotaci v nezavřené appce ukazoval slovo předminulého týdne.
            fetchLastWeeksMegaWord();
          }
          if (serverPosunulDen) {
            // Včerejšek se posunul, i když desku už uklidily hodiny — proto se
            // tohle neváže na úklid, ale na to, že den posunul server.
            fetchYesterdaysWord();
            // Skupinové kolo se otáčí na stejný herní den, takže i tady se
            // včerejšek posunul. Přes ref, protože tenhle poll se schválně
            // nasazuje jen při mountu a jinou skupinu by neviděl.
            fetchGroupYesterdaysWord(openRoundGidRef.current);
          }
        }

        // Daily hash check. A missing hash means the server could not answer
        // that part — NOT that the word changed. Reading null as a new value
        // would wipe the board of every open tab at once and tell them all that
        // the admin had swapped the word.
        if (typeof body.word_hash === "string") {
          const currentHash = body.word_hash;
          const savedHash = localStorage.getItem("wordHash");
          // Otisk nese svůj herní den. Jiný otisk z DŘÍVĚJŠÍHO dne (nebo bez dne,
          // z doby před 29. 9. 2026) je rotace — tu odbavuje razítko desky výš,
          // ne tahle větev. Když přihlášenému hráči při startu dorazil stav ze
          // serveru dřív než první poll, deska už byla dnešní, ale otisk ještě
          // včerejší: hra ji smazala, přepnula na Denní a hlásila zásah
          // administrátora. Tip psaný pro megaslovo pak šel k uhodnutému slovu.
          const hashDay = localStorage.getItem("wordHashDay");
          const zmenaVTemzeDni = hashDay === dnesniHerniDen();
          if (savedHash && savedHash !== currentHash && !zmenaVTemzeDni) {
            localStorage.setItem("wordHash", currentHash);
            localStorage.setItem("wordHashDay", dnesniHerniDen());
          } else if (savedHash && savedHash !== currentHash && await potvrditNovyOtisk(currentHash)) {
            localStorage.removeItem("attempts");
            localStorage.removeItem("lastAttempt");
            localStorage.removeItem("isGuessed");
            localStorage.removeItem("guessedWord");
            setGuessedWord("");
            localStorage.removeItem("megaCtaRevealed");
            localStorage.setItem("wordHash", currentHash);
            localStorage.setItem("wordHashDay", dnesniHerniDen());
            localStorage.removeItem("guessOrder"); // patří desce, viz resetDailyBoard
            setGuessOrder([]);
            // Nové slovo téhož dne — prázdná deska patří dnešku.
            boardDayRef.current = dnesniHerniDen();
            localStorage.setItem("attemptsDay", boardDayRef.current);
            localStorage.setItem("activeTab", "daily");
            setAttempts([]);
            setAttemptsCarried(0);
            setHeroLifted(false);
            setLastAttempt(null);
            setIsGuessed(false);
            setMegaCtaReady(false);
            setLbRevealReady(false);
            setActiveTab("daily");
            // The word that just ended is now YESTERDAY's. Fetched once at app
            // start otherwise, which for a PWA that is never closed meant the
            // line under the board kept naming the day before yesterday.
            fetchYesterdaysWord();
            setErrorMessage(t("game.error.wordChanged"));
            setTimeout(() => setErrorMessage(""), 5000);
          } else if (!savedHash) {
            localStorage.setItem("wordHash", currentHash);
            localStorage.setItem("wordHashDay", dnesniHerniDen());
          } else if (hashDay !== dnesniHerniDen()) {
            // Týž otisk, jen den chybí nebo je starší — dopsat, ať příští změna
            // uprostřed dne zase platí jako změna.
            localStorage.setItem("wordHashDay", dnesniHerniDen());
          }
        }

        // Weekly hash check — same null rule as above.
        if (typeof body.weekly_word_hash === "string" && body.weekly_word_hash) {
          const currentWeeklyHash = body.weekly_word_hash;
          const savedWeeklyHash = localStorage.getItem("weeklyWordHash");
          // Týmž pravidlem jako denní otisk výš, jen po herních týdnech.
          const hashWeek = localStorage.getItem("weeklyWordHashWeek");
          const zmenaVTemzeTydnu = hashWeek === dnesniHerniTyden();
          if (savedWeeklyHash && savedWeeklyHash !== currentWeeklyHash && !zmenaVTemzeTydnu) {
            localStorage.setItem("weeklyWordHash", currentWeeklyHash);
            localStorage.setItem("weeklyWordHashWeek", dnesniHerniTyden());
          } else if (
            savedWeeklyHash && currentWeeklyHash && savedWeeklyHash !== currentWeeklyHash
            // Totéž ověření jako u slova dne. 4. 11. 2026 jediná odpověď bez
            // megaslova smazala hráči uhodnuté megaslovo a nechala ho hádat znovu.
            && await potvrditNovyOtisk(currentWeeklyHash, `/weekly/word_hash`)
          ) {
            localStorage.removeItem("weeklyAttempts");
            localStorage.removeItem("weeklyLastAttempt");
            localStorage.removeItem("weeklyIsGuessed");
            localStorage.removeItem("weeklyGuessedWord");
            setWeeklyGuessedWord("");
            localStorage.setItem("weeklyWordHash", currentWeeklyHash);
            localStorage.setItem("weeklyWordHashWeek", dnesniHerniTyden());
            // Nové megaslovo tohoto týdne — prázdná deska patří tomuto týdnu.
            weeklyBoardWeekRef.current = dnesniHerniTyden();
            localStorage.setItem("weeklyAttemptsWeek", weeklyBoardWeekRef.current);
            setWeeklyAttempts([]);
            setWeeklyCarried(0);
            setWeeklyLastAttempt(null);
            setWeeklyIsGuessed(false);
            // Same as the daily line above: the week that just ended is now LAST
            // week, and this was fetched only at app start.
            fetchLastWeeksMegaWord();
          } else if (!savedWeeklyHash && currentWeeklyHash) {
            localStorage.setItem("weeklyWordHash", currentWeeklyHash);
            localStorage.setItem("weeklyWordHashWeek", dnesniHerniTyden());
          } else if (savedWeeklyHash && hashWeek !== dnesniHerniTyden()) {
            localStorage.setItem("weeklyWordHashWeek", dnesniHerniTyden());
          }
        }
      } catch (error) {
        // Silently fail
      }
    };

    // Jak často se ptát, jestli se pod námi slovo nezměnilo.
    //
    // Bylo to 30 s, pak 60, pak 10 minut. Teď 30 — protože se **mimo rotaci nic
    // nemění**. Slovo dne, megaslovo i skupinová kola se překlápějí ve 3:00
    // pražského času a jindy ne, takže denní dotazování hlídalo něco, co se
    // nedělo.
    //
    // Proč tedy ne nula? Protože tenhle tik je jediná pravidelná věc, která se
    // ptá serveru — a tím je i **záchranná síť pod celou probouzecí logikou**.
    // Když v ní něco praskne (a už jednou prasklo), s tikem to znamená půlhodinu
    // staré slovo; bez něj to znamená staré slovo, dokud člověk sám nenačte
    // stránku. Ten rozdíl mezi jedním a nula požadavky za hodinu za to nestojí.
    let timer;
    const tick = () => {
      if (document.visibilityState === 'visible') checkWordChanged();
      timer = setTimeout(tick, pollDelayMs({
        hidden: document.visibilityState === 'hidden',
        secondsToRotation: rotationInRef.current,
      }));
    };
    checkWordChanged();
    timer = setTimeout(tick, pollDelayMs({
      hidden: document.visibilityState === 'hidden',
      secondsToRotation: rotationInRef.current,
    }));

    // Probuzení záložky je přesně ta chvíle, kdy mohla rotace utéct — a je to
    // taky to, co dělá ticho skryté záložky bezpečným.
    //
    // OBĚ události, ne jen `visibilitychange`. Okno schované ZA jiným oknem
    // hlásí `visibilityState: "visible"`, takže nikdy nebylo skryté a při
    // návratu k němu se `visibilitychange` nespustí — spustí se jen `focus`.
    // Dokud se pollovalo po deseti minutách, zachránil to časovač. Při
    // půlhodinovém tiku by ten člověk koukal na včerejší slovo o to dýl.
    // `/me/refresh` poslouchá obojí odjakživa; tohle to jen dorovnává.
    //
    // Škrceno, protože `focus` se spustí při každém kliknutí zpátky do okna a
    // bez toho by z úspory nezbylo nic.
    let lastWake = 0;
    const wake = () => {
      if (document.visibilityState === 'hidden') return;
      const now = Date.now();
      if (now - lastWake < WAKE_THROTTLE_MS) return;
      lastWake = now;
      checkWordChanged();
    };
    // A třetí: `pageshow`. iOS stránku nezavírá, zmrazí ji — a když se k ní
    // člověk vrátí, obnoví ji i s běžícím stavem z back/forward cache. Tehdy
    // nepřijde ani `visibilitychange`, ani `focus`; přijde jedině tohle, s
    // `persisted: true`. Nahlásil to hráč 11. 9. 2026 v 9:11: v prohlížeči
    // uvnitř jiné aplikace mu pořád svítilo včerejší slovo a výhra na 133
    // pokusů. Tahle díra je starší než půlhodinový poll, ten ji jen prodloužil
    // z deseti minut na třicet, protože časovač je poslední, kdo to dožene.
    const restored = (e) => { if (e.persisted) wake(); };
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('focus', wake);
    window.addEventListener('pageshow', restored);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('focus', wake);
      window.removeEventListener('pageshow', restored);
    };
    // mount-only: the poll must not be torn down and re-armed on every re-render
    // (t changes identity when the language loads).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchYesterdaysWord();
    fetchLastWeeksMegaWord();
  }, [fetchYesterdaysWord, fetchLastWeeksMegaWord]);

  // Otevřela se deska jiné skupiny → jiné včerejší slovo, a zase schované.
  // Odkrytí se nepřenáší mezi skupinami: kdo si odkryl jednu, neprosil o druhou.
  useEffect(() => {
    openRoundGidRef.current = openRoundGid;
    setShowGroupYesterdaysWord(false);
    setGroupYesterday(null);
    if (openRoundGid && currentUser?.uid) fetchGroupYesterdaysWord(openRoundGid);
    // `uid`, ne celý `currentUser`: ten je při každém renderu jiný objekt, takže
    // by se efekt pouštěl pořád dokola — odkryté slovo by se okamžitě schovalo
    // zpátky a Firestore by platil čtení za každý render.
  }, [openRoundGid, currentUser?.uid, fetchGroupYesterdaysWord]);

  // Load per-language input validation rules (charset, length) from the backend.
  // The backend (languages/<lang>.json) is the single source of truth; adding a
  // language needs no frontend charset change.
  useEffect(() => {
    (async () => {
      try {
        const response = await apiFetch(`/word_rules`);
        if (!response.ok) return;
        const data = await response.json();
        let charRegex = null;
        if (data.char_pattern) {
          try {
            charRegex = new RegExp(data.char_pattern, "u");
          } catch (e) {
            charRegex = null; // never block on a bad pattern
          }
        }
        setWordRules({ minLength: data.min_length ?? 2, charRegex });
      } catch (error) {
        // Silently fail — validateWord falls back to the backend on charset.
      }
    })();
  }, []);

  // The token for a guess, with one forced refresh before giving up.
  //
  // `await getIdToken()` does a NETWORK call whenever the cached token is stale,
  // and both guess paths used to wrap it in `catch (e) {}`. One dropped second —
  // a sleeping tab, a blip — and the tip left with no Authorization header at
  // all. /similarity and /weekly/similarity serve guests on purpose, so that
  // request looked ordinary: scored correctly and silently never stored, because
  // the backend only persists for authenticated callers. Reported 2026-08-24 as
  // nine tips on the board and three in the history, one device, one session.
  //
  // Returns { token } when we have one, { expected: true } when the player is
  // signed in and we could not get one, and {} for a genuine guest.
  const guessAuth = async () => {
    if (!currentUser) return {};
    for (const force of [false, true]) {
      try {
        const token = await getIdToken(force);
        if (token) return { token };
      } catch (e) {
        // First failure: fall through and force a refresh. Second: give up, and
        // let the backend refuse rather than score the tip into a void.
      }
    }
    return { expected: true };
  };

  // Funkce pro odeslání slova na Python server
  const checkSimilarity = async (word1, guessNumber) => {
    try {
      // Hlavičky (i token) pro každý pokus znovu: pojistka „Počítám…" může
      // opakovat až minutu.
      const buildHeaders = async () => {
        const auth = await guessAuth();
        const headers = {
          "Content-Type": "application/json",
          "X-User-ID": getUserId(),
        };
        // Sent whenever the player believes they are signed in — WITH or WITHOUT a
        // token. It is what lets the backend tell a real guest from somebody whose
        // token went astray; the two were indistinguishable, which is why this went
        // unnoticed and uncounted. A guest never sends it, so guests are untouched.
        if (currentUser) headers["X-Auth-Expected"] = "1";
        if (auth.token) headers["Authorization"] = `Bearer ${auth.token}`;
        return headers;
      };
      // Bez limitu čekal tip na ztracenou odpověď navždy a hra hlásila „počkejte
      // na dokončení předchozího pokusu" (2. 11. 2026). Hodnocení samo trvá
      // ve špičce i přes 30 s, proto 45 s na jeden pokus.
      const response = await withScoringRetry(async () => {
        const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
        const timer = ctrl ? setTimeout(() => ctrl.abort(), SIMILARITY_TIMEOUT_MS) : null;
        try {
          return await apiFetch(`/similarity`, {
            method: "POST",
            headers: await buildHeaders(),
            body: JSON.stringify({ word1, guesses: guessNumber }),
            ...(ctrl ? { signal: ctrl.signal } : {}),
          });
        } finally {
          if (timer) clearTimeout(timer);
        }
      });

      if (!response.ok) {
        const errorData = await response.json();

        // The session went missing: the backend refused the tip rather than
        // scoring it into a void, and said so in the player's own language.
        // Deliberately NOT the sign-up modal — they have an account, they just
        // need it back.
        if (errorData.reauth) {
          setErrorMessage(errorData.error);
          return null;
        }

        // Check if it's a typo error - display only the message without prefix
        if (errorData.typo) {
          setErrorMessage(errorData.error);
          return null;
        }

        // Scoring is down (Gemini paused, out of credit, Google having a bad
        // minute). The backend has already said this in the player's language
        // and said the guess did not count — so show it as it stands. Throwing
        // sent it through the catch below, which prefixed "Chyba komunikace se
        // serverem:" and appended the raw exception text. 2026-08-22.
        if (errorData.unavailable) {
          setErrorMessage(errorData.error);
          return null;
        }

        // For other errors, show generic message
        throw new Error(errorData.error || await response.text());
      }

      const data = await response.json();

      // Set dynamic cooldown from backend (500ms for cache, 3000ms for API calls)
      if (data.recommended_cooldown) {
        setCurrentCooldown(data.recommended_cooldown);
      }

      return data.similarity; // Vrací hodnotu podobnosti (0.0 - 1.0)
    } catch (error) {
      if (error && error.name === "AbortError") {
        setErrorMessage(t("game.error.noAnswer"));
        return null;
      }
      setErrorMessage(t("game.error.serverCommunication") + error.message);
      return null; // Pokud nastane chyba, vrací null
    }
  };

  // Funkce pro inkrementaci počtu správných hádání na backendu
  const incrementCorrectGuesses = async () => {
    try {
      const response = await apiFetch(`/increment_correct_guesses`, {
        method: "POST",
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      const data = await response.json();
      console.log(data.status);
      // Refresh stats now that the win is recorded on the backend
      fetchStats();
    } catch (error) {
      setErrorMessage(t("game.error.incrementError") + error.message);
    }
  };

  // Weekly similarity check
  const checkWeeklySimilarity = async (word1) => {
    try {
      // Hlavičky (i token) pro každý pokus znovu: pojistka „Počítám…" může
      // opakovat až minutu.
      const buildHeaders = async () => {
        const auth = await guessAuth();
        const headers = {
          "Content-Type": "application/json",
          "X-User-ID": getUserId(),
        };
        // Sent whenever the player believes they are signed in — WITH or WITHOUT a
        // token. It is what lets the backend tell a real guest from somebody whose
        // token went astray; the two were indistinguishable, which is why this went
        // unnoticed and uncounted. A guest never sends it, so guests are untouched.
        if (currentUser) headers["X-Auth-Expected"] = "1";
        if (auth.token) headers["Authorization"] = `Bearer ${auth.token}`;
        return headers;
      };
      const response = await withScoringRetry(
        async () => apiFetch(`/weekly/similarity`, {
          method: "POST",
          headers: await buildHeaders(),
          body: JSON.stringify({ word1 }),
        }),
      );

      if (await handlePaywall(response, "mega")) return null;
      // Mega is an allowance now, so two answers are not errors but states:
      // 401 = this needs an account, 429 = today's tips are spent. Neither throws
      // and neither throws the player at the sales page — the board stays put.
      if (response.status === 401) {
        // Two different 401s share this status. A LAPSED session is not "you
        // need an account" — this player has one — so it must be read first,
        // or somebody whose token blinked gets shown a sign-up form.
        const data = await response.json().catch(() => ({}));
        if (data.reauth) {
          setWeeklyErrorMessage(data.error);
          return null;
        }
        otevriPrihlaseni();
        return null;
      }
      if (response.status === 429) {
        const data = await response.json().catch(() => ({}));
        setMegaLeft(0);
        if (data.limit) {
          setMegaLimit(data.limit);
          megaLimitRef.current = data.limit;
        }
        return null;
      }
      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.typo) {
          setWeeklyErrorMessage(errorData.error);
          return null;
        }
        // Scoring is down — the same as the daily path. Already in the player's
        // language, already says the guess did not count.
        if (errorData.unavailable) {
          setWeeklyErrorMessage(errorData.error);
          return null;
        }
        throw new Error(errorData.error || await response.text());
      }

      const data = await response.json();
      if (data.recommended_cooldown) setWeeklyCooldown(data.recommended_cooldown);
      // The server counts the tip it just took, so this is what is left AFTER it.
      if (data.mega_attempts_left !== undefined) setMegaLeft(data.mega_attempts_left);
      if (data.weekly_leaderboard != null) {
        setWeeklyRank({ leaderboard: data.weekly_leaderboard });
        localStorage.setItem('weeklyRank', JSON.stringify({ leaderboard: data.weekly_leaderboard }));
      }
      return data.similarity;
    } catch (error) {
      setWeeklyErrorMessage(t("game.error.serverCommunication") + error.message);
      return null;
    }
  };

  // Načtení žebříčku pro dnešní den
  const fetchLeaderboard = async () => {
    setLeaderboardLoading(true);
    setLeaderboardError(null);
    try {
      const headers = {};
      if (currentUser) {
        try {
          const token = await currentUser.getIdToken();
          if (token) headers['Authorization'] = `Bearer ${token}`;
        } catch (e) {}
      }
      const resp = await apiFetch(`/leaderboard`, { headers });
      if (resp.ok) {
        const data = await resp.json();
        setLeaderboard(data.leaderboard || []);
      } else if (resp.status === 503) {
        const data = await resp.json().catch(() => ({}));
        setLeaderboardError(data.error || t("leaderboard.disabled"));
        setLeaderboard([]);
      } else {
        setLeaderboard([]);
      }
    } catch (e) {
      setLeaderboard([]);
    }
    finally { setLeaderboardLoading(false); }
  };

  const fetchWeeklyLeaderboard = async () => {
    setWeeklyLeaderboardLoading(true);
    try {
      const headers = {};
      if (currentUser) {
        try {
          const token = await currentUser.getIdToken();
          if (token) headers['Authorization'] = `Bearer ${token}`;
        } catch (e) {}
      }
      const resp = await apiFetch(`/weekly/leaderboard`, { headers });
      if (resp.ok) {
        const data = await resp.json();
        setWeeklyLeaderboard(data.leaderboard || []);
      }
    } catch (e) {}
    finally { setWeeklyLeaderboardLoading(false); }
  };

  // Načtení osobní historie přihlášeného hráče
  const fetchUserHistory = async () => {
    if (!currentUser) return;
    try {
      const token = await getIdTokenRef.current();
      const resp = await apiFetch(`/user/history`, {
        headers: { "Authorization": `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        setUserHistory(data.history || []);
        setStreaks(data.streaks || null);
        setHistoryToday(data.today || null);
      }
    } catch (e) {}
  };

  // Friends: authorised GET/POST helper (adds the bearer token).
  const friendsApi = async (path, options = {}) => {
    const token = await getIdToken();
    return apiFetch(path, {
      ...options,
      headers: {
        ...(options.headers || {}),
        "Authorization": `Bearer ${token}`,
        ...(options.body ? { "Content-Type": "application/json" } : {}),
      },
    });
  };

  // ── Archive: replay past daily words ───────────────────────────────────────
  // A server similarity (0..1) becomes the board's 0..100 percentage.
  const toPct = (sim) => Math.round(sim * 100 * 100) / 100;

  // Load the archive calendar (also the source for the profile's "Rozehrané"
  // section — in-progress past days are just this list filtered by status).
  const fetchArchive = async () => {
    if (!currentUser) return;
    // Only the first load shows the spinner — a background refresh must not blank
    // the calendar the player is looking at.
    if (archiveDays === null) setArchiveLoading(true);
    try {
      const resp = await friendsApi(`/archive`);
      if (await handlePaywall(resp, "archive")) { setArchiveDays([]); return; }
      setArchiveDays(resp.ok ? (await resp.json()).days || [] : []);
    } catch (e) {
      setArchiveDays([]);
    } finally {
      setArchiveLoading(false);
    }
  };

  // The day whose word is live right now — gameEpoch() already accounts for the
  // 03:00 Prague rotation, so this is correct in any timezone. Nothing from this
  // day onwards is archive.
  const liveDayIso = () => todayIso(gameEpoch());

  // Pick a random day the player hasn't touched yet and start it. Never lands on a
  // locked day (a dice roll that opens the paywall is a bad surprise) and never on
  // the live day — a player reported the dice handing back the word they were
  // playing, replayable from zero. The server is the real guard; this is a belt.
  const pickRandomDay = () => {
    const live = liveDayIso();
    const unplayed = (archiveDays || []).filter(
      d => d.status === "not_started" && !d.locked && d.date < live
    );
    if (!unplayed.length) return;
    const pick = unplayed[Math.floor(Math.random() * unplayed.length)];
    openArchiveDay(pick.date);
  };

  const openArchiveDay = async (date) => {
    // The live day is never an archive day, whatever the list happens to contain:
    // replaying it would be playing today's word from zero.
    // Vyhraný dnešek ale přehled má (Teplota/Pořadí/Graf), jako aktuální megaslovo:
    // bez něj Moje výhry posílaly dnešní výhru do hry, kde přepínač není
    // (vera.jezkova67@, 14. 10. 2026). Nevyhraný dnešek patří dál na desku.
    if (date >= liveDayIso()) {
      if (isGuessed && await openTodayReview(date)) return;
      setActiveTab('daily'); localStorage.setItem('activeTab', 'daily'); goPage('game'); return;
    }
    // A locked (deep-archive) day for a free player: offer Premium in context
    // rather than sending a request that can only come back 402.
    if ((archiveDays || []).some(d => d.date === date && d.locked)) { openPremium('archive'); return; }
    // Load the day's state BEFORE navigating, so a non-playable day (e.g. the
    // archive rejecting today) never leaves the player on a broken empty board.
    let s;
    try {
      const resp = await friendsApi(`/archive/${date}/state`);
      if (await handlePaywall(resp, "archive")) return;
      if (!resp.ok) return; // shouldn't happen from the UI — today isn't offered
      s = await resp.json();
    } catch (e) {
      return;
    }
    // The guesses array is append-only, so its INDEX is the true chronological
    // order — more reliable than the stored `order` field, which can drift. Capture
    // it before the temperature sort below.
    const rows = (s.guesses || []).map((g, i) => ({
      guess: g.word,
      adjustedSimilarity: toPct(g.similarity),
      order: i + 1,
    }));
    rows.sort((a, b) => b.adjustedSimilarity - a.adjustedSimilarity); // warmest first (default)
    setArchiveType('daily');
    setArchiveDate(date);
    try { sessionStorage.setItem('archiveType', 'daily'); sessionStorage.setItem('archiveDate', date); } catch (e) {} // so a reload can restore this day
    setArchiveGuess("");
    setArchiveError("");
    setArchiveAttempts(rows);
    setArchiveLastAttempt(null);
    setArchiveSolvedWord(s.solved_word || null);
    goPage("archivePlay");
  };

  // Přehled dnešní VYHRANÉ hry, jen ke čtení. Archiv dnešek odmítá (přehrát ho by
  // znamenalo hrát dnešní slovo od nuly), takže tipy se berou ze stavu hry na
  // serveru, stejně jako při obnově na jiném zařízení — i s pořadím, které
  // telefon po obnově nemá. Vrací false, když není co ukázat; volající pak
  // pošle hráče na desku.
  const openTodayReview = async (date) => {
    let s;
    try {
      const resp = await friendsApi(`/user/game_state?type=daily`);
      if (!resp.ok) return false;
      s = ((await resp.json()) || {}).state;
    } catch (e) {
      return false;
    }
    if (!s || s.status !== "guessed" || !(s.guesses || []).length) return false;
    const rows = s.guesses.map((g, i) => ({
      guess: g.word,
      adjustedSimilarity: toPct(g.similarity),
      order: i + 1,
    }));
    rows.sort((a, b) => b.adjustedSimilarity - a.adjustedSimilarity);
    setArchiveType('daily');
    setArchiveDate(date);
    try { sessionStorage.setItem('archiveType', 'daily'); sessionStorage.setItem('archiveDate', date); } catch (e) {}
    setArchiveGuess("");
    setArchiveError("");
    setArchiveAttempts(rows);
    setArchiveLastAttempt(null);
    setArchiveSolvedWord(s.word || guessedWord || null);
    goPage("archivePlay");
    return true;
  };

  // Read-only review of a past MEGA win (no replay — B scope). Reuses the archive
  // review board; `period` is an ISO week (e.g. "2026-W29"), stored in archiveDate.
  const openMegaReview = async (period) => {
    let s;
    try {
      const resp = await friendsApi(`/archive/mega/${period}/state`);
      if (await handlePaywall(resp, "mega")) return;
      if (!resp.ok) return;
      s = await resp.json();
    } catch (e) {
      return;
    }
    const rows = (s.guesses || []).map((g, i) => ({
      guess: g.word,
      adjustedSimilarity: toPct(g.similarity),
      order: i + 1,
    }));
    rows.sort((a, b) => b.adjustedSimilarity - a.adjustedSimilarity);
    setArchiveType('mega');
    setArchiveDate(period);
    try { sessionStorage.setItem('archiveType', 'mega'); sessionStorage.setItem('archiveDate', period); } catch (e) {}
    setArchiveGuess("");
    setArchiveError("");
    setArchiveAttempts(rows);
    setArchiveLastAttempt(null);
    setArchiveSolvedWord(s.solved_word || null);
    goPage("archivePlay");
  };

  // Přehrání jedné animace "z inputu na kartu". Vrací až po dosednutí, takže
  // volající pokračuje přesně tam, kde denní hra: seznam se aktualizuje potom.
  const flyGuess = async ({ word, similarity, previous, setFlying, setLeaving, setLast }) => {
    if (previous) {
      setLeaving({
        word: previous.guess,
        similarity: previous.adjustedSimilarity,
        backgroundColor: getBackgroundColor(previous.adjustedSimilarity),
        label: getSimilarityLabel(previous.adjustedSimilarity),
      });
    }
    setFlying({
      word,
      similarity,
      backgroundColor: getBackgroundColor(similarity),
      label: getSimilarityLabel(similarity),
    });
    await new Promise((r) => setTimeout(r, 500));
    setFlying(null);
    setLeaving(null);
    setLast({ guess: word, originalSimilarity: similarity, adjustedSimilarity: similarity });
  };

  const submitArchiveGuess = async () => {
    const word = archiveGuess.trim().normalize("NFC").toLowerCase();  // viz validateWord
    if (!word || archiveBusy || archiveSolvedWord) return;
    // Respect the cooldown, same as the daily board (the bar shows the wait).
    if (Date.now() - archiveLastSubmit < archiveCooldown) return;
    // A word already tried costs no attempt — but it must SAY so. The live board
    // replays the word's own temperature and highlights the row it is already on;
    // here it used to vanish into a cleared input with no explanation, and a
    // player asked whether that was deliberate. Same rule, same feedback.
    const seenIndex = archiveAttempts.findIndex((a) => a.guess === word);
    if (seenIndex !== -1) {
      setArchiveGuess("");
      // Putting it back as the last attempt is what shows its temperature under
      // the input AND highlights the row it already occupies — the board derives
      // the highlight from exactly this. No attempt is spent, which was already
      // true; the difference is that the player can now see it happen.
      setArchiveLastAttempt(archiveAttempts[seenIndex]);
      return;
    }
    setArchiveBusy(true);
    setArchiveError("");
    setArchiveLastSubmit(Date.now()); // start the timeout bar immediately
    try {
      const resp = await withScoringRetry(
        () => friendsApi(`/archive/${archiveDate}/guess`, {
          method: "POST",
          body: JSON.stringify({ word1: word }),
        }),
      );
      const data = await resp.json();
      if (data.recommended_cooldown != null) setArchiveCooldown(data.recommended_cooldown);
      if (!resp.ok) {
        setArchiveError(data.error || t("game.error.serverCommunication"));
        return;
      }
      if (data.typo) {
        setArchiveError(data.error);
        return;
      }
      const pct = toPct(data.similarity);
      const attempt = { guess: word, originalSimilarity: pct, adjustedSimilarity: pct };
      // Input first, then the flight, then the list — the daily board's order, so
      // the word appears to leave the box and land on its card.
      setArchiveGuess("");
      await flyGuess({
        word,
        similarity: pct,
        previous: archiveLastAttempt,
        setFlying: setArchiveFlyingWord,
        setLeaving: setArchiveLeavingWord,
        setLast: setArchiveLastAttempt,
      });
      // `order` is what the review's chronological tab sorts on, and it is the
      // DEFAULT tab. Without it a replayed guess carried no order at all, the
      // sort read it as 0, and every word guessed in this session dropped to the
      // bottom of the list in temperature order — under words guessed before it.
      // Reported 2026-08-21: "co je tohle za pořadí? Podle čeho je to seřazeno?"
      // The winning word sat mid-list instead of first, which is what gave it
      // away: it was the last word guessed and has to head the list.
      // prev.length + 1 continues the numbering openArchiveDay laid down from the
      // stored array's index, so the two halves of the list share one scale.
      setArchiveAttempts((prev) =>
        [...prev, { ...attempt, order: prev.length + 1 }].sort(
          (a, b) => b.adjustedSimilarity - a.adjustedSimilarity
        )
      );
      if (data.guessed) setArchiveSolvedWord(data.solved_word || word);
      // Keep the calendar's cached entry in step. The list is fetched once per
      // app start (GET /archive reads the player's whole history, so refetching
      // it on every visit is not free), which left a day just played showing as
      // untouched until the app was closed and reopened — reported 2026-08-30 by
      // a player running it as a PWA, where there is no refresh to fall back on.
      setArchiveDays((prev) =>
        applyProgress(prev, archiveDate, archiveAttempts.length + 1, !!data.guessed)
      );
    } catch (e) {
      setArchiveError(t("game.error.serverCommunication") + e.message);
    } finally {
      setArchiveBusy(false);
    }
  };

  const fetchFriends = async () => {
    try {
      const resp = await friendsApi(`/friends`);
      if (!resp.ok) return;
      const data = await resp.json();
      setFriends(data.friends || []);
      setFriendsLimit(
        typeof data.limit === 'number'
          ? { accepted: data.accepted || 0, limit: data.limit, reached: !!data.limit_reached }
          : null // starší backend tahle pole neposílá; pak se poznámka prostě neukáže
      );
    } catch (e) {}
  };

  const fetchFriendsToday = async () => {
    try {
      const resp = await friendsApi(`/friends/today`);
      if (resp.ok) setFriendsToday((await resp.json()).friends || []);
    } catch (e) {}
  };

  // Asked once per account, then remembered — see friendCode.js. The code cannot
  // change, so a cached one is never stale, and this ran on every visit to the
  // friends page.
  const fetchFriendCode = async () => {
    const uid = currentUser?.uid;
    const cached = readCached(uid, typeof window !== "undefined" ? window.localStorage : null);
    if (cached) { setFriendCode(cached); return; }
    try {
      const resp = await friendsApi(`/friends/code`);
      if (resp.ok) {
        const code = (await resp.json()).code || null;
        setFriendCode(code);
        remember(uid, code, typeof window !== "undefined" ? window.localStorage : null);
      }
    } catch (e) {}
  };

  // Open the friends page and (re)load everything it shows.
  const openFriends = () => {
    if (!currentUser) return;
    setAddFriendMsg('');
    setAddCodeInput('');
    setFriendsToday(null);
    setFriendsTab(activeTab === 'weekly' ? 'mega' : 'daily'); // follow the sticky word-mode
    goPage('friends');
    fetchFriendCode();
    fetchFriends();
    fetchFriendsToday();
  };

  const copyFriendCode = async () => {
    if (!friendCode) return;
    try {
      await navigator.clipboard.writeText(friendCode);
      setFriendCodeCopied(true);
      setTimeout(() => setFriendCodeCopied(false), 1500);
    } catch (e) {}
  };

  const copyGroupCode = async () => {
    const code = selectedGroup && selectedGroup.invite_code;
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setGroupCodeCopied(true);
      setTimeout(() => setGroupCodeCopied(false), 1500);
    } catch (e) {}
  };

  const sendFriendRequest = async () => {
    const code = addCodeInput.trim().toUpperCase();
    if (!code) return;
    setAddFriendMsg('');
    try {
      const resp = await friendsApi(`/friends/request`, {
        method: "POST",
        body: JSON.stringify({ code }),
      });
      if (await handlePaywall(resp, "friends")) { setSocialModal(null); return; }
      const data = await resp.json().catch(() => ({}));
      const key = {
        sent: "friends.sent",
        already_pending: "friends.alreadyPending",
        already_friends: "friends.alreadyFriends",
        not_found: "friends.notFound",
        self: "friends.selfError",
        // Plno má TEN DRUHÝ. Proto vlastní věta a ne nabídka Premium — koupit by
        // si ho musel on, mně by nepomohlo.
        target_full: "friends.targetFull",
      }[data.status] || "friends.notFound";
      setAddFriendMsg(t(key, { limit: data.limit }));
      if (data.status === "sent") { setAddCodeInput(''); fetchFriends(); setSocialModal(null); }
    } catch (e) {
      setAddFriendMsg(t("friends.notFound"));
    }
  };

  const respondFriend = async (uid, accept) => {
    try {
      const resp = await friendsApi(`/friends/respond`, {
        method: "POST",
        body: JSON.stringify({ uid, accept }),
      });
      if (accept && await handlePaywall(resp, "friends")) return;
      if (resp.ok) { fetchFriends(); if (accept) fetchFriendsToday(); }
    } catch (e) {}
  };

  const removeFriend = async (uid) => {
    setFriends(fs => (fs || []).filter(f => f.uid !== uid)); // optimistic
    try {
      const resp = await friendsApi(`/friends/${uid}`, { method: "DELETE" });
      if (resp.ok) { fetchFriends(); fetchFriendsToday(); }
    } catch (e) {}
  };

  // A member's per-word standing rendered as a temperature-style bar, like the
  // game's own guess rows: coloured fill + dark text, label on the left, state and
  // attempt count on the right. `left` may be a string or a node (name/rank).
  // Account-menu icons (feather-style strokes, one per item so the list scans
  // by shape as well as by label).
  const menuIcon = (name) => {
    const paths = {
      chart: <><path d="M3 3v18h18" /><path d="M7 15l3-4 3 3 5-7" /></>,
      calendar: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 10h18" /></>,
      friends: <><path d="M16 20v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 20v-2a4 4 0 0 0-3-3.9" /></>,
      groups: <><circle cx="9" cy="8" r="3" /><circle cx="17" cy="9" r="2.5" /><path d="M3 20v-1.5A4.5 4.5 0 0 1 7.5 14h3A4.5 4.5 0 0 1 15 18.5V20" /><path d="M17 14a4 4 0 0 1 4 4v2" /></>,
      settings: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></>,
      sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></>,
      moon: <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />,
      zoom: <><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3M11 8v6M8 11h6" /></>,
      campfire: <><path d="M12 3c1.8 2.2 3.5 3.9 3.5 6.3A3.5 3.5 0 0 1 12 13a3.5 3.5 0 0 1-3.5-3.7c0-1.4.7-2.4 1.5-3.3.2 1.1.8 1.8 1.6 2C11 6.6 11.3 4.6 12 3z" /><path d="M4 21l16-5M4 16l16 5" /></>,
      logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5M21 12H9" /></>,
      login: <><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><path d="M10 17l-5-5 5-5M3 12h12" /></>,
      gift: <><path d="M20 12v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8" /><rect x="2" y="7" width="20" height="5" rx="1" /><path d="M12 22V7" /><path d="M12 7S10.5 3 8 3a2.5 2.5 0 0 0 0 5h4M12 7s1.5-4 4-4a2.5 2.5 0 0 1 0 5h-4" /></>,
    };
    if (name === "trophy") return <TrophyIcon size={17} />;
    if (name === "play") return <PlayIcon size={17} />;
    return (
      <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
        strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {paths[name]}
      </svg>
    );
  };

  // Return/Enter arrow for the in-input submit button — a visible affordance for
  // players who don't realise they can send a guess with the keyboard's Enter.
  const enterIcon = (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
      strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6v5a3 3 0 0 1-3 3H6" />
      <polyline points="10 10 6 14 10 18" />
    </svg>
  );

  // `finished` is for a round that is over: "Ještě nehádá" is right while it runs
  // and nonsense afterwards — nobody is going to start now. Reported 2026-08-20
  // together with the missing result itself.
  const tempBar = (left, status, attempts, best, finished = false, away = null) => {
    // fill/colour = warmest tip so far ("nejvýše umístěné slovo"): solved = full
    // hot, in progress = their best %, not started = 0 (neutral, no colour).
    const pct = status === 'guessed' ? 100
      : (status === 'not_started' || status === 'setter' || status === 'none') ? 0
      // Stejný převod jako deska (dvě desetinná místa). Do 24. 10. 2026 tu bylo
      // Math.round: 85,2 °C dalo 85 a hranice oranžová/červená je právě 85 —
      // deska svítila červeně a řádek v Přátelích/skupině oranžově (Taly, sk).
      : percentFromSimilarity(best);
    // 'none': the row is only telling you who is in the group — there is no word
    // to be doing anything with, so any status would be a made-up one.
    // 'setter': whoever set the word is not a slow guesser — they will never
    // guess it at all. Their row is headed by "today we are guessing X's word",
    // so a status on the right as well would say the same thing twice.
    // The setter's row is headed by its own caption — "dnes hádáme slovo od" while
    // a round runs, "slovo zadal/a" once it is over — so a status on the right
    // would say the same thing twice.
    const label = (status === 'none' || status === 'setter') ? ''
      : status === 'guessed' ? t("game.temperature.perfect")
      : status === 'in_progress' ? t(finished ? "friends.statusGaveUp" : "friends.statusInProgress")
      // „Ještě nehádá" je pravda o dnešku. U někoho, kdo je pryč šest týdnů, je
      // to zamlčení — skupina čeká na člověka, který tu dávno není.
      : (status === 'not_started' && away) ? away
      : t(finished ? "friends.statusNeverPlayed" : "friends.statusNotStarted");
    const meta = (status === 'not_started' || status === 'setter' || status === 'none')
      ? label
      : `${label} · ${attempts}`;
    // Solved = green (done), distinct from the warm "how close" scale of players
    // still guessing. Matches the calendar's solved colour.
    const fill = status === 'guessed' ? '#57b85e' : getBackgroundColor(pct);
    return (
      <div className="user-bar" data-fill={fill} style={{ background: `linear-gradient(to right, ${fill} ${pct}%, var(--bar-track) 0%)` }}>
        <span className="user-bar__name">{left}</span>
        <span className="user-bar__meta">{meta}</span>
      </div>
    );
  };

  // ── Groups ────────────────────────────────────────────────────────────────
  const fetchMyGroups = async () => {
    try {
      const resp = await friendsApi(`/groups`);
      if (!resp.ok) return;
      const d = await resp.json();
      setGroups(d.groups || []);
      setGroupRequests(d.requests || []);
      setOtherMarketGroups(d.other_markets || []);
    } catch (e) {}
  };

  const fetchNotifications = async () => {
    try {
      const resp = await friendsApi(`/notifications`);
      if (resp.ok) setNotifications((await resp.json()).notifications || []);
    } catch (e) {}
  };

  const fetchOwnerPending = async () => {
    try {
      const resp = await friendsApi(`/groups/pending`);
      if (resp.ok) setOwnerPending((await resp.json()).pending || []);
    } catch (e) {}
  };

  // The three above in one request — see GET /groups/overview. They fired
  // together on arrival, 132 + 127 + 127 a minute at the 2026-09-01 peak, for
  // what is logically one screen.
  //
  // null means the server could not build that part; [] means there is nothing.
  // The difference matters: the three calls this replaces were independently
  // fault-tolerant, so one part failing must leave the other pieces — and the
  // state we already had — alone rather than blanking them.
  const fetchGroupOverview = async () => {
    try {
      const resp = await friendsApi(`/groups/overview`);
      if (resp.ok) {
        applyOverview(await resp.json(), {
          groups: setGroups,
          requests: setGroupRequests,
          pending: setOwnerPending,
          invitations: setMyInvitations,
          other_markets: setOtherMarketGroups,
        });
        return;
      }
    } catch (e) { /* fall through to the three below */ }
    // The merged request failed AS A WHOLE — not one part of it, which the
    // null-handling above covers, but the request itself. Before the merge these
    // were three independent calls and one failure cost one piece; collapsing
    // them must not turn that into losing the groups list, the bell and the
    // pending requests together. So the worst case is exactly the old behaviour:
    // three requests. The good case is one.
    fetchMyGroups();
    fetchOwnerPending();
    fetchMyInvitations();
  };

  const dismissNotification = async (nid) => {
    setNotifications(ns => ns.filter(n => n.id !== nid)); // optimistic
    try { await friendsApi(`/notifications/${nid}`, { method: "DELETE" }); } catch (e) {}
  };

  // Open/close the bell; opening marks event notifications read (badge clears).
  const toggleNotifications = async () => {
    const opening = !notifOpen;
    setNotifOpen(opening);
    if (opening && notifications.some(n => !n.read)) {
      setNotifications(ns => ns.map(n => ({ ...n, read: true })));
      try { await friendsApi(`/notifications/read`, { method: "POST" }); } catch (e) {}
    }
  };

  // Every failure path must say something: a silent catch (or mapping ANY error to
  // "invalid name") made the save button look dead — reported from testing.
  const renameGroup = async () => {
    const name = renameGroupName.trim();
    if (!name || !selectedGroup || renameGroupBusy) return;
    setRenameGroupMsg('');
    setRenameGroupBusy(true);
    try {
      const resp = await friendsApi(`/groups/${selectedGroup.gid}`, { method: "PATCH", body: JSON.stringify({ name }) });
      if (await handlePaywall(resp, "groups")) { setSocialModal(null); return; }
      if (resp.ok) {
        setSocialModal(null);
        setSelectedGroup(g => ({ ...g, name }));
        fetchMyGroups();
        // Jen skupině bez světa — jinak by oprava překlepu v názvu nabídla přepsat
        // svět, který si správce zadal sám (server to taky odmítne: „ma_tema").
        if (!selectedGroup.tema) nabidnoutTema(selectedGroup.gid);
      } else {
        const data = await resp.json().catch(() => ({}));
        setRenameGroupMsg(t(
          data.error === "name_taken" ? "groups.nameTaken"
            : data.error === "invalid_name" ? "groups.invalidName"
              : "groups.renameError"
        ));
      }
    } catch (e) {
      setRenameGroupMsg(t("groups.renameError"));
    } finally {
      setRenameGroupBusy(false);
    }
  };

  // Téma skupiny — jen nabídka od modelu, uloží se až po potvrzení správcem.
  // Server text i tak kontroluje znovu (tema_skupiny.zkontroluj_popis).
  const navrhniTema = async (gid, text) => {
    try {
      const resp = await friendsApi(`/groups/${gid}/tema/navrh`, {
        method: "POST",
        body: JSON.stringify(text ? { text } : {}),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.status === 429) return { error: "limit" };
      if (!resp.ok) return { error: "error" };
      return data;
    } catch (e) {
      return { error: "error" };
    }
  };

  // Po založení / přejmenování: zeptat se tiše a nabídnout jen, když je co.
  const nabidnoutTema = async (gid) => {
    const d = await navrhniTema(gid);
    if (!(d && d.ma_tema && d.popis && !d.podezrele)) return;
    // Jen když je správce u téhle skupiny a nemá otevřené jiné okno — jinak by
    // nabídka vyskočila nad něčím nesouvisejícím. Kontext v refu se obnoví až
    // vykreslením (zavření okna založení, otevření skupiny), a to jeden tik
    // nemusí stihnout: na pomalém stroji se tak nabídka po založení tiše
    // ztrácela (CI 7. 10. 2026). Proto chvíli počkat, než se dokreslí. Nevadí,
    // že se občas ztratí: svět nabízí i box s novinkou a řádek ve správě.
    const sedi = () => {
      const k = temaKontextRef.current;
      return k.gid === gid && ["group", "groupAdmin"].includes(k.page) && !k.modal;
    };
    for (let i = 0; i < 20 && !sedi(); i++) {
      await new Promise((r) => setTimeout(r, 50));
    }
    if (!sedi()) return;
    setTemaState({ gid, mode: "offer", navrh: d, busy: false, msg: "" });
    setSocialModal("groupTema");
  };

  // `podpis` přišel s návrhem (POST …/tema/navrh): server uloží jen svět, který
  // sám navrhl, ať se jeden svět nerozpadne na víc názvů. Zrušení ho nepotřebuje.
  const ulozTema = async (gid, tema, podpis) => {
    if (temaUkladaRef.current) return; // dvojklik: druhé uložení neposílat
    temaUkladaRef.current = true;
    setTemaState((s) => (s ? { ...s, busy: true, msg: "" } : s));
    try {
      const resp = await friendsApi(`/groups/${gid}/tema`, {
        method: "POST",
        body: JSON.stringify(tema ? { tema, podpis } : { tema: null }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setTemaState((s) => (s ? { ...s, busy: false, msg: t(data.error === "tema_invalid" ? "groups.temaInvalid" : "groups.temaError") } : s));
        return;
      }
      setSelectedGroup((g) => (g && g.gid === gid ? { ...g, tema: data.tema || null } : g));
      setGroups((gs) => (gs ? gs.map((g) => (g.gid === gid ? { ...g, tema: data.tema || null } : g)) : gs));
      setTemaState(null);
      setSocialModal((m) => (m === "groupTema" ? null : m));
    } catch (e) {
      setTemaState((s) => (s ? { ...s, busy: false, msg: t("groups.temaError") } : s));
    } finally {
      temaUkladaRef.current = false;
    }
  };

  // Novinka pro správce (box nahoře na stránce Skupiny). Zavření si pamatuje
  // jen tento prohlížeč — je to oznámení, ne stav; na druhém zařízení se ukáže
  // ještě jednou. Po TEMA_NOVINKA_DO zmizí samo a blok jde smazat.
  const temaNovinkaKlic = currentUser ? `temaNovinkaZavrena:${currentUser.uid}` : null;
  const [temaNovinkaZavrena, setTemaNovinkaZavrena] = useState(false);
  useEffect(() => {
    if (!temaNovinkaKlic) return;
    try { setTemaNovinkaZavrena(localStorage.getItem(temaNovinkaKlic) === "1"); } catch (e) { /* bez úložiště se box ukáže */ }
  }, [temaNovinkaKlic]);
  const zavritTemaNovinku = () => {
    setTemaNovinkaZavrena(true);
    try { if (temaNovinkaKlic) localStorage.setItem(temaNovinkaKlic, "1"); } catch (e) { /* jen pohodlí */ }
  };
  const otevritTema = (gid) => {
    setTemaState({ gid, mode: "edit", text: "", navrh: null, busy: false, msg: "" });
    setSocialModal("groupTema");
  };

  // Název skupiny do nadpisu okna se světem — okno se otvírá i ze seznamu
  // skupin, kde není jasné, o kterou jde.
  const temaJmeno = temaState
    ? ((groups || []).find((g) => g.gid === temaState.gid)?.name
      || (selectedGroup?.gid === temaState.gid ? selectedGroup.name : "") || "")
    : "";

  // Stejný box na stránce Skupiny i v detailu skupiny: kdo má jedinou skupinu,
  // toho záložka pošle rovnou do ní a seznam nikdy neuvidí — a to je většina.
  const renderTemaNovinka = (kandidati) => {
    if (!kandidati.length || temaNovinkaZavrena || new Date() >= TEMA_NOVINKA_DO) return null;
    return (
      <div className="tema-notice" role="note">
        <button type="button" className="tema-notice__close" aria-label={t("groups.temaNoticeClose")}
          onClick={zavritTemaNovinku}>&times;</button>
        <p className="tema-notice__title">{t("groups.temaNoticeTitle")}</p>
        <p className="tema-notice__text">{t("groups.temaNoticeText")}</p>
        <p className="tema-notice__text">{t("groups.temaNoticeText2")}</p>
        {kandidati.length === 1 ? (
          <div className="tema-notice__actions">
            <button className="pill-btn pill-btn--accept" onClick={() => otevritTema(kandidati[0].gid)}>
              {t("groups.temaNoticeTry")}
            </button>
          </div>
        ) : (<>
          <p className="tema-notice__title">{t("groups.temaNoticeSetFor")}</p>
          <div className="tema-notice__actions tema-notice__actions--groups">
            {kandidati.slice(0, 3).map((g) => (
              <button key={g.gid} className="pill-btn" title={g.name} onClick={() => otevritTema(g.gid)}>{g.name}</button>
            ))}
          </div>
        </>)}
      </div>
    );
  };

  const navrhnoutZTextu = async () => {
    const st = temaState;
    // `busy` i tady: Enter v poli jinak obcházel zablokované tlačítko a posílal
    // souběžné návrhy (každý ubírá z denního limitu).
    if (!st || st.busy || !st.text || !st.text.trim()) return;
    const text = st.text.trim();
    setTemaState({ ...st, busy: true, msg: "", navrh: null });
    const d = await navrhniTema(st.gid, text);
    // Odpověď patří jen do okna, ze kterého odešla: stejná skupina a stejný text.
    // Po zavření okna (temaState null) nebo přepnutí jinam se zahodí.
    const sedi = (s) => s && s.gid === st.gid && (s.text || "").trim() === text;
    if (d.error) {
      setTemaState((s) => (sedi(s) ? { ...s, busy: false, msg: t(d.error === "limit" ? "groups.temaLimit" : "groups.temaError") } : s));
    } else if (d.ma_tema && d.popis && !d.podezrele) {
      setTemaState((s) => (sedi(s) ? { ...s, busy: false, navrh: d } : s));
    } else {
      setTemaState((s) => (sedi(s) ? { ...s, busy: false, msg: t("groups.temaNoneFound") } : s));
    }
  };

  // Zavřené okno světa = zahozený stav. Bez toho po zavření křížkem zůstal
  // rozpracovaný návrh viset a pozdní odpověď ho mohla oživit.
  useEffect(() => {
    if (socialModal !== "groupTema") setTemaState(null);
  }, [socialModal]);

  const deleteGroup = async (gid) => {
    try {
      const resp = await friendsApi(`/groups/${gid}`, { method: "DELETE" });
      if (resp.ok) { setSelectedGroup(null); goPage('groups'); fetchMyGroups(); }
    } catch (e) {}
  };

  const kickMember = async (memberUid) => {
    if (!selectedGroup) return;
    try {
      const resp = await friendsApi(`/groups/${selectedGroup.gid}/members/${memberUid}`, { method: "DELETE" });
      if (resp.ok) {
        const r = await friendsApi(`/groups/${selectedGroup.gid}`);
        if (r.ok) setSelectedGroup(await r.json());
        fetchStandings(selectedGroup.gid, groupStandingsTab);
      }
    } catch (e) {}
  };

  const openGroups = () => {
    if (!currentUser) return;
    setCreateGroupMsg(''); setJoinGroupMsg(''); setCreateGroupName(''); setJoinGroupCode('');
    setGroups(null);
    goPage('groups');
    fetchMyGroups();
  };

  // Bottom-nav "Groups": if the user is in exactly one group, jump straight into
  // it; otherwise show the group list. A pending join request counts as
  // something to show — skipping the list would hide the very answer to "did I
  // already ask?".
  const onGroupsTab = () => {
    if (groups && groups.length === 1 && groupRequests.length === 0) openGroup(groups[0].gid);
    else openGroups();
  };

  const createGroup = async () => {
    const name = createGroupName.trim();
    if (!name) return;
    setCreateGroupMsg('');
    try {
      const resp = await friendsApi(`/groups`, { method: "POST", body: JSON.stringify({ name }) });
      if (await handlePaywall(resp, "groups")) { setSocialModal(null); return; }
      if (resp.ok) {
        setCreateGroupName('');
        const data = await resp.json();
        setSocialModal(null);
        fetchMyGroups();
        if (data.gid) { await openGroup(data.gid); nabidnoutTema(data.gid); }
      } else {
        const data = await resp.json().catch(() => ({}));
        // Every failure used to come out as "Neplatný název (2-40 znaků)", so
        // somebody who already owned the maximum number of groups was told their
        // perfectly good name was wrong — reported 2026-08-24 with the name
        // "TEST" and three groups already founded. The backend had refused for
        // the right reason and said so; the client threw the reason away.
        // Anything unrecognised now says the group could not be created rather
        // than blaming the name, which is the one thing we know was fine.
        setCreateGroupMsg(
          data.error === "name_taken" ? t("groups.nameTaken")
            : data.error === "invalid_name" ? t("groups.invalidName")
              : data.error === "too_many_owned" ? t("groups.tooManyOwned", { limit: data.limit })
                : t("groups.createError")
        );
      }
    } catch (e) {}
  };

  const joinGroupByCode = async (confirmMarket) => {
    // `=== true`, ne pravdivostní test: kdyby se tahle funkce někdy zase
    // navěsila přímo na onClick, React by jako první argument předal událost a
    // ta je pravdivá — potvrzení cizího trhu by pak odjelo vždycky a dotaz,
    // kvůli kterému to celé je, by hráč nikdy neviděl.
    const potvrzeno = confirmMarket === true;
    const code = joinGroupCode.trim().toUpperCase();
    if (!code) return;
    setJoinGroupMsg('');
    try {
      const resp = await friendsApi(`/groups/join`, {
        method: "POST",
        body: JSON.stringify(potvrzeno ? { code, confirm_market: true } : { code }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.gid) {
        // email invite code → joined directly
        setJoinGroupMsg(''); setJoinGroupCode(''); setSocialModal(null);
        fetchMyGroups(); openGroup(data.gid);
      } else if (data.status === 'requested') {
        // group code → request sent, awaits owner approval
        setJoinGroupMsg(t("groups.requestSent")); setJoinGroupCode('');
      } else {
        // "full" and "free_full" are different walls and the person hitting one
        // can do something about only one of them: a full group needs a seat
        // freeing, a group out of free seats will take them if they subscribe.
        // too_many_groups fell through to "Kód nenalezen" until 2026-08-16, so a
        // free player already in a group was told their code did not exist. They
        // cannot fix a wall they are never told about — and this is the one
        // moment Premium is obviously worth buying.
        // Skupina z jiné jazykové verze. Do 16. 9. 2026 sem kód nedošel vůbec:
        // vstup trh nekontroloval, takže systém hráče dovnitř pustil a výpis mu
        // skupinu vzápětí schoval. Samotné odmítnutí by byla slepá ulička, proto
        // se říká, na které doméně ji otevřít — server posílá `lang`.
        if (data.status === 'wrong_market') {
          // Ne konec, ale dotaz. Zakázat vstup a zároveň v té skupině nechat
          // hrát je nesoudržné — další Slovenka by se ke stejným kamarádkám
          // nepřidala. Server proto posílá jazyk skupiny a čeká na potvrzení.
          const jina = LANGUAGE_LINKS.find(l => l.code === data.lang);
          setConfirmAction({
            message: t("groups.joinOtherMarketConfirm", { domain: jina ? jina.domain : data.lang }),
            confirmLabel: t("groups.joinButton"),
            onConfirm: () => joinGroupByCode(true),
          });
          return;
        }
        const key = { already_requested: "groups.alreadyRequested", not_found: "groups.joinNotFound", expired: "groups.joinExpired", already_member: "groups.alreadyMember", full: "groups.joinFull", free_full: "groups.joinFreeFull", too_many_groups: "groups.joinTooManyGroups", group_limit_reached: "groups.joinGroupLimit" }[data.status] || "groups.joinNotFound";
        // `limit` posílá server (_refusal_body). Bez něj hláška ukazovala
        // doslova „({{limit}})" — hlášeno 8. 10. 2026.
        setJoinGroupMsg(t(key, { limit: data.limit }));
      }
    } catch (e) {}
  };

  // Owner approves/declines a code join-request.
  // Push invites: the owner types the other person's FRIEND code, so the person
  // who copies a code is the one better placed to get it right.
  // Start a Premium purchase. The server opens a Stripe-hosted page and returns
  // its URL; we just send the browser there. Never touches a card.
  // Fetch display prices when the paywall opens, so the buttons show what things
  // cost. Fail-soft: no pricing just shows the plan names, checkout still works.
  useEffect(() => {
    // Fetch once on mount so a price is available wherever we quote one (Premium
    // page, gift-a-member confirm, friends/groups), not only on the Premium page.
    if (pricing) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiFetch(`/pricing`);
        const data = resp.ok ? await resp.json() : {};
        if (!cancelled) setPricing(data.plans || {});
      } catch (e) {
        if (!cancelled) setPricing({});
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Friendly currency symbols — Stripe gives an ISO code (CZK/EUR/PLN); show the
  // local symbol instead ("89 Kč", not "89 CZK").
  const CURRENCY_SYMBOL = { CZK: "Kč", EUR: "€", PLN: "zł" };
  const fmtPrice = (plan) => {
    const p = pricing?.[plan];
    if (!p || p.amount == null) return null;
    // toLocaleString, ne toFixed — ze stejného důvodu, který je popsaný o pár
    // řádků níž u měsíčního přepočtu. Tahle větev se do 11. 9. 2026 nikdy
    // nespustila: české ceny jsou celá čísla (79, 690), takže se vždycky vzala
    // ta první půlka podmínky. První desetinné číslo sem přinesla eurová cena a
    // vedle „2,42 €" se objevilo „3.49 €" — dvě desetinné čárky na jedné kartě,
    // každá jiná.
    const amount = p.amount.toLocaleString(i18n.language || undefined, {
      maximumFractionDigits: 2,
    });
    return `${amount} ${CURRENCY_SYMBOL[p.currency] || p.currency}`;
  };
  // The yearly price said in the unit people actually compare in. Derived from
  // Stripe's own amount, never written into the copy as a second number — a
  // hand-typed "57,50" would be one price change away from contradicting the
  // card it sits under. Only for a yearly interval: dividing anything else by
  // twelve would be inventing a figure.
  const fmtMonthlyEquivalent = (plan) => {
    const p = pricing?.[plan];
    if (!p || p.amount == null || p.interval !== "year") return null;
    // toLocaleString, not toFixed, because this is the first place a Czech
    // player meets a decimal here and it must be a comma. Trailing zeros are
    // dropped — "57,5 Kč" reads better than "57,50 Kč" on a small note.
    const per = (p.amount / 12).toLocaleString(i18n.language || undefined, {
      maximumFractionDigits: 2,
    });
    return `${per} ${CURRENCY_SYMBOL[p.currency] || p.currency}`;
  };
  // Price of the live subscription snapshot (amount is in minor units / cents).
  const fmtSubPrice = (sub) => {
    if (!sub || sub.amount == null || !sub.currency) return null;
    const amt = sub.amount / 100;
    // Stejná oprava jako u fmtPrice výše — tohle je cena, kterou předplatitel
    // vidí ve svém účtu, takže se nesmí psát jinak než ta, za kterou platil.
    const a = amt.toLocaleString(i18n.language || undefined, {
      maximumFractionDigits: 2,
    });
    const cur = String(sub.currency).toUpperCase();
    return `${a} ${CURRENCY_SYMBOL[cur] || cur}`;
  };
  // A unix (seconds) timestamp → a localized long date.
  const fmtTs = (ts) => {
    try {
      return new Date(ts * 1000).toLocaleDateString(i18n.language || undefined, {
        day: 'numeric', month: 'long', year: 'numeric',
      });
    } catch (e) { return null; }
  };

  // Derived premium standing for the account UI. `isPaid` is the REAL paid state
  // (shown as a badge + expiry); `showUpgradeCta` is when to nag — only a
  // signed-in free user with enforcement on, so dev/enforcement-off never nags.
  const isPaid = !!myPremium?.entitlement?.active;
  const showUpgradeCta = !!(myPremium && myPremium.enforcing && !myPremium.premium);
  // Premium z dárku nebo kompenzace, bez předplatného: smí si předplatit dopředu
  // a server první platbu odloží na konec dárku (main._deferred_first_payment).
  // Nahlásila to hráčka, která chtěla „aby plynule navázalo" a neměla kde (14. 10. 2026).
  const canSubscribeAhead = !!(myPremium && myPremium.enforcing && isPaid && !myPremium.has_subscription);

  // Paying has to unlock the calendar already on screen.
  //
  // The server flags every day `locked` for a free player, and tapping a locked
  // day opens the paywall from that flag WITHOUT asking the server. The calendar
  // is fetched once per visit, so the copy in hand when somebody pays still says
  // locked — and the person who just paid is exactly the one holding a stale one.
  // Reported 2026-08-16 by a player who bought Premium at 09:09 and was still
  // being asked to buy it; her first archive guess landed at 09:59, after she had
  // written to us. No 402 anywhere in between, because the app never asked.
  // Only when a calendar is actually in hand: at startup isPaid goes null →
  // true for every subscriber, and refetching there would put an extra /archive
  // call behind every app load for exactly the people who pay us. With no
  // calendar loaded there is nothing stale to fix — the normal path fetches a
  // fresh one when the page opens.
  const wasPaid = useRef(isPaid);
  useEffect(() => {
    if (isPaid && !wasPaid.current && archiveDays !== null) fetchArchive();
    wasPaid.current = isPaid;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaid]);
  const fmtPremiumUntil = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString(i18n.language || undefined, {
        day: 'numeric', month: 'long', year: 'numeric',
      });
    } catch (e) { return null; }
  };
  // Datum první platby, jak ho server spočítá: jen když do konce zbývá víc než
  // 49 hodin (kratší odložení Stripe nepřijme a platí se hned).
  const deferredStartDate = (() => {
    if (!canSubscribeAhead) return null;
    const until = myPremium?.entitlement?.valid_until;
    if (!until || new Date(until).getTime() - Date.now() < 49 * 3600 * 1000) return null;
    return fmtPremiumUntil(until);
  })();

  // Open the Stripe Customer Portal so a subscriber can cancel / manage Premium.
  const manageSubscription = async () => {
    setPortalBusy(true);
    try {
      const resp = await friendsApi(`/billing_portal`, { method: "POST" });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.url) { window.location.assign(data.url); return; }
    } catch (e) { /* fall through to re-enable */ }
    setPortalBusy(false);
  };
  // Zpět z portálu vrátí stránku z back/forward cache zmrazenou i s portalBusy,
  // takže místo tlačítka zůstalo „…“ až do znovunačtení (hráč, 28. 9. 2026).
  // Checkout tohle nemá: jeho `finally` stav vrátí ještě před odchodem.
  useEffect(() => {
    const onRestored = (e) => { if (e.persisted) setPortalBusy(false); };
    window.addEventListener('pageshow', onRestored);
    return () => window.removeEventListener('pageshow', onRestored);
  }, []);

  // de platí v našem okně: místo přesměrování otevřít OwnCheckout, který relaci
  // vyžádá až po zaškrtnutí souhlasu (viz OwnCheckout.js).
  const createOwnSession = async (body) => {
    try {
      const resp = await friendsApi(`/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await resp.json().catch(() => ({}));
      return resp.ok && data.client_secret ? data : { error: data.error || "checkout_failed" };
    } catch (e) {
      return { error: "checkout_failed" };
    }
  };

  const startCheckout = async (plan, beneficiaryUid) => {
    if (OWN_CHECKOUT) {
      setCheckoutError(null);
      setOwnCheckout({
        plan,
        kind: plan.startsWith("sub_") ? "sub" : "gift",
        body: { plan, ...(beneficiaryUid ? { beneficiary_uid: beneficiaryUid } : {}) },
      });
      return;
    }
    setCheckoutBusy(plan);
    try {
      const resp = await friendsApi(`/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan, ...(beneficiaryUid ? { beneficiary_uid: beneficiaryUid } : {}) }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.url) {
        window.location.assign(data.url);
        return;
      }
      // Not configured yet (503) or refused — surface it, don't fail silently.
      setCheckoutError(data.error || "checkout_failed");
    } catch (e) {
      setCheckoutError("checkout_failed");
    } finally {
      setCheckoutBusy(null);
    }
  };

  // Buy a gift CODE (to share with anyone): no beneficiary — the webhook mints a
  // redeemable code the buyer forwards. `pendingGift` flags the return so the
  // success redirect lands on "Moje dárky" showing the fresh code.
  const startGiftCode = async (recipientEmail) => {
    if (OWN_CHECKOUT) {
      setCheckoutError(null);
      setOwnCheckout({
        plan: "gift_year",
        kind: "gift",
        giftCode: true,
        body: { plan: "gift_year", gift_kind: "code", ...(recipientEmail ? { recipient_email: recipientEmail } : {}) },
      });
      return;
    }
    setCheckoutBusy('gift_year');
    try {
      const resp = await friendsApi(`/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan: "gift_year", gift_kind: "code", ...(recipientEmail ? { recipient_email: recipientEmail } : {}) }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok && data.url) {
        try { localStorage.setItem('pendingGift', '1'); } catch (e) {}
        window.location.assign(data.url);
        return;
      }
      setCheckoutError(data.error || "checkout_failed");
    } catch (e) {
      setCheckoutError("checkout_failed");
    } finally {
      setCheckoutBusy(null);
    }
  };

  const fetchGiftCodes = async () => {
    if (!currentUser) return;
    try {
      const resp = await friendsApi(`/gift/mine`);
      if (resp.ok) setGiftCodes((await resp.json()).codes || []);
    } catch (e) {}
  };

  // Dárek za výpadek — jen se zeptá (GET), kód vzniká až tlačítkem (POST).
  const fetchDarek = async () => {
    try {
      const resp = await friendsApi(`/kompenzace/darek`);
      setDarek(resp.ok ? await resp.json() : null);
    } catch (e) { setDarek(null); }
  };
  const createDarek = async () => {
    setDarekBusy(true);
    try {
      const resp = await friendsApi(`/kompenzace/darek`, { method: "POST" });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok) { setDarek({ ...(darek || {}), eligible: true, code: data.code }); fetchGiftCodes(); }
      else setDarek({ ...(darek || {}), error: data.error || 'error' });
    } catch (e) {
      setDarek({ ...(darek || {}), error: 'error' });
    } finally {
      setDarekBusy(false);
    }
  };

  // Open the gift page (buy a code to share + redeem + "Moje dárky").
  const openGift = () => {
    setIsAccountMenuOpen(false);
    setGiftRedeemMsg(null);
    fetchGiftCodes();
    goPage('gift');
  };

  const redeemGiftCode = async (code) => {
    const c = (code || '').trim();
    if (!c) return;
    if (!currentUser) { otevriPrihlaseni(); return; }
    setGiftRedeemBusy(true);
    setGiftRedeemMsg(null);
    try {
      const resp = await friendsApi(`/gift/redeem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: c }),
      });
      const data = await resp.json().catch(() => ({}));
      if (data.ok) {
        setGiftRedeemMsg({ ok: true, days: data.days });
        refreshPremium(); // entitlement just changed
      } else {
        setGiftRedeemMsg({ ok: false, error: data.error || 'error' });
      }
    } catch (e) {
      setGiftRedeemMsg({ ok: false, error: 'error' });
    } finally {
      setGiftRedeemBusy(false);
    }
  };

  const giftLink = (code) => `${window.location.origin}/?gift=${encodeURIComponent(code)}`;
  const copyGift = async (text, code) => {
    try {
      await navigator.clipboard.writeText(text);
      setGiftCopied(code);
      setTimeout(() => setGiftCopied(null), 1500);
    } catch (e) {}
  };

  // Activation page: what the code grants + who it's from (no auth needed).
  const fetchGiftPreview = async (code) => {
    const c = (code || '').trim();
    if (!c) return;
    try {
      const resp = await apiFetch(`/gift/preview?code=${encodeURIComponent(c)}`);
      const data = await resp.json().catch(() => ({}));
      setGiftPreview(resp.ok ? data : { error: data.error || 'not_found' });
    } catch (e) {
      setGiftPreview({ error: 'not_found' });
    }
  };

  // Email one of my unclaimed codes to a friend so they sign in and redeem.
  const sendGiftEmail = async (code, email) => {
    const e = (email || '').trim();
    if (!e || e.indexOf('@') < 0) { setGiftSendMsg({ ok: false, error: 'bad_email' }); return; }
    setGiftSendBusy(true);
    setGiftSendMsg(null);
    try {
      const resp = await friendsApi(`/gift/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, email: e }),
      });
      const data = await resp.json().catch(() => ({}));
      if (data.ok) {
        setGiftSendMsg({ ok: true, email: e });
        setGiftSendEmail('');
        fetchGiftCodes();
      } else {
        setGiftSendMsg({ ok: false, error: data.error || 'error' });
      }
    } catch (err) {
      setGiftSendMsg({ ok: false, error: 'error' });
    } finally {
      setGiftSendBusy(false);
    }
  };

  // Any premium-gated call answers 402 when enforcement is on. Turn that into the
  // upgrade prompt instead of a dead end. Returns true if it WAS a paywall (so the
  // caller can stop), false otherwise.
  const handlePaywall = async (resp, feature) => {
    if (resp && resp.status === 402) {
      openPremium(feature);
      return true;
    }
    return false;
  };

  // Toggle a collapsible section (title + its next-sibling content).
  const toggleCollapse = (e) => {
    e.currentTarget.classList.toggle('active');
    e.currentTarget.nextElementSibling.classList.toggle('open');
  };

  // Contact form → POST /contact (emails the owner via Resend). `website` is a
  // honeypot: bots fill it, humans never see it.
  // The banner's two buttons. Store + tell Consent Mode, then close.
  //
  // The count goes to our own logs because from now on GA measures only the
  // people who said yes — without knowing the refusal rate, every GA number
  // becomes an unknown fraction of the truth. Fire-and-forget, and nothing about
  // the visitor goes with it: the request carries the choice and nothing else.
  const chooseConsent = (choice) => {
    writeConsent(choice);
    setConsentOpen(false);
    try {
      apiFetch(`/consent_event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ choice }),
      }).catch(() => {});
    } catch (e) {}
  };

  // Both message forms post to a PUBLIC route — somebody whose payment failed, or
  // who hit something broken before signing in, has to be able to write in. But a
  // signed-in writer should be recognised, because that is how the backend decides
  // whether the mail is marked for priority (see _writer_is_premium). Best effort:
  // no session, or a token that will not come, and the message still sends
  // unmarked. Losing the mark is acceptable; losing the message is not.
  //
  // Missed on 2026-08-24 — the backend read the token and every backend test
  // passed, but nothing sent one, so the first real Premium message arrived
  // unmarked. Same shape as the CORS outage: both layers right, the seam untested.
  const messageHeaders = async () => {
    const headers = { "Content-Type": "application/json" };
    if (!currentUser) return headers;
    try {
      const token = await getIdTokenRef.current();
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch (e) { /* unmarked is fine */ }
    return headers;
  };

  const submitContact = async () => {
    const { name, email, message, website } = contactForm;
    // Validate on submit so the user gets a message (rather than a dead button).
    if (email.indexOf('@') < 0) { setContactMsg({ ok: false, error: 'bad_email' }); return; }
    if (!message.trim()) { setContactMsg({ ok: false, error: 'empty_message' }); return; }
    setContactBusy(true);
    setContactMsg(null);
    try {
      const resp = await apiFetch(`/contact`, {
        method: "POST",
        headers: await messageHeaders(),
        body: JSON.stringify({ name, email, message, website, origin: contactForm.origin || undefined }),
      });
      const data = await resp.json().catch(() => ({}));
      if (data.ok) {
        setContactMsg({ ok: true });
        setContactForm({ name: '', email: '', message: '', website: '', origin: '' });
      } else {
        setContactMsg({ ok: false, error: data.error || 'error' });
      }
    } catch (e) {
      setContactMsg({ ok: false, error: 'error' });
    } finally {
      setContactBusy(false);
    }
  };

  // ── Nahlásit chybu ──────────────────────────────────────────────────────────

  // A phone screenshot is 3–8 MB and none of that helps anyone read it. Redrawn
  // to at most 1600px on the long edge as JPEG — a couple of hundred kB, which
  // arrives on a bad connection and still shows which button did not work.
  //
  // Done in the browser rather than server-side on purpose: the alternative is
  // asking someone on mobile data to upload eight megabytes before finding out
  // whether the form even worked.
  const shrinkImage = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const im = new Image();
    im.onload = () => {
      URL.revokeObjectURL(url);
      try {
        const MAX = 1600;
        const scale = Math.min(1, MAX / Math.max(im.width, im.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(im.width * scale));
        canvas.height = Math.max(1, Math.round(im.height * scale));
        canvas.getContext('2d').drawImage(im, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        // 4 base64 characters per 3 bytes, minus the "data:…," prefix.
        const bytes = Math.round(((dataUrl.length - dataUrl.indexOf(',') - 1) * 3) / 4);
        resolve({ dataUrl, type: 'image/jpeg', bytes });
      } catch (e) { reject(e); }
    };
    im.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode')); };
    im.src = url;
  });

  const pickBugImage = async (file) => {
    if (!file) return;
    setBugMsg(null);
    if (!String(file.type || '').startsWith('image/')) {
      setBugMsg({ ok: false, error: 'unsupported_image' });
      return;
    }
    try {
      setBugImage(await shrinkImage(file));
    } catch (e) {
      // A file the browser cannot decode is not a screenshot, whatever it claims.
      setBugMsg({ ok: false, error: 'unsupported_image' });
    }
  };

  const submitBugReport = async () => {
    const { description, email, website } = bugForm;
    if (!description.trim()) { setBugMsg({ ok: false, error: 'empty_message' }); return; }
    // The address is optional here — but a typed one has to be an address, or
    // the answer goes nowhere and nobody finds out.
    if (email.trim() && email.indexOf('@') < 0) { setBugMsg({ ok: false, error: 'bad_email' }); return; }
    setBugBusy(true);
    setBugMsg(null);
    try {
      const resp = await apiFetch(`/bug_report`, {
        method: "POST",
        headers: await messageHeaders(),
        body: JSON.stringify({
          description, email, website,
          image: bugImage ? { type: bugImage.type, data: bugImage.dataUrl } : null,
          // What the report is worth doubles with this, and none of it is
          // anything the player would have to look up. Named in the form, so
          // sending it is a choice rather than a surprise.
          context: {
            page: currentPage,
            user_agent: navigator.userAgent,
            screen: `${window.innerWidth}×${window.innerHeight}`,
            language: getLang(),
            uid: currentUser?.uid || '',
          },
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (data.ok) {
        setBugMsg({ ok: true });
        setBugForm({ description: '', email: '', website: '' });
        setBugImage(null);
      } else {
        setBugMsg({ ok: false, error: data.error || 'error' });
      }
    } catch (e) {
      setBugMsg({ ok: false, error: 'error' });
    } finally {
      setBugBusy(false);
    }
  };

  // ── Invite by email (friends + groups) ──────────────────────────────────────
  // Reaches people who don't play yet: the backend mints a token, emails a
  // ?invite=TOKEN link, and the invite auto-accepts once they sign in/register.
  const inviteFriendByEmail = async () => {
    const email = addEmailInput.trim();
    if (!email || email.indexOf('@') < 0) { setAddEmailMsg({ status: 'bad_email' }); return; }
    setAddEmailBusy(true);
    setAddEmailMsg(null);
    try {
      const resp = await friendsApi(`/friends/invite/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (await handlePaywall(resp, "friends")) { setSocialModal(null); return; }
      const data = await resp.json().catch(() => ({}));
      // "invited" whether or not they already had an account — the email differs,
      // the outcome to the sender is the same. Distinct refusals get their own line.
      const status = data.ok ? (data.status || 'invited') : (data.status || data.error || 'error');
      setAddEmailMsg({ status });
      if (status === 'invited') setAddEmailInput('');
    } catch (e) {
      setAddEmailMsg({ status: 'error' });
    } finally {
      setAddEmailBusy(false);
    }
  };

  const inviteGroupByEmail = async (gid) => {
    const email = inviteEmailInput.trim();
    if (!email || email.indexOf('@') < 0) { setInviteEmailMsg({ status: 'bad_email' }); return; }
    setInviteEmailBusy(true);
    setInviteEmailMsg(null);
    try {
      const resp = await friendsApi(`/groups/${gid}/invite/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (await handlePaywall(resp, "groups")) { setSocialModal(null); return; }
      const data = await resp.json().catch(() => ({}));
      const status = data.ok ? (data.status || 'invited') : (data.status || data.error || 'error');
      setInviteEmailMsg({ status });
      if (status === 'invited') setInviteEmailInput('');
    } catch (e) {
      setInviteEmailMsg({ status: 'error' });
    } finally {
      setInviteEmailBusy(false);
    }
  };

  // The ?invite=TOKEN landing: what the invite is + who it's from (no auth needed).
  const fetchInvitePreview = async (token) => {
    if (!token) return;
    try {
      const resp = await apiFetch(`/invites/${encodeURIComponent(token)}`);
      const data = await resp.json().catch(() => ({}));
      setInvitePreview(resp.ok ? data : { error: data.error || 'not_found' });
    } catch (e) {
      setInvitePreview({ error: 'not_found' });
    }
  };

  // Accept an email invite as the signed-in user (from the landing page or the
  // bell). Not signed in → open the auth modal, exactly like redeeming a gift.
  const acceptInvite = async (token, opts = {}) => {
    if (!token) return;
    if (!currentUser) { otevriPrihlaseni(); return; }
    setInviteAcceptBusy(true);
    if (!opts.fromBell) setInviteAcceptMsg(null);
    try {
      const resp = await friendsApi(`/invites/${encodeURIComponent(token)}/accept`, { method: "POST" });
      if (await handlePaywall(resp, "friends")) return;
      const data = await resp.json().catch(() => ({}));
      if (data.ok) {
        if (data.kind === 'group') fetchMyGroups(); else fetchFriends();
        if (opts.nid) dismissNotification(opts.nid);
        if (!opts.fromBell) setInviteAcceptMsg({ ok: true, kind: data.kind, group_name: data.group_name });
      } else if (!opts.fromBell) {
        setInviteAcceptMsg({ ok: false, error: data.error || 'error', limit: data.limit });
      }
    } catch (e) {
      if (!opts.fromBell) setInviteAcceptMsg({ ok: false, error: 'error' });
    } finally {
      setInviteAcceptBusy(false);
    }
  };

  const inviteByFriendCode = async (gid, code) => {
    const clean = (code || "").trim().toUpperCase();
    if (!clean) return;
    setInviteBusy(true);
    setInviteResult(null);
    try {
      const resp = await friendsApi(`/groups/${gid}/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: clean }),
      });
      const data = await resp.json().catch(() => ({}));
      // Each refusal has its own message — "no such code" and "already a member"
      // are very different problems for whoever is typing.
      setInviteResult(resp.ok ? "invited" : (data.status || "error"));
      if (resp.ok) setInviteCode("");
    } catch (e) {
      setInviteResult("error");
    } finally {
      setInviteBusy(false);
    }
  };

  const fetchMyInvitations = async () => {
    if (!currentUser) return;
    try {
      const resp = await friendsApi(`/groups/invitations`);
      setMyInvitations(resp.ok ? (await resp.json()).invitations || [] : []);
    } catch (e) {
      setMyInvitations([]);
    }
  };

  const respondToInvitation = async (gid, inviteId, accept) => {
    try {
      const resp = await friendsApi(`/groups/${gid}/invites/${inviteId}/respond`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accept }),
      });
      if (resp.ok) {
        fetchMyInvitations();
        if (accept) fetchMyGroups();
      }
    } catch (e) {
      /* best-effort */
    }
  };

  const approveGroupInvite = async (gid, inviteId) => {
    // Every refusal here used to be silent: `if (resp.ok)` and an empty catch, so
    // a 409 made the button do nothing at all. The owner sees the request, presses
    // Approve, and the row just sits there — which is exactly how it was reported,
    // and it happened 319 times on the first day before anyone said so.
    setGroupApproveMsg(null);
    try {
      const resp = await friendsApi(`/groups/${gid}/invites/${inviteId}/approve`, { method: "POST" });
      if (resp.ok) {
        fetchGroupInvites(gid); fetchOwnerPending();
        if (selectedGroup && selectedGroup.gid === gid) { const r = await friendsApi(`/groups/${gid}`); if (r.ok) setSelectedGroup(await r.json()); }
        return;
      }
      let data = {};
      try { data = await resp.json(); } catch (e) {}
      const key = {
        free_full: "groups.approveFreeFull",
        full: "groups.approveFull",
        too_many_groups: "groups.approveTooManyGroups",
        // Vlastní věta, ne ta o Premiu: tenhle člověk už předplatné má.
        group_limit_reached: "groups.approveGroupLimit",
      }[data.status] || "groups.approveFailed";
      setGroupApproveMsg(t(key, { limit: data.limit }));
    } catch (e) {
      setGroupApproveMsg(t("groups.approveFailed"));
    }
  };
  const withdrawGroupRequest = async (gid) => {
    try {
      const resp = await friendsApi(`/groups/${gid}/request/withdraw`, { method: "POST" });
      // 404 = už vyřízená (správce mezitím přijal nebo odmítl) — seznam se
      // stejně musí načíst znovu, ať ukazuje skutečný stav.
      if (resp.ok || resp.status === 404) fetchMyGroups();
    } catch (e) {}
  };

  const declineGroupInvite = async (gid, inviteId) => {
    try {
      const resp = await friendsApi(`/groups/${gid}/invites/${inviteId}/decline`, { method: "POST" });
      if (resp.ok) { fetchGroupInvites(gid); fetchOwnerPending(); }
    } catch (e) {}
  };

  const fetchStandings = async (gid, type) => {
    // The round's standings arrive with the round itself (one request feeds the
    // whole block), so there is nothing to fetch for 'group'.
    if (type === 'group') return;
    try {
      const resp = await friendsApi(`/groups/${gid}/standings?type=${type}`);
      if (resp.ok) setGroupStandings((await resp.json()).standings || []);
    } catch (e) {}
  };

  // One request feeds the whole round block: the round, my board, the standings.
  // `quiet`: a catch-up after a tip. A failed catch-up must not hide a board
  // that is on screen and fine — the tip already said what went wrong.
  const fetchGroupRound = async (gid, { quiet = false } = {}) => {
    try {
      const resp = await friendsApi(`/groups/${gid}/round`);
      if (!resp.ok) {
        if (quiet) return;
        // Silently returning left the whole block hidden with nothing to explain
        // it — "this group just doesn't have group words" is what that looked like.
        setRoundFailedForGid(gid);
        return;
      }
      const d = await resp.json();
      setRoundFailedForGid(null);
      setRoundInactive(!!d.inactive);
      setGroupRound(d.round || null);
      setRoundState(d.state || null);
      setRoundStandings(d.standings || []);
      setFinishedRound(d.finished_round || null);
      setFinishedState(d.finished_state || null);
      setFinishedStandings(d.finished_standings || []);
      setLastRound(d.last_round || null);
      setLastStandings(d.last_standings || []);
      setQueuedWord(d.queued || null);
      setRoundTurn({
        uid: d.next_setter_uid || null,
        name: d.next_setter_name || null,
        isMine: !!d.is_my_turn,
        canPass: !!d.can_pass_turn,
        todayUsed: !!d.today_used,
        canClose: !!d.can_close,
        // „Teď mě do rotace nedávejte." Chodí s kolem, ne zvlášť — přepínač je
        // hned pod panelem a druhý dotaz kvůli jednomu boolean nemá důvod.
        iAmSleeping: !!d.i_am_sleeping,
        // Nabízet uspání tam, kde řada k člověku nikdy nedojde, znamená tlačítko
        // pro nic. Rozhoduje server (sleep_is_meaningful).
        canSleep: !!d.can_sleep,
        allAsleep: !!d.all_asleep,
        // Zakladatel smí vzít slovo za skupinu, na kterou se den čeká.
        canTakeOver: !!d.can_take_over,
        // Whether I may ever hold the turn at all. Missing means yes: a market
        // that enforces nothing must not grow a Premium note it cannot sell.
        canSet: d.can_set_word !== false,
        // Kdo přijde po něm (jen v rotaci). Bez toho působilo pořadí náhodně.
        order: Array.isArray(d.turn_order) ? d.turn_order : [],
      });
      setRoundForGid(gid);
    } catch (e) {
      if (!quiet) setRoundFailedForGid(gid);
    }
  };

  // Everything the round block reads, back to nothing. Called before opening a
  // different group so its word can never be shown under someone else's name.
  const clearRoundState = () => {
    setGroupRound(null); setRoundState(null); setRoundStandings([]);
    setFinishedRound(null); setFinishedState(null); setFinishedStandings([]);
    setLastRound(null); setRoundTurn(null); setQueuedWord(null); setRoundForGid(null); setRoundFailedForGid(null);
    setRoundGuess(''); setRoundMsg('');
    setRoundLastAttempt(null); setRoundLastSubmit(0); setRoundTimeoutProgress(0);
  };

  // The round board is reachable from two places — the group page and the main
  // board's group tab — so every round action resolves its group the same way.
  const roundGid = openRoundGid || selectedGroup?.gid || null;

  // A board loaded once stays on its round. When the refreshed list says the
  // group has moved on to a NEW round — the next morning's, or a teacher's second
  // word in the same lesson (29. 9. 2026) — fetch the board again. Only on a
  // different round NUMBER: winning, or the round closing, keeps the number, so
  // the win screen is never refetched away (see submitRoundGuess).
  const shownRoundNo = groupRound?.round ?? finishedRound?.round ?? null;
  const listedRoundNo = myGroupRounds.find((r) => r.gid === roundGid)?.round ?? null;
  useEffect(() => {
    if (!roundGid || roundForGid !== roundGid) return;
    if (listedRoundNo == null || listedRoundNo === shownRoundNo) return;
    fetchGroupRound(roundGid);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundGid, roundForGid, listedRoundNo]);

  // The open group's own switch. Missing means on — every group that existed
  // before the switch played group words, and must keep playing them.
  const groupWordsOn = selectedGroup ? selectedGroup.group_words_enabled !== false : true;
  const setterRotates = (selectedGroup?.setter_mode || 'owner') === 'rotation';

  // „Potom: Marcela, Zbyněk, Kateřina …" pod tím, kdo je na řadě. Jeden drobný
  // řádek, ne seznam: stránka skupiny je plná a jde jen o to, aby pořadí nebylo
  // tajemství (31. 10. 2026 — zakladatelka zadávala nejčastěji a myslela si, že
  // skoro vůbec). Tři jména; kdo je dál, uvidí aspoň svoje místo.
  const turnOrderLine = () => {
    const order = roundTurn?.order || [];
    if (!setterRotates || !order.length) return null;
    const me = currentUser?.uid;
    const shown = order.slice(0, 3).map(o => (o.uid === me ? t("groupWord.orderYou") : (o.name || '—')));
    const myIdx = order.findIndex(o => o.uid === me);
    let text = t("groupWord.orderThen", { names: shown.join(', ') + (order.length > 3 ? ' …' : '') });
    // Na řadě je 1., takže první po něm je 2.
    if (myIdx >= 3) text += ' ' + t("groupWord.orderMyPlace", { n: myIdx + 2 });
    return <span className="round-day__order">{text}</span>;
  };

  // A free member guesses the group's word like everybody else but never gets
  // the turn to choose one — the rotation skips them server-side. Without this
  // the turn simply never arrives and nothing on the page says why, which reads
  // as a fault rather than as a rule. Rendered in both places a member meets the
  // round: the group page and the board's group tab.
  const setterPremiumNote = () => (roundTurn && !roundTurn.canSet ? (
    <div className="round-premium">
      <p className="round-premium__text">{t("groupWord.needPremiumToSet")}</p>
      <button className="pill-btn pill-btn--ghost round-premium__cta"
        onClick={() => openPremium('groups')}>
        {t("groupWord.needPremiumCta")}
      </button>
    </div>
  ) : null);

  // What the board shows: the running round, or the one that ended today. They
  // are the same screen. A round is not over for the person who played it the
  // moment its last cell is filled in — closing it used to take the win, the
  // tips and the result away all at once (reported 2026-08-22).
  const shownRound = groupRound || finishedRound;
  // Svět běžícího kola, když se liší od `tema` (svět skupiny nebo nový návrh).
  // Kolo si svět zkopíruje při otevření, změna platí až od dalšího. 22. 10. 2026
  // správkyně ve skupině viděla „Harry Potter" a hráči „lidské tělo" — nikde
  // nestálo, že se dnes hraje jiný svět, než je nastavený.
  const temaKolaJine = (gid, tema) =>
    !!groupRound && roundForGid === gid && (groupRound.tema || null) !== (tema || null);
  const temaKolaVeta = () => groupRound?.tema
    ? t("groups.temaRoundDiffers", { tema: groupRound.tema })
    : t("groups.temaRoundNone");
  const roundOver = !groupRound && !!finishedRound;
  const shownState = groupRound ? roundState : finishedState;
  const shownStandings = groupRound ? roundStandings : finishedStandings;

  // My tips on the open round, in the shape every board uses: warmest first,
  // similarity as a percentage. Derived from the server's copy, so a refetch
  // (another device, a returning tab) cannot drift from what is stored.
  const roundAttempts = ((shownState && shownState.guesses) || [])
    .map((g) => ({ guess: g.word, adjustedSimilarity: toPct(g.similarity || 0), order: g.order }))
    .sort((a, b) => b.adjustedSimilarity - a.adjustedSimilarity);

  const fetchMyGroupRounds = async () => {
    try {
      const resp = await friendsApi(`/user/group_rounds`);
      if (!resp.ok) return;
      const d = await resp.json();
      setMyGroupRounds(d.rounds || []);
    } catch (e) {
    } finally {
      // Even a failed call counts as an answer: the tab must not be left waiting
      // on a list that will never arrive.
      setGroupRoundsLoaded(true);
    }
  };

  // Open one group's word on the main board. Reuses the group-page round state,
  // so guessing behaves identically wherever you came in from.
  const openGroupBoard = (gid) => {
    setOpenRoundGid(gid);
    setRoundMsg('');
    setRoundGuess('');
    setRoundLastAttempt(null); // the highlighted card belongs to the board you left
    fetchGroupRound(gid);
    // The tab switch keeps the scroll position of the board you came from, which
    // left this board's heading hidden under the header.
    window.scrollTo({ top: 0 });
  };

  // Who gets to set the word. The endpoint has existed since group words shipped;
  // nothing ever called it, so all 109 groups sat in "owner" — the mode they are
  // created in — with no way out and a skip button that could not move the turn.
  const setSetterModeSwitch = async (gid, mode) => {
    if (groupWordsBusy) return;
    setGroupWordsBusy(true);
    try {
      const resp = await friendsApi(`/groups/${gid}/setter_mode`, {
        method: "POST",
        body: JSON.stringify({ mode }),
      });
      if (!resp.ok) return;
      setSelectedGroup(g => (g && g.gid === gid ? { ...g, setter_mode: mode } : g));
      fetchGroupRound(gid);
    } catch (e) {
    } finally {
      setGroupWordsBusy(false);
    }
  };

  // The group's own switch for group words. Turning it off pauses a running round
  // server-side (and drops the group from the rounds tab), so the page is
  // refetched rather than patched locally.
  const setGroupWordsSwitch = async (gid, enabled) => {
    if (groupWordsBusy) return;
    setGroupWordsBusy(true);
    try {
      const resp = await friendsApi(`/groups/${gid}/group_words`, {
        method: "POST",
        body: JSON.stringify({ enabled }),
      });
      if (!resp.ok) return;
      setSelectedGroup(g => (g && g.gid === gid ? { ...g, group_words_enabled: enabled } : g));
      fetchGroupRound(gid);
      fetchMyGroupRounds(); // a group that switched off leaves the board's tab
    } catch (e) {
    } finally {
      setGroupWordsBusy(false);
    }
  };

  // "Play the group's word": onto the board, on THIS group's round. Going through
  // the mode switch alone landed anyone with two running rounds on the picker —
  // a list, in answer to a button that already named the group.
  const playGroupWord = (gid) => {
    setActiveTab('group');
    localStorage.setItem('activeTab', 'group');
    setGroupStandingsTab('group');
    openGroupBoard(gid);
    fetchMyGroupRounds(); // keeps the tab honest if this round is news to the list
    goPage('game');
  };

  // Only reachable from a school group (see the button). The word comes out to
  // everyone, and the setter can open the next one straight away.
  const closeGroupRound = async () => {
    if (!roundGid || roundBusy) return;
    setRoundBusy(true);
    try {
      await friendsApi(`/groups/${roundGid}/round/close`, { method: "POST" });
      fetchGroupRound(roundGid);
      fetchMyGroupRounds();
    } catch (e) {
      setRoundMsg(t("groupWord.errGeneric"));
    } finally {
      setRoundBusy(false);
    }
  };

  // Výzvu „Jsi na řadě" pro skupinu uklidit hned i na obrazovce. Server ji maže
  // sám, ale zvoneček a puntík se obnovují jen jednou za minutu — po „Přeskočit
  // mě" nebo zadaném slovu by tam do té doby dál svítily (21. 10. 2026).
  const dropTurnBell = (gid) =>
    setNotifications(ns => ns.filter(n => !(n.kind === 'group_turn' && n.gid === gid)));

  const passGroupTurn = async () => {
    if (!roundGid || roundBusy) return;
    setRoundBusy(true);
    try {
      const resp = await friendsApi(`/groups/${roundGid}/turn/pass`, { method: "POST" });
      if (resp?.ok) dropTurnBell(roundGid);
      fetchGroupRound(roundGid);
    } catch (e) {
      setRoundMsg(t("groupWord.errGeneric"));
    } finally {
      setRoundBusy(false);
    }
  };

  // How long a round opened RIGHT NOW would actually run. It ends at the 03:00
  // rotation and no further, so at eleven at night the honest number is four
  // hours — which is the whole argument for keeping the word until morning
  // instead. Shown on the button rather than decided for them: at nine in the
  // evening some groups genuinely want the four hours.
  const msToRotation = () => {
    const prague = new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/Prague" }));
    const next = new Date(prague);
    next.setHours(ROTATION_HOUR, 0, 0, 0);
    if (next <= prague) next.setDate(next.getDate() + 1);
    return next - prague;
  };

  // One derived answer, both panels. See wordMove.
  // Otevřel formulář „zadat za skupinu"? Pak smí jen dnešek: slovo na zítra se
  // ukládá do fronty pod jméno zadavatele a otevře se jen tomu, kdo bude na řadě.
  const [takingOver, setTakingOver] = useState(false);

  // Kdy kdo ve skupině naposledy hrál. Vlastní dotaz, a schválně ne součást
  // kola: odpověď stojí desítky čtení a kolo se načítá dokola. Tenhle se ptá
  // jednou při otevření skupiny a server ho navíc drží čtvrt hodiny v paměti.
  const [groupActivity, setGroupActivity] = useState(null);
  const fetchGroupActivity = async (gid) => {
    try {
      const resp = await friendsApi(`/groups/${gid}/activity`);
      if (!resp.ok) return;
      const d = await resp.json();
      // Jen když odpověď ten údaj opravdu nese. `{}` od rozbitého nebo cizího
      // endpointu není „nikdo třicet dní nehrál" — a rozdíl mezi tím a nevědomostí
      // je tu zásadní: druhá varianta by nálepku „naposledy před víc než měsícem"
      // pověsila na každého člena včetně těch, kdo hráli před chvílí.
      if (!d || typeof d.last_played !== "object" || d.last_played === null) return;
      setGroupActivity({ gid, lastPlayed: d.last_played, streaks: d.streaks || {}, capped: d.streaks_capped || [] });
    } catch (e) {
      // Nevadí: bez téhle odpovědi se prostě neřekne nic navíc.
    }
  };

  // Věta o nehrajícím členovi, nebo null. `prah: 0` ve správě skupiny — tam se
  // člověk dívá právě proto, aby to viděl u všech.
  const awayLabel = (uid, joinedAt, prah, dlouze = false) => {
    if (!groupActivity || groupActivity.gid !== selectedGroup?.gid) return null;
    const info = lastPlayedInfo({
      lastPlayed: groupActivity.lastPlayed[uid],
      streak: (groupActivity.streaks || {})[uid] || 0,
      zaOknem: (groupActivity.capped || []).includes(uid),
      joinedAt,
      today: liveDayIso(),
      ...(prah === undefined ? {} : { prah }),
    });
    if (!info) return null;
    if (info.kind === "streak") {
      const kolik = `${info.days}${info.capped ? "+" : ""}`;
      const veta = t("groups.streakLong", {
        count: info.days,
        unit: pluralize(t, i18n.language, "plural.dayShort", info.days),
      });
      if (dlouze) return veta;
      // V tabulce dne ohníček a „25 dní v řadě“ bez slovesa — celá věta by se
      // nevešla a `white-space: nowrap` by místo popisku zalomil jméno. Samotné
      // „🔥 25“ nikdo nepochopil („k čemu jsou ty plamínky?“, 26. 9. 2026)
      // a `title` se na mobilu neukáže.
      const kratce = t("groups.streakShort", {
        n: kolik,
        unit: pluralize(t, i18n.language, "plural.dayShort", info.days),
      });
      return <span title={veta}>🔥 {kratce}</span>;
    }
    // Krátce. Popisek sedí v řádku vedle jména a `white-space: nowrap` ho nepustí
    // zalomit — takže dlouhá věta místo sebe zalomí JMÉNO. „Naposledy před víc
    // než měsícem“ udělalo z „Lenky Veselé“ dva řádky (hlášeno 23. 9. 2026).
    if (info.kind === "today") return t("groups.guessingToday");
    if (info.kind === "never") return t("groups.newMember");
    if (info.kind === "long") return t("groups.notGuessingMonth");
    return t("groups.notGuessingDays", {
      count: info.days,
      unit: pluralize(t, i18n.language, "plural.dayShort", info.days),
    });
  };
  const myWordMove = wordMove({
    round: groupRound,
    turn: roundTurn,
    queued: queuedWord,
    enabled: features.group_words !== false,
    rotates: setterRotates,
  });

  // The invitation and the way in, kept in one piece so they can never again say
  // different things about the same act.
  // A chosen word, as an object rather than a sentence about one. The action that
  // changes it belongs INSIDE it — a button underneath had to be read as
  // belonging to the word above it, and was not ("mělo by to být přímo u toho
  // slova"). Always through maskWord: a screenshot of this panel would otherwise
  // hand the word to the people meant to guess it.
  const WordSlab = ({ word, onEdit, editLabel }) => (
    <p className="round-word">
      <span className="round-word__tick" aria-hidden>✓</span>
      <span className="round-word__text">{maskWord(word)}</span>
      {onEdit && (
        <button type="button" className="round-word__edit" onClick={onEdit}>{editLabel}</button>
      )}
    </p>
  );

  // Kdy se slovo otevře — přepínač v okně místo dvou velkých tlačítek (21. 10. 2026).
  const [wordWhen, setWordWhen] = useState("now");
  const openWordForm = () => { setSetWordMsg(''); setSetWordInput(''); setTakingOver(false); setWordWhen("now"); setSocialModal('setGroupWord'); };
  // Přebrání řady zakladatelem. Samostatná cesta do stejného formuláře, protože
  // `openWordForm` se předává rovnou jako onClick — parametr by dostal událost,
  // a ta je pravdivá vždycky.
  const openTakeOverForm = () => { setSetWordMsg(''); setSetWordInput(''); setTakingOver(true); setSocialModal('setGroupWord'); };

  // Setting a word for TODAY is one act with no schedule around it, so it stays a
  // sentence and a button. Everything else is a two-day rota — today's word
  // playing out, tomorrow's already chosen — and a rota read as a stack of
  // centred sentences of equal weight, which is what it looked like: "působí to
  // moc nepřipraveně a ad hoc" (2026-09-06).
  const WordMoveBlock = ({ move }) => {
    if (!move) return null;
    // One band, whichever day it is about — the block owns its own heading and
    // rule so no caller has to guess which day it opened.
    return (
      <div className="round-day">
        {/* The hour is in the heading, not in a sentence under the button. It is
            the one fact the whole feature turns on and it belongs to the section,
            not to the action. */}
        <p className="round-day__label">
          {t(move.day === "tomorrow" ? "groupWord.dayTomorrow" : "groupWord.dayToday")}
        </p>
        {/* Whose word it is — but only in a group where that can vary. */}
        {move.lead && (
          <p className={`round-day__lead${move.waiting ? ' round-day__lead--strong' : ''}`}>
            {t(move.lead)}
          </p>
        )}
        {move.waiting?.word ? (
          <WordSlab word={move.waiting.word} onEdit={openWordForm} editLabel={t(move.cta)} />
        ) : (
          <button
            className={move.day === "tomorrow" ? "pill-btn pill-btn--ghost" : "pill-btn pill-btn--accept"}
            onClick={openWordForm}
          >
            {t(move.cta)}
          </button>
        )}
        {/* What follows FROM having chosen it, not just when. Choosing tomorrow's
            word is also what takes tomorrow's game away from you. */}
        {move.note && <p className="round-day__note">{t(move.note)}</p>}
      </div>
    );
  };

  // Pojistka proti dvojímu odeslání (Enter + klik, dvojklik). 22. 10. 2026 ve
  // Slovíčkách první odeslání otevřelo kolo a druhé, hned po něm, už vidělo den
  // obsazený a uložilo totéž okno jako slovo na zítřek.
  const setWordBusyRef = useRef(false);
  const submitGroupWord = async (when = "now") => {
    const word = setWordInput.trim().normalize("NFC");  // viz validateWord
    if (!word || !selectedGroup || setWordBusyRef.current) return;
    setWordBusyRef.current = true;
    setSetWordMsg('');
    try {
      const resp = await friendsApi(`/groups/${selectedGroup.gid}/word`, {
        method: "POST", body: JSON.stringify({ word, when }),
      });
      if (resp.ok) {
        const ok = await resp.json().catch(() => ({}));
        setSetWordInput('');
        dropTurnBell(selectedGroup.gid);
        // Two outcomes now. A word set while today's is still free opens a round;
        // one set afterwards is KEPT for tomorrow. Saying which is the whole
        // point — this modal used to invite the word and then refuse it.
        if (ok.queued) {
          setSetWordMsg(okMsg(t("groupWord.queuedOk")));
        } else {
          setSocialModal(null);
        }
        fetchGroupRound(selectedGroup.gid);
        fetchMyGroupRounds(); // a new word gives the board its group tab
        return;
      }
      const d = await resp.json().catch(() => ({}));
      // Every refusal names itself — a silent one here would look like the save
      // button is broken, which is exactly the bug we fixed on the rename.
      // The numbers travel with the refusal. "Vyčerpali jste počet slov" without
      // saying WHICH count is what one player called "limit o kterém bohužel
      // nevím" — she had no way to know the number, or that it frees up daily.
      setSetWordMsg(t(
        d.error === "word_is_live" ? "groupWord.errLive"
          : d.error === "round_already_open" ? "groupWord.errOpen"
            : d.error === "group_words_off_here" ? "groupWord.offHere"
              // No "round_today_done" branch: a word set after today's is KEPT for
              // tomorrow now, not refused, so the server never sends it. Leaving
              // the branch here would be a second answer to a settled question.
              : d.error === "round_limit_reached" ? "groupWord.errLimit"  // {{used}}/{{limit}} below
                  : d.error === "not_your_turn" ? "groupWord.errNotYourTurn"
                    : d.error === "word_already_prepared" ? "groupWord.errAlreadyPrepared"
                    : d.error === "already_set_today" ? "groupWord.errAlreadySetToday"
                    : d.error === "premium_required" ? "groupWord.errNeedPremium"
                      : "groupWord.errGeneric",
        { limit: d.limit, used: d.used }
      ));
    } catch (e) {
      setSetWordMsg(t("groupWord.errGeneric"));
    } finally {
      setWordBusyRef.current = false;
    }
  };

  const submitRoundGuess = async () => {
    const word = roundGuess.trim().normalize("NFC").toLowerCase();  // viz validateWord
    if (!word || !roundGid || roundBusy) return;
    // Respect the cooldown, same as every other board (the bar shows the wait).
    if (Date.now() - roundLastSubmit < roundCooldown) return;
    // A word already tried just highlights what is there — no round trip.
    if (roundAttempts.some((a) => a.guess === word)) {
      setRoundGuess('');
      setRoundMsg(t("groupWord.alreadyGuessed"));
      return;
    }
    setRoundBusy(true);
    setRoundMsg('');
    setRoundLastSubmit(Date.now()); // start the timeout bar immediately
    try {
      const resp = await withScoringRetry(
        () => friendsApi(`/groups/${roundGid}/guess`, {
          method: "POST", body: JSON.stringify({ word1: word }),
        }),
      );
      const d = await resp.json().catch(() => ({}));
      if (d.recommended_cooldown != null) setRoundCooldown(d.recommended_cooldown);
      if (!resp.ok) {
        setRoundMsg(d.error || t("groupWord.errGeneric"));
        return;
      }
      setRoundGuess('');
      // A repeat is not an error — it just already has a row, so say so instead
      // of letting the tip vanish with nothing happening.
      if (d.already_guessed) {
        setRoundMsg(t("groupWord.alreadyGuessed"));
        // Words on the board never get here (checked above), so the board is
        // behind the server: the first try was stored but its answer was lost.
        // 2. 11. 2026 "dřevo" took 32 s to score, the phone dropped the reply,
        // and every retry said "already tried" over a board without it.
        fetchGroupRound(roundGid, { quiet: true });
        return;
      }
      const pct = toPct(d.similarity);
      await flyGuess({
        word,
        similarity: pct,
        previous: roundLastAttempt,
        setFlying: setRoundFlyingWord,
        setLeaving: setRoundLeavingWord,
        setLast: setRoundLastAttempt,
      });
      if (d.guessed) {
        // Do NOT refetch this board. Solving it as the last member CLOSES the
        // round, and a closed round comes back as "no word running" — which
        // replaced the win screen with an empty board at the exact moment it was
        // earned. Reported 2026-08-21: "místo abych viděl vítěznou obrazovku (…)
        // se hádání ukončilo a skočilo na další skupinu." The daily and mega
        // boards keep their win screen; so does this one now.
        setRoundState((prev) => {
          const guesses = (prev && prev.guesses) || [];
          return {
            ...(prev || {}),
            status: 'guessed',
            guesses: [...guesses, { word, similarity: d.similarity, order: guesses.length + 1 }],
          };
        });
        fetchMyGroupRounds(); // the list still has to learn the round is over
      } else {
        fetchGroupRound(roundGid);
        // The list of groups is refreshed every five minutes, so without this
        // "← Jiná skupina" went back to a row still saying "Ještě nehádáš" over
        // a round with forty tips in it (reported 2026-09-29). Patched here, not
        // refetched: the new count is known, and a request per tip is not free.
        const gid = roundGid;
        // Only trusted when the board's state is this group's; otherwise +1 on the row.
        const count = roundForGid === gid ? (((roundState && roundState.guesses) || []).length) + 1 : 0;
        setMyGroupRounds((rs) => rs.map((r) => (r.gid !== gid || r.status === 'guessed' ? r : {
          ...r,
          status: 'in_progress',
          attempts: Math.max((r.attempts || 0) + 1, count),
          best_similarity: Math.max(r.best_similarity || 0, d.similarity || 0),
        })));
      }
    } catch (e) {
      setRoundMsg(t("groupWord.errGeneric"));
      // A lost reply is not a lost tip — the server may have stored it.
      fetchGroupRound(roundGid, { quiet: true });
    } finally {
      setRoundBusy(false);
    }
  };

  const fetchGroupInvites = async (gid) => {
    try {
      const resp = await friendsApi(`/groups/${gid}/invites`);
      if (resp.ok) setGroupInvites((await resp.json()).invites || []);
    } catch (e) {}
  };

  // Vrací, jestli se skupina povedlo otevřít. Volající z bubliny na to čeká:
  // notifikace ukazující do skupiny, která už neexistuje (nebo ze které jsem
  // odešel), by jinak jen potichu nic neudělala — a to je horší než chybová
  // hláška, protože to vypadá jako rozbité tlačítko.
  const openGroup = async (gid, opts = {}) => {
    try {
      // fetch the group BEFORE navigating so 'group' never renders with a null
      // selectedGroup (which would be a blank screen).
      const resp = await friendsApi(`/groups/${gid}`);
      if (!resp.ok) return false;
      const g = await resp.json();
      // follow the sticky word-mode, the group round included — unless the caller
      // knows better. A bell that says "new word in the group" has to land on the
      // tab that shows that word, not on whichever one was last open.
      // A group with a word of its own opens ON that word. Following the sticky
      // daily/mega tab is right for the board's own switch and wrong here:
      // tapping a group whose round is running landed on the DAILY standings,
      // under a line explaining that the group's word lives behind another tab.
      // That line was a workaround for this. Reported 2026-09-06.
      const ownWord = myGroupRounds.some((r) => r.gid === gid && showsRound(r));
      const mode = opts.mode ? opts.mode
        : ownWord ? 'group'
        : activeTab === 'group' ? 'group' : activeTab === 'weekly' ? 'mega' : 'daily';
      if (mode === 'group') { setActiveTab('group'); localStorage.setItem('activeTab', 'group'); }
      setGroupStandings(null); setGroupInvites(null);
      setGroupStandingsTab(mode);
      setSelectedGroup(g);
      // Drop whatever round was on screen — another group's, or one opened from
      // the main board, whose gid would otherwise still be the action target.
      clearRoundState();
      setOpenRoundGid(null);
      goPage('group');
      fetchStandings(gid, mode);
      fetchGroupRound(gid);
      // Kdo tu ještě hraje. Jednou při otevření, ne s každým načtením kola —
      // viz fetchGroupActivity.
      setGroupActivity(null);
      fetchGroupActivity(gid);
      if (g.owner_uid === currentUser?.uid) fetchGroupInvites(gid);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Unified daily/mega switch for the sticky bottom bar. One control drives the
  // game word (activeTab), the friends standings, and the group standings.
  // Group words are a third board, but NOT a third standings mode — friends and
  // group standings only know daily/mega, so `wordMode` deliberately ignores it.
  const wordMode = activeTab === 'weekly' ? 'mega' : 'daily';
  // Which segment is lit. The group segment only exists while some group of mine
  // has a word waiting; an empty "Skupinové" tab would be a puzzle.
  // The list carries every group of mine now, with `has_word` saying which have
  // one running — so the tab's existence has to ask that, not the list's length.
  //
  // MISSING means true, deliberately. A backend that predates the field only ever
  // listed groups that had a word, so every row it sends has one; reading absence
  // as "no word" would hide the group tab from everybody for as long as a new
  // bundle was live against an old backend.
  const hasWord = (r) => r.has_word !== false;
  // Worth a tab, which is not the same as playable. A round finished today is
  // read-only, but it is where the win screen, my tips and the group's result
  // live — and until 2026-08-22 solving the last one took the segment away and
  // there was no route back to any of it.
  const showsRound = (r) => hasWord(r) || !!r.finished_today;
  // Worth OPENING, which is narrower than "has something in it". A round I set
  // myself has a word, but the board behind it has no input for me — I know the
  // answer — so a card that looks pressable promises a screen it cannot deliver.
  //
  // Nor once it is over. The exception this line first carried — that a finished
  // round stays open even when it was mine — came from a report about losing the
  // route to "how did I guess" (2026-08-22), and a setter never guessed. Their
  // view of a finished round is how the GROUP did, which is on the group's own
  // page, not in the list of words to play. Corrected 2026-09-07 on sight.
  const canOpenRound = (r) => !r.is_setter && (hasWord(r) || !!r.finished_today);
  const hasGroupWords = myGroupRounds.some(showsRound);
  // Where a group board can actually be shown. The switcher must not offer a
  // segment the page underneath has nothing to fill: /friends/today carries a
  // friend's daily and mega summaries and nothing else — friends are not group
  // members, so there is no such thing as a friend's group standing — and "Moje
  // výhry" filters its list by daily/mega alone. On both pages Skupinové used to
  // light up over a list that silently stayed on whatever was picked before it.
  //
  // Reported 2026-08-24 by a player: "v záložce Přátelé se nezobrazuje progres ve
  // skupině. Zobrazuje se vždy stav z denního slova, případně z megaslova podle
  // toho, co bylo zvoleno předtím." Twice — the first answer said it was fixed and
  // it was not, because the reload fix that day was a different bug on the same
  // switch.
  //
  // Group progress is not lost: it lives on the group's own page, where every
  // member's standing already is. Leaving the tab there and going back to the
  // game still finds the group board, because `activeTab` itself is untouched.
  const groupModeAvailable = hasGroupWords && (currentPage === 'game' || currentPage === 'group');
  const boardMode = activeTab === 'group' && groupModeAvailable ? 'group' : wordMode;
  // The open group's name — the group board's headline, where the daily board
  // puts the date. From the list, so it is there before the round itself loads.
  const groupBoardName =
    myGroupRounds.find(r => r.gid === openRoundGid)?.group_name || selectedGroup?.name || '';
  // Mega is no longer a Premium wall but an allowance (2026-08-05):
  //   signed out -> needs an account (the allowance is per account)
  //   signed in   -> plays, with megaLeft tips today
  //   premium     -> unlimited (megaLeft stays null)
  // Only the signed-out case locks the toggle; a free player must be able to open
  // the tab, because they can actually play it.
  const megaNeedsAccount = !!(features.mega_requires_account && !currentUser);
  // The daily allowance a free account gets, quoted in the sign-up and Premium
  // pitches. From /health, not hardcoded: the admin can change it per language,
  // and a pitch promising a number nobody honours is worse than none.
  const megaFreeLimit = features.mega_free_daily_attempts || DEFAULT_MEGA_FREE_ATTEMPTS;
  // Groups I FOUNDED, which is not the same as groups I am in — the limit is on
  // founding. Reported 2026-08-24: with three of my own already, "+ Vytvořit
  // skupinu" still invited the tap and the dialog then refused it. A button that
  // cannot work should say why instead of opening a form that cannot submit.
  const ownedGroupLimit = myOwnedGroupLimit || features.owned_group_limit || DEFAULT_OWNED_GROUP_LIMIT;
  const ownedGroupCount = (groups || []).filter(g => g.owner_uid === currentUser?.uid).length;
  // Skupiny, které stojí na předplatném tohoto hráče — po jeho skončení ztichnou.
  const vlastniSkupiny = (groups || []).filter(g => g.owner_uid === currentUser?.uid);
  // Never while the list is still null: an empty list would read as "no groups"
  // and, on a slow connection, lock the button for somebody who owns none.
  const ownedGroupLimitReached = groups !== null && ownedGroupCount >= ownedGroupLimit;
  const megaLocked = megaNeedsAccount;
  // Free player who has spent today's tips: the board stays, the input goes.
  const megaSpent = megaLeft !== null && megaLeft <= 0;
  // My own standings row comes from the live board whenever the board is further
  // along than the server's mirror: the mirror lags a tip, and a mid-period word
  // rotation blanks the cell — both showed me "Ještě nehádá" about myself while I
  // was demonstrably guessing (reported from testing). Never downgrades the row.
  const mergeMyStanding = (row, mode) => {
    const list = mode === 'mega' ? weeklyAttempts : attempts;
    const guessed = mode === 'mega' ? weeklyIsGuessed : isGuessed;
    if (row.status === 'guessed' || (!guessed && list.length === 0)) return row;
    if (!guessed && list.length <= (row.attempts || 0)) return row;
    const best = list.reduce((m, a) => Math.max(m, a.adjustedSimilarity || 0), 0) / 100;
    return {
      ...row,
      status: guessed ? 'guessed' : 'in_progress',
      attempts: Math.max(list.length, row.attempts || 0),
      best_similarity: Math.max(best, row.best_similarity || 0),
    };
  };

  const switchWordMode = (mode) => {
    // Signed out players are NOT intercepted: the mega tab opens and shows what it
    // is and how to get in. Telling someone "no" before they have seen what they
    // are being offered is the thing testing complained about.
    if (mode === 'group') {
      setActiveTab('group');
      localStorage.setItem('activeTab', 'group');
      // The switcher drives every section, this one included: with Skupinové
      // picked, a group's page must show the ROUND, not the daily word it was
      // silently still showing under an unlabelled list.
      setGroupStandingsTab('group');
      fetchMyGroupRounds();
      // No navigation here: on a group page, Denní/Megaslovo re-label the list in
      // place, and Skupinové must behave the same. Getting to the board is what
      // the "play it" button is for.
      // Which group's board opens is settled by the effect below, so the click
      // and a reload into this tab end up in exactly the same place.
      window.scrollTo({ top: 0 });
      return;
    }
    const at = mode === 'mega' ? 'weekly' : 'daily';
    setActiveTab(at);
    localStorage.setItem('activeTab', at);
    setOpenRoundGid(null);
    setFriendsTab(mode);
    setGroupStandingsTab(mode);
    if (selectedGroup && currentPage === 'group') fetchStandings(selectedGroup.gid, mode);
  };

  // Nobody is bounced off the mega tab any more. Signed out lands on the sign-up
  // invitation, a signed-in free player lands on a board they can actually play.

  // The group tab exists only as long as some group has a word running, so it has
  // to be kept honest from anywhere — a click, a reload, a round that just closed.
  // With a single group there is nothing to choose: its board opens straight away.
  useEffect(() => {
    if (activeTab !== 'group') return;
    // Only for the board. A group's own page shows ITS round, from the same state
    // — opening a board here dragged another group's round onto the page you were
    // reading: its members wore that round's statuses and its own block vanished.
    if (currentPage !== 'game') return;
    // Right after a reload the session is still being restored: no user yet and an
    // empty list, which would read as "no word" and throw you off the tab.
    if (loadingAuth) return;
    if (currentUser && !groupRoundsLoaded) return; // the list has not answered yet
    if (!myGroupRounds.some(showsRound)) {
      // No group has a word at all; the tab is gone and staying here would leave
      // a blank board behind it. Asked of `has_word`, not of the list's length:
      // the list now carries every group of mine, solved and idle ones included.
      setActiveTab('daily');
      localStorage.setItem('activeTab', 'daily');
      setOpenRoundGid(null);
      return;
    }
    // Drop a board only when the group itself has left my list — removed from it,
    // or the group deleted. It used to close on "this group has nothing waiting",
    // which is also true the second you solve the word, so winning threw the
    // board away and, with one other group left, opened that one instead.
    if (openRoundGid && roundForGid === openRoundGid && !myGroupRounds.some(r => r.gid === openRoundGid)) {
      setOpenRoundGid(null);
      return;
    }
    // One group, one board: there is nothing to choose between. From two up the
    // list opens first — "seznam, který se otevře vždy, když jsem ve více
    // skupinách" (2026-08-21) — so a win is never followed by a jump into
    // somebody else's round.
    if (!openRoundGid && myGroupRounds.length === 1 && showsRound(myGroupRounds[0])) {
      openGroupBoard(myGroupRounds[0].gid);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentPage, currentUser, loadingAuth, groupRoundsLoaded, myGroupRounds, openRoundGid, roundForGid]);

  const leaveGroup = async (gid) => {
    try {
      const resp = await friendsApi(`/groups/${gid}/members/${currentUser.uid}`, { method: "DELETE" });
      if (resp.ok) { goPage('groups'); fetchMyGroups(); }
    } catch (e) {}
  };

  // Uspat se v téhle skupině, nebo se probudit. O sobě rozhoduje jen člověk sám:
  // uspat někoho jiného nejde ani vlastníkovi, protože to by bylo tiché odebrání
  // práva zadávat slovo — a to je něco jiného než „teď mě vynechte".
  const [sleepBusy, setSleepBusy] = useState(false);
  const toggleSleep = async (gid, sleeping) => {
    setSleepBusy(true);
    try {
      const resp = await friendsApi(`/groups/${gid}/sleep`, {
        method: "POST",
        body: JSON.stringify({ sleeping }),
      });
      // Řada se tím posouvá, takže se kolo musí načíst znovu — jinak by panel
      // dál tvrdil, že jsi na řadě ty. A seznam členů kvůli značce u jmen.
      if (resp.ok) { if (sleeping) dropTurnBell(gid); await fetchGroupRound(gid); fetchMyGroups(); }
    } catch (e) {
    } finally { setSleepBusy(false); }
  };

  // Pull pending invitations on login so the notification badge has a count
  // without the user having to open the friends/groups pages first.
  useEffect(() => {
    if (!currentUser || loadingAuth || !features.registration) return;
    fetchFriends();
    fetchNotifications();
    // One request for what used to be three: the owner's pending items, the
    // invitations addressed to me (both feed the same bell) and my groups (the
    // bottom-nav "Groups" label shows a single group's name).
    fetchGroupOverview();
    fetchMyGroupRounds(); // decides whether the board even has a group tab
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, loadingAuth, features.registration]);

  // Close the notifications dropdown on an outside click.
  useEffect(() => {
    if (!notifOpen) return;
    const onClick = (e) => {
      if (notifMenuRef.current && !notifMenuRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [notifOpen]);

  // Pending items shown as notifications (also power the badge count).
  const incomingFriendReqs = (friends || []).filter(f => f.direction === 'incoming');
  const unreadNotifs = notifications.filter(n => !n.read).length;
  // Skupiny, kde mám zadat slovo (21. 10. 2026: „mělo by se víc ukazovat, že
  // jsou na řadě"). Z výzvy „Jsi na řadě" ve zvonečku, ne z dalšího dotazu: tu
  // server uklízí, jakmile slovo zadám, připravím nebo řada přejde dál. Na
  // `read` nezáleží — otevřený zvoneček není zadané slovo.
  // Kde už mám slovo na zítra připravené (tomorrow_word nese jen moje), tam
  // puntík nesvítí, i kdyby výzva ještě visela — úkol je splněný.
  // A jen ve skupinách, kde jsem: výzva po smazané skupině (nebo té, ze které
  // jsem odešel) ve zvonečku zůstává a rozsvítila by tab bez puntíku u karty.
  const myTurnGids = new Set(notifications
    .filter(n => n.kind === 'group_turn' && n.gid
      && (groups || []).some(g => g.gid === n.gid)
      && !myGroupRounds.some(r => r.gid === n.gid && r.tomorrow_word))
    .map(n => n.gid));
  const notifCount =
    incomingFriendReqs.length + ownerPending.length + myInvitations.length + unreadNotifs;
  const hasAnyNotif =
    incomingFriendReqs.length + ownerPending.length + myInvitations.length + notifications.length > 0;
  const eventNotifTitle = (kind) => t(
    kind === 'group_joined' ? "notifications.joinedTitle"
    : kind === 'group_declined' ? "notifications.declinedTitle"
    : kind === 'group_request' ? "notifications.requestTitle"
    : kind === 'group_approved' ? "notifications.approvedTitle"
    : kind === 'gift_redeemed' ? "notifications.giftRedeemedTitle"
    : kind === 'friend_added' ? "notifications.friendAddedTitle"
    : kind === 'group_word' ? "notifications.newWordTitle"
    : kind === 'group_turn' ? "notifications.yourTurnTitle"
    : kind === 'group_round_done' ? "notifications.roundDoneTitle"
    // Skupinu zrušil výmaz účtu jejího zakladatele (backend/vymaz_uctu.py).
    : kind === 'group_disbanded' ? "notifications.disbandedTitle"
    : "notifications.removedTitle"
  );
  // Email-invite pointers (a known player invited by email) are actionable — split
  // them out of the plain event list so they can carry an Accept button.
  const emailInviteNotifs = notifications.filter(
    n => n.token && (n.kind === 'friend_invite' || n.kind === 'group_invite_email')
  );
  const eventNotifs = notifications.filter(n => !(n.token && (n.kind === 'friend_invite' || n.kind === 'group_invite_email')));

  // Navigation with real browser history: forward navigation pushes a state, so
  // the device Back button / iOS swipe-back / in-app back arrow all pop it.
  // Opening a win: a finished PAST day replays in the archive, but the current
  // day/week has no archive entry (the archive rejects the live period) — those
  // still live on the game board, so send the player there instead of nowhere.
  const openWin = (row) => {
    // A solved day/week opens the review board (temperature/order sort + hide-words),
    // read straight from the result doc so it can be shared. Daily replays in the
    // archive; a past mega opens the read-only mega review (no replay yet).
    if (row.type === "mega" && row.period) { openMegaReview(row.period); return; }
    if (row.period) { openArchiveDay(row.period); return; }
    // No period (shouldn't happen for a win) → fall back to the live board.
    const tab = row.type === "mega" ? "weekly" : "daily";
    setActiveTab(tab);
    localStorage.setItem("activeTab", tab);
    goPage('game');
  };

  // Nová stránka začíná nahoře. Bez toho zůstala pozice z předchozí stránky —
  // kdo sjel dolů pro „Spravovat", přistál ve správě skupiny uprostřed a musel
  // scrollovat nahoru (6. 10. 2026). Platí i pro Zpět.
  useEffect(() => {
    try { window.scrollTo({ top: 0 }); } catch (e) { /* jsdom */ }
  }, [currentPage]);

  const goPage = (page) => {
    if (page === currentPage) return;
    // Push a history entry per navigation so the back arrow / device Back / swipe
    // step to the PREVIOUS page (game → groups → detail → admin, and back again).
    window.history.pushState({ page }, '');
    setCurrentPage(page);
  };

  // Open the Premium page. `feature` names what the player just bumped into (mega,
  // groups, archive, friends) so the page can lead with a relevant line; defaults
  // to a generic pitch. Replaces the old paywall modal.
  const openPremium = (feature) => {
    // Pojistka: na trzích bez prodeje (pl/hr/de) žádná stránka s nákupem.
    if (!sellsPremium) return;
    setPremiumPrompt(feature || 'premium');
    setCheckoutError(null);
    goPage('premium');
  };

  // Pick a plan on the Premium page. Buying needs an account (Premium is tied to a
  // uid), so an anonymous visitor is sent to sign up/in first; once signed in they
  // land back on the same page and can buy.
  const choosePlan = (plan) => {
    if (!currentUser) { setIsAccountMenuOpen(false); otevriPrihlaseni(); return; }
    startCheckout(plan);
  };

  // The archive opens for everyone. A free player gets the last FREE_ARCHIVE_DAYS
  // days playable and the rest flagged `locked` by the server, so the section shows
  // what it is instead of bouncing them onto the sales page — being told "you need
  // Premium" only AFTER tapping was read as aggressive in testing.
  const openArchivePage = (page) => {
    setIsAccountMenuOpen(false);
    fetchArchive();
    goPage(page);
  };

  // Remember the page per-tab so a refresh can land back on it.
  useEffect(() => {
    try { sessionStorage.setItem('currentPage', currentPage); } catch (e) {}
  }, [currentPage]);

  useEffect(() => {
    // Seed the history entry with the restored page (not a hardcoded 'game'), so
    // the back button and the restored page agree after a refresh. Also strip the
    // one-shot ?gift / ?checkout params so a refresh doesn't re-trigger them.
    let url = '';
    let cancelledCheckout = false;
    try {
      const p = new URLSearchParams(window.location.search);
      if (justBoughtGift) localStorage.removeItem('pendingGift');
      if (p.get('gift') || p.get('invite') || p.get('checkout') || p.has('darek')) url = window.location.pathname;
      cancelledCheckout = p.get('checkout') === 'cancel';
    } catch (e) {}
    // `first`: tahle položka je v historii první (stránka otevřená odkazem —
    // ?darek z e-mailu, ?gift…). Zpět z ní nemá kam, viz `goBack`.
    window.history.replaceState({ page: currentPage, first: true }, '', url || undefined);
    // Stripe's "back"/cancel sends the user to cancel_url (…/?checkout=cancel), which
    // is a NEW history entry sitting right after the payment page — so our in-app
    // Back would return to the gateway. Step back past it (gateway → launcher page)
    // so Back behaves. Guarded on length in case the checkout was the first entry.
    if (cancelledCheckout && window.history.length > 2) {
      window.history.go(-2);
      return () => {};
    }
    const onPop = (e) => setCurrentPage((e.state && e.state.page) || 'game');
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Subscription success screen: the entitlement is granted by the webhook (async),
  // which can take longer than a few seconds. Poll until Premium shows up (so the
  // crown/menu updates without a manual refresh), capped so it can't run forever.
  useEffect(() => {
    if (currentPage !== 'premiumSuccess') return;
    if (isPaid) return; // already active → nothing to wait for
    refreshPremium();
    let n = 0;
    const iv = setInterval(() => {
      n += 1;
      if (n > 12) { clearInterval(iv); return; } // ~30s cap
      refreshPremium();
    }, 2500);
    return () => clearInterval(iv);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, isPaid]);

  // Load "Moje dárky" when the gift page is shown; re-poll once after a purchase
  // (the webhook mints the code asynchronously, so the first fetch can be empty).
  useEffect(() => {
    if (currentPage !== 'gift' || !currentUser) return;
    fetchGiftCodes();
    fetchDarek();
    if (justBoughtGift) { const tmo = setTimeout(fetchGiftCodes, 2500); return () => clearTimeout(tmo); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, currentUser]);

  // Activation page: load the code preview (from-name, status) once.
  useEffect(() => {
    if (currentPage === 'redeem' && giftCodeInput && giftPreview === null) fetchGiftPreview(giftCodeInput);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, giftCodeInput]);

  // Invite landing: load who/what the ?invite=TOKEN is from, once.
  useEffect(() => {
    if (currentPage === 'invite' && inviteToken && invitePreview === null) fetchInvitePreview(inviteToken);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, inviteToken]);

  // Record whether the invitee ARRIVED logged out (so we can tell "just signed in
  // to accept" from "was already logged in"). Latched once auth first resolves.
  const inviteArrivedLoggedOut = useRef(null);
  useEffect(() => {
    if (currentPage === 'invite' && !loadingAuth && inviteArrivedLoggedOut.current === null) {
      inviteArrivedLoggedOut.current = !currentUser;
    }
  }, [currentPage, loadingAuth, currentUser]);

  // Auto-accept ONLY when a logged-out invitee signs in on the invite page: they
  // clicked "sign in to accept" and shouldn't have to hunt for the button again. A
  // user who was already logged in keeps the confirm step (they see who invited them
  // and tap Accept). Fires once (ref guard), only for a still-pending invite.
  const inviteAutoAccepted = useRef(false);
  useEffect(() => {
    if (
      currentPage === 'invite' && currentUser && inviteArrivedLoggedOut.current === true &&
      inviteToken && !inviteAutoAccepted.current &&
      invitePreview && !invitePreview.error &&
      (!invitePreview.status || invitePreview.status === 'pending') &&
      inviteAcceptMsg === null
    ) {
      inviteAutoAccepted.current = true;
      acceptInvite(inviteToken);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, currentUser, inviteToken, invitePreview]);

  // Detect the on-screen keyboard via visualViewport (it shrinks the visual
  // viewport well below the layout viewport). Reliable across guesses, unlike
  // input focus/blur which can stay "focused" after submitting. Desktop has no
  // such shrink, so the bottom nav simply stays visible there.
  // Start visible; only a real viewport shrink (the keyboard) hides the bar, and
  // growing back shows it again. No initial check — that could false-positive at
  // mount and, with no later resize on desktop, leave the nav stuck hidden.
  // The measurement itself lives in keyboard.js: it used to lean on
  // window.innerHeight, which iOS does not define consistently across versions.
  useEffect(() => watchKeyboard(setKeyboardOpen), []);

  // Safety net: a group/admin page needs selectedGroup; if we land there without it
  // (e.g. Back into a stale history entry), fall back to the game instead of a
  // blank screen.
  useEffect(() => {
    if ((currentPage === 'group' || currentPage === 'groupAdmin') && !selectedGroup) {
      setCurrentPage('game');
    }
  }, [currentPage, selectedGroup]);
  // Zpět o stránku. Na stránce otevřené přímo odkazem (první v historii) by
  // `history.back()` nedělalo nic nebo odešlo z webu — tam se jde do hry.
  // Hlášeno 13. 10. 2026 ze stránky Dárky otevřené odkazem z e-mailu.
  const goBack = () => {
    if (window.history.state && window.history.state.first) {
      window.history.replaceState({ page: 'game', first: true }, '');
      setCurrentPage('game');
      return;
    }
    window.history.back();
  };
  const backArrow = (
    <div className="page-back-wrapper">
      <button className="account-btn account-btn--active page-back" aria-label={t("buttons.backToGame")} onClick={goBack}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
      </button>
    </div>
  );

  const saveNickname = async () => {
    const trimmed = nicknameValue.trim();
    if (!trimmed) { setNicknameError(t("settings.nicknameEmpty")); return; }
    if (trimmed.length > 30) { setNicknameError(t("settings.nicknameMaxLength")); return; }
    setNicknameSaving(true);
    setNicknameError('');
    try {
      let token = await getIdToken(true);
      if (!token) {
        setNicknameError(t("settings.sessionExpired"));
        return;
      }

      let resp = await apiFetch(`/user/nickname`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ nickname: trimmed }),
      });

      if (resp.status === 401) {
        token = await getIdToken(true);
        if (!token) {
          setNicknameError(t("settings.sessionExpired"));
          return;
        }
        resp = await apiFetch(`/user/nickname`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ nickname: trimmed }),
        });
      }

      const data = await resp.json();
      if (!resp.ok) { setNicknameError(data.error || 'Chyba'); }
      else {
        setNicknameError('');
        setNicknameSaved(true);
        setProfileDisplayName(trimmed);
      }
    } catch (e) { setNicknameError(t("settings.networkError")); }
    finally { setNicknameSaving(false); }
  };

  const saveEmail = async () => {
    const trimmed = emailValue.trim();
    if (!trimmed || !trimmed.includes('@')) { setEmailMsg(t("settings.emailInvalid")); return; }
    setEmailSaving(true);
    setEmailMsg('');
    try {
      await changeEmail(trimmed);
      setEmailMsg(okMsg(t("auth.messages.emailChangePending")));
    } catch (e) {
      // Never e.message. Firebase speaks English and in its own vocabulary, and
      // the one player who hit this was told something he could neither read nor
      // act on. Every branch here says what happened AND what to do next.
      const code = e && e.code;
      if (code === 'auth/requires-recent-login') {
        setEmailMsg(t("auth.messages.recentLoginRequired"));
      } else if (code === 'auth/email-already-in-use') {
        setEmailMsg(t("auth.errors.emailInUse"));
      } else if (code === 'auth/invalid-email') {
        setEmailMsg(t("settings.emailInvalid"));
      } else if (code === 'auth/too-many-requests') {
        // Ne ta hláška o zámku přihlašování: tady se žádné heslo nezkoušelo.
        setEmailMsg(t("auth.errors.tooManyRequestsGeneric"));
      } else {
        setEmailMsg(t("settings.emailChangeError"));
      }
    }
    finally { setEmailSaving(false); }
  };

  // Ask for the confirm-your-address mail again. A player wrote that hers never
  // arrived and there was no way to ask for another — she had to write to us.
  const resendVerification = async () => {
    if (verifyBusy) return;
    setVerifyBusy(true);
    setVerifyMsg(null);
    try {
      const token = await getIdTokenRef.current();
      const resp = await apiFetch(`/auth/send_verification`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json().catch(() => ({}));
      setVerifyMsg(resp.ok && data.sent ? { ok: true } : { ok: false, reason: data.reason });
    } catch (e) {
      setVerifyMsg({ ok: false });
    } finally {
      setVerifyBusy(false);
    }
  };

  const sendPasswordReset = async () => {
    setPasswordMsg('');
    try {
      await resetPassword(currentUser.email);
      setPasswordMsg(okMsg(t("settings.passwordResetSent", { email: currentUser.email })));
    } catch (e) {
      setPasswordMsg(e.message || t("settings.passwordResetError"));
    }
  };

  // Validace slova
  const validateWord = (word) => {
    if (!word || typeof word !== 'string') {
      return { valid: false, error: t("game.error.wordInvalid") };
    }

    // Složený tvar (NFC): některé klávesnice posílají „ť" jako „t" + háček a
    // kontrola znaků ho odmítala (sk, 24. 10. 2026). Server dělá totéž.
    const trimmedWord = word.trim().normalize("NFC");

    // Check length (max 20 characters)
    if (trimmedWord.length > 20) {
      return { valid: false, error: t("game.error.wordTooLong") };
    }

    if (trimmedWord.length < (wordRules?.minLength ?? 2)) {
      return { valid: false, error: t("game.error.wordTooShort") };
    }

    // Check for spaces FIRST (better error message)
    if (/\s/.test(trimmedWord)) {
      return { valid: false, error: t("game.error.multipleWords") };
    }

    // Charset check uses the ACTIVE language's rules (from the backend). If the
    // rules aren't loaded yet, skip it and let the backend validate — never
    // wrongly reject a valid letter of the user's alphabet on the client.
    if (wordRules?.charRegex && !wordRules.charRegex.test(trimmedWord)) {
      return { valid: false, error: t("game.error.invalidCharacters") };
    }

    return { valid: true, word: trimmedWord };
  };

  const handleGuess = async () => {
    if (guess.trim() === "") {
      setErrorMessage(t("game.error.enterWord"));
      return;
    }

    // Prevent rapid submissions (rate limiting on frontend)
    const now = Date.now();
    const timeSinceLastSubmit = now - lastSubmitTime;
    if (timeSinceLastSubmit < currentCooldown) { // Dynamic cooldown from backend
      const secondsLeft = Math.ceil((currentCooldown - timeSinceLastSubmit) / 1000);
      setErrorMessage(t("game.wait.cooldown", { seconds: secondsLeft }));
      return;
    }

    if (isSubmitting) {
      setErrorMessage(t("game.wait.previousAttempt"));
      return;
    }

    setErrorMessage(""); // Vyčistit případnou chybu
    setIsSubmitting(true);
    setLastSubmitTime(now);

    try {
      // Validate input
      const validation = validateWord(guess);
      if (!validation.valid) {
        setErrorMessage(validation.error);
        return;
      }

      // Převést vstup na malá písmena
      const normalizedGuess = validation.word.toLowerCase();

      // Lift the centered input to the top immediately, before the scoring round-trip.
      setHeroLifted(true);

      // Check if the word has already been guessed
      const existingAttemptIndex = attempts.findIndex(attempt => attempt.guess === normalizedGuess);
      if (existingAttemptIndex !== -1) {
        // Animate old last attempt leaving if it exists
        if (lastAttempt) {
          setLeavingWord({
            word: lastAttempt.guess,
            similarity: lastAttempt.adjustedSimilarity,
            backgroundColor: getBackgroundColor(lastAttempt.adjustedSimilarity),
            label: getSimilarityLabel(lastAttempt.adjustedSimilarity)
          });
        }
        
        // Show animation for already guessed word
        const existingAttempt = attempts[existingAttemptIndex];
        setFlyingWord({
          word: normalizedGuess,
          similarity: existingAttempt.adjustedSimilarity,
          backgroundColor: getBackgroundColor(existingAttempt.adjustedSimilarity),
          label: getSimilarityLabel(existingAttempt.adjustedSimilarity)
        });
        setGuess("");
        
        // Wait for animation, then highlight
        setTimeout(() => {
          setFlyingWord(null);
          setLeavingWord(null);
          setHighlightedIndex(existingAttemptIndex);
          setLastAttempt(existingAttempt);
        }, 500);
        return;
      }

      // Check similarity with the word of the day (backend handles the target word)
      const similarity = await checkSimilarity(normalizedGuess, attempts.length + 1);

      if (similarity === null) {
        return; // Pokud byla chyba, nepokračovat
      }

      // Convert similarity to percentage (0-100) with 2 decimal places
      const similarityPercent = Math.round(similarity * 100 * 100) / 100;

      // Vytvořit nový pokus
      const newAttempt = {
        guess: normalizedGuess,
        originalSimilarity: similarityPercent,
        adjustedSimilarity: similarityPercent, // No adjustment needed with Gemini AI
      };

      // Animate old last attempt leaving if it exists
      if (lastAttempt) {
        setLeavingWord({
          word: lastAttempt.guess,
          similarity: lastAttempt.adjustedSimilarity,
          backgroundColor: getBackgroundColor(lastAttempt.adjustedSimilarity),
          label: getSimilarityLabel(lastAttempt.adjustedSimilarity)
        });
      }

      // Start flying word animation
      setFlyingWord({
        word: normalizedGuess,
        similarity: similarityPercent,
        backgroundColor: getBackgroundColor(similarityPercent),
        label: getSimilarityLabel(similarityPercent)
      });

      // Clear input immediately to show the word is "leaving"
      setGuess("");

      // Wait for animation to complete before updating the list
      await new Promise(resolve => setTimeout(resolve, 500));

      // Uložit jako poslední pokus
      setLastAttempt(newAttempt);

      // Přidat nový pokus do seznamu a seřadit podle podobnosti
      const updatedAttempts = [...attempts, newAttempt].sort(
        (a, b) => b.adjustedSimilarity - a.adjustedSimilarity
      );
      setAttempts(updatedAttempts);
      // My own progress just changed, so anything derived from it on another page
      // is now stale. Drop the archive snapshot instead of letting "Rozehrané hry"
      // reopen on data from before this guess.
      setArchiveDays(null);

      // Průběžně sledovat pořadí hadů
      const updatedGuessOrder = [...guessOrder, { word: normalizedGuess, similarity: similarityPercent }];
      setGuessOrder(updatedGuessOrder);
      localStorage.setItem("guessOrder", JSON.stringify(updatedGuessOrder));

      // Zvýraznit poslední přidané slovo
      const newIndex = updatedAttempts.findIndex(
        (attempt) =>
          attempt.guess === normalizedGuess &&
          attempt.adjustedSimilarity === similarityPercent
      );
      setHighlightedIndex(newIndex);

      // Clear flying word animation
      setFlyingWord(null);
      setLeavingWord(null);

      // Zkontrolovat, zda uživatel uhádl
      if (similarity === 1) {
        setIsGuessed(true);
        localStorage.setItem("guessedWord", normalizedGuess);
        setGuessedWord(normalizedGuess);
        // Win is now persisted server-side by /similarity itself (record_guess_result,
        // balík 2.2) — no separate save_game_log/claim_result round-trip needed.
        incrementCorrectGuesses(); // Inkrementace počtu správných hádání na backendu
        setTimeout(() => {
          setMegaCtaReady(true);
          localStorage.setItem("megaCtaRevealed", "true");
        }, 1700);
      }
    } catch (error) {
      setErrorMessage(t("game.error.unexpected") + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Zpracování klávesových událostí
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleGuess();
    }
  };

  // Weekly guess handler — same pattern as handleGuess
  const handleWeeklyGuess = async () => {
    if (weeklyGuess.trim() === "") {
      setWeeklyErrorMessage(t("game.error.enterWord"));
      return;
    }

    const now = Date.now();
    if (now - weeklyLastSubmitTime < weeklyCooldown) {
      const secondsLeft = Math.ceil((weeklyCooldown - (now - weeklyLastSubmitTime)) / 1000);
      setWeeklyErrorMessage(t("game.wait.cooldown", { seconds: secondsLeft }));
      return;
    }

    if (weeklyIsSubmitting) {
      setWeeklyErrorMessage(t("game.wait.previousAttempt"));
      return;
    }

    setWeeklyErrorMessage("");
    setWeeklyIsSubmitting(true);
    setWeeklyLastSubmitTime(now);

    try {
      const validation = validateWord(weeklyGuess);
      if (!validation.valid) {
        setWeeklyErrorMessage(validation.error);
        return;
      }

      const normalizedGuess = validation.word.toLowerCase();

      // Duplicate check
      const existingIndex = weeklyAttempts.findIndex(a => a.guess === normalizedGuess);
      if (existingIndex !== -1) {
        if (weeklyLastAttempt) {
          setWeeklyLeavingWord({
            word: weeklyLastAttempt.guess,
            similarity: weeklyLastAttempt.adjustedSimilarity,
            backgroundColor: getBackgroundColor(weeklyLastAttempt.adjustedSimilarity),
            label: getSimilarityLabel(weeklyLastAttempt.adjustedSimilarity)
          });
        }
        const existing = weeklyAttempts[existingIndex];
        setWeeklyFlyingWord({
          word: normalizedGuess,
          similarity: existing.adjustedSimilarity,
          backgroundColor: getBackgroundColor(existing.adjustedSimilarity),
          label: getSimilarityLabel(existing.adjustedSimilarity)
        });
        setWeeklyGuess("");
        setTimeout(() => {
          setWeeklyFlyingWord(null);
          setWeeklyLeavingWord(null);
          setWeeklyHighlightedIndex(existingIndex);
          setWeeklyLastAttempt(existing);
        }, 500);
        return;
      }

      const similarity = await checkWeeklySimilarity(normalizedGuess);
      if (similarity === null) return;

      const similarityPercent = Math.round(similarity * 100 * 100) / 100;
      const newAttempt = {
        guess: normalizedGuess,
        originalSimilarity: similarityPercent,
        adjustedSimilarity: similarityPercent,
      };

      if (weeklyLastAttempt) {
        setWeeklyLeavingWord({
          word: weeklyLastAttempt.guess,
          similarity: weeklyLastAttempt.adjustedSimilarity,
          backgroundColor: getBackgroundColor(weeklyLastAttempt.adjustedSimilarity),
          label: getSimilarityLabel(weeklyLastAttempt.adjustedSimilarity)
        });
      }

      setWeeklyFlyingWord({
        word: normalizedGuess,
        similarity: similarityPercent,
        backgroundColor: getBackgroundColor(similarityPercent),
        label: getSimilarityLabel(similarityPercent)
      });
      setWeeklyGuess("");

      await new Promise(resolve => setTimeout(resolve, 500));

      setWeeklyLastAttempt(newAttempt);
      const updatedAttempts = [...weeklyAttempts, newAttempt].sort(
        (a, b) => b.adjustedSimilarity - a.adjustedSimilarity
      );
      setWeeklyAttempts(updatedAttempts);

      const newIndex = updatedAttempts.findIndex(
        a => a.guess === normalizedGuess && a.adjustedSimilarity === similarityPercent
      );
      setWeeklyHighlightedIndex(newIndex);
      setWeeklyFlyingWord(null);
      setWeeklyLeavingWord(null);

      if (similarity === 1) {
        setWeeklyIsGuessed(true);
        localStorage.setItem("weeklyGuessedWord", normalizedGuess);
        setWeeklyGuessedWord(normalizedGuess);
      }
    } catch (error) {
      setWeeklyErrorMessage(t("game.error.unexpected") + error.message);
    } finally {
      setWeeklyIsSubmitting(false);
    }
  };

  const getBackgroundColor = (similarity) => {
    if (similarity <= 30) {
      return "#83B5D1";
    } else if (similarity <= 50) {
      return "#a68ee6";
    } else if (similarity <= 70) {
      return "#FD80D1";
    } else if (similarity <= 85) {
      return "#FF8F5C";
    } else {
      return "#FF5D5D";
    }
  };

  const getSimilarityLabel = (similarity) => {
    if (similarity === 100) {
      return t("game.temperature.perfect");
    } else if (similarity >= 95) {
      return t("game.temperature.veryClose");
    } else if (similarity >= 85) {
      return t("game.temperature.close");
    } else if (similarity >= 75) {
      return t("game.temperature.hot");
    } else if (similarity >= 65) {
      return t("game.temperature.warm");
    } else if (similarity >= 55) {
      return t("game.temperature.lukewarm");
    } else if (similarity >= 45) {
      return t("game.temperature.cool");
    } else if (similarity >= 35) {
      return t("game.temperature.cold");
    } else if (similarity >= 25) {
      return t("game.temperature.veryCold");
    } else if (similarity >= 15) {
      return t("game.temperature.freezing");
    } else if (similarity >= 5) {
      return t("game.temperature.veryFreezing");
    } else {
      return t("game.temperature.ice");
    }
  };

  const getTemperatureLabel = (similarity) => {
    return `${similarity.toFixed(1).replace('.', ',')} °C`;
  };

  const pluralPokusy = (n) => pluralize(t, i18n.language, 'plural.attempt', n);
  const pluralVyhry = (n) => pluralize(t, i18n.language, 'plural.win', n);
  // Playing the archive is Premium (a free window was tried and pulled). The
  // section still OPENS for everyone — being ambushed by the paywall after the tap
  // was the complaint, not the price — so it says plainly what it is, once, above
  // a calendar whose days all carry a lock.
  const archiveHasLockedDays = (archiveDays || []).some(d => d.locked);
  const archivePremiumLine = !sellsPremium ? null : (
    <div className="free-taste">
      <span className="free-taste__text">{t("archive.premiumNote")}</span>
      <button className="free-taste__cta" onClick={() => openPremium('archive')}>
        {t("premium.status.getPremium")}
      </button>
    </div>
  );

  const renderEyeIcon = (hidden) => {
    if (hidden) {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16" aria-hidden="true">
          <path d="M3 3l18 18" />
          <path d="M10.58 10.58a2 2 0 102.83 2.83" />
          <path d="M9.88 5.09A9.77 9.77 0 0112 4c5 0 9.27 3.11 11 8-1.03 2.9-2.96 5.15-5.37 6.42" />
          <path d="M6.61 6.61C4.62 8 3.11 9.84 2 12c.66 1.79 1.66 3.34 2.91 4.56" />
        </svg>
      );
    }
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16" aria-hidden="true">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    );
  };

  // Mask a word when the spoiler switch is on. Used at every place a word is
  // rendered, so one toggle covers the whole screen.
  const maskWord = (word) => (wordsHidden ? t("word.hidden") : displayWord(word));

  // The switch only appears where there is actually something to hide: a board
  // with guesses on it, or a page that lists words.
  //
  // Všechny TŘI záložky, ne dvě. Do 14. 9. 2026 tu stálo „weekly, jinak denní",
  // jenže skupinová je třetí a spadla do toho „jinak" — oko se na ní tedy řídilo
  // tím, kolik tipů má DENNÍ deska. Kdo skupinové slovo hrál a denní ne, oko
  // neměl, i když měl na obrazovce plnou desku. Nahlásila to lila.janca@hotmail.com
  // s tím, že jiná hráčka ve skupině oko vidí — ta prostě hrála i denní slovo,
  // takže se jí objevilo ze správného důvodu omylem.
  // A word I set myself is on screen too — in the list of groups, on the group's
  // page and on the board of the round I am running. A teacher shows these to a
  // class on the projector (29. 9. 2026), and there the eye was simply missing.
  const mySetWordShown = myGroupRounds.some((r) => r.is_setter && (r.word || r.tomorrow_word));
  const pageShowsWords =
    (currentPage === 'game' && (
      activeTab === 'weekly' ? weeklyAttempts.length > 0
        : activeTab === 'group' ? roundAttempts.length > 0 || mySetWordShown
          : attempts.length > 0)) ||
    (currentPage === 'groups' && mySetWordShown) ||
    (currentPage === 'group' && (!!groupRound?.word || !!queuedWord?.is_mine)) ||
    (currentPage === 'archivePlay' && archiveAttempts.length > 0) ||
    (currentPage === 'wins' && (userHistory || []).some((r) => r.status === 'guessed')) ||
    (currentPage === 'inProgress' && (archiveDays || []).some((d) => d.status === 'in_progress')) ||
    currentPage === 'archive';

  // The bottom panel makes no sense on the deep account pages (history / settings).
  // 'wins' keeps it: the daily/mega switcher lives there like everywhere else.
  // Which section owns the bottom bar, and therefore which hue both of its rows
  // wear. Same page lists the tabs' own is-active checks use, so the colour and
  // the lit tab can never disagree. Null on pages no tab claims (the wins page),
  // where the indicator is hidden anyway and the bar stays neutral.
  const navSection =
    currentPage === 'game' ? 'game'
      : ['friends', 'friendsManage'].includes(currentPage) ? 'friends'
        : ['groups', 'group', 'groupAdmin'].includes(currentPage) ? 'groups'
          : null;
  const onBoardPage = !['settings', 'inProgress', 'archive', 'archivePlay', 'premium', 'gift', 'redeem'].includes(currentPage);
  const bottomNavVisible = features.registration && currentUser && !keyboardOpen && onBoardPage;
  // Daily/mega only matters where a word or a standings board is shown — not on the
  // group list, group admin, or the manage-friends page.
  const showWordModes = ['game', 'friends', 'group', 'wins'].includes(currentPage);

  // Signed OUT, the switcher alone — no section tabs.
  //
  // Reported 2026-08-17: "V případě, že není hráč registrovaný a klikne na
  // megaslovo, není možnost vrátit se na běžné denní slovo." Mega shows them a
  // sign-up wall, and the only way back was the browser's own Back button. They
  // came to play and the game let them into a room with no door.
  //
  // Only the modes. Přátelé and Skupiny need an account to mean anything, and a
  // tab bar that answers "sign up first" three times over is a worse welcome
  // than no tab bar.
  // „Minulé slovo" skupiny. Mezi koly i během běžícího kola: kola se otevírají
  // ve 3:00, takže „mezi koly" skoro nenastává, a zadavatel dnešního kola nemá
  // hrací desku, pod kterou se včerejší slovo jinak ukazuje. Nahlásil Davros
  // na Discordu 28. 9. 2026: „nemám šanci se k němu dostat".
  const lastWordBlock = () => (
    <>
      {/* What the last word was — a group that never guessed it would
          otherwise never find out. */}
      {lastRound?.word && (
        <div className="round-day">
          <p className="round-day__label">{t("groupWord.dayLast")}</p>
          {/* The result of the round that just ended. It closes the
              moment the last member solves it — the same moment every
              cell is finally filled in — so without this the one screen
              worth seeing appeared and vanished together, and a group
              never learned who took how many tries. Reported 2026-08-20.
              Folded away by default: between rounds the page is about
              the next word, and the result is there for whoever asks. */}
          <p className="round-last">
            {t("groupWord.lastWord", { word: lastRound.word, name: lastRound.setter_name || '—' })}
          </p>
          {/* The result used to hang off that line itself — muted text
              with a chevron, which read as a sentence and not as
              something you could press. Reported 2026-08-21: "ten odkaz
              je neviditelný a divný". The line stays a line; what opens
              the result is a button that looks like one. */}
          {lastStandings.length > 0 && (
            <button
              type="button"
              className="pill-btn pill-btn--ghost round-last__toggle"
              aria-expanded={lastOpen}
              onClick={() => setLastOpen(o => !o)}
            >
              {lastOpen ? t("groupWord.hideResult") : t("groupWord.showResult")}
            </button>
          )}
          {lastOpen && lastStandings.length > 0 && (
            <div className="social-list social-list--tight round-last__result">
              {[...lastStandings]
                .map(r => ({ r, name: r.display_name || '—' }))
                .sort((a, b) => compareStandings(a.r, b.r, a.name, b.name))
                .map(({ r, name }) => (
                  <div
                    key={r.uid}
                    className={`standing-line${r.uid === currentUser?.uid ? ' standing-line--me' : ''}`}
                  >
                    {/* finished: true swaps "Ještě nehádá" for a final
                        form — nobody is going to start now. */}
                    {tempBar(name, r.status, r.attempts, r.best_similarity, true)}
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </>
  );

  const modesOnlyNav = features.registration && !currentUser && !keyboardOpen
    && onBoardPage && currentPage === 'game';
  return (
    <div className={`app-container${bottomNavVisible ? ' has-bottom-nav' : ''}${bottomNavVisible && showWordModes ? ' has-word-modes' : ''}${modesOnlyNav ? ' has-modes-only' : ''}`}>
      {/* Signed-in players get a compact header on the game screen (small logo
          left, icons right). Signed out keeps the original centred header, and
          subpages keep the centred flame + back arrow. */}
      <div className={`app-header${currentPage !== 'game' ? ' app-header--sub' : ''}${
        looksSignedIn ? ' app-header--compact' : ''
      }`}>
        <div className="logo">
          <div
            className={`fire-icon${currentPage !== 'game' ? ' fire-icon--home' : ''}`}
            role={currentPage !== 'game' ? 'button' : undefined}
            aria-label={currentPage !== 'game' ? t("buttons.backToGame") : undefined}
            onClick={currentPage !== 'game' ? () => goPage('game') : undefined}
          >
            <div className="fire">
              <div className="fire-left">
                <div className="main-fire"></div>
                <div className="particle-fire"></div>
              </div>
              <div className="fire-center">
                <div className="main-fire"></div>
                <div className="particle-fire"></div>
              </div>
              <div className="fire-right">
                <div className="main-fire"></div>
                <div className="particle-fire"></div>
              </div>
              <div className="fire-bottom">
                <div className="main-fire"></div>
              </div>
            </div>
          </div>
          {currentPage === 'game' && (
            <h1>
              {activeTab === "weekly"
                ? (weeklyIsGuessed ? t("game.header.burning") : t("game.header.smoldering"))
                : (isGuessed ? t("game.header.burning") : t("game.header.smoldering"))}
            </h1>
          )}
        </div>

        {/* Back arrow (top-left) on any subpage — mirrors the account icons */}
        {currentPage !== 'game' && backArrow}

        {/* Spoiler switch — sits with the account icons, but is not gated on being
            signed in: anyone with words on screen may want to share it safely. */}
        {pageShowsWords && (
          <button
            className={`account-btn word-visibility-btn--header${
              features.registration && currentUser ? '' : ' word-visibility-btn--no-bell'
            }${wordsHidden ? ' account-btn--active' : ''}`}
            onClick={() => setWordsHidden(v => !v)}
            aria-label={wordsHidden ? t("word.visibility.show") : t("word.visibility.hide")}
            title={wordsHidden ? t("word.visibility.showShort") : t("word.visibility.hideShort")}
          >
            {renderEyeIcon(wordsHidden)}
          </button>
        )}

        {/* Account menu */}
        {features.registration && currentUser && (
          <div className="account-menu-wrapper notif-menu-wrapper" ref={notifMenuRef}>
            <button
              className={`account-btn${notifCount > 0 ? ' account-btn--active' : ''}`}
              onClick={toggleNotifications}
              aria-label={t("notifications.title")}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
                <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
              </svg>
              {notifCount > 0 && <span className="notif-badge">{notifCount}</span>}
            </button>
            {notifOpen && (
              <div className="notif-dropdown">
                <div className="notif-dropdown__title">{t("notifications.title")}</div>
                {!hasAnyNotif ? (
                  <p className="notif-empty">{t("notifications.empty")}</p>
                ) : (
                  <>
                    {/* Proč to nejde, DŘÍV než člověk klikne na Přijmout a dostane
                        nabídku Premium, kterou si nespojí s ničím. Strop platí i na
                        přijetí, ne jen na odesílání — to překvapí i toho, kdo si
                        limitu všiml. */}
                    {incomingFriendReqs.length > 0 && friendsLimit && friendsLimit.reached && (
                      <div className="notif-action notif-action--note">
                        <span className="chip chip--warn">{t("friends.limitReached", { count: friendsLimit.accepted, limit: friendsLimit.limit })}</span>
                        <span className="notif-action__name">{t("friends.limitHint")}</span>
                      </div>
                    )}
                    {incomingFriendReqs.map(f => (
                      <div key={`f-${f.uid}`} className="notif-action">
                        <span className="chip chip--info">{t("notifications.friendRequest")}</span>
                        <span className="notif-action__name">{f.display_name || '—'}</span>
                        <span className="notif-action__buttons">
                          <button className="pill-btn pill-btn--accept" onClick={() => respondFriend(f.uid, true)}>{t("friends.accept")}</button>
                          <button className="pill-btn pill-btn--ghost" onClick={() => respondFriend(f.uid, false)}>{t("friends.decline")}</button>
                        </span>
                      </div>
                    ))}
                    {/* Invitations addressed to me — answering is free, so this
                        must work for a non-subscriber too. */}
                    {myInvitations.map(inv => (
                      <div key={`i-${inv.invite_id}`} className="notif-action">
                        <span className="chip chip--info">{t("notifications.groupInvite")}</span>
                        <span className="notif-action__name">{inv.group_name || '—'}</span>
                        <span className="notif-action__buttons">
                          <button className="pill-btn pill-btn--accept" onClick={() => respondToInvitation(inv.gid, inv.invite_id, true)}>{t("groups.accept")}</button>
                          <button className="pill-btn pill-btn--ghost" onClick={() => respondToInvitation(inv.gid, inv.invite_id, false)}>{t("groups.decline")}</button>
                        </span>
                      </div>
                    ))}
                    {ownerPending.map(p => (
                      <div key={`p-${p.invite_id}`} className="notif-action">
                        <span className="chip chip--requested">{t("groups.statusRequested")}</span>
                        <span className="notif-action__name">{p.requester_name || '—'}<span className="social-card__sub"> · {p.group_name}</span></span>
                        <span className="notif-action__buttons">
                          <button className="pill-btn pill-btn--accept" onClick={() => approveGroupInvite(p.gid, p.invite_id)}>{t("groups.approve")}</button>
                          <button className="pill-btn pill-btn--ghost" onClick={() => declineGroupInvite(p.gid, p.invite_id)}>{t("groups.decline")}</button>
                        </span>
                      </div>
                    ))}
                    {/* Same message, second place it can be triggered from. The
                        bell is where a request surfaces first, so it is the more
                        likely of the two to be pressed — and rendering the reason
                        only on the manage page left this button as silent as it
                        was before. */}
                    {groupApproveMsg && ownerPending.length > 0 && (
                      <p className="group-approve-msg">{groupApproveMsg}</p>
                    )}
                    {emailInviteNotifs.map(n => (
                      <div key={`e-${n.id}`} className="notif-action">
                        <span className="chip chip--info">{t(n.kind === 'group_invite_email' ? "notifications.groupInvite" : "notifications.friendRequest")}</span>
                        <span className="notif-action__name">{[n.actor_name, n.group_name].filter(Boolean).join(' · ') || '—'}</span>
                        <span className="notif-action__buttons">
                          <button className="pill-btn pill-btn--accept" disabled={inviteAcceptBusy} onClick={() => acceptInvite(n.token, { fromBell: true, nid: n.id })}>{t("friends.accept")}</button>
                          <button className="pill-btn pill-btn--ghost" onClick={() => dismissNotification(n.id)}>{t("friends.decline")}</button>
                        </span>
                      </div>
                    ))}
                    {/* A notification carrying a gid goes somewhere: tapping "new
                        word in the group" opens that group's board. */}
                    {eventNotifs.map(n => {
                      const kdy = notifTime(n.created_at, t, { lang: i18n.language });
                      return (
                      <div key={`n-${n.id}`}
                        className={`notif-item notif-item--event${n.read ? '' : ' notif-item--unread'}${n.gid ? ' notif-item--clickable' : ''}`}
                        onClick={n.gid ? async () => {
                          setNotifOpen(false);
                          // The two word notifications open the group under
                          // Skupinové: that is the tab the round lives on.
                          const wordy = n.kind === 'group_word' || n.kind === 'group_turn';
                          const ok = await openGroup(n.gid, wordy ? { mode: 'group' } : {});
                          // Skupina je pryč (smazaná, nebo už v ní nejsem).
                          // Zahodit: notifikace, po jejímž stisku se nic nestane,
                          // vypadá jako rozbitá aplikace.
                          if (!ok) dismissNotification(n.id);
                        } : undefined}>
                        <span className="notif-item__text">
                          <span className="social-card__name">{eventNotifTitle(n.kind)}</span>
                          {/* Kdy se to stalo. Bez toho se ve více skupinách nedá
                              poznat nové od včerejšího — a otevření zvonečku
                              navíc označí všechno za přečtené, takže i to
                              zvýraznění zmizí (hlášeno 4. 9. 2026). */}
                          <span className="social-card__sub">{[n.actor_name, n.group_name].filter(Boolean).join(' · ')}</span>
                          {kdy && <span className="notif-item__time">{kdy}</span>}
                        </span>
                        <button className="notif-dismiss" aria-label="×" onClick={(e) => { e.stopPropagation(); dismissNotification(n.id); }}>&times;</button>
                      </div>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {features.registration && (
          <div className="account-menu-wrapper" ref={accountMenuRef}>
            <button
              className={`account-btn${currentUser ? ' account-btn--active' : ''}${isPaid ? ' account-btn--premium' : ''}`}
              onClick={() => setIsAccountMenuOpen(v => !v)}
              aria-label={t("account.menu")}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              {/* Crown badge marks a Premium account at a glance. */}
              {isPaid && <span className="account-btn__crown" aria-hidden="true"><CrownIcon size={11} /></span>}
            </button>
          {isAccountMenuOpen && (
            <div className="account-dropdown">
              {currentUser ? (
                <>
                  <div className="account-dropdown-name">
                    {profileDisplayName || (() => { const dn = currentUser.displayName || currentUser.email; const parts = dn.trim().split(/\s+/); return parts.length >= 2 ? `${parts[0]} ${parts[parts.length-1][0]}.` : dn; })()}
                    {/* Premium sits right on the name, not in its own row. */}
                    {isPaid && <span className="premium-name-badge" title={t("premium.status.active")}><CrownIcon size={13} />Premium</span>}
                    {isTestAccount && <span className="test-account-badge">TEST</span>}
                  </div>
                  {/* Free user (enforcement on): a highlighted call to upgrade. The
                      paid status itself lives on the name above + the settings page. */}
                  {showUpgradeCta && (
                    <button className="account-upgrade-cta" onClick={() => { setIsAccountMenuOpen(false); openPremium('premium'); }}>
                      <LockIcon size={13} />{t("premium.status.getPremium")}
                    </button>
                  )}
                  {features.leaderboard && (
                    <button onClick={() => { setIsAccountMenuOpen(false); fetchLeaderboard(); setIsTop10ModalOpen(true); }}>
                      {menuIcon("chart")}{t("account.today10")}
                    </button>
                  )}
                  <button onClick={() => { setIsAccountMenuOpen(false); goPage('wins'); }}>
                    {menuIcon("trophy")}{t("profile.sectionWins")}
                  </button>
                  {/* This dropdown is the menu the tester meant: it listed
                      everything and only the tap said "you need Premium". The
                      allowance goes on the row, same as on the settings page. */}
                  <button onClick={() => openArchivePage('inProgress')}>
                    {menuIcon("play")}{t("profile.sectionInProgress")}
                    {showUpgradeCta && <span className="account-menu__badge">{t("archive.premiumBadge")}</span>}
                  </button>
                  <button onClick={() => openArchivePage('archive')}>
                    {menuIcon("calendar")}{t("profile.sectionArchive")}
                    {showUpgradeCta && <span className="account-menu__badge">{t("archive.premiumBadge")}</span>}
                  </button>
                  {/* Friends/Groups are also in the bottom nav; kept here too for discoverability */}
                  <button onClick={() => { setIsAccountMenuOpen(false); openFriends(); }}>
                    {menuIcon("friends")}{t("account.friends")}
                  </button>
                  <button onClick={() => { setIsAccountMenuOpen(false); openGroups(); }}>
                    {menuIcon("groups")}{t("account.groups")}
                  </button>
                  {/* Gifting Premium to a friend lives on the friends page (a
                      gift button per friend); this makes the feature discoverable
                      from the menu instead of only inside a group's member list. */}
                  {sellsPremium && (
                  <button onClick={() => openGift()}>
                    {menuIcon("gift")}{t("account.giftPremium")}
                  </button>
                  )}
                  <button onClick={() => { setIsAccountMenuOpen(false); const dn = profileDisplayName || currentUser?.displayName || ''; const parts = dn.trim().split(/\s+/); setNicknameValue(profileDisplayName || (parts.length >= 2 ? `${parts[0]} ${parts[parts.length-1][0]}.` : dn)); setNicknameError(''); goPage('settings'); }}>
                    {menuIcon("settings")}{t("account.settings")}
                  </button>
                </>
              ) : (
                // Not signed in: still pitch Premium (the page's plan buttons send
                // them to sign up first). Only where registration is possible —
                // and only where Premium is sold at all.
                features.registration && sellsPremium && (
                  <button className="account-upgrade-cta" onClick={() => { setIsAccountMenuOpen(false); openPremium('premium'); }}>
                    <LockIcon size={13} />{t("premium.status.getPremium")}
                  </button>
                )
              )}
              {/* Theme switch sits above the sign-in/out action and is outside the
                  logged-in branch: it's a display preference, so it works signed
                  out too. The separator above it only makes sense when there are
                  account items above — otherwise it's an empty band at the top. */}
              {currentUser && <hr className="account-dropdown-separator" />}
              <button onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
                {menuIcon(theme === "dark" ? "sun" : "moon")}
                {theme === "dark" ? t("account.themeLight") : t("account.themeDark")}
              </button>
              {/* Velikost slov po vzoru Zoomu v menu Chromu: ikona a popisek
                  vlevo, tři úrovně vpravo (15. 10. 2026). */}
              <div className="word-size-row">
                {menuIcon("zoom")}
                <span className="word-size-row__label">{t("settings.wordSize")}</span>
                {wordSizePicker}
              </div>
              {/* Zobrazení zvlášť, hry zvlášť. Jen když nějaká hra následuje —
                  jinak by se nad „Odhlásit" sešly dvě čáry. */}
              {((ANOCINE_LINK && isPaid) || OHEN_LINK) && <hr className="account-dropdown-separator" />}
              {/* The second game — a row like any other, next to the theme switch,
                  which is exactly where Anočine's own menu puts its link back to
                  us. It used to be a highlighted button above the account items,
                  which made a sister game shout louder than "Pořídit Premium";
                  that button belongs to the thing we are actually selling.

                  Outside the signed-in branch and ungated by payment: Anočine has
                  its own free puzzle, so there is somewhere to arrive even with no
                  account, and a link that appears and disappears is a link nobody
                  learns where to find.

                  An <a>, not a button: it leaves the app, so middle-click,
                  long-press and "open in new tab" should all work, and a screen
                  reader should announce it as somewhere to go. */}
              {ANOCINE_LINK && isPaid && (
                <a
                  className="account-menu-link"
                  /* The hint, not the handover. Anočine starts the sign-in flow
                     itself so that it is the one issuing the `state` it will later
                     check; a link straight to our own /sso would leave it
                     accepting any token anyone put in an address. */
                  href={ANOCINE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsAccountMenuOpen(false)}
                >
                  <AnocineIcon />
                  {t("account.playAnocine")}
                </a>
              )}
              {/* Udrž oheň — for everyone, signed in or not. Same tab: it is our
                  own page and its "Přihořívá už zase běží!" link leads back here. */}
              {OHEN_LINK && (
                <a className="account-menu-link account-menu-link--ohen" href="/ohen.html"
                  onClick={() => setIsAccountMenuOpen(false)}>
                  {menuIcon("campfire")}
                  {t("account.playOhen")}
                </a>
              )}
              <hr className="account-dropdown-separator" />
              {currentUser ? (
                <button onClick={() => { setIsAccountMenuOpen(false); logout(); }}>
                  {menuIcon("logout")}{t("account.logout")}
                </button>
              ) : (
                <button onClick={() => { setIsAccountMenuOpen(false); otevriPrihlaseni(); }}>
                  {menuIcon("login")}{t("account.login")}
                </button>
              )}
            </div>
          )}
          </div>
        )}
      </div>

      {features.registration && isAuthModalOpen && (
        <AuthModal
          onClose={() => setIsAuthModalOpen(false)}
          onGoogleLoginSuccess={handleGoogleLoginSuccess}
          initialView={authModalView}
        />
      )}

      {isGoogleNicknameModalOpen && (
        <div className="auth-overlay" onClick={(e) => { if (e.target.className === 'auth-overlay') closeGoogleNicknameModal(); }}>
          <div className="auth-modal">
            <button className="auth-close" onClick={closeGoogleNicknameModal} aria-label={t("buttons.close")}>✕</button>
            <p className="auth-reset-title" style={{ marginBottom: '0.6rem' }}>{t("google.promptTitle")}</p>
            <div style={{ textAlign: 'left', margin: '0.45rem 0 1.25rem', padding: '0 0.1rem' }}>
              <p style={{ margin: '0 0 0.8rem', color: 'var(--text)', lineHeight: 1.5, fontSize: '0.9rem' }}>
                {t("google.promptSubtitle")}
              </p>
              <p style={{ margin: 0, color: 'var(--text)', lineHeight: 1.5, fontSize: '0.9rem' }}>
                {t("google.promptHint")}
              </p>
            </div>

            {googleNicknameError && <p className="auth-error">{googleNicknameError}</p>}

            <form
              className="auth-form"
              onSubmit={(e) => { e.preventDefault(); saveGoogleNickname(); }}
            >
              <input
                value={googleNicknameValue}
                onChange={(e) => { setGoogleNicknameValue(e.target.value); setGoogleNicknameError(''); }}
                maxLength={30}
                placeholder={t("google.placeholder.nickname")}
                autoFocus
              />
              <button
                className="auth-btn primary"
                type="submit"
                disabled={googleNicknameSaving}
              >
                {googleNicknameSaving ? t("auth.loading.saving") : t("buttons.saveAndContinue")}
              </button>
              <button
                className="auth-link"
                type="button"
                onClick={closeGoogleNicknameModal}
                disabled={googleNicknameSaving}
              >
                {t("buttons.keepCurrent")}
              </button>
            </form>
          </div>
        </div>
      )}

      {currentPage === 'settings' && (() => {
        const dn = profileDisplayName || currentUser?.displayName || currentUser?.email || '';
        const initial = (dn.trim()[0] || '?').toUpperCase();
        const googleUser = (currentUser?.providerData || []).some(p => p?.providerId === 'google.com');
        const emailVerified = !!currentUser?.emailVerified;
        // The profile counts the game's own words; group rounds are their own thing.
        const stats = winsSummary((userHistory || []).filter(r => r.type !== "group"), liveDayIso());
        return (
        <div className="profile-page settings-page">
          {/* Identity hero — a banner card; warm gradient + crown when Premium. */}
          <div className={`settings-hero${isPaid ? ' settings-hero--premium' : ''}`}>
            <div className={`settings-avatar${isPaid ? ' settings-avatar--premium' : ''}`}>
              <span>{initial}</span>
              {isPaid && <span className="settings-avatar__crown"><CrownIcon size={13} /></span>}
            </div>
            <div className="settings-hero__id">
              <div className="settings-hero__name">
                {dn}
                {isTestAccount && <span className="test-account-badge">TEST</span>}
              </div>
              {currentUser?.email && <div className="settings-hero__email">{currentUser.email}</div>}
              <div className="settings-hero__meta">
                <span className="settings-chip">{googleUser ? t("settings.signInGoogle") : t("settings.signInPassword")}</span>
                <span className={`settings-chip settings-chip--${emailVerified ? 'ok' : 'warn'}`}>
                  {emailVerified ? t("settings.emailVerified") : t("settings.emailUnverified")}
                </span>
                {!emailVerified && (
                  <button className="link-button" disabled={verifyBusy} onClick={resendVerification}>
                    {verifyBusy ? '…' : t("settings.emailResend")}
                  </button>
                )}
              </div>
              {!emailVerified && verifyMsg && (
                <p className={`settings-msg ${verifyMsg.ok ? 'settings-msg--ok' : 'settings-msg--error'}`}>
                  {verifyMsg.ok
                    ? t("settings.emailResendSent")
                    : verifyMsg.reason === 'too_soon'
                      ? t("settings.emailResendTooSoon")
                      : t("settings.emailResendError")}
                </p>
              )}
              {/* Said out loud because a player believed the opposite and did not
                  buy Premium over it. */}
              {!emailVerified && (
                <p className="settings-hero__note">{t("settings.emailUnverifiedNote")}</p>
              )}
            </div>
          </div>

          {/* Předplatné — jen tam, kde se prodává. V pl/hr/de je všechno zdarma
              a sekce s „Free" a seznamem placených výhod by lhala. */}
          {sellsPremium && (
          <section className="settings-section">
            <h3 className="settings-section__title">{t("settings.sectionPremium")}</h3>
            {isPaid ? (
              <div className="account-level-card account-level-card--premium">
                <div className="account-level-row">
                  <span className="account-level-name"><CrownIcon size={16} />{t("premium.status.active")}</span>
                  <span className="account-level-tag account-level-tag--ok">{t("premium.entitlementStatus.active")}</span>
                </div>
                {(() => {
                  const sub = myPremium?.subscription;
                  const recurring = !!(sub && sub.active);
                  const cancelled = recurring && sub.cancel_at_period_end;
                  // Active recurring → plan + price, no end date (it renews).
                  if (recurring && !cancelled) {
                    const plan = t(sub.interval === 'year' ? 'premium.plan.yearly' : 'premium.plan.monthly');
                    const price = fmtSubPrice(sub);
                    return <p className="account-level-detail">{plan}{price ? ` · ${price}` : ''}</p>;
                  }
                  // Cancelled → the date Premium stays until (no plan/price).
                  if (cancelled && fmtTs(sub.current_period_end)) {
                    return <p className="account-level-detail">{t("premium.status.until", { date: fmtTs(sub.current_period_end) })}</p>;
                  }
                  // Gift / non-subscription Premium → the entitlement expiry.
                  if (fmtPremiumUntil(myPremium?.entitlement?.valid_until)) {
                    return <p className="account-level-detail">{t("premium.status.until", { date: fmtPremiumUntil(myPremium?.entitlement?.valid_until) })}</p>;
                  }
                  return null;
                })()}
                {/* Obnoví se samo? Nejčastější obava předplatitele, a nejkratší
                    odpověď — proto viditelně, ne v rozbalovacím bloku. Nahlášeno
                    26. 9. 2026 platícím hráčem: „Mám měsíční a nevím jestli se
                    další platba strhne automaticky, nebo to musím hlídat?" */}
                {(() => {
                  const sub = myPremium?.subscription;
                  const recurring = !!(sub && sub.active);
                  if (recurring && sub.cancel_at_period_end) {
                    return <p className="account-level-note">{t("account.renewCancelled")}</p>;
                  }
                  // Bez data další platby, schválně: rozhodnutí z 26. 9. 2026.
                  if (recurring || myPremium?.has_subscription) {
                    return <p className="account-level-note">{t("account.renews")} {t("account.renewsHint")}</p>;
                  }
                  // Dárek a jiné Premium bez předplatného.
                  return <p className="account-level-note">{t("account.giftNoRenew")}</p>;
                })()}
                <div className="account-level-actions">
                  {myPremium?.has_subscription && (
                    <button className="settings-btn-secondary" disabled={portalBusy} onClick={manageSubscription}>
                      {portalBusy ? '…' : t("account.manageSubscription")}
                    </button>
                  )}
                  {canSubscribeAhead && (
                    <button className="settings-btn-primary" onClick={() => openPremium('premium')}>{t("account.subscribeAhead")}</button>
                  )}
                  {sellsPremium && <button className="settings-btn-secondary" onClick={() => openGift()}>{menuIcon("gift")}{t("account.giftPremium")}</button>}
                </div>
                {/* Pravidla jdou z TÉHOŽ klíče jako otázka ve FAQ, a proto jsou psaná
                    neosobně: nastavení tyká, FAQ vyká, a dvě verze pravidel by se
                    časem rozešly. Osobní je jen věta se jmény skupin — seznam se
                    stáhne až při rozbalení, ať tu nikdo neplatí požadavek za blok,
                    který neotevře. */}
                <details
                  className="premium-ends"
                  onToggle={(e) => { if (e.currentTarget.open && groups === null) fetchMyGroups(); }}
                >
                  <summary className="premium-ends__summary">{t("faq.premiumEnds.title")}</summary>
                  <div className="premium-ends__body">
                    {faqOdstavce(t("faq.premiumEnds.content")).map((kusy, i) => (
                      <p key={i}>{kusy.map((k, ki) => (k.tucne ? <strong key={ki}>{k.text}</strong> : k.text))}</p>
                    ))}
                    {vlastniSkupiny.length > 0 && (
                      <p className="premium-ends__mine">
                        {/* Celá věta podle tvaru, ne jedno slovo: v češtině se
                            mění i to kolem („skupiny Hauz" / „skupin Hauz a Hauz 2"). */}
                        {t(`account.premiumEndsYourGroups.${pluralCategory(i18n.language, vlastniSkupiny.length)}`, {
                          names: seznamJmen(vlastniSkupiny.map((g) => g.name)),
                          defaultValue: t("account.premiumEndsYourGroups.other", {
                            names: seznamJmen(vlastniSkupiny.map((g) => g.name)),
                          }),
                        })}
                      </p>
                    )}
                  </div>
                </details>
              </div>
            ) : (
              <div className="account-level-card">
                <div className="account-level-row">
                  <span className="account-level-name account-level-name--free">{t("premium.status.free")}</span>
                </div>
                <p className="account-level-pitch">{t("settings.freePitch")}</p>
                <ul className="settings-perks">
                  {ANOCINE_BENEFIT && <li>{t("premium.b.anocine.t")}</li>}
                  <li>{t("premium.b.mega.t")}</li>
                  <li>{t("premium.b.groups.t")}</li>
                  <li>{t("premium.b.archive.t")}</li>
                  <li>{t("premium.b.resume.t")}</li>
                  <li>{t("premium.b.friends.t")}</li>
                </ul>
                <div className="account-level-actions">
                  {showUpgradeCta && (
                    <button className="settings-btn-primary" onClick={() => openPremium('premium')}>{t("premium.status.getPremium")}</button>
                  )}
                  {sellsPremium && <button className="settings-btn-secondary" onClick={() => openGift()}>{menuIcon("gift")}{t("account.giftPremium")}</button>}
                  {/* Obnovení neprošlo: Premium skončilo, ale předplatné ve Stripe
                      žije dál a Stripe ho zkouší strhnout znovu. Bez tohohle ho
                      hráč neměl jak zrušit a psal podpoře (20. 10. 2026). */}
                  {myPremium?.has_subscription && (
                    <button className="settings-btn-secondary" disabled={portalBusy} onClick={manageSubscription}>
                      {portalBusy ? '…' : t("account.manageSubscription")}
                    </button>
                  )}
                </div>
              </div>
            )}
          </section>
          )}

          {/* Statistiky */}
          <section className="settings-section">
            <h3 className="settings-section__title">{t("settings.sectionStats")}</h3>
            <div className="settings-stats">
              <div className="stat-tile">
                <span className="stat-tile__value">{stats.total}</span>
                <span className="stat-tile__label">{t("settings.statWins")}</span>
              </div>
              <div className="stat-tile">
                <span className="stat-tile__value">{stats.streak > 0 ? <>{stats.streak}<span className="stat-tile__unit">🔥</span></> : '—'}</span>
                <span className="stat-tile__label">{t("settings.statStreak")}</span>
              </div>
              <div className="stat-tile">
                <span className="stat-tile__value">{stats.best ? stats.best.attempts : '—'}</span>
                <span className="stat-tile__label">{t("settings.statBest")}</span>
              </div>
              <div className="stat-tile">
                <span className="stat-tile__value stat-tile__medals">🥇{stats.tally.gold} 🥈{stats.tally.silver} 🥉{stats.tally.bronze}</span>
                <span className="stat-tile__label">{t("settings.statMedals")}</span>
              </div>
            </div>
            <div className="settings-group">
              <button className="settings-row settings-row--tap" onClick={() => { goPage('wins'); }}>
                {menuIcon("trophy")}<span className="settings-row__label">{t("profile.sectionWins")}</span><span className="settings-row__chevron">›</span>
              </button>
              {/* A free player is told the allowance HERE, on the row — the whole
                  complaint was learning about the lock only after tapping. */}
              <button className="settings-row settings-row--tap" onClick={() => openArchivePage('inProgress')}>
                {menuIcon("play")}<span className="settings-row__label">{t("profile.sectionInProgress")}</span>
                {showUpgradeCta && <span className="settings-row__badge">{t("archive.premiumBadge")}</span>}
                <span className="settings-row__chevron">›</span>
              </button>
              <button className="settings-row settings-row--tap" onClick={() => openArchivePage('archive')}>
                {menuIcon("calendar")}<span className="settings-row__label">{t("profile.sectionArchive")}</span>
                {showUpgradeCta && <span className="settings-row__badge">{t("archive.premiumBadge")}</span>}
                <span className="settings-row__chevron">›</span>
              </button>
            </div>
          </section>

          {/* Předvolby */}
          <section className="settings-section">
            <h3 className="settings-section__title">{t("settings.sectionPreferences")}</h3>
            <div className="settings-group">
              <div className="settings-row">
                <div className="settings-row__text">
                  <span className="settings-row__label">{t("settings.theme")}</span>
                  <span className="settings-row__hint">{theme === 'dark' ? t("account.themeDark") : t("account.themeLight")}</span>
                </div>
                <button className={`pref-switch${theme === 'dark' ? ' is-on' : ''}`} role="switch" aria-checked={theme === 'dark'} aria-label={t("settings.theme")} onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
                  <span className="pref-switch__track"><span className="pref-switch__thumb" /></span>
                </button>
              </div>
              <div className="settings-row">
                <div className="settings-row__text">
                  <span className="settings-row__label">{t("settings.wordVisibility")}</span>
                  <span className="settings-row__hint">{t("settings.wordVisibilityHint")}</span>
                </div>
                <button className={`pref-switch${wordsHidden ? ' is-on' : ''}`} role="switch" aria-checked={wordsHidden} aria-label={t("settings.wordVisibility")} onClick={() => setWordsHidden(v => !v)}>
                  <span className="pref-switch__track"><span className="pref-switch__thumb" /></span>
                </button>
              </div>
              <div className="settings-row">
                <div className="settings-row__text">
                  <span className="settings-row__label">{t("settings.wordSize")}</span>
                  <span className="settings-row__hint">{t("settings.wordSizeHint")}</span>
                </div>
                {wordSizePicker}
              </div>
            </div>
          </section>

          {/* Profil */}
          <section className="settings-section">
            <h3 className="settings-section__title">{t("settings.sectionProfile")}</h3>
            <div className="settings-card">
              <div className="settings-field">
                <label className="settings-label">{t("settings.nickname")}</label>
                <p className="settings-hint">{t("settings.nicknameHint")}</p>
                <input
                  className="settings-input"
                  value={nicknameValue}
                  onChange={e => { setNicknameValue(e.target.value); setNicknameError(''); setNicknameSaved(false); }}
                  maxLength={30}
                  placeholder={t("settings.placeholder.nickname")}
                />
                {nicknameError && <p className="settings-msg settings-msg--error">{nicknameError}</p>}
              </div>
              <div className="settings-field">
                <label className="settings-label">{t("settings.email")}</label>
                <p className="settings-hint">{t("settings.emailHint")}</p>
                <input
                  className="settings-input"
                  type="email"
                  value={emailValue}
                  onChange={e => { setEmailValue(e.target.value); setEmailMsg(''); }}
                  placeholder={currentUser?.email || ''}
                />
                {/* The tone comes from the code that set it. Reading it back out
                    of the words ('změněn') coloured every success as a failure in
                    the four languages that are not Czech. */}
                <Msg value={emailMsg} className="settings-msg" />
              </div>
              <div className="settings-actions">
                <button
                  className="settings-btn-primary"
                  onClick={async () => {
                    let anyError = false;
                    if (nicknameValue.trim()) { await saveNickname(); }
                    if (emailValue.trim() && emailValue !== currentUser?.email) { await saveEmail(); }
                    if (!anyError) setNicknameSaved(true);
                  }}
                  disabled={nicknameSaving || emailSaving}
                >
                  {(nicknameSaving || emailSaving) ? t("auth.loading.saving") : t("buttons.saveChanges")}
                </button>
                {nicknameSaved && !nicknameError && !emailMsg && <span className="settings-msg settings-msg--ok">{t("settings.saved")}</span>}
              </div>
            </div>
          </section>

          {/* Zabezpečení a účet */}
          <section className="settings-section">
            <h3 className="settings-section__title">{t("settings.sectionSecurity")}</h3>
            {/* A Google account has no password here, so offering to reset one
                sends nothing and says it did. Firebase answers the same way
                whether or not there is a password to reset — that is deliberate,
                it refuses to reveal which addresses have accounts — so the app
                cannot tell the difference afterwards. Here it does not have to
                guess: the provider is on the signed-in user.

                Reported 2026-08-28 by a player who tried the reset several
                times, was told each time that the link was on its way, and never
                got one. She signs in with Google. */}
            {googleUser ? (
              <div className="settings-group">
                <div className="settings-row settings-row--static">
                  <LockIcon size={16} />
                  <span className="settings-row__label">{t("settings.passwordGoogleAccount")}</span>
                </div>
              </div>
            ) : (
              <>
                <div className="settings-group">
                  <button className="settings-row settings-row--tap" onClick={sendPasswordReset}>
                    <LockIcon size={16} /><span className="settings-row__label">{t("buttons.sendPasswordReset")}</span><span className="settings-row__chevron">›</span>
                  </button>
                </div>
                <Msg value={passwordMsg} className="settings-msg" />
              </>
            )}
            <button className="settings-btn-danger" onClick={() => logout()}>
              {menuIcon("logout")}{t("account.logout")}
            </button>
          </section>
        </div>
        );
      })()}

      {/* ── Premium: a full page (was a modal). Reached from any paywall gate,
           the account menu, and the settings upgrade button. `premiumPrompt`
           still names what the player bumped into, for a relevant lead line. ── */}
      {currentPage === 'premium' && (
        <div className="profile-page premium-page">
          <div className="premium-hero">
            <div className="premium-hero__icon"><CrownIcon size={28} /></div>
            <h2 className="premium-hero__title">{t("premium.title")}</h2>
          </div>

          <button className="premium-gift-link" onClick={() => { setGiftCodeInput(''); setGiftRedeemMsg(null); setSocialModal('redeemGift'); }}>
            🎟️ {t("gift.redeemTitle")}
          </button>

          <div className="premium-benefits">
            {premiumBenefitRows(ANOCINE_BENEFIT).map(b => (
              <div key={b.k} className="premium-benefit">
                <span className="premium-benefit__icon">{b.e === "anocine" ? <AnocineIcon size={20} /> : b.e}</span>
                <div className="premium-benefit__text">
                  <span className="premium-benefit__title">{t(`premium.b.${b.k}.t`)}</span>
                  <span className="premium-benefit__desc">{t(`premium.b.${b.k}.d`, { limit: megaFreeLimit })}</span>
                  {/* No link out. This list is read by people who have NOT paid,
                      and since 2026-08-16 anocine.cz turns them away — a button
                      saying "Vyzkoušet" would send them to a wall and spend the
                      one click they were willing to give on a disappointment.
                      The page's own "Získat Premium" is the way in. */}
                </div>
              </div>
            ))}
          </div>

          {currentUser && sellsPremium && (
            <button className="premium-gift-link" onClick={() => openGift()}>
              🎁 {t("account.giftPremium")}
            </button>
          )}

          {/* Pricing pinned to the bottom of the viewport, always reachable. */}
          <div className="premium-cta-bar">
            <div className="premium-cta-bar__inner">
              {checkoutError && (
                <p className="premium-page__error">
                  {t(`premium.error.${checkoutError}`, { defaultValue: t("premium.error.checkout_failed") })}
                </p>
              )}
              {myPremium?.subscription?.active && myPremium?.premium ? (
                // Already subscribed → no plan buttons (the server refuses a second
                // subscription anyway); offer to manage it instead.
                //
                // `premium` musí platit taky, a to je celá oprava z 13. 9. 2026.
                // Otisk `subscription` píše jedině webhook, a ten na
                // customer.subscription.* nebyl přihlášený, takže se od nákupu
                // nikdy nezměnil. Předplatné doběhlo, oprávnění vypršelo, ale
                // otisk pořád hlásil `active: true` — a tahle podmínka pak
                // schovala nákupní tlačítka právě lidem, kteří se chtěli vrátit.
                // Zaseknutých bylo 31 a přibývali každý den.
                //
                // Čas rozhoduje, otisk jen popisuje. Je to stejné pravidlo jako
                // v entitlement.is_active na serveru a ze stejného důvodu:
                // zmeškaná událost musí skončit nabídkou prodeje, ne odmítnutím.
                // Dvojí předplatné to nezpůsobí, to hlídá server dotazem přímo
                // do Stripu a vrací 409.
                <>
                  <p className="premium-cta-bar__note">{t("premium.alreadySubscribed")}</p>
                  {/* Tady hledal přechod na roční a nenašel ho (26. 9. 2026). Portál
                      Stripe to umí, jen to nikde nestálo. */}
                  <p className="premium-cta-bar__note">{t("premium.alreadySubscribedHint")}</p>
                  <button className="settings-btn-secondary" disabled={portalBusy} onClick={manageSubscription}>
                    {portalBusy ? '…' : t("account.manageSubscription")}
                  </button>
                </>
              ) : (
                <>
                  {deferredStartDate && (
                    <p className="premium-cta-bar__note">{t("premium.deferredStart", { date: deferredStartDate })}</p>
                  )}
                  <div className="premium-cta-bar__plans">
                    <button className="premium-plan-card" disabled={!!checkoutBusy} onClick={() => choosePlan("sub_monthly")}>
                      <span className="premium-plan-card__label">{t("premium.plan.monthly")}</span>
                      <span className="premium-plan-card__price">
                        {checkoutBusy === "sub_monthly" ? "…" : (fmtPrice("sub_monthly") || "")}
                      </span>
                    </button>
                    <button className="premium-plan-card premium-plan-card--featured" disabled={!!checkoutBusy} onClick={() => choosePlan("sub_yearly")}>
                      <span className="premium-plan-card__badge">{t("premium.plan.bestValue")}</span>
                      <span className="premium-plan-card__label">{t("premium.plan.yearly")}</span>
                      <span className="premium-plan-card__price">
                        {checkoutBusy === "sub_yearly" ? "…" : (fmtPrice("sub_yearly") || "")}
                      </span>
                      {/* What the badge above claims, in a number. Shown only when
                          Stripe gave us an amount — the card renders either way. */}
                      {fmtMonthlyEquivalent("sub_yearly") && (
                        <span className="premium-plan-card__note">
                          {t("premium.plan.perMonth", { price: fmtMonthlyEquivalent("sub_yearly") })}
                        </span>
                      )}
                    </button>
                  </div>
                  <p className="premium-cta-bar__note">{t("premium.secureNote")}</p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Subscription purchase success (return from Stripe ?checkout=success). ── */}
      {currentPage === 'premiumSuccess' && (
        <div className="profile-page settings-page">
          <div className="gift-activate">
            <div className="gift-activate__icon">🎉</div>
            <h2 className="gift-activate__title">{t("premiumSuccess.title")}</h2>
            <p className="gift-activate__lead">{t("premiumSuccess.lead")}</p>
            {cameFromAnocine ? (
              <>
                {/* They came here to unlock the other game, so that is the
                    primary way out. Not a redirect: an automatic jump after a
                    payment reads as something going wrong. */}
                <a
                  className="settings-btn-primary gift-activate__btn"
                  href={ANOCINE_RETURN_URL}
                  onClick={() => {
                    try { localStorage.removeItem(ANOCINE_RETURN_KEY); } catch (e) {}
                    setCameFromAnocine(false);
                  }}
                >
                  {t("premiumSuccess.backToAnocine")}
                </a>
                {/* The webhook grants access asynchronously; this screen polls
                    for it. Said out loud so an early click is a choice. */}
                {!isPaid && (
                  <p className="gift-activate__note">{t("premiumSuccess.confirming")}</p>
                )}
                <button className="settings-btn-secondary gift-activate__btn" onClick={() => goPage('game')}>{t("premiumSuccess.cta")}</button>
              </>
            ) : (
              <button className="settings-btn-primary gift-activate__btn" onClick={() => goPage('game')}>{t("premiumSuccess.cta")}</button>
            )}
          </div>
        </div>
      )}

      {/* ── Gift activation: the focused landing for a ?gift=CODE link. ── */}
      {currentPage === 'redeem' && (
        <div className="profile-page settings-page">
          <div className="gift-activate">
            <div className="gift-activate__icon">🎁</div>
            {giftRedeemMsg && giftRedeemMsg.ok ? (
              <>
                <h2 className="gift-activate__title">{t("gift.redeemOk")}</h2>
                <button className="settings-btn-primary gift-activate__btn" onClick={() => goPage('game')}>{t("redeem.toGame")}</button>
              </>
            ) : giftPreview && giftPreview.error ? (
              <>
                <h2 className="gift-activate__title">{t("redeem.notFoundTitle")}</h2>
                <p className="gift-activate__lead">{t(`gift.error.${giftPreview.error}`, { defaultValue: t("gift.error.generic") })}</p>
              </>
            ) : giftPreview && giftPreview.status === 'claimed' ? (
              <>
                <h2 className="gift-activate__title">{t("redeem.claimedTitle")}</h2>
                <p className="gift-activate__lead">{t("redeem.claimedLead")}</p>
              </>
            ) : (
              <>
                <h2 className="gift-activate__title">{t("redeem.title")}</h2>
                <p className="gift-activate__lead">
                  {giftPreview && giftPreview.from_name
                    ? t("redeem.fromNamed", { name: giftPreview.from_name })
                    : t("redeem.fromAnon")}
                </p>
                <div className="gift-activate__code">{giftCodeInput}</div>
                <button className="settings-btn-primary gift-activate__btn" disabled={giftRedeemBusy} onClick={() => redeemGiftCode(giftCodeInput)}>
                  {giftRedeemBusy ? '…' : (currentUser ? t("redeem.activate") : t("redeem.signInActivate"))}
                </button>
                {giftRedeemMsg && !giftRedeemMsg.ok && (
                  <p className="settings-msg settings-msg--error">{t(`gift.error.${giftRedeemMsg.error}`, { defaultValue: t("gift.error.generic") })}</p>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Invite accept: the focused landing for a ?invite=TOKEN link. ── */}
      {currentPage === 'invite' && (
        <div className="profile-page settings-page">
          <div className="gift-activate">
            <div className="gift-activate__icon">🔥</div>
            {inviteAcceptMsg && inviteAcceptMsg.ok ? (
              <>
                <h2 className="gift-activate__title">{t(inviteAcceptMsg.kind === 'group' ? "invitePage.okGroup" : "invitePage.okFriend")}</h2>
                <button className="settings-btn-primary gift-activate__btn" onClick={() => goPage('game')}>{t("invitePage.toGame")}</button>
              </>
            ) : invitePreview && invitePreview.error ? (
              <>
                <h2 className="gift-activate__title">{t("invitePage.notFoundTitle")}</h2>
                <p className="gift-activate__lead">{t(`invitePage.error.${invitePreview.error}`, { defaultValue: t("invitePage.error.generic") })}</p>
              </>
            ) : invitePreview && invitePreview.status && invitePreview.status !== 'pending' ? (
              <>
                <h2 className="gift-activate__title">{t("invitePage.usedTitle")}</h2>
                <p className="gift-activate__lead">{t("invitePage.usedLead")}</p>
              </>
            ) : (
              <>
                <h2 className="gift-activate__title">{t(invitePreview && invitePreview.kind === 'group' ? "invitePage.groupTitle" : "invitePage.friendTitle")}</h2>
                <p className="gift-activate__lead">
                  {invitePreview && invitePreview.kind === 'group'
                    ? t("invitePage.groupLead", { name: invitePreview.inviter_name || t("invitePage.someone"), group: invitePreview.group_name || '' })
                    : t("invitePage.friendLead", { name: (invitePreview && invitePreview.inviter_name) || t("invitePage.someone") })}
                </p>
                <p className="social-hint" style={{ textAlign: 'center' }}>{t("invitePage.blurb")}</p>
                <button className="settings-btn-primary gift-activate__btn" disabled={inviteAcceptBusy} onClick={() => acceptInvite(inviteToken)}>
                  {inviteAcceptBusy ? '…' : (currentUser ? t("invitePage.accept") : t("invitePage.signInAccept"))}
                </button>
                {inviteAcceptMsg && !inviteAcceptMsg.ok && (
                  <p className="settings-msg settings-msg--error">{t(`invitePage.error.${inviteAcceptMsg.error}`, { defaultValue: t("invitePage.error.generic"), limit: inviteAcceptMsg.limit })}</p>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Gift codes (§6.2): redeem a code, buy one to share, and "Moje dárky". ── */}
      {currentPage === 'gift' && (
        <div className="profile-page settings-page">
          <div className="settings-hero settings-hero--premium">
            <div className="settings-avatar settings-avatar--premium"><span>🎁</span></div>
            <div className="settings-hero__id">
              <div className="settings-hero__name">{t("gift.title")}</div>
              <div className="settings-hero__email">{t("gift.subtitle")}</div>
            </div>
          </div>

          {/* Dárek za výpadek — kdo má Premium, vytvoří si JEDEN kód pro
              kamaráda. Bez přihlášení jen výzva (odkaz z e-mailu). */}
          {SELLS_PREMIUM && ((currentUser && darek?.eligible) || (!currentUser && new URLSearchParams(window.location.search).has('darek'))) && (
            <section className="settings-section">
              <h3 className="settings-section__title">{t("darek.title")}</h3>
              <div className="settings-card">
                {!currentUser ? (
                  <>
                    <p className="gift-buy__lead">{t("darek.lead")}</p>
                    <button className="settings-btn-primary" onClick={() => otevriPrihlaseni()}>{t("darek.login")}</button>
                  </>
                ) : darek.code ? (
                  <p className="gift-buy__lead">{t("darek.ready", { code: darek.code })}</p>
                ) : (
                  <>
                    <p className="gift-buy__lead">{t("darek.lead")}</p>
                    <button className="settings-btn-primary" disabled={darekBusy} onClick={createDarek}>
                      🎁 {darekBusy ? '…' : t("darek.button")}
                    </button>
                    {darek.error && (
                      <p className="settings-msg settings-msg--error">{t(`darek.error.${darek.error}`, { defaultValue: t("gift.error.generic") })}</p>
                    )}
                  </>
                )}
              </div>
            </section>
          )}

          {/* Moje dárky */}
          {currentUser && (
            <section className="settings-section">
              <h3 className="settings-section__title">{t("gift.mineTitle")}</h3>
              {giftCodes === null ? (
                <p className="social-hint">{t("profile.empty")}</p>
              ) : giftCodes.length === 0 ? (
                <p className="social-hint">{t("gift.mineEmpty")}</p>
              ) : (
                <div className="group-members">
                  {giftCodes.map(c => (
                    <div key={c.code} className="gift-code-card">
                      <div className="gift-code-card__top">
                        <span className="gift-code-card__code">{c.code}</span>
                        <span className={`gift-code-card__status gift-code-card__status--${c.status}`}>
                          {t(`gift.status.${c.status}`, { defaultValue: c.status })}
                        </span>
                      </div>
                      {c.recipient_email && (
                        <div className="gift-code-card__sent">{t("gift.sentTo", { email: c.recipient_email })}</div>
                      )}
                      {c.status === 'unclaimed' && (
                        <>
                          <div className="gift-code-card__actions">
                            <button className="pill-btn pill-btn--accept" onClick={() => { setGiftSendFor(giftSendFor === c.code ? null : c.code); setGiftSendEmail(''); setGiftSendMsg(null); }}>
                              ✉ {t("gift.sendEmail")}
                            </button>
                            <button className="pill-btn pill-btn--ghost" onClick={() => copyGift(giftLink(c.code), c.code + ':link')}>
                              {giftCopied === c.code + ':link' ? t("gift.copied") : t("gift.copyLink")}
                            </button>
                            <button className="pill-btn pill-btn--ghost" onClick={() => copyGift(c.code, c.code + ':code')}>
                              {giftCopied === c.code + ':code' ? t("gift.copied") : t("gift.copyCode")}
                            </button>
                          </div>
                          {giftSendFor === c.code && (
                            <div className="settings-inline">
                              <input className="settings-input" type="email" value={giftSendEmail} onChange={e => { setGiftSendEmail(e.target.value); setGiftSendMsg(null); }} placeholder={t("gift.recipientEmail")} />
                              <button className="settings-btn-primary" disabled={giftSendBusy || !giftSendEmail.trim()} onClick={() => sendGiftEmail(c.code, giftSendEmail)}>
                                {giftSendBusy ? '…' : t("gift.send")}
                              </button>
                            </div>
                          )}
                          {giftSendFor === c.code && giftSendMsg && (
                            <p className={`settings-msg ${giftSendMsg.ok ? 'settings-msg--ok' : 'settings-msg--error'}`}>
                              {giftSendMsg.ok ? t("gift.sent") : t(`gift.error.${giftSendMsg.error}`, { defaultValue: t("gift.error.generic") })}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Redeem — a row that opens the code modal (a ?gift link uses the
              focused activation page instead). */}
          <section className="settings-section">
            <h3 className="settings-section__title">{t("gift.redeemTitle")}</h3>
            <div className="settings-group">
              <button className="settings-row settings-row--tap" onClick={() => { setGiftCodeInput(''); setGiftRedeemMsg(null); setSocialModal('redeemGift'); }}>
                {menuIcon("gift")}<span className="settings-row__label">{t("gift.redeemLabel")}</span><span className="settings-row__chevron">›</span>
              </button>
            </div>
          </section>

          {/* Buy a gift to share with anyone. */}
          {sellsPremium && (
          <section className="settings-section">
            <h3 className="settings-section__title">{t("gift.buyTitle")}</h3>
            <div className="settings-card">
              <p className="gift-buy__lead">{t("gift.buyLead")}</p>
              <button className="settings-btn-primary" disabled={!!checkoutBusy} onClick={() => { if (!currentUser) { otevriPrihlaseni(); return; } startGiftCode(); }}>
                🎁 {t("gift.buyButton")}{fmtPrice("gift_year") ? ` · ${fmtPrice("gift_year")}` : ''}
              </button>
            </div>
          </section>
          )}

        </div>
      )}

      {/* ── Menu → Moje výhry: solved days only (the word is shown, it's won).
           A trophy shelf, not a ledger: medal tiers by attempts, plus a summary
           strip so the page opens with an achievement rather than a table head. ── */}
      {currentPage === 'wins' && (
        <div className="profile-page">
          <h2 className="profile-name">{t("profile.sectionWins")}</h2>
          {!userHistory ? (
            <p className="profile-empty">{t("profile.empty")}</p>
          ) : userHistory.filter(r => r.status === "guessed").length === 0 ? (
            <p className="profile-empty">{t("profile.winsEmpty")}</p>
          ) : (() => {
            // Daily vs mega is chosen in the sticky bottom bar (wordMode), the same
            // switcher used across game/friends/groups.
            // Group rounds live in the same results collection, so "not mega" is no
            // longer the same as "daily" — they would land here as daily wins whose
            // period is a round id instead of a date.
            const allWins = userHistory.filter(r => r.status === "guessed" && r.type !== "group");
            const wins = allWins.filter((r) => (wordMode === "mega" ? r.type === "mega" : r.type !== "mega"));
            const summary = winsSummary(wins, liveDayIso());
            return (
            <>
              {wins.length === 0 ? (
                <p className="profile-empty">{t("profile.winsEmpty")}</p>
              ) : (
              <>
              <div className="wins-summary">
                <div className="wins-summary__headline">
                  <span className="wins-summary__count">{summary.total}</span>
                  <span className="wins-summary__count-label">{pluralVyhry(summary.total)}</span>
                </div>
                <div className="wins-summary__medals">
                  {["gold", "silver", "bronze"].map((tier) => (
                    <span key={tier} className={`wins-medal-chip wins-medal--${tier}`}>
                      <TrophyFilled size={16} />
                      <span>{summary.tally[tier]}</span>
                    </span>
                  ))}
                </div>
                <div className="wins-summary__facts">
                  {summary.best && (
                    <span className="wins-fact">
                      {t("wins.best")}<strong>{summary.best.attempts}</strong>
                    </span>
                  )}
                </div>
              </div>

              {wordMode === "daily" && (() => {
                // Water-line timeline, one cell per calendar day from the first day
                // played to today (scrollable into history, opens on the right). Solved
                // days float above the surface as trophies (fewer attempts = higher);
                // days that were started but not solved bob just below the surface as
                // treasure; days never opened sink to the sea floor. The water waves
                // like the mega-word button.
                const dailyRows = (userHistory || []).filter((r) => r.type === "daily" && r.period);
                const played = dailyRows.filter((r) => r.status === "guessed" || r.status === "in_progress");
                if (!played.some((r) => r.status === "guessed")) return null;
                const attemptsBy = {};
                dailyRows.forEach((r) => { if (r.status === "guessed") attemptsBy[r.period] = Number(r.attempts); });
                const first = played.map((r) => r.period).sort()[0];
                const end = historyToday || liveDayIso();
                const days = Math.min(
                  400,
                  Math.round((new Date(end + "T00:00:00Z") - new Date(first + "T00:00:00Z")) / 86400000) + 1
                );
                const cells = winTimeline(userHistory, end, days); // {date, outcome: won|lost|none}
                const STEP = 44, H = 138, SURFACE = 82;
                // Deterministic 0..1 from the date string, so a sunk day keeps its depth.
                const seeded = (s) => {
                  let h = 2166136261;
                  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
                  return ((h >>> 0) % 1000) / 1000;
                };
                // Height is now discrete by medal tier (not a continuous gradient),
                // so it agrees with the trophy colour instead of adding a second,
                // noisy axis: gold floats highest, then silver, bronze, plain — all
                // clearly above the surface.
                const WON_LEVEL = { gold: 18, silver: 40, bronze: 58, plain: 66 };
                const topFor = (c) => {
                  if (c.outcome === "won") {
                    return WON_LEVEL[medalFor(attemptsBy[c.date])] ?? WON_LEVEL.plain;
                  }
                  if (c.outcome === "lost") return SURFACE - 6; // being guessed → boat afloat on the surface
                  return H - 22 - seeded(c.date) * 6; // never opened → sea floor
                };
                const trophyIcon = (a) => {
                  // Barva i velikost podle stupně — viz wins.trophyStyle.
                  const { color: c, size } = trophyStyle(a);
                  return (
                    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={c} strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
                      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
                      <path d="M4 22h16" />
                      <path d="M10 14.7V17c0 .6-.5 1-1 1.2C7.9 18.8 7 20.2 7 22" />
                      <path d="M14 14.7V17c0 .6.5 1 1 1.2 1.1.6 2 2 2 3.8" />
                      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" fill={c} fillOpacity="0.18" />
                    </svg>
                  );
                };
                const chestIcon = (
                  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#e0a35c" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4 11h16v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8Z" fill="#e0a35c" fillOpacity="0.2" />
                    <path d="M4 11V9a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v2" />
                    <path d="M4 11h16" />
                    <path d="M11 11v2.5h2V11" />
                    <rect x="10.3" y="13" width="3.4" height="2.6" rx="0.5" fill="#e0a35c" fillOpacity="0.4" />
                  </svg>
                );
                const boatIcon = (
                  <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4 16h16l-2 4a1 1 0 0 1-.9.6H6.9a1 1 0 0 1-.9-.6L4 16Z" fill="currentColor" fillOpacity="0.18" />
                    <path d="M12 15V4" />
                    <path d="M12 5.5l5.5 8.5H12z" fill="currentColor" fillOpacity="0.28" />
                  </svg>
                );
                // Tiny inline timelines under each streak number: a run of
                // consecutive solved days lit up. One shows the current trailing
                // run, the other the all-time longest run.
                const wonArr = cells.map((c) => c.outcome === "won");
                let curEnd = wonArr.length - 1;
                while (curEnd >= 0 && !wonArr[curEnd]) curEnd--;
                let curStart = curEnd;
                while (curStart >= 0 && wonArr[curStart]) curStart--;
                curStart++;
                const MINI = 24;
                // Weekday initials in getDay() order (Sun..Sat), localised.
                const WD = (t("wins.weekdayInitials") || "N,P,Ú,S,Č,P,S").split(",");
                const renderRail = (ws, we, hs, he, cls) => {
                  const cols = [];
                  for (let i = ws; i <= we; i++) {
                    const w = wonArr[i];
                    const hi = w && i >= hs && i <= he;
                    const d = cells[i]?.date;
                    const wd = d ? (WD[new Date(d + "T00:00:00").getDay()] || "") : "";
                    cols.push(
                      <span key={i} className="mini-col">
                        <span className={`mini-dot${w ? " mini-dot--won" : ""}${hi ? " mini-dot--hi " + cls : ""}`} />
                        <span className="mini-wd">{wd}</span>
                      </span>
                    );
                  }
                  return <div className="mini-rail" aria-hidden="true">{cols}</div>;
                };
                const curWin = [Math.max(0, wonArr.length - MINI), wonArr.length - 1];
                // Plný pruh: v posledních MINI dnech ani jedna mezera. Hlídá se i
                // délka historie — u účtu starého tři dny by „všechny tečky svítí“
                // platilo hned první týden a odznak by nic neznamenal.
                const plnyPruh =
                  wonArr.length >= MINI && wonArr.slice(curWin[0], curWin[1] + 1).every(Boolean);
                return (
                  <div className="streak-panel">
                    <div className="streak-sections">
                      {/* Oheň se hýbe jen u plného pruhu. Když plápolá pořád, je to
                          výzdoba; když se rozhoří až za něco, je to odměna. */}
                      <div className={`streak-sec streak-sec--fire${plnyPruh ? ' streak-sec--ablaze' : ''}`}>
                        <div className="streak-sec__head">
                          <span className="streak-sec__icon" aria-hidden="true">🔥</span>
                          <span className="streak-sec__num">{streaks?.current ?? 0}</span>
                          <span className="streak-sec__label">{t("wins.streakCurrent")}</span>
                        </div>
                        {renderRail(curWin[0], curWin[1], curStart, curEnd, "mini-dot--fire")}
                        {plnyPruh && (
                          <p className="streak-award">
                            <span className="streak-award__name">{t("wins.awardUnbroken")}</span>
                            <span className="streak-award__sub">
                              {t("wins.awardUnbrokenSub", {
                                count: MINI,
                                unit: pluralize(t, i18n.language, "plural.dayShort", MINI),
                              })}
                            </span>
                          </p>
                        )}
                      </div>
                      {/* Nejdelší série je jedno číslo, ne kalendář. Pruh teček u ní
                          ukazoval dny odněkud z minulosti bez data — nedalo se z něj
                          nic přečíst a soupeřil o pozornost s tím aktuálním. */}
                      <div className="streak-sec streak-sec--gold">
                        <div className="streak-sec__head">
                          <span className="streak-sec__icon" aria-hidden="true">🏆</span>
                          <span className="streak-sec__num">{streaks?.longest ?? 0}</span>
                          <span className="streak-sec__label">{t("wins.streakLongest")}</span>
                        </div>
                      </div>
                    </div>
                    <div className="water-timeline" style={{ "--wt-surface": `${SURFACE}px`, height: `${H}px` }}
                      role="img" aria-label={t("wins.streakCurrent")}>
                      <div className="water-timeline__water" aria-hidden="true">
                        <div className="water-timeline__wave water-timeline__wave--a" />
                        <div className="water-timeline__wave water-timeline__wave--b" />
                      </div>
                      <div className="water-timeline__scroll" ref={streakRailRef}>
                        <div className="water-timeline__inner" style={{ width: `${cells.length * STEP}px` }}>
                          <div className="wt-axis" />
                          {cells.map((c, i) => {
                            const won = c.outcome === "won";
                            const boat = c.outcome === "lost"; // started, still being guessed → afloat
                            const deep = c.outcome === "none";
                            const x = i * STEP + STEP / 2;
                            const top = topFor(c);
                            const today = c.date === end;
                            const title = won
                              ? `${c.date} · ${attemptsBy[c.date]} ${pluralize(t, i18n.language, "plural.attempt", attemptsBy[c.date])}`
                              : `${c.date} · ${boat ? t("wins.legendPlaying") : t("wins.legendNotPlayed")}`;
                            const cls = `wt-marker${boat ? " wt-marker--boat" : won ? "" : " wt-marker--sunk"}` +
                              `${deep ? " wt-marker--deep" : ""}${today ? " wt-marker--today" : ""}`;
                            return (
                              <React.Fragment key={c.date}>
                                {i > 0 && <div className="wt-seg" style={{ left: `${i * STEP}px` }} />}
                                {!boat && (
                                  <div className={`wt-stem${won ? "" : " wt-stem--sunk"}`}
                                    style={{ left: `${x}px`, top: `${Math.min(top, SURFACE)}px`, height: `${Math.abs(top - SURFACE)}px` }} />
                                )}
                                <div className="wt-node" style={{ left: `${x}px` }} />
                                <div className={cls}
                                  style={{ left: `${x}px`, top: `${top}px`, ...(boat ? { animationDelay: `-${(seeded(c.date) * 2.8).toFixed(2)}s` } : {}) }}
                                  title={title}>
                                  {won ? trophyIcon(attemptsBy[c.date] ?? 60) : boat ? boatIcon : chestIcon}
                                </div>
                              </React.Fragment>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    <div className="wt-legend">
                      <span className="wt-legend__item">{trophyIcon(10)}<span>{t("wins.legendWon")}</span></span>
                      <span className="wt-legend__item">{boatIcon}<span>{t("wins.legendPlaying")}</span></span>
                      <span className="wt-legend__item">{chestIcon}<span>{t("wins.legendNotPlayed")}</span></span>
                    </div>
                    <p className="wt-legend__hint">{t("wins.legendHint")}</p>
                  </div>
                );
              })()}

              {groupByMonth(wins).map((group) => (
              <div className="wins-month" key={group.key || "unknown"}>
                {group.key && (() => {
                  const [gy, gm] = group.key.split('-');
                  return (
                    <div className="wins-month__label">
                      {t(`archive.month.${parseInt(gm)}`)} {gy}
                    </div>
                  );
                })()}
              <div className="wins-list">
                {group.rows.map((row, i) => {
                  const isMega = row.type === "mega";
                  const dateLabel = isMega
                    ? `${t("game.header.megaWordDaily")} · ${row.period || ''}`
                    : (() => {
                        const [y, m, d] = (row.period || '').split('-');
                        return row.period ? `${parseInt(d)}. ${parseInt(m)}. ${y}` : '—';
                      })();
                  // Every win is reachable: past dailies replay in the archive,
                  // the live day/week jump to the board. Only a PAST mega has
                  // nowhere to go — the archive covers daily words only.
                  // Every win with a period is now reviewable — daily replays in the
                  // archive, a past mega opens its read-only review.
                  const canReview = row.is_current || !!row.period;
                  const tier = medalFor(row.attempts);
                  return (
                    <div
                      key={i}
                      className={`wins-row wins-row--${tier}${canReview ? ' wins-row--tappable' : ''}`}
                      onClick={canReview ? () => openWin(row) : undefined}
                    >
                      <span className={`wins-row__medal wins-medal--${tier}`}>
                        <TrophyFilled size={22} />
                      </span>
                      <span className="wins-row__main">
                        <span className="wins-row__word">{maskWord(row.word) || '—'}</span>
                        <span className="wins-row__date">{dateLabel}</span>
                      </span>
                      <span className="wins-row__attempts">
                        <strong>{row.attempts}</strong>
                        <span className="wins-row__attempts-label">{pluralPokusy(row.attempts)}</span>
                      </span>
                      {canReview && <span className="wins-row__chevron">›</span>}
                    </div>
                  );
                })}
              </div>
              </div>
              ))}
              </>
              )}
            </>
            );
          })()}
        </div>
      )}

      {/* ── Menu → Rozehrané hry: past days started but unsolved → resume. Never
           the word. Excludes today (the archive endpoint drops it) — today's game
           lives on the front page. ── */}
      {currentPage === 'inProgress' && (
        <div className="profile-page">
          <h2 className="profile-name">{t("profile.sectionInProgress")}</h2>
          {archiveHasLockedDays && archivePremiumLine}
          {(() => {
            // Most recently played first; the fill shows how warm I got (best tip)
            // — same read as a friend's bar. No "Rozehráno" label: it's implied.
            const started = (archiveDays || [])
              .filter(d => d.status === "in_progress")
              .sort((a, b) => (b.last_guess_at || "").localeCompare(a.last_guess_at || ""));
            // Today's daily counts too — the archive endpoint drops today, so add it
            // here (labelled "Dnešek", tapping it jumps back to the live board).
            const dailyPlaying = !isGuessed && attempts.length > 0;
            const todayBest = dailyPlaying
              ? attempts.reduce((m, a) => (a.adjustedSimilarity > (m ? m.adjustedSimilarity : -1) ? a : m), null)
              : null;
            // This week's mega counts too (labelled "Tento týden", tap → live mega board).
            const weeklyPlaying = !weeklyIsGuessed && weeklyAttempts.length > 0;
            const weekBest = weeklyPlaying
              ? weeklyAttempts.reduce((m, a) => (a.adjustedSimilarity > (m ? m.adjustedSimilarity : -1) ? a : m), null)
              : null;
            if (!archiveDays && !dailyPlaying && !weeklyPlaying) return <p className="profile-empty">{t("profile.empty")}</p>;
            if (started.length === 0 && !dailyPlaying && !weeklyPlaying) return <p className="profile-empty">{t("profile.inProgressEmpty")}</p>;
            return (
              <div className="resume-list">
                {dailyPlaying && (() => {
                  const pct = Math.round(todayBest ? todayBest.adjustedSimilarity : 0);
                  return (
                    <button
                      key="__today"
                      className="resume-btn"
                      onClick={() => { setActiveTab('daily'); localStorage.setItem('activeTab', 'daily'); goPage('game'); }}
                      style={{ background: `linear-gradient(to right, ${getBackgroundColor(pct)} ${pct}%, var(--bar-track) 0%)` }}
                    >
                      <span className="resume-btn__main">
                        <span className="resume-btn__date">{t("inProgress.today")}</span>
                        {todayBest && <span className="resume-btn__best">{maskWord(todayBest.guess)}</span>}
                      </span>
                      <span className="resume-btn__meta">{dailyCount} {pluralPokusy(dailyCount)} ›</span>
                    </button>
                  );
                })()}
                {weeklyPlaying && (() => {
                  const pct = Math.round(weekBest ? weekBest.adjustedSimilarity : 0);
                  return (
                    <button
                      key="__thisweek"
                      className="resume-btn"
                      onClick={() => { setActiveTab('weekly'); localStorage.setItem('activeTab', 'weekly'); goPage('game'); }}
                      style={{ background: `linear-gradient(to right, ${getBackgroundColor(pct)} ${pct}%, var(--bar-track) 0%)` }}
                    >
                      <span className="resume-btn__main">
                        <span className="resume-btn__date">
                          {t("inProgress.thisWeek")}
                          <span className="resume-btn__tag">{t("game.header.megaWordDaily")}</span>
                        </span>
                        {weekBest && <span className="resume-btn__best">{maskWord(weekBest.guess)}</span>}
                      </span>
                      <span className="resume-btn__meta">{weeklyCount} {pluralPokusy(weeklyCount)} ›</span>
                    </button>
                  );
                })()}
                {started.map((d) => {
                  const [yy, mm, dd] = d.date.split('-');
                  const pct = percentFromSimilarity(d.best_similarity);  // jako deska, viz tempBar
                  return (
                    <button
                      key={d.date}
                      className="resume-btn"
                      onClick={() => openArchiveDay(d.date)}
                      style={{ background: `linear-gradient(to right, ${getBackgroundColor(pct)} ${pct}%, var(--bar-track) 0%)` }}
                    >
                      <span className="resume-btn__main">
                        <span className="resume-btn__date">
                          {`${parseInt(dd)}. ${parseInt(mm)}. ${yy}`}
                          {/* started while subscribed, now outside the free window */}
                          {d.locked && <LockIcon size={13} className="resume-btn__lock" />}
                        </span>
                        {/* my warmest guess so far — dates alone are hard to tell
                            apart. It's my own input, never the secret. */}
                        {d.best_word && (
                          <span className="resume-btn__best">{maskWord(d.best_word)}</span>
                        )}
                      </span>
                      <span className="resume-btn__meta">{d.attempts} {pluralPokusy(d.attempts)} ›</span>
                    </button>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Menu → Starší slova: browse any past day and open it to play ── */}
      {currentPage === 'archive' && (
        <div className="profile-page profile-page--archive">
          <h2 className="profile-name">{t("profile.sectionArchive")}</h2>
          {archiveHasLockedDays && archivePremiumLine}
          {/* `todayIso` je HERNÍ den, ne datum v telefonu. Slovo se otáčí ve
              3:00, takže mezi půlnocí a třetí je kalendářní datum o den
              napřed: dlaždice DNES seděla na zítřku a den, který se opravdu
              hrál, zůstal mrtvým políčkem mimo katalog — server ho do archivu
              správně nepustí (hlášeno 18. 9. 2026 v 1:33 platící hráčkou). */}
          <ArchiveCalendar
            days={archiveDays || []}
            loading={archiveLoading}
            onPickDay={openArchiveDay}
            maskWord={maskWord}
            todayIso={liveDayIso()}
            onPickToday={() => { setActiveTab('daily'); localStorage.setItem('activeTab', 'daily'); goPage('game'); }}
            month={archiveMonth}
            onMonthChange={setArchiveMonth}
          />
          {/* `disabled` must match pickRandomDay's filter: a free player whose free
              window is used up would otherwise get a button that does nothing. */}
          <button
            className="random-day-btn"
            onClick={pickRandomDay}
            disabled={!(archiveDays || []).some(d => d.status === "not_started" && !d.locked && d.date < liveDayIso())}
          >
            {t("archive.randomDay")}
          </button>
        </div>
      )}

      {currentPage === 'archivePlay' && (
        <div className={`profile-page${archiveSolvedWord ? ' profile-page--review' : ''}`}>
          <h3 className="profile-section-title">
            {archiveType === 'mega'
              ? (archiveDate ? `${t("game.header.megaWordDaily")} · ${archiveDate}` : '')
              : (() => { const [y, m, d] = (archiveDate || '').split('-'); return archiveDate ? `${parseInt(d)}. ${parseInt(m)}. ${y}` : ''; })()}
          </h3>
          {archiveSolvedWord ? (
            <>
              <div className="success-message">
                <div className="success-win-summary">
                  🎉<br />{t("game.success.wordGuessed")}
                  <div className="guessed-word-row">
                    <strong className={`guessed-word-value${wordsHidden ? ' guessed-word-value--hidden' : ''}`}>{maskWord(archiveSolvedWord)}</strong>
                  </div>
                  {t("game.success.attempts", { count: archiveAttempts.length, attempts: pluralPokusy(archiveAttempts.length) })}!
                </div>
              </div>

              {/* Controls for reviewing/sharing the path: hide the words, and sort
                  by temperature (warmest first) or the order they were guessed. */}
              <div className="review-controls">
                <button className={`review-eye${wordsHidden ? ' is-on' : ''}`}
                  onClick={() => setWordsHidden(v => !v)}
                  aria-label={wordsHidden ? t("word.visibility.show") : t("word.visibility.hide")}
                  title={wordsHidden ? t("word.visibility.show") : t("word.visibility.hide")}>
                  {renderEyeIcon(wordsHidden)}
                </button>
                <div className="review-seg" role="tablist">
                  <span className="segment-indicator" style={{ width: "calc((100% - 6px) / 3)", transform: `translateX(${winSort === 'graph' ? 200 : winSort === 'order' ? 100 : 0}%)` }} />
                  <button className={`review-seg__tab${winSort === 'temp' ? ' is-active' : ''}`} role="tab" aria-selected={winSort === 'temp'} onClick={() => setWinSort('temp')}>{t("win.byTemp")}</button>
                  <button className={`review-seg__tab${winSort === 'order' ? ' is-active' : ''}`} role="tab" aria-selected={winSort === 'order'} onClick={() => setWinSort('order')}>{t("win.byOrder")}</button>
                  <button className={`review-seg__tab${winSort === 'graph' ? ' is-active' : ''}`} role="tab" aria-selected={winSort === 'graph'} onClick={() => setWinSort('graph')}>{t("win.byGraph")}</button>
                </div>
              </div>

              {winSort === 'graph' ? (
                archiveAttempts.length < 2 ? (
                  <p className="profile-empty">{t("win.approachNeedsMore")}</p>
                ) : (() => {
                  // The guessing journey as a mountain: temperature (0–100) over the
                  // guesses in order, a flowing ridge climbing to the summit — the win
                  // at 100 °C, flagged with a flame. Drawn in real measured pixels.
                  const journey = [...archiveAttempts].sort((a, b) => (a.order || 0) - (b.order || 0));
                  const n = journey.length;
                  const clamp = (v) => Math.max(0, Math.min(100, v));
                  const W = winGraphSize.w, H = winGraphSize.h;
                  const padX = 12, padT = 26, padB = 16;
                  const innerW = W - 2 * padX, innerH = H - padT - padB;
                  const baseY = padT + innerH;
                  const xAt = (i) => padX + (n === 1 ? innerW / 2 : (i / (n - 1)) * innerW);
                  const yAt = (v) => baseY - (clamp(v) / 100) * innerH;
                  const pts = journey.map((a, i) => [xAt(i), yAt(a.adjustedSimilarity)]);
                  // Catmull-Rom → cubic Bézier: a smooth, flowing mountain ridge.
                  const smooth = (P) => {
                    let d = `M${P[0][0].toFixed(2)},${P[0][1].toFixed(2)}`;
                    for (let i = 0; i < P.length - 1; i++) {
                      const p0 = P[i - 1] || P[i], p1 = P[i], p2 = P[i + 1], p3 = P[i + 2] || p2;
                      const c1x = p1[0] + (p2[0] - p0[0]) / 6, c1y = p1[1] + (p2[1] - p0[1]) / 6;
                      const c2x = p2[0] - (p3[0] - p1[0]) / 6, c2y = p2[1] - (p3[1] - p1[1]) / 6;
                      d += ` C${c1x.toFixed(2)},${c1y.toFixed(2)} ${c2x.toFixed(2)},${c2y.toFixed(2)} ${p2[0].toFixed(2)},${p2[1].toFixed(2)}`;
                    }
                    return d;
                  };
                  const ridge = smooth(pts);
                  const area = `${ridge} L${pts[n - 1][0].toFixed(2)},${baseY.toFixed(2)} L${pts[0][0].toFixed(2)},${baseY.toFixed(2)} Z`;
                  const summit = pts[n - 1];
                  const WATER = 33; // sea level ≈ 0.33 similarity: cold guesses sink below it
                  const yWater = yAt(WATER);
                  return (
                    <div className="approach-graph">
                      <div className="approach-graph__plot" ref={winGraphRef}>
                      <svg className="approach-graph__svg" viewBox={`0 0 ${W} ${H}`} width="100%" height="100%"
                        preserveAspectRatio="none" role="img" aria-label={t("win.byGraph")}>
                        <defs>
                          <linearGradient id="approachRidge" x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0%" stopColor="#7fb2d4" />
                            <stop offset="45%" stopColor="#a78bd0" />
                            <stop offset="80%" stopColor="#f2884a" />
                            <stop offset="100%" stopColor="#ef5a3a" />
                          </linearGradient>
                          <linearGradient id="approachArea" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#f0793f" stopOpacity="0.42" />
                            <stop offset="55%" stopColor="#f2a35a" stopOpacity="0.18" />
                            <stop offset="100%" stopColor="#f2a35a" stopOpacity="0.05" />
                          </linearGradient>
                          <linearGradient id="approachWater" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#5aa9dd" stopOpacity="0.32" />
                            <stop offset="100%" stopColor="#2f6fa8" stopOpacity="0.5" />
                          </linearGradient>
                        </defs>
                        <line x1={padX} y1={yAt(100).toFixed(2)} x2={W - padX} y2={yAt(100).toFixed(2)} className="approach-graph__hot" />
                        <path d={area} className="approach-graph__area" fill="url(#approachArea)" />
                        {/* Sea level: cold guesses dip below the surface, warm ones rise above it. */}
                        <rect x={padX} y={yWater.toFixed(2)} width={innerW.toFixed(2)} height={(baseY - yWater).toFixed(2)}
                          fill="url(#approachWater)" className="approach-graph__water" />
                        <line x1={padX} y1={yWater.toFixed(2)} x2={W - padX} y2={yWater.toFixed(2)} className="approach-graph__waterline" />
                        <path d={ridge} className="approach-graph__ridge" pathLength="1" />
                        <g className="approach-graph__summit">
                          <circle cx={summit[0].toFixed(2)} cy={summit[1].toFixed(2)} r="4.5" className="approach-graph__summit-dot" />
                          <text x={summit[0].toFixed(2)} y={(summit[1] - 9).toFixed(2)} textAnchor="middle" className="approach-graph__flag">🔥</text>
                        </g>
                      </svg>
                      </div>
                      <div className="approach-graph__axis">
                        <span>{t("win.approachStart")}</span>
                        <span>{t("win.approachEnd")}</span>
                      </div>
                    </div>
                  );
                })()
              ) : (
                <GuessBoard
                  maskWord={maskWord}
                  readOnly
                  attempts={winSort === 'order' ? [...archiveAttempts].sort((a, b) => (b.order || 0) - (a.order || 0)) : archiveAttempts}
                  colorFor={getBackgroundColor}
                  labelFor={getTemperatureLabel}
                />
              )}
            </>
          ) : (
            <GuessBoard
              maskWord={maskWord}
              value={archiveGuess}
              onChange={setArchiveGuess}
              onSubmit={submitArchiveGuess}
              placeholder={t("game.input.placeholder.daily")}
              submitLabel={t("buttons.submitGuess")}
              errorMessage={archiveError}
              pendingMessage={scoringPending ? t("game.scoringPending") : ""}
              attempts={archiveAttempts}
              colorFor={getBackgroundColor}
              labelFor={getTemperatureLabel}
              lastLabelFor={getSimilarityLabel}
              lastAttempt={archiveLastAttempt}
              inputRef={archiveInputRef}
              flyingWord={archiveFlyingWord}
              leavingWord={archiveLeavingWord}
              highlightedIndex={
                archiveLastAttempt
                  ? archiveAttempts.findIndex((a) => a.guess === archiveLastAttempt.guess)
                  : -1
              }
              countLabel={`${archiveAttempts.length} ${pluralPokusy(archiveAttempts.length)}`}
              timeoutProgress={archiveTimeoutProgress}
            />
          )}
        </div>
      )}

      {currentPage === 'friends' && (
        <div className="profile-page">
          <h2 className="profile-name">{t("friends.title")}</h2>

          {/* daily/mega is chosen in the sticky bottom bar (friendsTab) */}
          {/* My own row is in this list (the backend marks it `me`), so the page
              answers "where do I come" and not only "how is everyone else doing".
              The friend count is therefore taken WITHOUT me: a standing of one is
              not a standing, and somebody with no friends yet still needs telling
              so rather than being shown themselves in a league of one. */}
          {!friendsToday ? (
            <p className="social-hint">{t("profile.empty")}</p>
          ) : friendsToday.filter(f => !f.me).length === 0 ? (
            <p className="social-hint">{t("friends.noFriends")}</p>
          ) : (
            <div className="social-list social-list--tight">
              {[...friendsToday]
                .map(f => {
                  const base = friendsTab === 'mega' ? f.mega : f.daily;
                  // Same treatment the group standings give my row: the server's
                  // mirror lags a tip behind the board I am looking at, and after
                  // a word rotation it is blank — both would tell me I have not
                  // started while I am demonstrably guessing.
                  return { f, s: f.me ? mergeMyStanding(base, friendsTab) : base };
                })
                .sort((a, b) => compareStandings(a.s, b.s, a.f.display_name, b.f.display_name))
                .map(({ f, s }) => (
                  <div key={f.uid} className={`standing-line${f.me ? ' standing-line--me' : ''}`}>
                    {tempBar(
                      // A player who never set a nickname has no name to show, and
                      // "—" against my own row reads like a fault. Friends keep the
                      // dash: it is genuinely all we know about them.
                      f.me ? (f.display_name || t("friends.me")) : (
                        // Hraje druhou verzi (cs ↔ sk, totéž slovo přeložené) —
                        // server výsledek dočetl odtamtud. Bez štítku by to vypadalo,
                        // že hrál tady. Nahlásila Katka ze Slovenska 29. 9. 2026.
                        s.lang ? (
                          <span className="name-with-chip">
                            {f.display_name || '—'}
                            <span className="name-chip" title={t(`friends.playedIn.${s.lang}`)}>{s.lang === 'cs' ? 'CZ' : s.lang.toUpperCase()}</span>
                          </span>
                        ) : (f.display_name || '—')
                      ),
                      s.status, s.attempts, s.best_similarity
                    )}
                  </div>
                ))}
            </div>
          )}

          {friends && friends.some(f => f.direction === 'outgoing') && (
            <p className="social-hint">
              {t("friends.outgoingPending")}: {friends.filter(f => f.direction === 'outgoing').map(f => f.display_name || '—').join(', ')}
            </p>
          )}

          {/* actions after the content */}
          <div className="social-actions" style={{ marginTop: '1.5rem' }}>
            <button className="pill-btn pill-btn--accept social-actions__wide" onClick={() => { setAddFriendMsg(''); setAddCodeInput(''); setAddEmailInput(''); setAddEmailMsg(null); setSocialModal('addFriend'); }}>+ {t("friends.addTitle")}</button>
            {friends && friends.some(f => f.direction === 'accepted') ? (
              <>
                <button className="pill-btn pill-btn--ghost" onClick={() => setSocialModal('myCode')}>{t("friends.sendCodeTitle")}</button>
                <button className="pill-btn pill-btn--ghost" onClick={() => goPage('friendsManage')}>{menuIcon("settings")}{t("actions.manage")}</button>
              </>
            ) : (
              <button className="pill-btn pill-btn--ghost social-actions__wide" onClick={() => setSocialModal('myCode')}>{t("friends.sendCodeTitle")}</button>
            )}
          </div>
        </div>
      )}

      {currentPage === 'friendsManage' && (
        <div className="profile-page">
          <h2 className="profile-name">{t("friends.manage")}</h2>
          {!friends || !friends.some(f => f.direction === 'accepted') ? (
            <p className="social-hint" style={{ marginTop: '1.2rem' }}>{t("friends.noAccepted")}</p>
          ) : (
            <div className="social-list" style={{ marginTop: '1.2rem' }}>
              {friends.filter(f => f.direction === 'accepted').map(f => (
                <div key={f.uid} className="social-card">
                  <span className="social-card__name">{f.display_name || '—'}</span>
                  <div className="social-card__actions">
                    {/* Gift a year of Premium to this friend — the §6.1 sales path,
                        surfaced here so it's not buried in a group's member list. */}
                    {sellsPremium && (
                    <button className="pill-btn pill-btn--gift" disabled={!!checkoutBusy}
                      onClick={() => startCheckout("gift_year", f.uid)}>
                      🎁 {t("friends.giftPremium")}
                    </button>
                    )}
                    <button className="pill-btn pill-btn--ghost" onClick={() => setConfirmAction({ message: t("friends.removeFriendConfirm"), confirmLabel: t("friends.removeFriend"), onConfirm: () => removeFriend(f.uid) })}>{t("friends.removeFriend")}</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {currentPage === 'groups' && (
        <div className="profile-page">
          <h2 className="profile-name">{t("groups.title")}</h2>

          {/* Jen skupiny, kde to jde použít: moje, se skupinovými slovy, bez světa. */}
          {renderTemaNovinka((groups || []).filter((g) =>
            g.owner_uid === currentUser?.uid && g.group_words_enabled !== false && !g.tema
            // Moje skupiny jsou neaktivní přesně tehdy, když mně propadlo Premium
            // (licence skupiny = předplatné zakladatele). Seznam `active` nenese
            // a dopočítat ho by stálo čtení; `premium` respektuje vymáhání.
            && !!myPremium?.premium))}

          {/* content first: my groups */}
          {!groups ? (
            <p className="social-hint">{t("profile.empty")}</p>
          ) : groups.length === 0 && groupRequests.length === 0 && otherMarketGroups.length === 0 ? (
            <p className="social-hint">{t("groups.noGroups")}</p>
          ) : (
            <div className="social-list social-list--groups">
              {/* Správcovské první, pak ty, kde jsem jen člen (6. 10. 2026). Nadpisy
                  jen když jsou obě skupiny — u jednoho druhu by nic neříkaly. */}
              {(() => {
                const spravuji = groups.filter(g => g.owner_uid === currentUser?.uid);
                const clenem = groups.filter(g => g.owner_uid !== currentUser?.uid);
                const rozdelit = spravuji.length > 0 && clenem.length > 0;
                return [...spravuji, ...clenem].map((g, i) => {
                // The same facts the board's group picker shows, joined from the
                // rounds list already in state — no extra request. Asked for
                // 2026-09-07: the tab listed a name and a member count, so the
                // only way to learn whether your own group had a word today, or
                // how it was going, was to open every one of them in turn.
                const r = myGroupRounds.find(x => x.gid === g.gid);
                const live = r && showsRound(r);
                return (
                <React.Fragment key={g.gid}>
                {rozdelit && i === 0 && <h3 className="social-list__heading">{t("groups.listOwned")}</h3>}
                {rozdelit && i === spravuji.length && <h3 className="social-list__heading">{t("groups.listMember")}</h3>}
                <button key={g.gid} className="social-card social-card--clickable" onClick={() => openGroup(g.gid)}>
                  <span className="social-card__id"><span className="social-card__title">
                    <span className="social-card__name">{g.name}</span>
                    {/* No member count. It was the only thing here before the
                        word and the standing arrived, and next to those it is
                        the one line nobody opened the tab to read. */}
                    {/* Today's word, to whoever chose it. Not "kdo zadal": a
                        name I do not act on, and my own rounds say they are mine
                        by showing the word. */}
                    {live && r.is_setter && r.word && (
                      <span className="social-card__sub social-card__sub--mine">{maskWord(r.word)}</span>
                    )}
                    {/* Where I stand, above tomorrow: today is what I can act
                        on. A setter has no standing in their own round — they
                        cannot play it — so they get nothing here. */}
                    {live && !r.is_setter && (
                      <span className={`social-card__sub${
                        r.status === 'guessed' ? ' social-card__sub--done'
                        : !r.finished_today && !r.attempts ? ' social-card__sub--waiting' : ''}`}>
                        {r.status === 'guessed'
                          ? t("groupWord.pickSolved", { count: r.attempts })
                          : r.finished_today
                            ? t("groupWord.pickMissed")
                            : r.attempts > 0
                              ? `${r.attempts} ${pluralPokusy(r.attempts)}`
                              : t("groupWord.pickNotStarted")}
                      </span>
                    )}
                    {/* And tomorrow last, a step quieter — it has not happened
                        yet. Either the word I put aside, or, where the answer was
                        free to compute, that tomorrow is on me. */}
                    {r?.tomorrow_word ? (
                      <span className="social-card__sub social-card__sub--tomorrow">
                        {t("groupWord.tomorrowIs", { word: maskWord(r.tomorrow_word) })}
                      </span>
                    ) : r?.tomorrow_is_mine ? (
                      <span className="social-card__sub social-card__sub--tomorrow">
                        {t("groupWord.tomorrowYours")}
                      </span>
                    ) : null}
                  </span></span>
                  {/* Puntík „jsi na řadě" vpravo u šipky, ne za názvem: tam nebyl
                      svisle na středu a u dlouhého názvu skákal na druhý řádek. */}
                  <span className="social-card__end">
                    {myTurnGids.has(g.gid) && (
                      <i className="turn-dot" role="img" aria-label={t("notifications.yourTurnTitle")} title={t("notifications.yourTurnTitle")} />
                    )}
                    <ChevronRight size={18} className="social-card__chevron" />
                  </span>
                </button>
                </React.Fragment>
                );
              });
              })()}
              {/* Groups I asked to join. Not clickable — I am not in them yet —
                  but listed, because the only other answer to "did I already
                  ask?" was to ask again. */}
              {groupRequests.map(r => (
                <div key={`req-${r.gid}`} className="social-card social-card--muted">
                  <span className="social-card__id"><span className="social-card__title"><span className="social-card__name">{r.group_name || '—'}</span><span className="social-card__sub">{t("groups.requestPending")}</span></span></span>
                  {/* Zrušit si ji dřív mohl jen správce (29. 10. 2026: platící
                      hráčka čekala dny a do skupiny už nechtěla). */}
                  <span className="social-card__actions">
                    <button className="pill-btn pill-btn--ghost" onClick={() => withdrawGroupRequest(r.gid)}>{t("groups.withdrawRequest")}</button>
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Skupiny z jiné jazykové verze.
              Do seznamu patří jako ostatní a otevírají se stejně: hráč do nich
              vstoupil záměrně, za svými lidmi, a hrát se v nich dá — tip se
              hodnotí podle jazyka KOLA, ne podle domény.
              Filtr z 11. 9. 2026 je zahazoval, zatímco zvonek o nich mluvil dál,
              takže slovenské hráčce chodilo „Nové slovo v skupine" na skupinu,
              kterou v seznamu neměla (16. 9. 2026).
              Vlajka a řádek pod názvem říkají, že se v ní hraje cizí slovo — bez
              toho by hráč nevěděl, proč mu nesedí klávesnice. */}
          {otherMarketGroups.length > 0 && (
            <div className="social-list social-list--groups" style={{ marginTop: '1rem' }}>
              {otherMarketGroups.map(g => {
                const jina = LANGUAGE_LINKS.find(l => l.code === g.lang);
                return (
                  <button key={`jinde-${g.gid}`} className="social-card social-card--clickable"
                          onClick={() => openGroup(g.gid)}>
                    <span className="social-card__id"><span className="social-card__title">
                      <span className="social-card__name">{jina ? `${jina.flag} ` : ''}{g.name}</span>
                      <span className="social-card__sub">
                        {t("groups.otherMarketWord", { domain: jina ? jina.domain : g.lang })}
                      </span>
                    </span></span>
                    <ChevronRight size={18} className="social-card__chevron" />
                  </button>
                );
              })}
            </div>
          )}

          {/* actions after the content */}
          <div className="social-actions" style={{ marginTop: '1.5rem' }}>
            {/* Founding a group is Premium — say so ON the button, not after the
                tap. Joining one (next to it) stays free, so the lock also makes
                the difference between the two visible. */}
            <button
              className="pill-btn pill-btn--accept social-actions__wide"
              disabled={ownedGroupLimitReached}
              onClick={() => { if (showUpgradeCta) { openPremium('groups'); return; } setCreateGroupMsg(''); setCreateGroupName(''); setSocialModal('createGroup'); }}
            >
              {ownedGroupLimitReached
                ? t("groups.createLimitReached", { limit: ownedGroupLimit })
                : <>+ {t("groups.createTitle")}</>}
              {!ownedGroupLimitReached && showUpgradeCta && <LockIcon size={13} className="pill-btn__lock" />}
            </button>
            <button className="pill-btn pill-btn--ghost social-actions__wide" onClick={() => { setJoinGroupMsg(''); setJoinGroupCode(''); setSocialModal('joinGroup'); }}>{t("groups.joinTitle")}</button>
          </div>
        </div>
      )}

      {currentPage === 'group' && selectedGroup && (
        <div className="profile-page">
          <h2 className="profile-name">{selectedGroup.name}</h2>

          {selectedGroup.owner_uid === currentUser?.uid && selectedGroup.active !== false
            && groupWordsOn && !selectedGroup.tema && renderTemaNovinka([selectedGroup])}

          {/* The founder's Premium lapsed. The group keeps its members and its
              history — nothing is deleted — but it reports nothing until the
              subscription is back, so the page says that instead of showing a
              board of blanks that would read as "nobody played today". */}
          {selectedGroup.active === false && (
            <div className="group-inactive">
              <h3 className="group-inactive__title">{t("groups.inactiveTitle")}</h3>
              <p className="group-inactive__body">{t("groups.inactiveBody")}</p>
              {selectedGroup.owner_uid === currentUser?.uid && (
                <button className="pill-btn pill-btn--accept" onClick={() => openPremium('groups')}>
                  {t("groups.inactiveCta")}
                </button>
              )}
            </div>
          )}

          {/* Which word this is about comes from the sticky bottom bar: daily,
              mega, or the group's own round. */}
          {/* Under Skupinové the list is the group's own members either way: with
              no word running there is nothing to report about them, but "who is in
              this group" is still the question the page answers. */}
          {selectedGroup.active === false ? null : (groupStandingsTab === 'group' ? false : !groupStandings) ? (
            <p className="social-hint">{t("profile.empty")}</p>
          ) : (
            <>
            {/* Which word these rows are about. The segment at the bottom changes
                them silently, so "Ještě nehádá" under Megaslovo read as a lost
                result rather than as a different board — reported twice on
                2026-08-21, once as "proč mi zmizelo, co jsem uhádl". */}
            <h3 className="group-standings-title">
              {groupStandingsTab === 'group' ? t("groups.boardGroup")
                : groupStandingsTab === 'mega' ? t("groups.boardMega")
                : t("groups.boardDaily")}
            </h3>
            <div className="social-list social-list--tight">
              {selectedGroup.members
                .map(m => {
                  if (groupStandingsTab === 'group') {
                    // Only ever THIS group's round. Without the gate a reply for
                    // another group painted its statuses under these names — while
                    // the round block below, which does check, showed nothing.
                    if (roundForGid !== selectedGroup.gid) {
                      return { m, s: { status: 'none' } };
                    }
                    if (!groupRound) {
                      // Between rounds, show how the LAST one went rather than a
                      // row of blank white bars. Reported 2026-08-21: "tady ani
                      // nevidím, že jsem uhodl! Je to bílé. Taky není označený
                      // zadavatel, že on nehádá." The result was already on the
                      // page — folded away behind "Minulé slovo" — which is a
                      // strange place for the only thing the page has to say.
                      if (lastRound?.setter_uid && m.uid === lastRound.setter_uid) {
                        return { m, s: { status: 'setter' }, finished: true };
                      }
                      const r = lastStandings.find(x => x.uid === m.uid);
                      return r
                        ? { m, s: r, finished: true }
                        : { m, s: { status: 'none' } };
                    }
                    // The setter never guesses their own word — a "not started"
                    // row against their name is simply wrong.
                    if (m.uid === groupRound.setter_uid) return { m, s: { status: 'setter' } };
                    const r = roundStandings.find(s => s.uid === m.uid);
                    return { m, s: r || { status: 'not_started', attempts: 0 } };
                  }
                  const s = groupStandings.find(s => s.uid === m.uid) || { status: 'not_started', attempts: 0 };
                  return { m, s: m.uid === currentUser?.uid ? mergeMyStanding(s, groupStandingsTab) : s };
                })
                .sort((a, b) => compareStandings(a.s, b.s, a.m.display_name, b.m.display_name))
                .map(({ m, s, finished }) => (
                  <div key={m.uid} className={`standing-line${m.uid === currentUser?.uid ? ' standing-line--me' : ''}${s.status === 'setter' ? ' standing-line--setter' : ''}`}>
                    {/* The setter always looks the same: the framed block, the
                        caption, the name under it. Only the caption changes, and
                        only because "DNES hádáme slovo od" is untrue on a day with
                        no word running.
                        Giving the two states two different shapes is what produced
                        "Proč je stále ta podoba zadavatele jiná ve dvou různých
                        skupinách????" (2026-08-21) — the two groups were simply in
                        two different states, which is a distinction the player has
                        no reason to see. The frame is also what makes this row
                        display:block; without it the caption lands beside the bar
                        instead of above it. */}
                    {s.status === 'setter' && (
                      <p className="standing-line__banner">
                        {finished ? t("groupWord.wasSetter") : t("groupWord.todaysAuthor")}
                      </p>
                    )}
                    {tempBar(
                      <span className="name-with-chip">{m.display_name || '—'}{m.role === 'owner' && <span className="name-chip">{t("groups.ownerBadge")}</span>}</span>,
                      s.status, s.attempts, s.best_similarity, finished,
                      awayLabel(m.uid, m.joined_at)
                    )}
                  </div>
                ))}
            </div>
            {/* Where the group's word went. Hiding it silently is what produced
                "ani nevidím výzvu k zadání slova" an hour after it was first
                hidden, so the page now points at the tab that has it. */}
            {groupStandingsTab !== 'group' && hasGroupWords
              && (roundForGid === selectedGroup.gid || roundFailedForGid === selectedGroup.gid)
              && (features.group_words !== false || groupRound) && (
              <p className="round-last">{t("groups.wordUnderGroupTab")}</p>
            )}
            </>
          )}

          {/* ── The group's own word ────────────────────────────────────────
               A round the group plays against each other, separate from the
               daily/mega standings above. The setter never sees a board — they
               know the answer — they see who is closing in on them. */}
          {/* The group's word lives under Skupinové: playing or setting it while
              the standings above report the daily one asks about two words at
              once. Decided 2026-08-21 — "když jsem v tabu Megaslovo nebo Denní,
              tak ten box pro zadání slova nemá být vidět".
              Hiding it was tried once that evening and reverted within the hour,
              because it took away the only way to start a round and nothing said
              where it had gone. Both of those are answered now: the standings
              carry a heading naming the board, and the line under them points at
              the tab. The escape hatch below is the rest of it — the Skupinové
              segment does not exist while NO group has a word, and with nowhere
              to point, the block has to stay where the player is.
              Only ever this group's round, too: a reply for another group (or a
              slow one arriving late) must not paint a word under the wrong name.
              Switched off in settings: a round already running still finishes
              (the backend lets it), but nothing invites a new one. */}
          {(groupStandingsTab === 'group' || (!groupRound && !hasGroupWords))
            && (roundForGid === selectedGroup.gid || roundFailedForGid === selectedGroup.gid)
            && (features.group_words !== false || groupRound) && (
          <section className="round-block">
            <h3 className="round-block__title">{t("groupWord.title")}</h3>

            {!groupWordsOn ? (
              // Switched off by the group itself. Said out loud, because a block
              // that simply disappears looks like a fault, not a decision.
              <div className="round-empty">
                <p className="round-empty__text">{t("groupWord.offHere")}</p>
              </div>
            ) : roundFailedForGid === selectedGroup.gid ? (
              // Hiding the block on a failed load made it look like this group
              // simply has no group words, and no amount of waiting fixed it.
              <div className="round-empty">
                <p className="round-empty__text">{t("groupWord.errGeneric")}</p>
                <button className="pill-btn pill-btn--ghost" onClick={() => fetchGroupRound(selectedGroup.gid)}>
                  {t("groupWord.retry")}
                </button>
              </div>
            ) : !groupRound ? (
              <div className="round-empty">
                {/* Between rounds the block's job is to say whose move it is —
                    "no word yet" alone leaves everyone waiting for each other. */}
                {/* One card, banded. The parts belong together — they are all
                    about this group's word — so they keep one frame; what they
                    needed was somewhere to end. Stacked flat at equal weight the
                    block read as a list of unrelated sentences: "je plný
                    informací a špatně se v tom orientuje" (2026-09-06). */}
                {myWordMove ? (
                  <>
                    <WordMoveBlock move={myWordMove} />
                    {!setterRotates && selectedGroup?.owner_uid === currentUser?.uid && (
                      <p className="round-empty__hint">
                        {t("groupWord.rotateTip")}{' '}
                        <button className="link-button" onClick={() => goPage('groupAdmin')}>
                          {t("groupWord.rotateTipCta")}
                        </button>
                      </p>
                    )}
                  </>
                ) : (
                  <div className="round-day">
                    <p className="round-day__label">{t("groupWord.dayToday")}</p>
                    <p className="round-day__lead">
                      {queuedWord && !queuedWord.is_mine
                        ? t("groupWord.queuedTheirs", { name: queuedWord.setter_name || '—' })
                        : roundTurn?.name ? t("groupWord.theirTurn", { name: roundTurn.name })
                        // Spí úplně všichni: „Zatím tu žádné slovo není" by byla
                        // pravda o slovu, ale ne o tom, proč žádné nebude.
                        : roundTurn?.allAsleep ? t("groupWord.allAsleep")
                        : t("groupWord.none")}
                      {roundTurn?.name && turnOrderLine()}
                    </p>
                  </div>
                )}
                {/* Vzít slovo za skupinu, na kterou se čeká. Nabízí se až po dni
                    ticha (server, owner_may_take_over) — dřív by to nebyla
                    pojistka proti zaseknutí, ale právo sebrat komukoli tah. */}
                {roundTurn?.canTakeOver && (
                  <div className="round-day round-day--minor">
                    <button className="pill-btn pill-btn--accept" onClick={openTakeOverForm}>
                      {t("groupWord.takeOverCta")}
                    </button>
                    <p className="round-day__note">
                      {t("groupWord.takeOverNote", { name: roundTurn.name || '—' })}
                    </p>
                  </div>
                )}
                {roundTurn?.canPass && (
                  <div className="round-day round-day--minor">
                  <button className="pill-btn pill-btn--ghost" disabled={roundBusy} onClick={passGroupTurn}>
                    {/* "Nemám slovo" is untrue once you have given one. The act
                        is the same — the turn moves on — but giving up a word you
                        prepared is a different sentence from never having had one.
                        Reported 2026-08-21. */}
                    {!roundTurn.isMine
                      ? t("groupWord.passTheirs")
                      : queuedWord?.is_mine
                        ? t("groupWord.passGiveUp")
                        : t("groupWord.passMine")}
                  </button>
                  {/* What the button does, next to the button. "Přeskočit" alone
                      does not say the turn goes to somebody else. Asked 2026-08-21. */}
                  <p className="round-day__note">
                    {queuedWord?.is_mine && roundTurn.isMine
                      ? t("groupWord.passExplainsDrops")
                      : t("groupWord.passExplains")}
                  </p>
                  </div>
                )}
                {lastWordBlock()}
              </div>
            ) : (
              /* A layout box, not a bare fragment. The other branch is a flex
                 column with a gap and this one was a loose pile of elements
                 leaning on whatever margins they happened to carry, so the two
                 halves of the same panel were spaced by different rules —
                 "tady je to nalepené na sebe" (2026-09-06). */
              <div className="round-days">
                {/* Playing it happens on the board's Skupinové tab, not here —
                    two places to guess the same word is one too many. This block
                    keeps only what that tab cannot do. */}
                <p className="round-day__label round-day__label--first">{t("groupWord.dayToday")}</p>
                {groupRound.is_setter ? (
                  <>
                    {/* Three sentences used to say this: the setter's own name,
                        "this one is from you", and the word. Same fact, three
                        registers, and the box read as a pile — "úplně marný a
                        nepřehledný" (2026-09-06). The word IS the fact; one line
                        under it says what follows from it. */}
                    {/* Said in the band that actually shows a word — it is what
                        explains why the word is on screen and why this person is
                        not playing it. Restored 2026-09-06 after being cut with
                        the duplicate: "mělo by tady být napsáno, že je to moje
                        slovo, je tam nadepsáno jen DNES." */}
                    <p className="round-day__lead round-day__lead--strong">{t("groupWord.todayMine")}</p>
                    {/* Only ever rendered when the backend sent it, which it does
                        for the setter alone. */}
                    {groupRound.word && <WordSlab word={groupRound.word} />}
                    <p className="round-day__note">{t("groupWord.todayMineNote")}</p>
                  </>
                ) : (
                  <p className="round-by">{t("groupWord.setBy", { name: groupRound.setter_name || '—' })}</p>
                )}
                {/* Not for the setter: they cannot guess their own word, and a
                    button that says "play" and leads to a board with no input is
                    a promise the page does not keep. Reported 2026-08-21 — the
                    line above it already says the word is theirs. */}
                {!groupRound.is_setter && (
                  <button className="pill-btn pill-btn--accept round-play"
                    onClick={() => playGroupWord(selectedGroup.gid)}>
                    {t("groupWord.playCta")}
                  </button>
                )}

                {/* Today is running; the next word is already somebody's job.
                    During a round the rotation names TOMORROW's setter, so this
                    is where the person who is up next can get it done before
                    bed instead of at ten the next morning. In owner mode that
                    is the founder, who is also the one playing out today. */}
                {/* A round is playing out, and the next word is already
                    somebody's job — during a round the rotation names TOMORROW's
                    setter, so this is where they get it done before bed. */}
                <WordMoveBlock move={myWordMove} />
                {/* Tomorrow when it is somebody else's. In a rotating group this
                    is the ordinary case the moment you set today's word — the
                    turn moves on the same instant — and the panel said nothing
                    at all about tomorrow, which reads as "the group has nothing
                    after this". Reported 2026-09-06 with the missing test state.
                    Owner-mode groups never reach it: the founder is always next. */}
                {!myWordMove && (queuedWord?.setter_name || roundTurn?.name) && (
                  <div className="round-day">
                    <p className="round-day__label">{t("groupWord.dayTomorrow")}</p>
                    <p className="round-day__lead">
                      {/* Ready or not, said outright. "Na řadě je Jana" answers
                          whose turn it is; what the group actually wants to know
                          is whether tomorrow is covered — and that is a
                          different question with the same subject. Only reached
                          in rotating groups: where the founder sets every word,
                          tomorrow's setter is always the reader. */}
                      {queuedWord && !queuedWord.is_mine
                        ? t("groupWord.queuedTheirs", { name: queuedWord.setter_name || '—' })
                        : t("groupWord.tomorrowNotReady", { name: roundTurn.name })}
                      {turnOrderLine()}
                    </p>
                  </div>
                )}

                {lastWordBlock()}

                {/* Back ONLY in a school group (30. 9. 2026): a teacher plays
                    several words in one lesson, ends the round and sets the next
                    — which only a school group may do the same day. Everywhere
                    else the reasoning below still holds. */}
                {selectedGroup?.school && roundTurn?.canClose && (
                  <button className="pill-btn pill-btn--ghost round-close" disabled={roundBusy}
                    onClick={() => setConfirmAction({
                      message: t("groupWord.closeConfirm"),
                      confirmLabel: t("groupWord.closeCta"),
                      onConfirm: closeGroupRound,
                    })}>
                    {t("groupWord.closeCta")}
                  </button>
                )}

                {/* No "end the round" button any more (2026-09-06). The word
                    rotates at 03:00 by itself, and ending one early never let the
                    group play another the same day — one word a game day is the
                    rule either way. The last thing it did that nothing else did
                    was free the next setter, and since they can now write their
                    word down while today is still playing, even that is gone.
                    What was left was a destructive button whose label named a
                    mechanism, next to a panel that already explains the schedule.

                    The endpoint stays: sk/pl/hr/de are still served by the V1
                    build, which still has the button. */}
              </div>
            )}

            {/* Outside both branches on purpose: the rule holds whether or not a
                round is running, and it is the same sentence either way. */}
            {groupWordsOn && setterPremiumNote()}
          </section>
          )}

          {/* ── Správa skupiny ─────────────────────────────────────────────
              Vlastní karta se svým nadpisem, jako slovo dne nad ní a moje účast
              pod ní. Do 23. 9. 2026 to byl jediný nerámovaný blok mezi dvěma
              kartami — a zároveň ten nejhlasitější, protože „Poslat pozvánku"
              byla plná modrá přes celou šířku. Hlášeno jako „úplný guláš".

              Pozvánka si drží přednost uvnitř téhle karty, ale obtažením místo
              výplně: plná modrá patří tomu, co má hráč udělat TEĎ, a to je
              „Zadat slovo" o kus výš. Když se zrovna nic hrát nedá, je tahle
              tlumená modrá jediný akcent na obrazovce — a to je správné pořadí.

              Zakladatel otevírá správu jako hlubší stránku, šipka zpět vrací sem. */}
          {selectedGroup.owner_uid === currentUser?.uid && (
            <section className="round-block round-block--me">
              <h3 className="round-block__title">{t("groups.adminHereTitle")}</h3>
              <div className="social-actions">
                <button className="pill-btn pill-btn--accent-soft social-actions__wide" onClick={() => { setInviteEmailInput(''); setInviteEmailMsg(null); setSocialModal('inviteGroupEmail'); }}>{t("groups.sendInviteTitle")}</button>
                <button className="pill-btn pill-btn--ghost" onClick={() => { setGroupCodeCopied(false); setSocialModal('inviteGroup'); }}>{t("groups.groupCodeTitle")}</button>
                <button className="pill-btn pill-btn--ghost" onClick={() => goPage('groupAdmin')}>{menuIcon("settings")}{t("actions.manage")}</button>
              </div>
            </section>
          )}

          {/* ── Moje účast ve skupině ──────────────────────────────────────
              Spánek (námět Petry Caltové, 22. 9. 2026) a odchod. Vlastní karta,
              a to schválně: je to jiný předmět než slovo dne. Tamto je tah v
              kole, tohle je můj vztah ke skupině.

              Do 23. 9. 2026 to byly dvě holé pilulky pod kartou se slovem —
              stejně velké, stejně barevné, hned pod sebou, a jedna z nich byla
              nevratná. „Je to nepřehledné a hodně toho je.“ Spánek si navíc
              půjčoval `round-day`, tedy pruh, který patří DOVNITŘ karty, takže
              pod ní visela osiřelá linka.

              Spánek se nabízí i vlastníkovi — v režimu „zadává zakladatel" je to
              právě on, na kom se skupina zasekne, když odjede. Odchod jen
              ostatním: vlastník skupinu maže, neopouští ji. */}
          {((groupWordsOn && roundTurn) || selectedGroup.owner_uid !== currentUser?.uid) && (
            <section className="round-block round-block--me">
              <h3 className="round-block__title">{t("groups.myPartTitle")}</h3>
              {/* Jen tam, kde spánek něco změní — tedy kam vůbec chodí řada.
                  V režimu „zadává zakladatel" to platí jen pro zakladatele; ostatní
                  by dostali přepínač, po kterém se nestane nic.

                  `iAmSleeping` v podmínce je záchranná brzda: kdyby skupina přepla
                  z rotace na zakladatele, zatímco někdo spí, bez ní by se už neměl
                  jak probudit. */}
              {groupWordsOn && roundTurn && (roundTurn.canSleep || roundTurn.iAmSleeping) && (
                <div className="group-me">
                  {/* Stav, ne jen text na tlačítku. Že mě skupina přeskakuje, je
                      něco, co má jít poznat pohledem — jinak se to dozvím, až mi
                      dojde, že na mě nikdy nevyjde řada. */}
                  {roundTurn.iAmSleeping && (
                    <p className="group-me__state">{t("groupWord.sleepingNow")}</p>
                  )}
                  <button
                    type="button"
                    className="pill-btn pill-btn--ghost"
                    disabled={sleepBusy}
                    onClick={() => toggleSleep(selectedGroup.gid, !roundTurn.iAmSleeping)}
                  >
                    {roundTurn.iAmSleeping ? t("groupWord.wakeMe") : t("groupWord.sleepMe")}
                  </button>
                  <p className="group-me__note">
                    {roundTurn.iAmSleeping ? t("groupWord.sleepOnNote") : t("groupWord.sleepOffNote")}
                  </p>
                </div>
              )}

              {selectedGroup.owner_uid !== currentUser?.uid && (
                <div className="group-me__exit">
                  <button
                    className="pill-btn pill-btn--quiet-danger"
                    onClick={() => setConfirmAction({ message: t("groups.leaveConfirm"), confirmLabel: t("groups.leave"), onConfirm: () => leaveGroup(selectedGroup.gid) })}
                  >
                    {t("groups.leave")}
                  </button>
                </div>
              )}
            </section>
          )}
        </div>
      )}

      {currentPage === 'groupAdmin' && selectedGroup && (
        <div className="profile-page settings-page">
          <div className="settings-hero">
            <div className="settings-avatar settings-avatar--group"><span>{(selectedGroup.name.trim()[0] || '#').toUpperCase()}</span></div>
            <div className="settings-hero__id">
              <div className="settings-hero__name">{selectedGroup.name}</div>
              {/* Obsazenost: členové/strop a (kde free místa opravdu platí) free/strop.
                  Dřív tu stálo „Administrace" — pozůstatek okopírované hlavičky
                  profilu, kde je na tomhle místě e-mail. */}
              <div className="settings-hero__email">
                {selectedGroup.limits?.members
                  ? t("groups.adminHeaderMembers", { count: selectedGroup.members.length, max: selectedGroup.limits.members })
                  : `${selectedGroup.members.length} ${t("groups.membersLabel")}`}
                {sellsPremium && selectedGroup.limits?.free != null && ` (${t("groups.adminHeaderFree", {
                  count: selectedGroup.members.filter((m) => !m.premium).length, max: selectedGroup.limits.free })})`}
              </div>
              {selectedGroup.tema && (
                <div className="settings-hero__email">{t("groups.adminHeaderTema", { tema: selectedGroup.tema })}</div>
              )}
            </div>
          </div>

          {/* Pozvat členy — dvě akce jako řádky, obě otevřou modal */}
          <section className="settings-section">
            <h3 className="settings-section__title">{t("groups.inviteMembersTitle")}</h3>
            <div className="settings-group">
              <button className="settings-row settings-row--tap" onClick={() => { setInviteEmailInput(''); setInviteEmailMsg(null); setSocialModal('inviteGroupEmail'); }}>
                {menuIcon("friends")}<span className="settings-row__label">{t("groups.inviteEmailTitle")}</span><span className="settings-row__chevron">›</span>
              </button>
              <button className="settings-row settings-row--tap" onClick={() => { setInviteCode(''); setInviteResult(null); setSocialModal('inviteByCode'); }}>
                {menuIcon("friends")}<span className="settings-row__label">{t("groups.inviteByCodeTitle")}</span><span className="settings-row__chevron">›</span>
              </button>
            </div>
          </section>

          {/* Žádosti o vstup */}
          {groupInvites && groupInvites.length > 0 && (
            <section className="settings-section">
              <h3 className="settings-section__title">{t("groups.pendingTitle")}</h3>
              <div className="settings-group">
                {groupInvites.map(inv => (
                  <div key={inv.invite_id} className="settings-row">
                    <div className="settings-row__text">
                      <span className="settings-row__label">{inv.requester_name || '—'}</span>
                      <span className="settings-row__hint">{t("groups.statusRequested")}</span>
                    </div>
                    <div className="group-row-actions">
                      <button className="pill-btn pill-btn--accept" onClick={() => approveGroupInvite(selectedGroup.gid, inv.invite_id)}>{t("groups.approve")}</button>
                      <button className="pill-btn pill-btn--ghost" onClick={() => declineGroupInvite(selectedGroup.gid, inv.invite_id)}>{t("groups.decline")}</button>
                    </div>
                  </div>
                ))}
              </div>
              {/* The reason, where the button that produced it is. A toast would
                  be gone before the owner finished reading a sentence about
                  seats and Premium. */}
              {groupApproveMsg && (
                <p className="settings-row__hint group-approve-msg">{groupApproveMsg}</p>
              )}
            </section>
          )}

          {/* Členové — samostatné karty oddělené mezerou */}
          <section className="settings-section">
            <h3 className="settings-section__title">{t("groups.membersTitle")}</h3>
            <div className="group-members">
              {selectedGroup.members.map(m => (
                <div key={m.uid} className="group-member">
                  <div className="group-member__info">
                    {/* Ve správě skupiny bez prahu a u každého: sem se člověk
                        dívá právě proto, aby viděl, kdo ještě hraje. V tabulce
                        dne se to říká až od týdne — tam by denní „naposledy
                        včera" u všech udělalo z hry dohlížecí panel. */}
                    <span className="group-member__name">
                      {m.display_name || '—'}
                      {(m.role === 'owner' || m.uid === currentUser?.uid || m.sleeping) && (
                        <span className="group-member__hint">
                          {[
                            m.role === 'owner' ? t("groups.owner") : null,
                            m.uid === currentUser?.uid ? t("groups.you") : null,
                            // Proč na něj nikdy nevyjde řada. Bez toho to vypadá
                            // jako porucha rotace.
                            m.sleeping ? t("groups.sleeping") : null,
                          ].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </span>
                    {awayLabel(m.uid, m.joined_at, 0, true) && (
                      <span className="group-member__away">{awayLabel(m.uid, m.joined_at, 0, true)}</span>
                    )}
                    {!sellsPremium ? null : m.premium ? (
                      <span className="member-premium"><CrownIcon size={12} />Premium</span>
                    ) : (
                      // Jen příznak. „Darovat Premium" tu bylo do 6. 10. 2026 —
                      // ve správě skupiny nedávalo smysl; darovat jde z Přátel.
                      <span className="member-free">{t("premium.status.free")}</span>
                    )}
                  </div>
                  <div className="group-row-actions">
                    {m.role !== 'owner' && m.uid !== currentUser?.uid && selectedGroup.owner_uid === currentUser?.uid && (
                      <button className="pill-btn pill-btn--ghost" onClick={() => setConfirmAction({ message: t("groups.removeMemberConfirm"), confirmLabel: t("groups.removeMember"), onConfirm: () => kickMember(m.uid) })}>{t("groups.removeMember")}</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* The group's own switch. Separate from the language-wide one in the
              admin panel: this is the group deciding for itself. */}
          <section className="settings-section">
            <h3 className="settings-section__title">{t("groupWord.title")}</h3>
            <div className="settings-group">
              <div className="settings-row">
                <div className="settings-row__text">
                  <span className="settings-row__label">{t("groupWord.switchLabel")}</span>
                  <span className="settings-row__hint">{t("groupWord.switchHint")}</span>
                </div>
                <button className={`pref-switch${groupWordsOn ? ' is-on' : ''}`} role="switch"
                  aria-checked={groupWordsOn} aria-label={t("groupWord.switchLabel")}
                  disabled={groupWordsBusy}
                  onClick={() => {
                    const gid = selectedGroup.gid;
                    // Switching off PAUSES a running round (since 3. 10. 2026 — it
                    // used to end it and show everyone the word; a founder flicked
                    // it off and on in 1.4 s and twenty-two players lost a round).
                    // Still asked first, because nobody can guess while it is off.
                    // Asked also while the round is not known yet.
                    const roundMayRun = roundForGid !== gid || !!groupRound;
                    if (groupWordsOn && roundMayRun) {
                      setConfirmAction({
                        message: t("groupWord.switchOffConfirm"),
                        confirmLabel: t("groupWord.switchOffCta"),
                        onConfirm: () => setGroupWordsSwitch(gid, false),
                      });
                      return;
                    }
                    setGroupWordsSwitch(gid, !groupWordsOn);
                  }}>
                  <span className="pref-switch__track"><span className="pref-switch__thumb" /></span>
                </button>
              </div>
              {groupWordsOn && selectedGroup.owner_uid === currentUser?.uid && (
                <div className="settings-row">
                  <div className="settings-row__text">
                    <span className="settings-row__label">{t("groupWord.rotateLabel")}</span>
                    <span className="settings-row__hint">{t("groupWord.rotateHint")}</span>
                  </div>
                  <button className={`pref-switch${setterRotates ? ' is-on' : ''}`} role="switch"
                    aria-checked={setterRotates} aria-label={t("groupWord.rotateLabel")}
                    disabled={groupWordsBusy}
                    onClick={() => setSetterModeSwitch(selectedGroup.gid, setterRotates ? 'owner' : 'rotation')}>
                    <span className="pref-switch__track"><span className="pref-switch__thumb" /></span>
                  </button>
                </div>
              )}
              {groupWordsOn && selectedGroup.owner_uid === currentUser?.uid && (
                <div className="settings-row settings-row--tema">
                  <div className="settings-row__text">
                    <span className="settings-row__label">{t("groups.temaLabel")}</span>
                    <span className="settings-row__hint">{t("groups.temaHint")}</span>
                    {selectedGroup.tema && <span className="settings-row__value settings-row__value--set">{selectedGroup.tema}</span>}
                    {temaKolaJine(selectedGroup.gid, selectedGroup.tema) && (
                      <span className="settings-row__hint settings-row__hint--tema-kola">{temaKolaVeta()}</span>
                    )}
                  </div>
                  <div className="settings-row__actions">
                    <button className="settings-btn-secondary"
                      onClick={() => otevritTema(selectedGroup.gid)}>
                      {t(selectedGroup.tema ? "groups.temaChange" : "groups.temaSetup")}
                    </button>
                    {selectedGroup.tema && (
                      <button className="settings-btn-secondary" onClick={() => ulozTema(selectedGroup.gid, null)}>
                        {t("groups.temaRemove")}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Nastavení skupiny — přejmenovat je normální tlačítko */}
          <section className="settings-section">
            <h3 className="settings-section__title">{t("groups.settingsTitle")}</h3>
            <div className="group-settings-actions">
              <button className="settings-btn-secondary" onClick={() => { setRenameGroupMsg(''); setRenameGroupName(selectedGroup.name); setSocialModal('renameGroup'); }}>
                {t("groups.rename")}
              </button>
              <button className="settings-btn-danger" onClick={() => setConfirmAction({ message: t("groups.deleteConfirm"), confirmLabel: t("groups.delete"), onConfirm: () => deleteGroup(selectedGroup.gid) })}>
                {t("groups.delete")}
              </button>
            </div>
          </section>
        </div>
      )}

      {/* Social action modals (add friend / create group / join / invite) */}
      {/* Checkout error raised outside the Premium page (e.g. gifting from a
          group). A brief banner so a 503 "not live yet" isn't a silent no-op.
          On the Premium page the error shows inline instead. */}
      {checkoutError && currentPage !== 'premium' && (
        <div className="checkout-toast" onClick={() => setCheckoutError(null)}>
          {t(`premium.error.${checkoutError}`, { defaultValue: t("premium.error.checkout_failed") })}
        </div>
      )}

      {/* Premium upgrade prompt. Opens when a gated feature answers 402, and names
          what the player just reached for so the pitch is concrete, not generic. */}

      {ownCheckout && (
        <Suspense fallback={null}>
          <OwnCheckout
            order={ownCheckout}
            price={fmtPrice(ownCheckout.plan)}
            createSession={createOwnSession}
            onClose={() => setOwnCheckout(null)}
            t={t}
          />
        </Suspense>
      )}

      {socialModal && (
        <div className="modal" onClick={(e) => { if (e.target.classList.contains('modal')) setSocialModal(null); }}>
          <div className="modal-content social-modal">
            <div className="modal-header">
              <h2 style={{ textTransform: 'none' }}>{t(
                socialModal === 'addFriend' ? "friends.addTitle"
                : socialModal === 'myCode' ? "friends.sendCodeTitle"
                : socialModal === 'inviteGroupEmail' ? "groups.inviteEmailTitle"
                : socialModal === 'inviteGroup' ? "groups.groupCodeTitle"
                : socialModal === 'createGroup' ? "groups.createTitle"
                : socialModal === 'setGroupWord'
                  ? (takingOver ? "groupWord.takeOverTitle"
                     : myWordMove?.day === "tomorrow" ? "groupWord.setTitleTomorrow" : "groupWord.setTitle")
                : socialModal === 'renameGroup' ? "groups.renameTitle"
                : socialModal === 'groupTema' ? "groups.temaTitle"
                : socialModal === 'inviteByCode' ? "groups.inviteByCodeTitle"
                : socialModal === 'redeemGift' ? "gift.redeemTitle"
                : "groups.joinTitle"
              )}</h2>
              <span className="close" onClick={() => setSocialModal(null)}>&times;</span>
            </div>

            {socialModal === 'addFriend' && (<>
              {/* Email is the primary way in — it reaches people who don't play yet.
                  The friend code stays below as the "already plays" shortcut. */}
              <input className="social-modal__input" autoFocus type="email" value={addEmailInput}
                onChange={(e) => { setAddEmailInput(e.target.value); setAddEmailMsg(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') inviteFriendByEmail(); }}
                placeholder={t("friends.emailPlaceholder")} />
              {addEmailMsg && (
                <p className={`social-modal__msg${addEmailMsg.status === 'invited' ? ' social-modal__msg--ok' : ''}`}>
                  {t(`friends.emailResult.${addEmailMsg.status}`, { defaultValue: t("friends.emailResult.error") })}
                </p>
              )}
              <button className="pill-btn pill-btn--accept social-modal__submit" disabled={addEmailBusy || !addEmailInput.trim()} onClick={inviteFriendByEmail}>{t("friends.emailButton")}</button>
              <p className="social-modal__or">{t("friends.orCode")}</p>
              <input className="social-modal__input" type="text" value={addCodeInput} maxLength={12}
                onChange={(e) => setAddCodeInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') sendFriendRequest(); }}
                placeholder={t("friends.addPlaceholder")} />
              <Msg value={addFriendMsg} className="social-modal__msg" />
              <button className="pill-btn pill-btn--accept social-modal__submit" disabled={!addCodeInput.trim()} onClick={sendFriendRequest}>{t("friends.addButton")}</button>
            </>)}

            {socialModal === 'myCode' && (<>
              <div className="my-code-block" style={{ margin: 0, border: 'none', background: 'none', padding: 0 }}>
                <code className="friend-code friend-code--big">{friendCode || '…'}</code>
                <button className="pill-btn pill-btn--accept social-modal__submit" disabled={!friendCode} onClick={copyFriendCode}>
                  {friendCodeCopied ? t("friends.copied") : t("friends.copy")}
                </button>
                <p className="social-hint" style={{ margin: 0, textAlign: 'center' }}>{t("friends.codeHint")}</p>
              </div>
            </>)}

            {socialModal === 'inviteGroup' && selectedGroup && (<>
              <div className="my-code-block" style={{ margin: 0, border: 'none', background: 'none', padding: 0 }}>
                <code className="friend-code friend-code--big">{selectedGroup.invite_code || '…'}</code>
                <button className="pill-btn pill-btn--accept social-modal__submit" disabled={!selectedGroup.invite_code} onClick={copyGroupCode}>
                  {groupCodeCopied ? t("friends.copied") : t("friends.copy")}
                </button>
                <p className="social-hint" style={{ margin: 0, textAlign: 'center' }}>{t("groups.inviteCodeHint")}</p>
              </div>
            </>)}

            {socialModal === 'inviteGroupEmail' && selectedGroup && (<>
              <input className="social-modal__input" autoFocus type="email" value={inviteEmailInput}
                onChange={(e) => { setInviteEmailInput(e.target.value); setInviteEmailMsg(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') inviteGroupByEmail(selectedGroup.gid); }}
                placeholder={t("friends.emailPlaceholder")} />
              {inviteEmailMsg && (
                <p className={`social-modal__msg${inviteEmailMsg.status === 'invited' ? ' social-modal__msg--ok' : ''}`}>
                  {t(`groups.emailResult.${inviteEmailMsg.status}`, { defaultValue: t("groups.emailResult.error") })}
                </p>
              )}
              <p className="social-hint" style={{ textAlign: 'center' }}>{t("groups.emailHint")}</p>
              <button className="pill-btn pill-btn--accept social-modal__submit" disabled={inviteEmailBusy || !inviteEmailInput.trim()} onClick={() => inviteGroupByEmail(selectedGroup.gid)}>{t("groups.emailButton")}</button>
            </>)}

            {socialModal === 'createGroup' && (<>
              <input className="social-modal__input" autoFocus type="text" value={createGroupName} maxLength={40}
                onChange={(e) => setCreateGroupName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') createGroup(); }}
                placeholder={t("groups.createPlaceholder")} />
              <Msg value={createGroupMsg} className="social-modal__msg" />
              <button className="pill-btn pill-btn--accept social-modal__submit" onClick={createGroup}>{t("groups.createButton")}</button>
            </>)}

            {socialModal === 'setGroupWord' && (<>
              {/* Pilly nad polem pod sebou: svět skupiny, a když slovo může jít
                  jen na zítřek, šedý „Zítra · od 3:00" (od 21. 10. 2026 tady,
                  dřív pod polem s větou o důvodu). */}
              {/* U převzetí správcem den vždy („Dnes" / „Zítra · od 3:00"), ať je
                  na stejném místě jako v běžném okně (21. 10. 2026). */}
              {(() => {
                const den = takingOver
                  ? (roundTurn?.todayUsed ? "groupWord.dayTomorrow" : "groupWord.dayToday")
                  : myWordMove?.day === "tomorrow" ? "groupWord.dayTomorrow" : null;
                return (selectedGroup?.tema || den) && (
                  <div className="social-modal__pills">
                    <TemaReminder tema={selectedGroup?.tema} t={t} />
                    {den && <p className="tema-group-pill tema-group-pill--day">{t(den)}</p>}
                  </div>
                );
              })()}
              <input className="social-modal__input social-modal__input--word" autoFocus type="text" value={setWordInput} maxLength={20}
                onChange={(e) => setSetWordInput(e.target.value)}
                onKeyDown={(e) => {
                  // Enter = totéž, co tlačítko pod polem v dané větvi (dřív vždy "now").
                  if (e.key === 'Enter') submitGroupWord(takingOver ? "now" : myWordMove?.day === "tomorrow" ? "tomorrow" : wordWhen);
                }}
                placeholder={t("groupWord.placeholder")} />
              {/* With today's word already used, or a round still running, there
                  is only one thing this form can do. A choice with one outcome is
                  not a choice — so it says which day it is about and offers one
                  button, rather than asking. */}
              {takingOver ? (
                <>
                  {/* Co se stane kromě otevření kola. Bez toho by to vypadalo, že
                      se řada jen obchází — přitom se tomu, kdo neodpověděl,
                      zapisuje přeskočený tah, aby zítra vyšla na dalšího. */}
                  {/* Dnes už skupina hrála? Pak tohle slovo nemůže otevřít nic
                      dnešního a jde stranou na ráno — a modal to musí říct
                      PŘEDEM. Do 23. 9. 2026 tu svítil chip „Dnes", server slovo
                      odložil na zítřek a správkyně dostala dvě různé odpovědi na
                      jeden klik. Posílá se dál "now": „na zítra" je zápis do
                      fronty za SEBE, tohle je zápis za skupinu. */}
                  {/* Den je pill nahoře pod světem; tady jen co se stane. Od 21. 10.
                      2026 bez „Pokud Klára nechal…": hra rod hráčů nezná. */}
                  <p className="social-modal__explain">
                    {t(roundTurn?.todayUsed ? "groupWord.takeOverExplainsTomorrow" : "groupWord.takeOverExplains",
                      { name: roundTurn?.name || '—' })}
                  </p>
                  <Msg value={setWordMsg} className="social-modal__msg" />
                  <button className="pill-btn pill-btn--accept social-modal__submit"
                    onClick={() => submitGroupWord("now")}>
                    {t(roundTurn?.todayUsed ? "groupWord.takeOverSubmitTomorrow" : "groupWord.takeOverSubmit")}
                  </button>
                </>
              ) : myWordMove?.day === "tomorrow" ? (
                <>
                  {/* The day as a chip, the same one the panel bands carry — it
                      was a second grey paragraph of the same weight as the rule
                      above it, and the two read as a wall. */}
                  {/* Den je šedý pill nad polem (pod světem); věta o důvodu
                      („Dnes se hraje jiné slovo.") od 21. 10. 2026 pryč. */}
                  <Msg value={setWordMsg} className="social-modal__msg" />
                  <button className="pill-btn pill-btn--accept social-modal__submit"
                    onClick={() => submitGroupWord("tomorrow")}>{t("groupWord.setSubmitTomorrow")}</button>
                </>
              ) : (
                <>
                  {/* Asked for on 2026-09-06: "měl by mít možnost udělat to
                      dopředu na další den, ne jen v ten den." Until now an
                      evening word opened at once and died at 03:00, and the
                      group woke up with nothing again.
                      Od 21. 10. 2026 přepínač dne + jedno tlačítko místo dvou
                      velkých dvouřádkových: okno bylo vysoké a rozházené. */}
                  <div className="social-modal__when">
                    <div className="social-modal__when-opts" role="radiogroup" aria-label={t("groupWord.setTitle")}>
                      {[["now", "groupWord.setNow"], ["tomorrow", "groupWord.setTomorrow"]].map(([v, k]) => (
                        <button key={v} type="button" role="radio" aria-checked={wordWhen === v}
                          className={`social-modal__when-opt${wordWhen === v ? " is-on" : ""}`}
                          onClick={() => setWordWhen(v)}>
                          {t(k)}
                        </button>
                      ))}
                    </div>
                    <p className="social-modal__when-note">
                      {wordWhen === "now"
                        ? t("groupWord.setNowNote", { left: formatLeft(msToRotation()) })
                        : t("groupWord.setTomorrowNote")}
                    </p>
                  </div>
                  <Msg value={setWordMsg} className="social-modal__msg" />
                  <button className="pill-btn pill-btn--accept social-modal__submit"
                    onClick={() => submitGroupWord(wordWhen)}>
                    {t("groupWord.setCta")}
                  </button>
                </>
              )}
            </>)}

            {socialModal === 'groupTema' && temaState && (<>
              {/* Pro kterou skupinu: okno se otvírá i ze seznamu skupin a z boxu
                  s novinkou, kde to z okolí není poznat. Fialová = skupiny. */}
              {temaJmeno && <p className="tema-group-pill" title={temaJmeno}>{t("groups.temaGroupPill", { name: temaJmeno })}</p>}
              {temaState.mode === "offer" && temaState.navrh ? (<>
                {/* Stejná podoba jako ruční okno: krátký úvod, rámeček s tím, co se
                    uloží, a tlačítka. Úvodní věta je pevná (model psal „Vypad to,
                    ye budete…" a název neuměl skloňovat — staging, 7. 10. 2026),
                    název světa je jen v rámečku, kde se neskloňuje. */}
                <p className="social-modal__lead">{t("groups.temaOfferLead")}</p>
                <div className="tema-preview">
                  <span className="tema-preview__label">{t("groups.temaWillUse")}</span>
                  <strong className="tema-preview__value">{temaState.navrh.popis}</strong>
                </div>
                {temaKolaJine(temaState.gid, temaState.navrh.popis) && (
                  <p className="social-modal__explain">{temaKolaVeta()}</p>
                )}
                <Msg value={temaState.msg} className="social-modal__msg" />
                <button className="pill-btn pill-btn--accept social-modal__submit" disabled={temaState.busy}
                  onClick={() => ulozTema(temaState.gid, temaState.navrh.popis, temaState.navrh.podpis)}>{t("groups.temaSaveYes")}</button>
                {/* Model trefil špatný svět → rovnou ruční zadání. */}
                <button className="pill-btn pill-btn--ghost social-modal__submit" disabled={temaState.busy}
                  onClick={() => setTemaState({ ...temaState, mode: "edit", text: "", navrh: null, msg: "" })}>{t("groups.temaRetry")}</button>
                <button className="pill-btn pill-btn--ghost social-modal__submit" disabled={temaState.busy}
                  onClick={() => { setTemaState(null); setSocialModal(null); }}>{t("groups.temaNoThanks")}</button>
              </>) : (<>
                {/* Co se s textem stane: z něj se teprve najde název světa, a ten
                    (ne text sám) jde do hodnocení. Druhý odstavec o pokynech a
                    „cokoli jiného" byl pryč 6. 10. 2026 — při prvním vyzkoušení
                    mátl víc, než pomáhal. */}
                <p className="social-modal__lead">{t("groups.temaEditLead")}</p>
                {/* Bez autoFocus: nejdřív si má správce přečíst, co se s textem stane
                    (a na telefonu by klávesnice text nad polem zakryla).
                    Bez doplňování (23. 10. 2026): prohlížeč nabízel dřív zadaný svět
                    a jedno ťuknutí ho mohlo vrátit — podezření u skupiny, kde se
                    střídal Harry Potter s „lidským tělem". */}
                <input className="social-modal__input" type="text" value={temaState.text} maxLength={60}
                  autoComplete="off" autoCorrect="off" spellCheck={false} name="tema-skupiny"
                  onChange={(e) => setTemaState({ ...temaState, text: e.target.value, navrh: null, msg: "" })}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !temaState.busy) navrhnoutZTextu(); }}
                  placeholder={t("groups.temaPlaceholder")} />
                {/* Hláška nad tlačítky, jako v ostatních oknech — pod nimi se lepila. */}
                <Msg value={temaState.msg} className="social-modal__msg" />
                {!temaState.navrh && (
                  <button className="pill-btn pill-btn--accept social-modal__submit" disabled={temaState.busy || !temaState.text.trim()}
                    onClick={navrhnoutZTextu}>{t("groups.temaSuggest")}</button>
                )}
                {temaState.navrh && (<>
                  {/* Co přesně se uloží — model z „Ze světa Harryho Pottera" udělá
                      „Harry Potter" a správce to musí vidět dřív, než klikne. */}
                  <div className="tema-preview">
                    <span className="tema-preview__label">{t("groups.temaWillUse")}</span>
                    <strong className="tema-preview__value">{temaState.navrh.popis}</strong>
                  </div>
                  {temaKolaJine(temaState.gid, temaState.navrh.popis) && (
                    <p className="social-modal__explain">{temaKolaVeta()}</p>
                  )}
                  <button className="pill-btn pill-btn--accept social-modal__submit" disabled={temaState.busy}
                    onClick={() => ulozTema(temaState.gid, temaState.navrh.popis, temaState.navrh.podpis)}>{t("groups.temaSaveYes")}</button>
                  <button className="pill-btn pill-btn--ghost social-modal__submit" disabled={temaState.busy}
                    onClick={() => setTemaState({ ...temaState, navrh: null, msg: "" })}>{t("groups.temaRetry")}</button>
                </>)}
              </>)}
            </>)}

            {socialModal === 'renameGroup' && (<>
              <input className="social-modal__input" autoFocus type="text" value={renameGroupName} maxLength={40}
                onChange={(e) => setRenameGroupName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') renameGroup(); }}
                placeholder={t("groups.createPlaceholder")} />
              <Msg value={renameGroupMsg} className="social-modal__msg" />
              <button className="pill-btn pill-btn--accept social-modal__submit" onClick={renameGroup} disabled={renameGroupBusy}>{renameGroupBusy ? t("groups.renameBusy") : t("groups.rename")}</button>
            </>)}

            {socialModal === 'joinGroup' && (<>
              <input className="social-modal__input" autoFocus type="text" value={joinGroupCode} maxLength={12}
                onChange={(e) => setJoinGroupCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') joinGroupByCode(); }}
                placeholder={t("groups.joinPlaceholder")} />
              <Msg value={joinGroupMsg} className="social-modal__msg" />
              <button className="pill-btn pill-btn--accept social-modal__submit" onClick={() => joinGroupByCode()}>{t("groups.joinButton")}</button>
            </>)}

            {socialModal === 'inviteByCode' && selectedGroup && (<>
              <input className="social-modal__input" autoFocus type="text" value={inviteCode} maxLength={12}
                autoCapitalize="characters"
                onChange={(e) => { setInviteCode(e.target.value); setInviteResult(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') inviteByFriendCode(selectedGroup.gid, inviteCode); }}
                placeholder={t("groups.inviteByCodePlaceholder")} />
              {inviteResult && (
                <p className={`social-modal__msg${inviteResult === 'invited' ? ' social-modal__msg--ok' : ''}`}>
                  {t(`groups.inviteResult.${inviteResult}`, { defaultValue: t("groups.inviteResult.error") })}
                </p>
              )}
              <button className="pill-btn pill-btn--accept social-modal__submit" disabled={inviteBusy || !inviteCode.trim()} onClick={() => inviteByFriendCode(selectedGroup.gid, inviteCode)}>{t("groups.inviteButton")}</button>
            </>)}

            {/* Po úspěchu už jen potvrzení a zavřít — znovu „Uplatnit" by mátlo. */}
            {socialModal === 'redeemGift' && giftRedeemMsg?.ok && (<>
              <p className="social-modal__msg social-modal__msg--ok">{t("gift.redeemOk")}</p>
              <button className="pill-btn pill-btn--accept social-modal__submit" onClick={() => setSocialModal(null)}>{t("buttons.close")}</button>
            </>)}
            {socialModal === 'redeemGift' && !giftRedeemMsg?.ok && (<>
              <input className="social-modal__input" autoFocus type="text" value={giftCodeInput} maxLength={16}
                autoCapitalize="characters"
                onChange={(e) => { setGiftCodeInput(e.target.value.toUpperCase()); setGiftRedeemMsg(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') redeemGiftCode(giftCodeInput); }}
                placeholder="GIFT-XXXX-XXXX" />
              {giftRedeemMsg && (
                <p className="social-modal__msg">
                  {t(`gift.error.${giftRedeemMsg.error}`, { defaultValue: t("gift.error.generic") })}
                </p>
              )}
              <button className="pill-btn pill-btn--accept social-modal__submit" disabled={giftRedeemBusy || !giftCodeInput.trim()} onClick={() => redeemGiftCode(giftCodeInput)}>{t("gift.redeemButton")}</button>
            </>)}

          </div>
        </div>
      )}

      {/* Contact form (footer) — posts to /contact, emailed to the owner. */}
      {contactOpen && (
        <div className="modal" onClick={(e) => { if (e.target.classList.contains('modal')) setContactOpen(false); }}>
          <div className="modal-content social-modal">
            <div className="modal-header">
              <h2 style={{ textTransform: 'none' }}>{t("contact.title")}</h2>
              <span className="close" onClick={() => setContactOpen(false)}>&times;</span>
            </div>
            {contactMsg && contactMsg.ok ? (
              <p className="social-modal__msg social-modal__msg--ok">
                {t("contact.sent")}{isPaid ? ` ${t("contact.priority.sent")}` : ''}
              </p>
            ) : (<>
              {/* A subscriber is answered first, and the backend marks the mail
                  from their token rather than from anything sent here. Saying so
                  ON the form is the point: an advantage nobody is told about is an
                  advantage they are paying for and cannot see. */}
              {isPaid && (
                <p className="contact-priority">{t("contact.priority.note")}</p>
              )}
              {/* Většina zpráv, co chodí, jsou tři pořád stejné otázky o teplotě
                  — a na všechny tři je odpověď ve FAQ. Odkaz musí být tlačítko,
                  ne jen věta: poslat člověka „do častých otázek" a nechat ho je
                  hledat je totéž jako ho tam neposlat. Modal se přitom zavře,
                  jinak by FAQ otevřelo pod ním. */}
              <div className="contact-faq-first">
                <p className="contact-faq-first__q">{t("contact.faqFirst.q")}</p>
                <p className="contact-faq-first__lead">{t("contact.faqFirst.lead")}</p>
                <button
                  className="link-button"
                  onClick={() => { setContactOpen(false); goPage('faq'); }}
                >
                  {t("contact.faqFirst.cta")}
                </button>
              </div>
              <input className="social-modal__input" type="text" placeholder={t("contact.name")}
                value={contactForm.name} onChange={(e) => setContactForm(f => ({ ...f, name: e.target.value }))} />
              <input className="social-modal__input" type="email" placeholder={t("contact.email")}
                value={contactForm.email} onChange={(e) => setContactForm(f => ({ ...f, email: e.target.value }))} />
              <textarea className="social-modal__input" rows={5} placeholder={t("contact.message")}
                value={contactForm.message} onChange={(e) => setContactForm(f => ({ ...f, message: e.target.value }))} />
              {/* honeypot — hidden from real users */}
              <input tabIndex={-1} autoComplete="off" aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
                value={contactForm.website} onChange={(e) => setContactForm(f => ({ ...f, website: e.target.value }))} />
              {contactMsg && !contactMsg.ok && (
                <p className="social-modal__msg">{t(`contact.error.${contactMsg.error}`, { defaultValue: t("contact.error.error") })}</p>
              )}
              <button className="pill-btn pill-btn--accept social-modal__submit"
                disabled={contactBusy}
                onClick={submitContact}>{t("contact.send")}</button>
            </>)}
          </div>
        </div>
      )}

      {/* Nahlásit chybu — posts to /bug_report, screenshot attached to the mail. */}
      {/* Výpadek 9.–12. 10. 2026 — jednou každému, od 15. 10. nikomu; pak smazat. */}
      <OutageNotice />

      {/* Výpadek 1.–2. 10. 2026 — jednou každému, od 9. 10. nikomu; pak smazat. */}

      {bugOpen && (
        <div className="modal" onClick={(e) => { if (e.target.classList.contains('modal')) setBugOpen(false); }}>
          <div className="modal-content social-modal">
            <div className="modal-header">
              <h2 style={{ textTransform: 'none' }}>{t("bug.title")}</h2>
              <span className="close" onClick={() => setBugOpen(false)}>&times;</span>
            </div>
            {bugMsg && bugMsg.ok ? (
              <p className="social-modal__msg social-modal__msg--ok">
                {t("bug.sent")}{isPaid ? ` ${t("contact.priority.sent")}` : ''}
              </p>
            ) : (<>
              {/* Same promise as the contact form, same mechanism behind it: the
                  mail is marked from their token, not from this form. */}
              {isPaid && (
                <p className="contact-priority">{t("contact.priority.note")}</p>
              )}
              <p className="social-modal__lead">{t("bug.lead")}</p>
              <textarea className="social-modal__input" rows={5} placeholder={t("bug.description")}
                value={bugForm.description} onChange={(e) => setBugForm(f => ({ ...f, description: e.target.value }))} />

              {/* A label wrapping a hidden input: the native file button cannot be
                  styled, and this keeps it operable by keyboard all the same. */}
              <label className="bug-attach">
                <input type="file" accept="image/*" className="bug-attach__input"
                  onChange={(e) => { pickBugImage(e.target.files?.[0]); e.target.value = ''; }} />
                <span>📎 {bugImage ? t("bug.imageChange") : t("bug.imageAdd")}</span>
              </label>
              {bugImage && (
                <div className="bug-preview">
                  <img src={bugImage.dataUrl} alt={t("bug.imageAlt")} className="bug-preview__img" />
                  <button type="button" className="bug-preview__remove"
                    onClick={() => setBugImage(null)}>{t("bug.imageRemove")}</button>
                </div>
              )}

              <input className="social-modal__input" type="email" placeholder={t("bug.emailOptional")}
                value={bugForm.email} onChange={(e) => setBugForm(f => ({ ...f, email: e.target.value }))} />
              {/* Said before sending, not after: the diagnostics below travel with
                  the report and they are about the player's own device. */}
              <p className="social-modal__note">{t("bug.contextNote")}</p>
              {/* honeypot — hidden from real users */}
              <input tabIndex={-1} autoComplete="off" aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
                value={bugForm.website} onChange={(e) => setBugForm(f => ({ ...f, website: e.target.value }))} />
              {bugMsg && !bugMsg.ok && (
                <p className="social-modal__msg">{t(`bug.error.${bugMsg.error}`, { defaultValue: t("bug.error.error") })}</p>
              )}
              <button className="pill-btn pill-btn--accept social-modal__submit"
                disabled={bugBusy}
                onClick={submitBugReport}>{t("bug.send")}</button>
            </>)}
          </div>
        </div>
      )}

      {/* Generic confirmation for destructive actions (leave / kick / remove friend) */}
      {confirmAction && (
        <div className="modal" onClick={(e) => { if (e.target.classList.contains('modal')) setConfirmAction(null); }}>
          <div className="modal-content social-modal">
            <div className="modal-header">
              <h2 style={{ textTransform: 'none' }}>{confirmAction.title || confirmAction.confirmLabel}</h2>
              <span className="close" onClick={() => setConfirmAction(null)}>&times;</span>
            </div>
            {confirmAction.sections ? (
              <div className="confirm-sections">
                {confirmAction.sections.map((s, i) => (
                  <div key={i} className="confirm-section">
                    <span className="confirm-section__label">{s.label}</span>
                    <span className="confirm-section__value">{s.value}</span>
                  </div>
                ))}
                {confirmAction.note && <p className="confirm-note">{confirmAction.note}</p>}
              </div>
            ) : (
              <p style={{ margin: '0 0 1.3rem', color: 'var(--text-dim)' }}>{confirmAction.message}</p>
            )}
            <div className="confirm-actions">
              <button className="settings-btn-secondary" onClick={() => setConfirmAction(null)}>{t("buttons.cancel")}</button>
              <button className={confirmAction.tone === 'primary' ? "settings-btn-primary" : "settings-btn-danger"} onClick={() => { const a = confirmAction; setConfirmAction(null); a.onConfirm(); }}>{confirmAction.confirmLabel}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── FAQ page (moved off the home page): how-to-play on top, then FAQ. ── */}
      {currentPage === 'faq' && (
        <div className="profile-page settings-page faq-page">
          {FAQ_SECTIONS.map((section) => {
            // The benefit rows and the account CTA sit behind ANOCINE_BENEFIT,
            // which no production build turns on. This section was not, so a
            // Czech production build answered questions about a game it does
            // not otherwise mention — including how much of it is free, which
            // is a promise Anočine's own gate is not yet keeping.
            if (section.key === "anocine" && !ANOCINE_BENEFIT) return null;
            // Only what this market actually has wording for.
            const items = section.items
              .map((it) => (typeof it === "string"
                ? { title: `faq.${it}.title`, content: [`faq.${it}.content`], id: it }
                : { ...it, id: it.title }))
              .filter((it) => i18n.exists(it.title))
              .filter((it) => sellsPremium || !PAID_FAQ_ITEMS.has(it.id));
            if (!items.length || !i18n.exists(`faq.section.${section.key}`)) return null;
            return (
              <div className="content-section" key={section.key}>
                <h2 className="content-heading">{t(`faq.section.${section.key}`)}</h2>
                {items.map((it) => (
                  <div className="faq-collapsible" key={it.id}>
                    {/* All closed. Nine sections opened by default would be a wall
                        of text with extra clicks in it; the headings are the map. */}
                    <h3 className="faq-title" onClick={toggleCollapse}>{t(it.title)}</h3>
                    <div className="faq-content">
                      {it.content.flatMap((c) =>
                        faqOdstavce(t(c)).map((kusy, oi) => (
                          <p key={`${c}-${oi}`}>
                            {kusy.map((k, ki) =>
                              k.tucne ? <strong key={ki}>{k.text}</strong> : k.text,
                            )}
                          </p>
                        )),
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* The legal documents are no longer screens: every market has its own
          published page at its own URL (see LEGAL_LINKS), which is what the
          footer links to. */}

      {currentPage === 'game' && (
      <>

      {isGuessed && !weeklyIsGuessed && activeTab === "daily" && (
        <div className={`mega-cta${megaCtaReady ? " mega-cta--visible" : ""}`}>
          <p className="mega-cta-text">{t("game.header.megaWordUnlocked")}</p>
          <button className="mega-cta-btn mega-water-btn" onClick={() => setActiveTab("weekly")}>
            <span className="mega-water-btn__text">{t("buttons.guessMegaWord")}</span>
          </button>
        </div>
      )}

      {/* Nothing here once the mega word is won too — the CTA only has a job while
          there is still something to go and guess. */}

      {/* ===== DAILY TAB ===== */}
      {activeTab === "daily" && (
      <div className="tab-content">

      {isGuessed ? (
        <>
          <div className="success-message">
            <div className="success-win-summary">
              🎉 <br />{t("game.success.wordGuessed")}
              <div className="guessed-word-row">
                <strong className={`guessed-word-value${wordsHidden ? ' guessed-word-value--hidden' : ''}`}>
                  {maskWord(guessedWord)}
                </strong>
                {/* The same switch as the header eye and the settings toggle.
                    It stays here because this is where a winner looks before
                    sharing — it is the reachable one, not a lesser one. */}
                <button
                  type="button"
                  className="word-visibility-btn"
                  onClick={() => setWordsHidden(v => !v)}
                  aria-label={wordsHidden ? t("word.visibility.show") : t("word.visibility.hide")}
                  title={wordsHidden ? t("word.visibility.showShort") : t("word.visibility.hideShort")}
                >
                  {renderEyeIcon(wordsHidden)}
                </button>
              </div>
              {t("game.success.attempts", { count: dailyCount, attempts: pluralPokusy(dailyCount) })}!
            </div>
            {/* Žebříček: TOP10 + moje pozice (pokud mimo TOP10) */}
            {features.leaderboard && !loadingAuth && currentUser && (() => {
              // Prefer leaderboard if it has registered (non-anonymous) users,
              // otherwise fall back to dailyRank.top10 returned directly from /similarity win response
              const lbRegistered = (leaderboard || []).filter(r => !r.uid?.startsWith('user_'));
              const lbSource = lbRegistered.length > 0 ? leaderboard : (dailyRank?.top10 || []);
              const allWinners = lbSource.filter(r => r.won !== false && !r.uid?.startsWith('user_'));
              if (allWinners.length === 0) return null;
              
              // Reassign ranks 1, 2, 3... for registered users only (dense ranking by guesses)
              const sortedByGuesses = [...allWinners].sort((a, b) => a.guesses - b.guesses);
              const guessesToNewRank = {};
              let currentRank = 1;
              for (const player of sortedByGuesses) {
                if (guessesToNewRank[player.guesses] === undefined) {
                  guessesToNewRank[player.guesses] = currentRank++;
                }
              }
              allWinners.forEach(player => { player.newRank = guessesToNewRank[player.guesses]; });
              
              const myIdx = allWinners.findIndex(r => r.uid === currentUser.uid);
              const myRank = myIdx >= 0 ? allWinners[myIdx].newRank : null;

              // DEBUG: Log to check if user is found
              console.log('🔍 Leaderboard DEBUG:', {
                currentUserUid: currentUser.uid,
                allWinnersCount: allWinners.length,
                myIdx,
                myRank,
                dailyRankGuesses: dailyRank?.guesses,
                allWinnersUIDs: allWinners.map(w => ({ uid: w.uid, guesses: w.guesses, rank: w.newRank }))
              });

              // TOP10: Show first 10 ranks (all players with ranks 1-10)
              const rows = [];
              const distinctRanks = [...new Set(allWinners.map(r => r.newRank))].sort((a, b) => a - b);
              const top10Ranks = new Set(distinctRanks.slice(0, 10));
              
              // Add all players in TOP10
              for (let i = 0; i < allWinners.length; i++) {
                if (top10Ranks.has(allWinners[i].newRank)) {
                  rows.push({ row: allWinners[i], globalIdx: i });
                }
              }
              
              // If I'm NOT in TOP10, add separator + my position
              const meInTop10 = myRank != null && top10Ranks.has(myRank);
              if (!meInTop10) {
                // If user is in allWinners (found by UID), show their position
                if (myIdx >= 0) {
                  rows.push({ isSeparator: true });
                  // Include all players at my rank
                  for (let i = 0; i < allWinners.length; i++) {
                    if (allWinners[i].newRank === myRank) {
                      rows.push({ row: allWinners[i], globalIdx: i });
                    }
                  }
                } 
                // If user is NOT in allWinners but we have their guesses from dailyRank, add them manually
                else if (dailyRank?.guesses) {
                  rows.push({ isSeparator: true });
                  // Calculate user's rank based on guesses
                  const myGuesses = dailyRank.guesses;
                  const betterGuesses = allWinners.filter(w => w.guesses < myGuesses);
                  const uniqueBetterGuesses = [...new Set(betterGuesses.map(w => w.guesses))];
                  const calculatedRank = uniqueBetterGuesses.length + 1;
                  
                  // Get user's display name from Firebase Auth
                  const userDisplayName = currentUser.displayName || currentUser.email?.split('@')[0] || 'Ty';
                  
                  rows.push({ 
                    row: { 
                      uid: currentUser.uid, 
                      display_name: userDisplayName,
                      guesses: myGuesses, 
                      newRank: calculatedRank 
                    }, 
                    globalIdx: allWinners.length 
                  });
                }
              }

              return (
                <>
                  <span
                    className="stats-modal-trigger"
                    onClick={() => setLbRevealReady(v => !v)}
                  >{lbRevealReady ? t("buttons.hideLeaderboard") : t("buttons.howAmIDoing")}</span>
                  <div className={`success-leaderboard${lbRevealReady ? ' success-leaderboard--visible' : ''}`}>
                    <table>
                      <tbody>
                        {rows.map((entry, i) => {
                          if (entry.isSeparator) {
                            return (
                              <tr key={`sep-${i}`} className="success-lb-separator">
                                <td colSpan="3">•••</td>
                              </tr>
                            );
                          }
                          const { row, globalIdx } = entry;
                          const isMe = row.uid === currentUser.uid;
                          const thisRank = row.newRank ?? globalIdx + 1;
                          const prevEntry = rows[i - 1];
                          const prevRank = prevEntry && !prevEntry.isSeparator
                            ? (prevEntry.row.newRank ?? prevEntry.globalIdx + 1)
                            : null;
                          const prevGuesses = prevEntry && !prevEntry.isSeparator
                            ? prevEntry.row.guesses
                            : null;
                          const showRank = prevEntry?.isSeparator || thisRank !== prevRank;
                          const showGuesses = prevEntry?.isSeparator || row.guesses !== prevGuesses;
                          return (
                            <tr key={i} className={isMe ? 'success-lb-me' : ''}>
                              <td className="success-lb-rank">{showRank ? `${thisRank}.` : ''}</td>
                              <td className="success-lb-name">
                                {isMe ? t("leaderboard.youMarker") : (row.display_name || t("leaderboard.anonymous"))}
                              </td>
                              <td className="success-lb-guesses">{showGuesses ? `${row.guesses} ${pluralPokusy(row.guesses)}` : ''}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {dailyStats && (
                      <div style={{ marginTop: '1.5rem', borderTop: '1px solid #eee', paddingTop: '1rem', textAlign: 'left' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
                          <tbody><StatsRows stats={dailyStats} t={t} /></tbody>
                        </table>
                        {dailyStats.top_wrong_guesses && dailyStats.top_wrong_guesses.length > 0 && (
                          <div style={{ marginTop: '1.5rem' }}>
                            <div style={{ fontSize: '0.75rem', color: 'inherit', marginBottom: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t("stats.topGuesses")}</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                              {dailyStats.top_wrong_guesses.slice(0, 5).map((item, i) => {
                                const rankColors = [85, 70, 50, 35, 20];
                                const bg = getBackgroundColor(rankColors[i] ?? 20);
                                return (
                                  <span key={i} style={{ background: bg, borderRadius: '999px', padding: '0.3rem 0.8rem', fontSize: '0.9rem', color: '#fff' }}>
                                    {maskWord(item.word)} <span style={{ opacity: 0.7, fontSize: '0.8rem' }}>{item.count}×</span>
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
            
            {/* Tlačítko pro zobrazení statistik když je leaderboard vypnutý */}
            {!features.leaderboard && dailyStats && (
              <span
                className="stats-modal-trigger"
                onClick={() => setIsStatsModalOpen(true)}
                style={{ marginTop: '1rem', display: 'inline-block' }}
              >{t("buttons.showDailyStats")}</span>
            )}
            
            {/* The one sentence a winner without an account should read. It used
                to offer a leaderboard place, which was the only thing an account
                gave you when it was written — there are now six, and the box
                under the guess list lists them. This says the part that is only
                true right here: the win they are looking at is not saved. */}
            {features.registration && !loadingAuth && !currentUser && (
              <div style={{ marginTop: '1.25rem', padding: '0.35rem 0 0.55rem' }}>
                <p style={{ margin: '0 0 0.9rem', fontSize: '0.6em', lineHeight: 1.5, color: 'var(--text-muted)' }}>
                  {t("leaderboard.registerPrompt")}
                </p>
                {/* Deliberately the same class and the same label as the CTA in
                    the benefits box a scroll below. They are one offer; two
                    sizes and two words for it read as two. */}
                <button
                  className="settings-btn-primary signup-cta"
                  onClick={() => otevriPrihlaseni()}
                >
                  {t("signupTeaser.cta")}
                </button>
              </div>
            )}
          </div>

        </>
      ) : (
        <div className={`daily-input-hero${isPaid && attempts.length === 0 && !heroLifted ? ' daily-input-hero--centered' : ''}`}>
        <div className="input-container">
          <div className="input-wrapper">
            <input
              ref={inputRef}
              type="text"
              value={guess}
              onChange={(e) => setGuess(e.target.value)}
              onKeyDown={handleKeyPress} // Přidána událost pro stisknutí klávesy Enter
              placeholder={t("game.input.placeholder.daily")}
              className={`guess-input${guess ? "" : " guess-input--empty"}`}
            />
            {guess.trim() && (
              <button
                type="button"
                className="guess-submit"
                aria-label={t("buttons.submitGuess")}
                onClick={handleGuess}
              >
                {enterIcon}
              </button>
            )}
            {timeoutProgress > 0 && (
              <div 
                className="timeout-progress" 
                style={{ width: `${timeoutProgress}%` }}
              ></div>
            )}
          </div>
        </div>
        {scoringPending && (
          <div className="scoring-pending" role="status">{t("game.scoringPending")}</div>
        )}
        {errorMessage && (
          <div className="error-message">{errorMessage}</div>
        )}
        </div>
      )}

      {/* Container for animated last attempt - prevents layout shift */}
      {(flyingWord || leavingWord || lastAttempt) && (
        <div className="last-attempt-container">
          {leavingWord && (
            <div
              className="attempt attempt-leaving"
              style={{
                background: `linear-gradient(to right, ${leavingWord.backgroundColor} ${leavingWord.similarity}%, var(--bar-track) 0%)`
              }}>
              <div className="attempt_label">
                {maskWord(leavingWord.word)}
              </div>
              <div className="attempt_value">
                {leavingWord.label}
              </div>
            </div>
          )}

          {flyingWord && (() => {
            // Calculate the offset from input to flying word position
            let inputOffset = -150; // default fallback
            if (inputRef.current) {
              const inputRect = inputRef.current.getBoundingClientRect();
              const flyingWordTop = inputRect.bottom + window.scrollY + 20; // 20px is margin
              // Calculate how far the element needs to travel from input position
              inputOffset = -(flyingWordTop - inputRect.top - 20);
            }
            
            return (
              <div
                className="attempt attempt-flying"
                style={{
                  background: `linear-gradient(to right, ${flyingWord.backgroundColor} ${flyingWord.similarity}%, var(--bar-track) 0%)`,
                  '--input-offset': `${inputOffset}px`
                }}>
                <div className="attempt_label">
                  {maskWord(flyingWord.word)}
                </div>
                <div className="attempt_value">
                  {flyingWord.label}
                </div>
              </div>
            );
          })()}

          {lastAttempt && !leavingWord && !flyingWord && (
            <div
              className="attempt attempt-last"
              style={{
            background: `linear-gradient(to right, ${getBackgroundColor(lastAttempt.adjustedSimilarity)} ${lastAttempt.adjustedSimilarity}%, var(--bar-track) 0%)` // Dynamická výplň
            }}>
          <div className="attempt_label">
            {maskWord(lastAttempt.guess)}
          </div>
          <div className="attempt_value">
            {getSimilarityLabel(lastAttempt.adjustedSimilarity)}
          </div>
        </div>
          )}
        </div>
      )}
        
      {(flyingWord || leavingWord || lastAttempt) && attempts.length > 1 && (
        <div className="separator separator-animate">
          <span className="separator-pill">{dailyCount} {pluralPokusy(dailyCount)}</span>
        </div>
      )}
          
      {attempts.length > 1 ? (
        <div className="attempt-list attempt-list-animate">
          {attempts.map((attempt, index) => (
          <div
            className={`attempt ${index === highlightedIndex ? "attempt-last" : ""}`}
            title={`${attempt.adjustedSimilarity.toFixed(2)}%`}
            key={index}
            style={{
            background: `linear-gradient(to right, ${getBackgroundColor(attempt.adjustedSimilarity)} ${attempt.adjustedSimilarity}%, var(--bar-track) 0%)` // Dynamická výplň
            }}
          >
            <div className="attempt_label">
              {maskWord(attempt.guess)}
            </div>
            <div className="attempt_value">
              {getTemperatureLabel(attempt.adjustedSimilarity)}
            </div>
          </div>
        ))}
        </div>
      ) : null}

      {/* Game rules — only before the first attempt, and only for players who are
          NOT paying: logged out, or signed in on the free tier. A subscriber gets
          the reduced, info-free home instead (dd9152b, 2026-07-26). The full rules
          live on the FAQ page for everyone.

          This said "only for logged-OUT visitors" until 2026-08-15, which stopped
          being true on 26 July and read as a bug when somebody checked the page
          against it. Confirmed as intended: a free signed-in player still gets the
          rules. */}
      {attempts.length === 0 && !isPaid && (
      <div className="game-description">
        <div className="description-section">
          <h3 className="section-title" onClick={(e) => {
              e.currentTarget.classList.toggle('active');
              e.currentTarget.nextElementSibling.classList.toggle('open');
            }}>{t("game.description.rulesTitle")}</h3>
            <div className="section-content">
              <p>
                {t("game.description.howToPlayLine1")}
              </p>
              <p>
                {t("game.description.howToPlayLine2")}
              </p>
            </div>
          </div>
        
        <div className="description-section">
          <h3 className="section-title" onClick={(e) => {
            e.currentTarget.classList.toggle('active');
            e.currentTarget.nextElementSibling.classList.toggle('open');
          }}>{t("game.description.similarityTitle")}</h3>
          <div className="section-content">
            <p>
              {t("game.description.similarityContent")}
            </p>
          </div>
        </div>
        
        <div className="description-section">
          <h3 className="section-title" onClick={(e) => {
            e.currentTarget.classList.toggle('active');
            e.currentTarget.nextElementSibling.classList.toggle('open');
          }}>{t("game.description.tipsTitle")}</h3>
          <div className="section-content">
            <p>
              {t("game.description.tipsContent")}
            </p>
          </div>
        </div>
        </div>
      )}

      {/* ===== END DAILY TAB ===== */}
      </div>
      )}

      {/* ===== WEEKLY / MEGA WORD TAB ===== */}
      {activeTab === "weekly" && (
      <div className="tab-content">
        {/* Signed out: the mega word is played on an account, because the daily
            allowance has to hang on one. Show what it is and how to get in —
            never a price list, this costs nothing. */}
        {megaNeedsAccount ? (
          <div className="weekly-locked">
            <div className="weekly-locked-icon">⭐</div>
            <p className="weekly-locked-text">{t("game.mega.needAccountTitle")}</p>
            <p className="weekly-locked-sub">{t("game.mega.needAccountSub")}</p>
            {/* DVĚ dveře, ne jedny. Vracející se hráč, kterému zmizela relace,
                četl jenom „Registrujte se" a bral to jako informaci, že účet
                nemá — viz otevriPrihlaseni. */}
            <div className="weekly-locked-actions">
              <button
                className="mega-cta-btn"
                onClick={() => otevriPrihlaseni("register")}
              >
                {t("buttons.registerCta")}
              </button>
              <button
                className="mega-cta-btn mega-cta-btn--ghost"
                onClick={() => otevriPrihlaseni("login")}
              >
                {t("buttons.login")}
              </button>
            </div>
          </div>
        ) : !isGuessed && !weeklyIsGuessed ? (
          /* The gate asks "have you solved today's daily word", and until
             2026-08-18 asked nothing else — so somebody who had ALREADY WON the
             mega word was sent back to earn a door they were standing behind.
             Reported publicly: "včera jsem uhodla megaslovo a když jsem se na
             něj chtěla dnes podívat, tak vidím tohle". Winning it is a week's
             achievement; the day after, it should be the first thing she sees. */
          <div className="weekly-locked">
            <div className="weekly-locked-icon">⭐</div>
            <p className="weekly-locked-text">
              {t("game.weekly.locked")}<strong>{t("game.weekly.lockedEmphasis")}</strong>!
            </p>
            <p className="weekly-locked-sub">
              {t("game.weekly.lockedSubtext")}
            </p>
            <button className="mega-cta-btn" onClick={() => setActiveTab("daily")}>
              {t("buttons.backToDaily")}
            </button>
          </div>
        ) : (
          <>
            {weeklyIsGuessed ? (
              <>
              <div className="success-message">
                ⭐ <br />{t("game.success.megaWordGuessed")}
                <div className="guessed-word-row">
                  <strong className={`guessed-word-value${wordsHidden ? ' guessed-word-value--hidden' : ''}`}>
                    {maskWord(weeklyGuessedWord)}
                  </strong>
                  {/* The same switch as every other eye — see the daily one. */}
                  <button
                    type="button"
                    className="word-visibility-btn"
                    onClick={() => setWordsHidden(v => !v)}
                    aria-label={wordsHidden ? t("word.visibility.showWeekly") : t("word.visibility.hideWeekly")}
                    title={wordsHidden ? t("word.visibility.showShort") : t("word.visibility.hideShort")}
                  >
                    {renderEyeIcon(wordsHidden)}
                  </button>
                </div>
                {t("game.success.attempts", { count: weeklyCount, attempts: pluralPokusy(weeklyCount) })}!<br />
                {features.leaderboard && !loadingAuth && currentUser && (() => {
                  const wlbRegistered = (weeklyLeaderboard || []).filter(r => !r.uid?.startsWith('user_'));
                  const wlbSource = wlbRegistered.length > 0 ? weeklyLeaderboard : (weeklyRank?.leaderboard || []);
                  const allWinners = wlbSource.filter(r => r.won && !r.uid?.startsWith('user_'));
                  if (allWinners.length === 0) return (
                    <span className="stats-modal-trigger" onClick={() => setIsWeeklyStatsModalOpen(true)}>{t("buttons.howAmIDoing")}</span>
                  );

                  const sortedByGuesses = [...allWinners].sort((a, b) => a.guesses - b.guesses);
                  const guessesToNewRank = {};
                  let currentRank = 1;
                  for (const player of sortedByGuesses) {
                    if (guessesToNewRank[player.guesses] === undefined) {
                      guessesToNewRank[player.guesses] = currentRank++;
                    }
                  }
                  allWinners.forEach(player => { player.newRank = guessesToNewRank[player.guesses]; });

                  const myIdx = allWinners.findIndex(r => r.uid === currentUser.uid);
                  const myRank = myIdx >= 0 ? allWinners[myIdx].newRank : null;

                  // TOP10: Show first 10 ranks (all players with ranks 1-10)
                  const rows = [];
                  const distinctRanks = [...new Set(allWinners.map(r => r.newRank))].sort((a, b) => a - b);
                  const top10Ranks = new Set(distinctRanks.slice(0, 10));

                  // Add all players in TOP10
                  for (let i = 0; i < allWinners.length; i++) {
                    if (top10Ranks.has(allWinners[i].newRank)) rows.push({ row: allWinners[i], globalIdx: i });
                  }

                  // If I'm NOT in TOP10, add separator + my position
                  const meInTop10 = myRank != null && top10Ranks.has(myRank);
                  if (!meInTop10) {
                    // If user is in allWinners (found by UID), show their position
                    if (myIdx >= 0) {
                      rows.push({ isSeparator: true });
                      for (let i = 0; i < allWinners.length; i++) {
                        if (allWinners[i].newRank === myRank) rows.push({ row: allWinners[i], globalIdx: i });
                      }
                    }
                    // If user is NOT in allWinners but won (weeklyIsGuessed), calculate from weeklyAttempts
                    else if (weeklyIsGuessed && weeklyAttempts.length > 0) {
                      rows.push({ isSeparator: true });
                      const myGuesses = weeklyCount;
                      const betterGuesses = allWinners.filter(w => w.guesses < myGuesses);
                      const uniqueBetterGuesses = [...new Set(betterGuesses.map(w => w.guesses))];
                      const calculatedRank = uniqueBetterGuesses.length + 1;
                      const userDisplayName = currentUser.displayName || currentUser.email?.split('@')[0] || 'Ty';
                      
                      rows.push({ 
                        row: { 
                          uid: currentUser.uid, 
                          display_name: userDisplayName,
                          guesses: myGuesses, 
                          newRank: calculatedRank 
                        }, 
                        globalIdx: allWinners.length 
                      });
                    }
                  }

                  return (
                    <>
                      <span
                        className="stats-modal-trigger"
                        onClick={() => setWeeklyLbRevealReady(v => !v)}
                      >{weeklyLbRevealReady ? t("buttons.hideLeaderboard") : t("buttons.howAmIDoing")}</span>
                      <div className={`success-leaderboard${weeklyLbRevealReady ? ' success-leaderboard--visible' : ''}`}>
                        <table>
                          <tbody>
                            {rows.map((entry, i) => {
                              if (entry.isSeparator) {
                                return (
                                  <tr key={`sep-${i}`} className="success-lb-separator">
                                    <td colSpan="3">•••</td>
                                  </tr>
                                );
                              }
                              const { row, globalIdx } = entry;
                              const isMe = row.uid === currentUser.uid;
                              const thisRank = row.newRank ?? globalIdx + 1;
                              const prevEntry = rows[i - 1];
                              const prevRank = prevEntry && !prevEntry.isSeparator ? (prevEntry.row.newRank ?? prevEntry.globalIdx + 1) : null;
                              const prevGuesses = prevEntry && !prevEntry.isSeparator ? prevEntry.row.guesses : null;
                              const showRank = prevEntry?.isSeparator || thisRank !== prevRank;
                              const showGuesses = prevEntry?.isSeparator || row.guesses !== prevGuesses;
                              return (
                                <tr key={i} className={isMe ? 'success-lb-me' : ''}>
                                  <td className="success-lb-rank">{showRank ? `${thisRank}.` : ''}</td>
                                  <td className="success-lb-name">{isMe ? t("leaderboard.youMarker") : (row.display_name || t("leaderboard.anonymous"))}</td>
                                  <td className="success-lb-guesses">{showGuesses ? `${row.guesses} ${pluralPokusy(row.guesses)}` : ''}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        {weeklyStats && (
                          <div style={{ marginTop: '1.5rem', borderTop: '1px solid #eee', paddingTop: '1rem', textAlign: 'left' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
                              <tbody><StatsRows stats={weeklyStats} t={t} /></tbody>
                            </table>
                            {weeklyStats.top_wrong_guesses && weeklyStats.top_wrong_guesses.length > 0 && (
                              <div style={{ marginTop: '1.5rem' }}>
                                <div style={{ fontSize: '0.75rem', color: 'inherit', marginBottom: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t("stats.topGuesses")}</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                  {weeklyStats.top_wrong_guesses.slice(0, 5).map((item, idx) => {
                                    const rankColors = [85, 70, 50, 35, 20];
                                    const bg = getBackgroundColor(rankColors[idx] ?? 20);
                                    return (
                                      <span key={idx} style={{ background: bg, borderRadius: '999px', padding: '0.3rem 0.8rem', fontSize: '0.9rem', color: '#fff' }}>
                                        {maskWord(item.word)} <span style={{ opacity: 0.7, fontSize: '0.8rem' }}>{item.count}×</span>
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}
                {(!features.leaderboard || loadingAuth || !currentUser) && (
                  <span className="stats-modal-trigger" onClick={() => setIsWeeklyStatsModalOpen(true)}>{t("buttons.howAmIDoing")}</span>
                )}
              </div>
              </>
            ) : megaSpent ? (
              /* Today's allowance is spent. The board above stays exactly where it
                 is — the player keeps what they built; only the input goes, with
                 the two ways forward: wait, or stop waiting. */
              <div className="mega-spent">
                <p className="mega-spent__text">
                  {t("game.mega.spent", { limit: megaLimit || 0 })}
                </p>
                <button className="mega-spent__cta" onClick={() => openPremium('mega')}>
                  {t("premium.status.getPremium")}
                </button>
              </div>
            ) : (
              <div className="input-container">
                <div className="input-wrapper">
                  <input
                    ref={weeklyInputRef}
                    type="text"
                    value={weeklyGuess}
                    onChange={(e) => setWeeklyGuess(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleWeeklyGuess(); }}
                    placeholder={t("game.input.placeholder.weekly")}
                    className={`guess-input${weeklyGuess ? "" : " guess-input--empty"}`}
                  />
                  {weeklyGuess.trim() && (
                    <button
                      type="button"
                      className="guess-submit"
                      aria-label={t("buttons.submitGuess")}
                      onClick={handleWeeklyGuess}
                    >
                      {enterIcon}
                    </button>
                  )}
                  {weeklyTimeoutProgress > 0 && (
                    <div className="timeout-progress" style={{ width: `${weeklyTimeoutProgress}%` }}></div>
                  )}
                </div>
              </div>
            )}

            {scoringPending && (
              <div className="scoring-pending" role="status">{t("game.scoringPending")}</div>
            )}
            {weeklyErrorMessage && (
              <div className="error-message">{weeklyErrorMessage}</div>
            )}

            {(weeklyFlyingWord || weeklyLeavingWord || weeklyLastAttempt) && (
              <div className="last-attempt-container">
                {weeklyLeavingWord && (
                  <div
                    className="attempt attempt-leaving"
                    style={{ background: `linear-gradient(to right, ${getBackgroundColor(weeklyLeavingWord.similarity)} ${weeklyLeavingWord.similarity}%, var(--bar-track) 0%)` }}>
                    <div className="attempt_label">{maskWord(weeklyLeavingWord.word)}</div>
                    <div className="attempt_value">{weeklyLeavingWord.label}</div>
                  </div>
                )}
                {weeklyFlyingWord && (() => {
                  let inputOffset = -150;
                  if (weeklyInputRef.current) {
                    const rect = weeklyInputRef.current.getBoundingClientRect();
                    inputOffset = -(rect.bottom + window.scrollY + 20 - rect.top - 20);
                  }
                  return (
                    <div
                      className="attempt attempt-flying"
                      style={{
                        background: `linear-gradient(to right, ${getBackgroundColor(weeklyFlyingWord.similarity)} ${weeklyFlyingWord.similarity}%, var(--bar-track) 0%)`,
                        '--input-offset': `${inputOffset}px`
                      }}>
                      <div className="attempt_label">{maskWord(weeklyFlyingWord.word)}</div>
                      <div className="attempt_value">{weeklyFlyingWord.label}</div>
                    </div>
                  );
                })()}
                {weeklyLastAttempt && !weeklyLeavingWord && !weeklyFlyingWord && (
                  <div
                    className="attempt attempt-last"
                    style={{ background: `linear-gradient(to right, ${getBackgroundColor(weeklyLastAttempt.adjustedSimilarity)} ${weeklyLastAttempt.adjustedSimilarity}%, var(--bar-track) 0%)` }}>
                    <div className="attempt_label">{maskWord(weeklyLastAttempt.guess)}</div>
                    <div className="attempt_value">{getSimilarityLabel(weeklyLastAttempt.adjustedSimilarity)}</div>
                  </div>
                )}
              </div>
            )}

            {(weeklyFlyingWord || weeklyLeavingWord || weeklyLastAttempt) && weeklyAttempts.length > 1 && (
              <div className="separator separator-animate separator--mega">
                {/* For a limited player the pill carries the allowance instead of a
                    separate line: "3 pokusy z 50". Note it counts TODAY's tips, so
                    on a board that also holds earlier days of the same week it will
                    read lower than the list beneath it — the allowance is daily,
                    the board is weekly. */}
                <span className="separator-pill">
                  {megaLeft !== null && megaLimit
                    ? `${megaLimit - megaLeft} ${pluralPokusy(megaLimit - megaLeft)} z ${megaLimit}`
                    : `${weeklyCount} ${pluralPokusy(weeklyCount)}`}
                </span>
              </div>
            )}

            {weeklyAttempts.length > 1 && (
              <div className="attempt-list attempt-list-animate">
                {weeklyAttempts.map((attempt, index) => (
                  <div
                    className={`attempt ${index === weeklyHighlightedIndex ? "attempt-last" : ""}`}
                    title={`${attempt.adjustedSimilarity.toFixed(2)}%`}
                    key={index}
                    style={{ background: `linear-gradient(to right, ${getBackgroundColor(attempt.adjustedSimilarity)} ${attempt.adjustedSimilarity}%, var(--bar-track) 0%)` }}
                  >
                    <div className="attempt_label">{maskWord(attempt.guess)}</div>
                    <div className="attempt_value">{getTemperatureLabel(attempt.adjustedSimilarity)}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Kdo megaslovo týdne vybral — jen česky, jen ve svém týdnu (25. 10.
                2026). Pod seznamem tipů (jede s ním dolů), nad „Co je megaslovo". */}
            <MegaOsobnostBox osobnost={megaOsobnost} t={t} />

            {weeklyAttempts.length === 0 && !weeklyIsGuessed && (
              <div className="game-description">
                <div className="description-section">
                  <h3 className="section-title" onClick={(e) => {
                    e.currentTarget.classList.toggle('active');
                    e.currentTarget.nextElementSibling.classList.toggle('open');
                  }}>{t("game.weekly.aboutTitle")}</h3>
                  <div className="section-content">
                    <p>
                      {t("game.weekly.aboutContentInline")}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      )}

      {/* ===== GROUP WORD TAB ===== */}
      {activeTab === "group" && hasGroupWords && (
      <div className="tab-content">
        {/* The list is for a real choice only — with one group its board opens
            straight away, and the effect that opens it lands within a render. */}
        {!openRoundGid && myGroupRounds.length === 1 && showsRound(myGroupRounds[0]) ? (
          <p className="round-empty__text">{t("game.wait.loading")}</p>
        ) : !openRoundGid ? (
          <div className="group-round-picker">
            <h2 className="group-round-picker__title">{t("groupWord.pickTitle")}</h2>
            <div className="social-list">
              {myGroupRounds.map(r => (
                <button
                  key={r.gid}
                  className={`social-card${canOpenRound(r) ? ' social-card--clickable' : ' social-card--idle'}`}
                  disabled={!canOpenRound(r)}
                  onClick={() => canOpenRound(r) && openGroupBoard(r.gid)}
                >
                  <span className="social-card__id">
                    <span className="social-card__title">
                      <span className="social-card__name">{r.group_name}</span>
                      {/* Only that there is NO word — the answer to "why is this
                          one greyed out". Who set it is gone (2026-09-07): in a
                          list about my own guessing it is a name I do not act
                          on, and my own rounds already say they are mine by
                          showing the word. */}
                      {!hasWord(r) && !r.finished_today && (
                        <span className="social-card__sub">{t("groupWord.pickNoWord")}</span>
                      )}
                      {/* Same here, and for the same reason: this list is how a
                          setter with several groups tells them apart. */}
                      {hasWord(r) && r.is_setter && r.word && (
                        <span className="social-card__sub social-card__sub--mine">{maskWord(r.word)}</span>
                      )}
                      {/* Where I stand in it: solved, part-way, or not begun.
                          Asked for 2026-08-21 — "informace, jestli má skupina
                          slovo, nebo už jsem ho uhodl nebo ještě ne".

                          On its own line, inside the title stack. Hung on the
                          right of the row it read as a label belonging to the
                          group's name rather than to me, and on a narrow phone
                          it squeezed the name it was sitting next to. */}
                      {/* Where I stand — the line this whole list exists for.
                          Solved wears the same trophy as Moje výhry, because it
                          is the same thing being reported. */}
                      {showsRound(r) && !r.is_setter && (
                        <span className={`social-card__sub${
                          r.status === 'guessed' ? ' social-card__sub--done'
                          : !r.finished_today && !r.attempts ? ' social-card__sub--waiting' : ''}`}>
                          {r.status === 'guessed' ? (
                            <>
                              <TrophyFilled size={13} className="social-card__trophy" />
                              {t("groupWord.pickSolved", { count: r.attempts })}
                            </>
                          ) : r.finished_today
                            ? t("groupWord.pickMissed")
                            : r.attempts > 0
                              ? `${r.attempts} ${pluralPokusy(r.attempts)}`
                              : t("groupWord.pickNotStarted")}
                        </span>
                      )}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : roundFailedForGid === openRoundGid ? (
          // Bez téhle větve se deska po spadlém dotazu zasekla na „Načítá se" —
          // `fetchGroupRound` při chybě `roundForGid` schválně nepřepíše, takže
          // se ta podmínka níž nikdy nesrovnala a čekalo se donekonečna.
          <div className="round-empty">
            <p className="round-empty__text">{t("groupWord.errGeneric")}</p>
            <button
              className="pill-btn pill-btn--ghost"
              onClick={() => fetchGroupRound(openRoundGid)}
            >
              {t("groupWord.retry")}
            </button>
          </div>
        ) : roundForGid !== openRoundGid ? (
          <p className="round-empty__text">{t("game.wait.loading")}</p>
        ) : (
          <div className="group-board">
            {/* Where the date sits on the daily board: which group, and who set it. */}
            <div className="group-board__head">
              <h2 className="group-board__group">{groupBoardName}</h2>
              {/* The setter gets the same sentence in the body below — say it once. */}
              {shownRound && !shownRound.is_setter && (
                <p className="group-board__by">
                  {t("groupWord.setBy", { name: shownRound.setter_name || '—' })}
                </p>
              )}
              {/* Herní svět kola: tipy se hodnotí v jeho kontextu, a bez štítku
                  hráči nechápali, proč slova mimo svět vycházejí na 0 °C. */}
              {shownRound?.tema && (
                <p className="tema-group-pill" title={t("groups.temaRoundHint")}>
                  {t("groups.temaRoundPill", { name: shownRound.tema })}
                </p>
              )}
              {myGroupRounds.length > 1 && (
                <button className="group-board__back" onClick={() => setOpenRoundGid(null)}>
                  {t("groupWord.backToList")}
                </button>
              )}
            </div>

            {!shownRound ? (
              // Jedna věta tu zakrývala tři různé pravdy. Skupinová stránka je
              // rozlišuje odjakživa, deska ne — a hráčka z toho 24. 9. 2026
              // usoudila „asi problém na serveru", protože seznam skupin jí
              // u téže skupiny ukazoval 26 pokusů.
              <p className="round-empty__text">
                {roundInactive
                  ? t("groups.inactiveBody")
                  : roundTurn?.allAsleep
                    ? t("groupWord.allAsleep")
                    : roundTurn?.name
                      ? t("groupWord.theirTurn", { name: roundTurn.name })
                      : t("groupWord.none")}
              </p>
            ) : shownRound.is_setter ? (
              <>
                <p className="round-by round-by--muted">{t("groupWord.youSetIt")}</p>
                {shownRound.word && (
                  <p className="round-by round-by--word">
                    {t("groupWord.yourWordIs", { word: maskWord(shownRound.word) })}
                  </p>
                )}
              </>
            ) : (
              <>
                {/* The SAME win box as the daily word, down to the eye. It said
                    only "Uhodnuto na 2 pokusů" — no word, and a plural nobody
                    writes. Reported 2026-08-22: "na denním slovu je v tom boxu i
                    to uhodnuté slovo ne?"

                    The word comes from my own result, which carries it once I
                    have solved it: a running round never hands its word to a
                    player, and this box only exists for somebody who has already
                    typed it. Falls back to the round's own copy, which a closed
                    round does reveal. */}
                {shownState && shownState.status === 'guessed' && (
                  <div className="success-message">
                    <div className="success-win-summary">
                      🎉 <br />{t("game.success.wordGuessed")}
                      <div className="guessed-word-row">
                        <strong className={`guessed-word-value${wordsHidden ? ' guessed-word-value--hidden' : ''}`}>
                          {maskWord(shownState.word || shownRound.word || '')}
                        </strong>
                        <button
                          type="button"
                          className="word-visibility-btn"
                          onClick={() => setWordsHidden(v => !v)}
                          aria-label={wordsHidden ? t("word.visibility.show") : t("word.visibility.hide")}
                          title={wordsHidden ? t("word.visibility.showShort") : t("word.visibility.hideShort")}
                        >
                          {renderEyeIcon(wordsHidden)}
                        </button>
                      </div>
                      {t("game.success.attempts", { count: roundAttempts.length, attempts: pluralPokusy(roundAttempts.length) })}!
                    </div>
                  </div>
                )}
                {/* The round is over — everybody solved it, or somebody called
                    it. Said here rather than by taking the screen away, and with
                    the word, which a closed round no longer keeps secret. */}
                {roundOver && (
                  <p className="round-by round-by--muted">
                    {shownState && shownState.status === 'guessed'
                      ? t("groupWord.overSolved")
                      : t("groupWord.overWord", { word: shownRound.word || '—' })}
                  </p>
                )}
                {/* The SAME board as the daily word and the archive replay: one
                    component, so the cooldown bar, the last-guess card and the
                    counted separator cannot drift apart between modes. */}
                <GuessBoard
                  maskWord={maskWord}
                  readOnly={roundOver || (shownState && shownState.status === 'guessed')}
                  value={roundGuess}
                  onChange={setRoundGuess}
                  onSubmit={submitRoundGuess}
                  placeholder={t("groupWord.placeholder")}
                  submitLabel={t("buttons.submitGuess")}
                  errorMessage={roundMsg}
                  pendingMessage={scoringPending ? t("game.scoringPending") : ""}
                  attempts={roundAttempts}
                  colorFor={getBackgroundColor}
                  labelFor={getTemperatureLabel}
                  lastLabelFor={getSimilarityLabel}
                  lastAttempt={roundLastAttempt}
                  inputRef={roundInputRef}
                  flyingWord={roundFlyingWord}
                  leavingWord={roundLeavingWord}
                  highlightedIndex={
                    roundLastAttempt
                      ? roundAttempts.findIndex((a) => a.guess === roundLastAttempt.guess)
                      : -1
                  }
                  countLabel={`${roundAttempts.length} ${pluralPokusy(roundAttempts.length)}`}
                  timeoutProgress={roundTimeoutProgress}
                />
              </>
            )}

            {/* Under the board, not above it: this is where the word is guessed,
                and a free member came here to guess. The note says what they may
                not do, after they have done what they may. */}
            {setterPremiumNote()}

            {/* Who else is closing in — the reason to play it here and not alone.
                Me excluded: my own progress IS the board above, and a row
                repeating it under my own tips is just confusing. */}
            {/* Said out loud, because both lists are the same bar: without it
                "postoloprty · Moře vody" (a tip of mine) and "Robert Hájek ·
                Hádá · 1" (a player) read as one list. Reported 2026-08-21. */}
            {(() => {
              // Me out (my progress IS the board above) and the setter out (they
              // wrote it — they are not a guesser who has not started).
              const others = shownStandings.filter(
                s => s.uid !== currentUser?.uid && s.uid !== shownRound?.setter_uid
              );
              if (!others.length) return null;
              return (
                <>
                  <h3 className="group-board__standings-title">{t("groupWord.othersTitle")}</h3>
                  <div className="social-list social-list--tight group-board__standings">
                    {others
                      .sort((a, b) => compareStandings(a, b, a.display_name, b.display_name))
                      .map(s => (
                        <div key={s.uid} className="standing-line">
                          {/* Over: "Ještě nehádá" becomes a final form —
                              nobody is going to start now. */}
                          {tempBar(s.display_name || '—', s.status, s.attempts, s.best_similarity, roundOver)}
                        </div>
                      ))}
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>
      )}

      {/* ===== COMMON SECTIONS (visible in both tabs) ===== */}

      {/* Yesterday's word / last week's mega word / the group's word from
          yesterday — each hidden behind a tap. Restored 2026-08-15 at the user's
          request, in the place and shape it had before 15e07d6.

          Tři větve, ne dvě. Původně to byl ternár „denní, jinak megaslovo",
          napsaný v době, kdy záložky byly dvě — skupinová pak spadla do toho
          „jinak" a ukazovala minulé MEGASLOVO, které tam nepatří. Od 19. 9. 2026
          má svoji větev a ukazuje slovo své skupiny (přáno hráči).

          Skupina se od denní hry v jedné věci liší: včerejší slovo tam být
          nemusí. Nikdo ho nemusel zadat, nebo zadavatel tah přeskočil — pak se
          neukáže vůbec nic, ani nabídka. */}
      {activeTab === "daily" ? (
        yesterdaysWord && (
          <div className="yesterdays-word-hint">
            {showYesterdaysWord ? (
              <p>{t("yesterday.label")}<strong>{yesterdaysWord}</strong></p>
            ) : (
              <span className="show-yesterdays-btn" onClick={() => {
                setShowYesterdaysWord(true);
                // Přihlášenému se to poznamená k účtu: archiv pak ten den nepřijme
                // jako výhru (27. 10. 2026 — dny „na 1. pokus", které hráčka nehrála).
                if (currentUser) friendsApi(`/yesterdays_word/odhaleno`, { method: "POST" }).catch(() => {});
              }}>
                {t("yesterday.show")}
              </span>
            )}
          </div>
        )
      ) : activeTab === "weekly" ? (
        lastWeeksMegaWord && (
          <div className="yesterdays-word-hint">
            {showLastWeeksMegaWord ? (
              <p>{t("lastWeek.label")}<strong>{lastWeeksMegaWord}</strong></p>
            ) : (
              <span className="show-yesterdays-btn" onClick={() => setShowLastWeeksMegaWord(true)}>
                {t("lastWeek.show")}
              </span>
            )}
          </div>
        )
      ) : activeTab === "group" ? (
        // Kontrola na `gid` není opatrnost navíc: hráč přepne skupinu a odpověď
        // té předchozí může dorazit až potom. Bez ní by u jedné skupiny chvíli
        // svítilo slovo druhé.
        groupYesterday?.word && groupYesterday.gid === openRoundGid && (
          <div className="yesterdays-word-hint">
            {showGroupYesterdaysWord ? (
              <p>{t("groupYesterday.label")}<strong>{groupYesterday.word}</strong></p>
            ) : (
              <span className="show-yesterdays-btn" onClick={() => setShowGroupYesterdaysWord(true)}>
                {t("groupYesterday.show")}
              </span>
            )}
          </div>
        )
      ) : null}
      {/* A group word was picked by a member, so the "hand-picked by the author"
          note would be a lie on that tab. */}
      {attempts.length === 0 && activeTab !== "group" && (
        <div className="curated-note">
          <p>{t("curatedNote")}</p>
        </div>
      )}
      {/* "Odemkni celou hru" has two variants. Logged OUT: the benefits of making a
          (free) account — Moje výhry, Přátelé, Skupiny — with a sign-up CTA. Free but
          signed IN: the Premium benefits with an upgrade CTA. A paying user sees
          neither (they get the reduced, info-free home above). */}
      {/* Also shown after a win, where it sits directly under the guess list.
          Winning is the moment the game has earned the right to ask — before the
          first guess it is a pitch to somebody who does not know yet whether
          they like this.
          Both free tiers get it, each their own version: signed out, the account
          benefits; signed in and free, the Premium ones. `!isPaid` is what keeps
          it away from a subscriber, who is being sold nothing and gets the win
          card and nothing else. Nothing extra is added to the card itself — the
          sentence and button up there stay signed-out only. */}
      {/* Nepřihlášenému výzva k registraci (zdarma všude), přihlášenému nabídka
          Premium — ta jen tam, kde se prodává. */}
      {/* Výzva k registraci jen tam, kde registrace je (16. 10. 2026: pl/hr/de
          se spustí až s placením a do té doby se tam účty nezakládají). */}
      {((!currentUser && features.registration) || (currentUser && sellsPremium && !isPaid)) && activeTab !== "group"
        && (attempts.length === 0 || isGuessed || weeklyIsGuessed) && (
        <div className="premium-teaser">
          <div className="premium-teaser__head">
            <div className="premium-teaser__crown"><CrownIcon size={24} /></div>
            <h3>{t(currentUser ? "premiumTeaser.title" : "signupTeaser.title")}</h3>
            <p>{t(currentUser ? "premiumTeaser.subtitle" : "signupTeaser.subtitle")}</p>
          </div>
          <div className="premium-benefits">
            {(currentUser
              ? premiumBenefitRows(ANOCINE_BENEFIT).map((r) => ({
                  e: r.e === "anocine" ? <AnocineIcon size={20} /> : r.e,
                  b: `premium.b.${r.k}`,
                }))
              : [
                  // No Anočine row here. It used to sit at the top because a
                  // free account got the first puzzle of three; from 2026-08-16
                  // it gets none, so on the list of what an ACCOUNT gives you it
                  // would be a promise nobody can keep. It stays on the Premium
                  // list above, which is now the only true place for it.
                  { e: "🔥", b: "signup.b.mega" },
                  // Second, right after the hook: the reason not to lose what you
                  // already played. Free and signed in is enough for it — the
                  // cross-device rehydration above is not Premium-gated.
                  { e: "📱", b: "signup.b.sync" },
                  { e: "🏆", b: "signup.b.wins" },
                  { e: "👥", b: "signup.b.friends" },
                  { e: "🤝", b: "signup.b.groups" },
                ]
            ).map((item) => (
              <div key={item.b} className="premium-benefit">
                <span className="premium-benefit__icon">{item.e}</span>
                <div className="premium-benefit__text">
                  <span className="premium-benefit__title">{t(`${item.b}.t`)}</span>
                  <span className="premium-benefit__desc">{t(`${item.b}.d`, { limit: megaFreeLimit })}</span>
                  {/* No link out — see the Premium page list. Whoever reads this
                      has not paid, and Anočine turns them away. */}
                </div>
              </div>
            ))}
          </div>
          <button className="settings-btn-primary premium-teaser__cta signup-cta" onClick={() => currentUser ? openPremium('premium') : otevriPrihlaseni()}>
            {t(currentUser ? "premiumTeaser.cta" : "signupTeaser.cta")}
          </button>
        </div>
      )}
      
      {/* Temporarily disabled - stats tracking needs improvement
      {dailyStats && (
        <div className="donation-stats">
          <div className="stat-item">
            <span className="stat-label">Počet dněšních hráčů:</span>
            <span className="stat-value">{dailyStats.unique_users}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Počet dnešních vítězů:</span>
            <span className="stat-value">{dailyStats.winners || 0}</span>
          </div>
        </div>
      )}
      */}
      
      
      {isStatsModalOpen && dailyStats && (
        <div className="modal" onClick={(e) => { if (e.target.classList.contains('modal')) setIsStatsModalOpen(false); }}>
          <div className="modal-content" style={{ maxHeight: 'calc(100vh - 60px)', overflowY: 'auto', width: '100%' }}>
            <div className="modal-header">
              <h2 style={{ textTransform: 'none' }}>{t("modal.statsTitle")}</h2>
              <span className="close" onClick={() => setIsStatsModalOpen(false)}>&times;</span>
            </div>
            <div style={{ padding: '1rem 0' }}>
              {/* Žebříček */}
              {features.leaderboard && leaderboard && leaderboard.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ fontSize: '0.75rem', color: 'inherit', marginBottom: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{t("leaderboard.title")}</span>
                    <button onClick={fetchLeaderboard} disabled={leaderboardLoading} style={{ background: 'none', border: 'none', color: leaderboardLoading ? '#555' : '#ff8c42', cursor: leaderboardLoading ? 'default' : 'pointer', fontSize: '0.75rem', fontFamily: 'inherit', padding: 0 }}>
                      {leaderboardLoading ? t("game.wait.loading") : t("buttons.refresh")}
                    </button>
                  </div>
                  {leaderboardLoading && (
                    <p style={{ color: '#ff8c42', fontSize: '0.85rem', marginBottom: '0.8rem', fontStyle: 'italic' }}>
                      {t("leaderboard.updating")}
                    </p>
                  )}
                  {(() => {
                    const allWinners = leaderboard.filter(r => r.won && !r.uid.startsWith('user_'));
                    if (allWinners.length === 0) return <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem' }}>{t("leaderboard.noRegisteredWinners")}</p>;
                    // Reassign ranks 1, 2, 3... for registered users only (dense ranking by guesses)
                    const sortedByGuesses = [...allWinners].sort((a, b) => a.guesses - b.guesses);
                    const guessesToNewRank = {};
                    let currentRank = 1;
                    for (const player of sortedByGuesses) {
                      if (guessesToNewRank[player.guesses] === undefined) {
                        guessesToNewRank[player.guesses] = currentRank++;
                      }
                    }
                    // Apply new ranks to all winners
                    allWinners.forEach(player => {
                      player.newRank = guessesToNewRank[player.guesses];
                    });

                    const myIdx = currentUser ? allWinners.findIndex(r => r.uid === currentUser.uid) : -1;
                    const myRank = myIdx >= 0 ? allWinners[myIdx].newRank : null;

                    // TOP10: Show first 10 ranks
                    const rows = [];
                    const distinctRanks = [...new Set(allWinners.map(r => r.newRank))].sort((a, b) => a - b);
                    const top10Ranks = new Set(distinctRanks.slice(0, 10));

                    // Add all players in TOP10
                    for (let i = 0; i < allWinners.length; i++) {
                      if (top10Ranks.has(allWinners[i].newRank)) {
                        rows.push({ row: allWinners[i], globalIdx: i });
                      }
                    }

                    // If I'm NOT in TOP10, add separator + my position
                    const meInTop10 = myRank != null && top10Ranks.has(myRank);
                    if (myIdx >= 0 && !meInTop10) {
                      rows.push({ isSeparator: true });
                      for (let i = 0; i < allWinners.length; i++) {
                        if (allWinners[i].newRank === myRank) {
                          rows.push({ row: allWinners[i], globalIdx: i });
                        }
                      }
                    }

                    return (
                      <div className="success-leaderboard success-leaderboard--visible">
                      <table>
                        <tbody>
                          {rows.map((entry, i) => {
                            if (entry.isSeparator) {
                              return (
                                <tr key={`sep-${i}`} className="success-lb-separator">
                                  <td colSpan="3">•••</td>
                                </tr>
                              );
                            }
                            const { row, globalIdx } = entry;
                            const isMe = currentUser && row.uid === currentUser.uid;
                            const thisRank = row.newRank ?? globalIdx + 1;
                            const prevEntry = rows[i - 1];
                            const prevRank = prevEntry && !prevEntry.isSeparator
                              ? (prevEntry.row.newRank ?? prevEntry.globalIdx + 1)
                              : null;
                            const prevGuesses = prevEntry && !prevEntry.isSeparator
                              ? prevEntry.row.guesses
                              : null;
                            const showRank = prevEntry?.isSeparator || thisRank !== prevRank;
                            const showGuesses = prevEntry?.isSeparator || row.guesses !== prevGuesses;
                            return (
                              <tr key={i} className={isMe ? 'success-lb-me' : ''}>
                                <td className="success-lb-rank">{showRank ? `${thisRank}.` : ''}</td>
                                <td className="success-lb-name">
                                  {isMe ? t("leaderboard.youMarker") : (row.display_name || t("leaderboard.anonymous"))}
                                </td>
                                <td className="success-lb-guesses">{showGuesses ? `${row.guesses} ${pluralPokusy(row.guesses)}` : ''}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      </div>
                    );
                  })()}
                  {features.registration && !currentUser && (
                    <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.6rem' }}>
                      <button className="auth-link-inline" onClick={() => { setIsStatsModalOpen(false); otevriPrihlaseni(); }}>{t("leaderboard.loginInline")}</button> {t("leaderboard.appearInLeaderboard")}
                    </p>
                  )}
                </div>
              )}
              {features.leaderboard && leaderboard && leaderboard.length === 0 && (
                <div style={{ marginBottom: '1.5rem', color: 'var(--text-muted)', fontSize: '0.88rem' }}>
                  {t("leaderboard.noPlayersToday")}
                  {features.registration && !currentUser && (<> <button className="auth-link-inline" onClick={() => { setIsStatsModalOpen(false); otevriPrihlaseni(); }}>{t("leaderboard.loginInline")}</button>{t("leaderboard.emptyWithCta")}</>)}
                </div>
              )}

              {/* Statistiky */}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '0.6rem 0', color: 'var(--text-dim)' }}>{t("stats.yourAttempts")}</td>
                    <td style={{ padding: '0.6rem 0', textAlign: 'right', fontWeight: 'bold' }}>{dailyCount}</td>
                  </tr>
                  <StatsRows stats={dailyStats} t={t} />
                </tbody>
              </table>
              {dailyStats.top_wrong_guesses && dailyStats.top_wrong_guesses.length > 0 && (
                <div style={{ marginTop: '1.5rem' }}>
                  <div style={{ fontSize: '0.75rem', color: 'inherit', marginBottom: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t("stats.topGuesses")}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {dailyStats.top_wrong_guesses.slice(0, 5).map((item, i) => {
                      const rankColors = [85, 70, 50, 35, 20];
                      const bg = getBackgroundColor(rankColors[i] ?? 20);
                      return (
                        <span key={i} style={{ background: bg, borderRadius: '999px', padding: '0.3rem 0.8rem', fontSize: '0.9rem', color: '#fff' }}>
                          {maskWord(item.word)} <span style={{ opacity: 0.7, fontSize: '0.8rem' }}>{item.count}×</span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isWeeklyStatsModalOpen && weeklyStats && (
        <div className="modal" onClick={(e) => { if (e.target.classList.contains('modal')) setIsWeeklyStatsModalOpen(false); }}>
          <div className="modal-content" style={{ maxHeight: 'calc(100vh - 60px)', overflowY: 'auto', width: '100%' }}>
            <div className="modal-header">
              <h2 style={{ textTransform: 'none' }}>{t("modal.weeklyStatsTitle")}</h2>
              <span className="close" onClick={() => setIsWeeklyStatsModalOpen(false)}>&times;</span>
            </div>
            
            {/* Týdenní žebříček - pouze pro přihlášené uživatele */}
            {features.leaderboard && !loadingAuth && currentUser && (() => {
              const wlbRegistered = (weeklyLeaderboard || []).filter(r => !r.uid?.startsWith('user_'));
              const wlbSource = wlbRegistered.length > 0 ? weeklyLeaderboard : (weeklyRank?.leaderboard || []);
              const allWinners = wlbSource.filter(r => r.won && !r.uid?.startsWith('user_'));
              if (allWinners.length === 0) return null;
              const myIdx = allWinners.findIndex(r => r.uid === currentUser.uid);

              // Reassign ranks 1, 2, 3... for registered users only (dense ranking by guesses)
              const sortedByGuesses = [...allWinners].sort((a, b) => a.guesses - b.guesses);
              const guessesToNewRank = {};
              let currentRank = 1;
              for (const player of sortedByGuesses) {
                if (guessesToNewRank[player.guesses] === undefined) {
                  guessesToNewRank[player.guesses] = currentRank++;
                }
              }
              allWinners.forEach(player => { player.newRank = guessesToNewRank[player.guesses]; });

              const myRank = myIdx >= 0 ? allWinners[myIdx].newRank : null;

              // TOP10: Show first 10 ranks
              const rows = [];
              const distinctRanks = [...new Set(allWinners.map(r => r.newRank))].sort((a, b) => a - b);
              const top10Ranks = new Set(distinctRanks.slice(0, 10));

              // Add all players in TOP10
              for (let i = 0; i < allWinners.length; i++) {
                if (top10Ranks.has(allWinners[i].newRank)) {
                  rows.push({ row: allWinners[i], globalIdx: i });
                }
              }

              // If I'm NOT in TOP10, add separator + my position
              const meInTop10 = myRank != null && top10Ranks.has(myRank);
              if (!meInTop10) {
                // If user is in allWinners (found by UID), show their position
                if (myIdx >= 0) {
                  rows.push({ isSeparator: true });
                  for (let i = 0; i < allWinners.length; i++) {
                    if (allWinners[i].newRank === myRank) {
                      rows.push({ row: allWinners[i], globalIdx: i });
                    }
                  }
                }
                // If user is NOT in allWinners but won (weeklyIsGuessed), calculate from weeklyAttempts
                else if (weeklyIsGuessed && weeklyAttempts.length > 0) {
                  rows.push({ isSeparator: true });
                  const myGuesses = weeklyCount;
                  const betterGuesses = allWinners.filter(w => w.guesses < myGuesses);
                  const uniqueBetterGuesses = [...new Set(betterGuesses.map(w => w.guesses))];
                  const calculatedRank = uniqueBetterGuesses.length + 1;
                  const userDisplayName = currentUser.displayName || currentUser.email?.split('@')[0] || 'Ty';
                  
                  rows.push({ 
                    row: { 
                      uid: currentUser.uid, 
                      display_name: userDisplayName,
                      guesses: myGuesses, 
                      newRank: calculatedRank 
                    }, 
                    globalIdx: allWinners.length 
                  });
                }
              }

              return (
                <div style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #eee' }}>
                  <h3 style={{ fontSize: '0.85rem', color: 'var(--text-dim)', marginBottom: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t("stats.weeklyTitle")}</h3>
                  {weeklyLeaderboardLoading && (
                    <p style={{ color: '#4a9eff', fontSize: '0.85rem', marginBottom: '0.8rem', fontStyle: 'italic' }}>
                      {t("leaderboard.updating")}
                    </p>
                  )}
                  <div className="success-leaderboard success-leaderboard--visible">
                    <table>
                      <tbody>
                        {rows.map((entry, i) => {
                          if (entry.isSeparator) {
                            return (
                              <tr key={`sep-${i}`} className="success-lb-separator">
                                <td colSpan="3">•••</td>
                              </tr>
                            );
                          }
                          const { row, globalIdx } = entry;
                          const isMe = row.uid === currentUser.uid;
                          const thisRank = row.newRank ?? globalIdx + 1;
                          const prevEntry = rows[i - 1];
                          const prevRank = prevEntry && !prevEntry.isSeparator
                            ? (prevEntry.row.newRank ?? prevEntry.globalIdx + 1)
                            : null;
                          const prevGuesses = prevEntry && !prevEntry.isSeparator
                            ? prevEntry.row.guesses
                            : null;
                          const showRank = prevEntry?.isSeparator || thisRank !== prevRank;
                          const showGuesses = prevEntry?.isSeparator || row.guesses !== prevGuesses;
                          return (
                            <tr key={i} className={isMe ? 'success-lb-me' : ''}>
                              <td className="success-lb-rank">{showRank ? `${thisRank}.` : ''}</td>
                              <td className="success-lb-name">
                                {isMe ? t("leaderboard.youMarker") : (row.display_name || t("leaderboard.anonymous"))}
                              </td>
                              <td className="success-lb-guesses">{showGuesses ? `${row.guesses} ${pluralPokusy(row.guesses)}` : ''}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}

            <div style={{ padding: '1rem 0' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '0.6rem 0', color: 'var(--text-dim)' }}>{t("stats.yourAttempts")}</td>
                    <td style={{ padding: '0.6rem 0', textAlign: 'right', fontWeight: 'bold' }}>{weeklyCount}</td>
                  </tr>
                  <StatsRows stats={weeklyStats} t={t} />
                </tbody>
              </table>
              {weeklyStats.top_wrong_guesses && weeklyStats.top_wrong_guesses.length > 0 && (
                <div style={{ marginTop: '1.5rem' }}>
                  <div style={{ fontSize: '0.75rem', color: 'inherit', marginBottom: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t("stats.topGuesses")}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {weeklyStats.top_wrong_guesses.slice(0, 5).map((item, i) => {
                      const rankColors = [85, 70, 50, 35, 20];
                      const bg = getBackgroundColor(rankColors[i] ?? 20);
                      return (
                        <span key={i} style={{ background: bg, borderRadius: '999px', padding: '0.3rem 0.8rem', fontSize: '0.9rem', color: '#fff' }}>
                          {maskWord(item.word)} <span style={{ opacity: 0.7, fontSize: '0.8rem' }}>{item.count}×</span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cookie banner. Sits above the bottom navigation but never over the guess
          input — it must not be something you have to dismiss to play, and there
          is no cookie wall: refusing costs the visitor nothing. The two buttons
          are deliberately the same size and weight. */}
      {consentOpen && (
        <div className="consent-bar" role="dialog" aria-label={t("footer.cookies")}>
          <p className="consent-bar__text">
            {t("consent.text")}{' '}
            <a className="consent-bar__more" href={LEGAL_LINKS.privacy}>{t("consent.more")}</a>
          </p>
          <div className="consent-bar__actions">
            <button className="consent-btn" onClick={() => chooseConsent('granted')}>
              {t("consent.accept")}
            </button>
            <button className="consent-btn" onClick={() => chooseConsent('denied')}>
              {t("consent.deny")}
            </button>
          </div>
        </div>
      )}

      {/* Where the players talk to each other. Above the footer rather than in
          it: these are an invitation, not small print. rel=noopener because they
          open in a new tab and the target must not get a handle on this one. */}
      {COMMUNITY && (
        <div className="community">
          <p className="community__label">{t("community.label")}</p>
          <div className="community__links">
            <a className="community__link" href={COMMUNITY.facebook} target="_blank" rel="noopener noreferrer"
              aria-label={t("community.facebook")} title={t("community.facebook")}>
              <FacebookIcon size={22} />
            </a>
            <a className="community__link" href={COMMUNITY.discord} target="_blank" rel="noopener noreferrer"
              aria-label={t("community.discord")} title={t("community.discord")}>
              <DiscordIcon size={22} />
            </a>
          </div>
        </div>
      )}

      <footer className="site-footer">
        {/* Grouped, not one row of eight. The list had grown to where finding the
            cookie settings meant reading every entry, and the three kinds of link
            in it answer three different questions: how the game works, how to
            reach a person, and what we are legally obliged to publish. The
            headings are what make it scannable — without them this is the same
            flat list with gaps in it. */}
        <nav className="footer-groups">
          <div className="footer-group">
            <h3 className="footer-group__title">{t("footer.groupGame")}</h3>
            <button className="footer-link" onClick={() => goPage('faq')}>{t("footer.faq")}</button>
            {AUTHORS_PAGE && (
              <a className="footer-link" href={AUTHORS_PAGE}>{t("footer.authors")}</a>
            )}
          </div>

          <div className="footer-group">
            <h3 className="footer-group__title">{t("footer.groupHelp")}</h3>
          <button className="footer-link" onClick={() => {
            setContactMsg(null);
            // Prefill name + email from the signed-in account (editable) so a
            // logged-in user doesn't retype what we already know.
            setContactForm(f => ({
              ...f,
              name: f.name || currentUser?.displayName || '',
              email: f.email || currentUser?.email || '',
            }));
            setContactOpen(true);
          }}>
            {t("footer.contact")}
            {/* The badge is the whole visible half of the benefit: the mail is
                marked for us, this is what says so to them. */}
            {isPaid && <span className="footer-link__badge">{t("footer.priority")}</span>}
          </button>
            <button className="footer-link" onClick={() => {
              setBugMsg(null);
              setBugForm(f => ({ ...f, email: f.email || currentUser?.email || '' }));
              setBugOpen(true);
            }}>
              {t("footer.reportBug")}
              {/* A subscriber's bug report is marked and read first exactly like
                  their message is, so the badge belongs on both — one of the two
                  wearing it read as if the other did not count. */}
              {isPaid && <span className="footer-link__badge">{t("footer.priority")}</span>}
            </button>
          </div>

          {/* The legal group is laid out differently on purpose — see the CSS.
              Four obligatory entries nobody comes here to read should not take up
              four times the room of the two that people actually want. */}
          <div className="footer-group footer-group--legal">
            <h3 className="footer-group__title">{t("footer.groupLegal")}</h3>
            {/* The contract lives at a real URL as plain HTML — that is the
                published, indexed copy the payment provider was shown, and it
                stays readable when the bundle does not load. */}
            <a className="footer-link" href={LEGAL_LINKS.terms}>{t("footer.terms")}</a>
            <a className="footer-link" href={LEGAL_LINKS.privacy}>{t("footer.privacy")}</a>
            {/* Germany only: § 5 DDG wants the provider identified on its own
                page, reachable from everywhere, not folded into the terms. */}
            {LEGAL_LINKS.imprint && (
              <a className="footer-link" href={LEGAL_LINKS.imprint}>{t("footer.imprint")}</a>
            )}
            {LEGAL_LINKS.withdrawalInfo && (<>
              <a className="footer-link" href={LEGAL_LINKS.withdrawalInfo}>{t("footer.withdrawalInfo")}</a>
              <a className="footer-link" href={LEGAL_LINKS.cancel}>{t("footer.cancelContracts")}</a>
              <a className="footer-link" href={LEGAL_LINKS.withdraw}>{t("footer.withdraw")}</a>
            </>)}
            {/* Withdrawing consent has to be as easy as giving it. */}
            {CONSENT_BANNER && (
              <button className="footer-link" onClick={() => setConsentOpen(true)}>{t("footer.cookies")}</button>
            )}
          </div>
        </nav>
        <p className="footer-thanks">{t("footer.thanks")}</p>
        <div className="footer-langs" aria-label={t("footer.languages")}>
          {LANGUAGE_LINKS.map(l => (
            <a key={l.code} href={`https://${l.domain}`} className="footer-lang" title={l.name} aria-label={l.name}>{l.flag}</a>
          ))}
        </div>
        <p className="footer-copyright">{t("footer.copyright")}</p>
        {/* Last line of the page. It stays ON every page — Stripe's review wanted
            the operator findable from the game itself, not only inside the terms —
            but it is the smallest print here rather than the headline above the
            flags, which is what it had been. */}
        <p className="footer-legal">{t("footer.operator")}</p>
      </footer>
      

      </>
      )}

      {/* Leaderboard-only modal (z account menu) — outside game block so it works on all pages */}
      {features.leaderboard && isTop10ModalOpen && (
        <div className="modal" onClick={(e) => { if (e.target.classList.contains('modal')) setIsTop10ModalOpen(false); }}>
          <div className="modal-content" style={{ width: '100%' }}>
            <div className="modal-header">
              <h2 style={{ textTransform: 'none' }}>{t("modal.top10Title")}</h2>
              <span className="close" onClick={() => setIsTop10ModalOpen(false)}>&times;</span>
            </div>
            <div style={{ padding: '1rem 0' }}>
              {leaderboardLoading && <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem' }}>{t("game.wait.loading")}</p>}
              {!leaderboardLoading && leaderboardError && <p style={{ color: '#ff6b6b', fontSize: '0.9rem', marginTop: '1rem' }}>{leaderboardError}</p>}
              {!leaderboardLoading && !leaderboardError && leaderboard === null && <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem' }}>{t("game.wait.loading")}</p>}
              {leaderboard !== null && (() => {
                const lbRegistered = (leaderboard || []).filter(r => !r.uid?.startsWith('user_'));
                const lbSource = lbRegistered.length > 0 ? leaderboard : (dailyRank?.top10 || []);
                const allWinners = lbSource.filter(r => r.won !== false && !r.uid?.startsWith('user_'));
                if (allWinners.length === 0) return <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem' }}>{t("leaderboard.noRegisteredWinners")}</p>;
                // Reassign ranks 1, 2, 3... for registered users only (dense ranking by guesses)
                const sortedByGuesses = [...allWinners].sort((a, b) => a.guesses - b.guesses);
                const guessesToNewRank = {};
                let currentRank = 1;
                for (const player of sortedByGuesses) {
                  if (guessesToNewRank[player.guesses] === undefined) {
                    guessesToNewRank[player.guesses] = currentRank++;
                  }
                }
                // Apply new ranks to all winners
                allWinners.forEach(player => {
                  player.newRank = guessesToNewRank[player.guesses];
                });
                // Show all players in top 10 ranks (using new ranks)
                const distinctRanks = [...new Set(allWinners.map(r => r.newRank))].sort((a, b) => a - b);
                const top10Ranks = new Set(distinctRanks.slice(0, 10));
                const winners = allWinners.filter(r => top10Ranks.has(r.newRank));
                const myIdx = currentUser ? winners.findIndex(r => r.uid === currentUser.uid) : -1;
                const myRank = myIdx >= 0 ? winners[myIdx].newRank : null;

                // Build rows: top 10 ranks, then separator + me if not already shown
                const rows = [];
                for (let i = 0; i < winners.length; i++) {
                  rows.push({ row: winners[i], globalIdx: i });
                }
                // If user is not in top 10 ranks, add separator + their rank
                if (myRank == null && currentUser) {
                  const fullMyIdx = allWinners.findIndex(r => r.uid === currentUser.uid);
                  if (fullMyIdx >= 0) {
                    const fullMyRank = allWinners[fullMyIdx].newRank;
                    rows.push({ isSeparator: true });
                    for (let i = 0; i < allWinners.length; i++) {
                      if (allWinners[i].newRank === fullMyRank) {
                        rows.push({ row: allWinners[i], globalIdx: i });
                      }
                    }
                  }
                }
                return (
                  <div className="success-leaderboard success-leaderboard--visible">
                  <table>
                    <tbody>
                      {rows.map((entry, i) => {
                        if (entry.isSeparator) {
                          return (
                            <tr key={`sep-${i}`} className="success-lb-separator">
                              <td colSpan="3">•••</td>
                            </tr>
                          );
                        }
                        const { row, globalIdx } = entry;
                        const isMe = currentUser && row.uid === currentUser.uid;
                        const thisRank = row.newRank ?? globalIdx + 1;
                        const prevEntry = rows[i - 1];
                        const prevRank = prevEntry && !prevEntry.isSeparator
                          ? (prevEntry.row.newRank ?? prevEntry.globalIdx + 1)
                          : null;
                        const prevGuesses = prevEntry && !prevEntry.isSeparator
                          ? prevEntry.row.guesses
                          : null;
                        const showRank = prevEntry?.isSeparator || thisRank !== prevRank;
                        const showGuesses = prevEntry?.isSeparator || row.guesses !== prevGuesses;
                        return (
                          <tr key={i} className={isMe ? 'success-lb-me' : ''}>
                            <td className="success-lb-rank">{showRank ? `${thisRank}.` : ''}</td>
                            <td className="success-lb-name">
                              {isMe ? t("leaderboard.youMarker") : (row.display_name || t("leaderboard.anonymous"))}
                            </td>
                            <td className="success-lb-guesses">{showGuesses ? `${row.guesses} ${pluralPokusy(row.guesses)}` : ''}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Signed out: the daily/mega switcher on its own, so tapping Megaslovo is
          not a one-way door. Same markup and same handler as the signed-in bar —
          switchWordMode already renders the sign-up wall for mega without
          needing an account. */}
      {modesOnlyNav && (
        <nav className="bottom-nav bottom-nav--modes-only">
          <div className="bottom-nav__modes">
            <div className="bottom-nav__modes-track" role="tablist">
              <span
                className="segment-indicator"
                style={{ width: "calc((100% - 6px) / 2)", transform: `translateX(${boardMode === "mega" ? 100 : 0}%)` }}
              />
              <button className={`bottom-nav__mode${boardMode === 'daily' ? ' is-active' : ''}`} role="tab" aria-selected={boardMode === 'daily'} onClick={() => switchWordMode('daily')}>
                {isGuessed && <TrophyIcon size={14} />}
                <span>{t("friends.daily")}</span>
              </button>
              <button className={`bottom-nav__mode${boardMode === 'mega' ? ' is-active' : ''}${megaLocked ? ' is-locked' : ''}`} role="tab" aria-selected={boardMode === 'mega'} onClick={() => switchWordMode('mega')}>
                {megaLocked ? <LockIcon size={12} /> : (weeklyIsGuessed && <TrophyIcon size={14} />)}
                <span>{t("friends.mega")}</span>
              </button>
            </div>
          </div>
        </nav>
      )}

      {/* Bottom section switcher (mobile-style); hidden while the guess keyboard is up */}
      {bottomNavVisible && (
        <nav className={`bottom-nav${navSection ? ` bottom-nav--${navSection}` : ''}`}>
          {/* upper layer: daily/mega word-mode, drives every section below.
              Always rendered so it can slide in/out; `is-hidden` collapses it
              on sections with no daily/mega distinction. */}
          <div className={`bottom-nav__modes${showWordModes ? '' : ' is-hidden'}`} aria-hidden={!showWordModes}>
            {/* full-width transparent blur band; the tabs keep their own pill */}
            <div className="bottom-nav__modes-track" role="tablist">
              {/* the active fill is one element that slides between segments,
                  rather than each button lighting up in place */}
              <span
                className="segment-indicator"
                style={{
                  width: `calc((100% - 6px) / ${groupModeAvailable ? 3 : 2})`,
                  transform: `translateX(${boardMode === "group" ? 200 : boardMode === "mega" ? 100 : 0}%)`,
                }}
              />
              <button className={`bottom-nav__mode${boardMode === 'daily' ? ' is-active' : ''}`} role="tab" aria-selected={boardMode === 'daily'} tabIndex={showWordModes ? 0 : -1} onClick={() => switchWordMode('daily')}>
                {isGuessed && <TrophyIcon size={14} />}
                <span>{t("friends.daily")}</span>
              </button>
              <button className={`bottom-nav__mode${boardMode === 'mega' ? ' is-active' : ''}${megaLocked ? ' is-locked' : ''}`} role="tab" aria-selected={boardMode === 'mega'} tabIndex={showWordModes ? 0 : -1} onClick={() => switchWordMode('mega')}>
                {megaLocked ? <LockIcon size={12} /> : (weeklyIsGuessed && <TrophyIcon size={14} />)}
                <span>{t("friends.mega")}</span>
              </button>
              {/* Only while some group of mine has a word waiting AND this page can
                  show it — see groupModeAvailable. */}
              {groupModeAvailable && (
                <button className={`bottom-nav__mode${boardMode === 'group' ? ' is-active' : ''}`} role="tab" aria-selected={boardMode === 'group'} tabIndex={showWordModes ? 0 : -1} onClick={() => switchWordMode('group')}>
                  <span>{t("friends.groupWord")}</span>
                </button>
              )}
            </div>
          </div>
          {/* lower layer: section tabs */}
          <div className="bottom-nav__tabs">
            {/* No section owns the wins page → hide the indicator so it doesn't
                falsely light up "Hádat". */}
            {['game', 'friends', 'friendsManage', 'groups', 'group', 'groupAdmin'].includes(currentPage) && (
            <span
              className="segment-indicator"
              style={{
                width: "calc((100% - 40px) / 3)",
                transform: `translateX(${
                  ['friends', 'friendsManage'].includes(currentPage) ? 100
                    : ['groups', 'group', 'groupAdmin'].includes(currentPage) ? 200
                    : 0
                }%)`,
              }}
            />
            )}
          <button className={`bottom-nav__tab bottom-nav__tab--game${currentPage === 'game' ? ' is-active' : ''}`} onClick={() => goPage('game')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><path d="M12 2c1 3 3 4 3 7a3 3 0 0 1-6 0c0-1 .5-2 1-3" /><path d="M7 13a5 5 0 1 0 10 0c0-2-1-3-2-4-1 2-2 2-3 2-2 0-2-2-1-4-2 1-4 4-4 6z" /></svg>
            <span>{t("nav.guess")}</span>
          </button>
          <button className={`bottom-nav__tab bottom-nav__tab--friends${['friends', 'friendsManage'].includes(currentPage) ? ' is-active' : ''}`} aria-label={t("nav.friends")} onClick={openFriends}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
            <span>{t("nav.friends")}</span>
          </button>
          <button className={`bottom-nav__tab bottom-nav__tab--groups${['groups', 'group', 'groupAdmin'].includes(currentPage) ? ' is-active' : ''}`} aria-label={t("nav.groups")} onClick={onGroupsTab}>
            <i className="bottom-nav__icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="22" height="22"><circle cx="12" cy="7" r="3" /><circle cx="5.5" cy="9.5" r="2.5" /><circle cx="18.5" cy="9.5" r="2.5" /><path d="M8 20v-1a4 4 0 0 1 8 0v1" /><path d="M2 20v-.5A3.5 3.5 0 0 1 5.5 16" /><path d="M22 20v-.5a3.5 3.5 0 0 0-3.5-3.5" /></svg>
            {/* Červený puntík jako u zvonečku: v některé skupině jsem na řadě. */}
            {myTurnGids.size > 0 && <i className="turn-dot turn-dot--nav" aria-hidden="true" />}
            </i>
            <span className="bottom-nav__label">{(groups && groups.length === 1) ? groups[0].name : t("nav.groups")}</span>
          </button>
          </div>
        </nav>
      )}

    </div>
  );
}

function AppWithAuth() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}

export default AppWithAuth;